/**
 * Diccionario de la interfaz.
 *
 * El español es la fuente de verdad: es el idioma de los usuarios de Raza Pro
 * —contratistas latinos en EE. UU.— y el que promete la landing. El inglés se
 * deriva de él, no al revés.
 *
 * Por qué un diccionario propio y no `i18next`: hacen falta dos idiomas,
 * interpolación y plurales. Eso son ~80 líneas. `i18next` + `react-i18next`
 * pesan ~19 KB comprimidos sobre un bundle que ya va en 1.39 MB y que tarda más
 * de un segundo en montar — y el arranque lento es un problema medido de esta
 * app, no una hipótesis. Los puntos de llamada usan `t('clave')`, así que
 * cambiar a `i18next` más adelante es mecánico.
 *
 * Cómo añadir texto:
 *   1. Ponlo en ES con una clave por AREA: 'nav.clientes', no 'clientes'.
 *   2. Ponlo en EN. Si falta, TypeScript no compila — a propósito: media
 *      traducción es peor que ninguna, porque el usuario no sabe qué esperar.
 *   3. Plural: usa `{ uno, otros }` y llama con `t('clave', { count: n })`.
 */

type Plural = { uno: string; otros: string };
export type Texto = string | Plural;

export const ES = {
    // ── Navegación ──────────────────────────────────────────────
    'nav.grupo.ventas': 'Ventas',
    'nav.grupo.operaciones': 'Operaciones',
    'nav.grupo.equipo': 'Equipo',
    'nav.panel': 'Panel',
    'nav.clientes': 'Clientes',
    'nav.estimados': 'Estimados',
    'nav.precios': 'Precios',
    'nav.seguimientos': 'Seguimientos',
    'nav.proyectos': 'Proyectos',
    'nav.rutas': 'Rutas de servicio',
    'nav.inventario': 'Inventario',
    'nav.finanzas': 'Finanzas',
    'nav.gente': 'Gente',
    'nav.reloj': 'Reloj',
    'nav.agenda': 'Agenda',
    'nav.chat': 'Historial de chat',
    'nav.tienda': 'Tienda Raza',
    'nav.ajustes': 'Ajustes',
    'nav.salir': 'Cerrar sesión',
    'nav.abrirMenu': 'Abrir menú',
    'nav.cerrarMenu': 'Cerrar menú',
    'nav.colapsar': 'Colapsar menú',
    'nav.expandir': 'Expandir menú',

    // ── Idioma ──────────────────────────────────────────────────
    'idioma.titulo': 'Idioma',
    'idioma.es': 'Español',
    'idioma.en': 'Inglés',

    // ── Estados comunes ─────────────────────────────────────────
    'estado.cargando': 'Cargando Raza Pro…',
    'estado.reintentar': 'Reintentar',
    'estado.sinConexion': 'Revisa tu conexión e inténtalo de nuevo.',
    'estado.errorClientes': 'No se pudieron cargar los clientes',
    'estado.errorProspectos': 'No se pudieron cargar los prospectos',

    // ── Panel ───────────────────────────────────────────────────
    'panel.bienvenida': 'Qué gusto verte, {nombre}',
    'panel.clientes': { uno: '{count} cliente', otros: '{count} clientes' },
    'panel.prospectosNuevos': { uno: '{count} prospecto nuevo', otros: '{count} prospectos nuevos' },
    'panel.proyectosActivos': { uno: '{count} proyecto activo', otros: '{count} proyectos activos' },
    'panel.empezarTrabajo': 'Empezar un trabajo',
    'panel.clientesRecientes': 'Clientes recientes',
    'panel.verTodos': 'Ver todos',
    'panel.sinClientes': 'Todavía no tienes clientes.',
    'panel.sinClientesPista': 'Toca Cliente arriba para agregar el primero.',

    // ── Asistente de configuración ──────────────────────────────
    'wiz.paso.perfil': 'Tu negocio',
    'wiz.paso.contacto': 'Contacto',
    'wiz.paso.logo': 'Logo',
    'wiz.paso.temporada': 'Temporada',
    'wiz.paso.empleados': 'Empleados',
    'wiz.paso.gastos': 'Gastos fijos',
    'wiz.paso.equipo': 'Maquinaria',
    'wiz.paso.precios': 'Precios',
    'wiz.paso.reglas': 'Reglas',
    'wiz.paso.resumen': 'Resumen',
    'wiz.atras': 'Atrás',
    'wiz.cancelar': 'Cancelar',
    'wiz.siguiente': 'Siguiente',
    'wiz.siguienteLargo': 'Siguiente paso',
    'wiz.guardando': 'Guardando…',
    'wiz.guardarCerrar': 'Guardar y cerrar',
    'wiz.arrancando': 'Arrancando…',
    'wiz.terminar': 'Terminar',
    'wiz.terminarLargo': 'Terminar y entrar',
    'wiz.falta': 'Para terminar la configuración falta: {que}',
    'wiz.faltaMas': { uno: '(y {count} cosa más)', otros: '(y {count} cosas más)' },

    // ── Pantallas ───────────────────────────────────────────────
    'prospecto.nombre.aria': 'Nombre del posible cliente',
    'prospecto.nombre.placeholder': 'Nombre del cliente',
    'prospecto.etapa.aria': 'En qué va este cliente',
    'prospecto.creado': 'Creado el ',
    'prospecto.cerrar.aria': 'Cerrar los datos del cliente',
    'prospecto.proximoPaso.titulo': ' Próximo paso',
    'prospecto.proximoPaso.vence': 'Para el ',
    'prospecto.proximoPaso.hecho.title': 'Marcar el paso como hecho',
    'prospecto.proximoPaso.hecho': 'Hecho',
    'prospecto.proximoPaso.aria': 'Próximo paso',
    'prospecto.proximoPaso.placeholder': '¿Qué sigue con este cliente?',
    'prospecto.proximoPaso.fecha.aria': 'Fecha del próximo paso',
    'prospecto.contacto.titulo': 'Datos de contacto',
    'prospecto.telefono.aria': 'Teléfono',
    'prospecto.telefono.placeholder': 'Teléfono',
    'prospecto.correo.aria': 'Correo electrónico',
    'prospecto.correo.placeholder': 'Correo',
    'prospecto.direccion.placeholder': 'Escribe la dirección para buscarla…',
    'prospecto.valor.etiqueta': 'Precio estimado',
    'prospecto.tipoTrabajo.etiqueta': 'Tipo de trabajo',
    'prospecto.tipoTrabajo.sinDefinir': 'Falta escoger',
    'prospecto.notas.etiqueta': 'Notas',
    'prospecto.notas.placeholder': 'Lo que pide el cliente, código del portón, detalles de la obra…',
    'prospecto.marcarPerdido': 'Se perdió el trabajo',
    'prospecto.convertir': 'Pasar a cliente',
    'prospecto.yaEsCliente': ' Ya es cliente',
    'prospecto.crearPropuesta': 'Hacer propuesta',
    'prospecto.propuestas.titulo': ' Propuestas',
    'prospecto.propuestas.nueva': 'Nueva',
    'prospecto.propuestas.vacio': 'Todavía no le has cotizado nada. Al hacerle una propuesta no se crea ningún cliente: pasa a cliente el día que la marques como pagada.',
    'prospecto.propuestas.sinTitulo': 'Propuesta sin título',

    // ── Propuestas hechas a un prospecto ────────────────────────
    'propuestaProspecto.insignia': 'Prospecto',
    'propuestaProspecto.insigniaTitulo': 'Propuesta hecha a un prospecto. Al marcarla como pagada o aceptada se crea la ficha del cliente.',
    'propuestaProspecto.convertido': '{nombre} ya es cliente',
    'propuestaProspecto.errorConvertir': 'No se pudo crear la ficha de {nombre}. Inténtalo otra vez.',
    'herrEst.zona.destino': 'Agregar partidas a la zona',
    'herrEst.zona.sinZona': 'Sin zona',
    'herrEst.zona.nombrePlaceholder': 'Nombre de la zona…',
    'herrEst.rapido.titulo': 'Agregar rápido',
    'herrEst.item.manoDeObraGeneral': 'Mano de obra general',
    'herrEst.buscar.placeholder': 'Buscar en tu lista de precios…',
    'herrEst.vacio.titulo': 'Tu lista de precios está vacía',
    'herrEst.vacio.texto': 'Agrega tu primer material y te queda guardado para todos tus estimados.',
    'herrEst.grafica.centro': 'Costo',
    'herrEst.margen.titulo': 'Margen neto',
    'herrEst.margen.ganancia': 'Ganancia estimada',
    'herrEst.margen.sliderAria': 'Margen que quieres sacar',
    'herrEst.margen.meta': 'Meta (',
    'herrEst.margen.bloqueado': 'Este estimado ya salió al cliente: el margen queda como lo vio.',
    // Las DOS lecturas del mismo margen (plan de precios v2, A.5): el de TU
    // trabajo —el que persigue el dial— y el facturado, con el pase directo
    // dentro. El segundo solo se menciona cuando difiere del primero.
    'herrEst.margen.propio': 'de tu trabajo',
    'herrEst.margen.facturado': 'facturado',
    'herrEst.margen.objetivoReal': 'Objetivo {objetivo} · real {real}',
    // Lo que se dice cuando el dial NO pudo mover los precios. Sin esto quedan
    // dos números contradictorios en pantalla —el pulgar en 45 y el número
    // grande en 23,9— sin una palabra que lo explique.
    'herrEst.margen.pedido': 'Pediste {pedido} y no se movió ningún precio.',
    'herrEst.margen.motivo.minimoDeServicio': 'El precio lo fija tu mínimo de servicio, no las partidas: por debajo de ese mínimo el cliente paga el mínimo, así que mover las tarifas no cambia lo que se cobra.',
    'herrEst.margen.motivo.sinTrabajoPropio': 'Aquí no hay trabajo propio que escalar: todo lo que tienes es pase directo, y un pase directo se remite a costo.',
    'herrEst.margen.motivo.objetivoInvalido': 'El margen se mide sobre el precio, así que tiene que ser menor que 100%.',
    'herrEst.margen.motivo.margenIndependiente': 'El cobro de este estimado no responde al dial —lo habitual es un descuento plano que se come el subtotal—, así que el margen sale igual con cualquier factor.',
    'herrEst.margen.motivo.techoInalcanzable': 'Con estos costos, lo máximo que da este trabajo es {techo}. Para pasar de ahí hay que bajar el costo, no subir la tarifa.',
    'herrEst.margen.motivo.fueraDeRango': 'Ese margen queda fuera de lo que este estimado puede recorrer subiendo o bajando sus precios.',
    'herrEst.margen.motivo.codo': 'No se pudo confirmar el factor —se cruzó el mínimo de servicio o un descuento que se come el subtotal—, así que no se mueve ningún precio a ciegas.',
    'herrEst.desglose.titulo': 'A dónde se va el dinero',
    'herrEst.desglose.total': 'Total antes de impuestos',
    'herrEst.descuento.titulo': 'Descuento',
    'herrEst.descuento.dolaresAria': 'Descuento en dólares',
    'herrEst.descuento.porcentajeAria': 'Descuento en porcentaje',
    'herrEst.descuento.pasaSubtotal': 'El descuento es más grande que el subtotal — solo se aplicó  (le sigue el monto en el <span>; deja el espacio final)',
    'herrEst.anticipo.sliderAria': 'Porcentaje del anticipo',
    'herrEst.anticipo.alFirmar': 'Se paga al firmar',
    'herrEst.cargos.titulo': 'Cargos e impuestos',
    'herrEst.cargos.viaje': 'Cargo por viaje (',
    'herrEst.impuesto.titulo': 'Cobrar impuesto',
    'herrEst.impuesto.tasa': '% de impuesto',
    'herrEst.impuesto.manoDeObra': '¿Cobrar impuesto a la mano de obra?',
    'ajustesNeg.rapido.titulo': 'De un vistazo',
    'ajustesNeg.rapido.sub': 'Los tres números que mandan en cada estimado.',
    'ajustesNeg.stat.puntoEquilibrio': 'Lo que te cuesta la hora',
    'ajustesNeg.stat.tarifaMeta': 'Lo que debes cobrar por hora',
    'ajustesNeg.rapido.verQueArreglar': 'Ábrelo para ver qué hay que arreglar →',
    'ajustesNeg.perfil.titulo': 'Perfil del negocio',
    'ajustesNeg.perfil.sub': 'Tu logo y tus datos de contacto: lo que sale en los estimados.',
    'ajustesNeg.perfil.subirLogo': 'Subir logo',
    'ajustesNeg.perfil.nombreNegocio': 'Nombre del negocio',
    'ajustesNeg.perfil.moneda': 'Moneda',
    'ajustesNeg.perfil.zonaServicio': 'Zona donde trabajas',
    'ajustesNeg.perfil.errorLogo': 'No se pudo subir el logo.',
    'ajustesNeg.temporada.titulo': 'Tu año de trabajo',
    'ajustesNeg.temporada.sub': 'Define tu año de trabajo para saber cuánto tienes que recuperar de gastos fijos por hora.',
    'ajustesNeg.temporada.semanasAnio': 'Semanas al año',
    'ajustesNeg.temporada.diasSemana': 'Días por semana',
    'ajustesNeg.temporada.horasDia': 'Horas por día',
    'ajustesNeg.empleados.titulo': 'Empleados para calcular costos',
    'ajustesNeg.empleados.dueno.pregunta': '¿Tú también andas en la obra?',
    'ajustesNeg.empleados.dueno.texto': 'A casi todos los dueños se les olvida agregarse. Si tú también trabajas en las obras, tu costo de mano de obra tiene que estar aquí; si no, tu punto de equilibrio te sale más bajo de lo que en verdad es.',
    'ajustesNeg.empleados.dueno.boton': 'Agregarme como dueño ($60k/año)',
    'ajustesNeg.campo.puesto': 'Puesto',
    'ajustesNeg.empleados.burden.ayuda': '(FICA + WC + prestaciones, normal 20–30%)',
    'ajustesNeg.empleados.cobrable': 'Su tiempo se le cobra al cliente (anda en la obra)',
    'ajustesNeg.empleados.disponiblePct': 'Horas disponibles %',
    'ajustesNeg.empleados.cobrablePct': 'Horas que sí cobras %',
    'ajustesNeg.empleados.vacio.titulo': 'Todavía no hay empleados',
    'ajustesNeg.gastos.titulo': 'Gastos fijos',
    'ajustesNeg.gastos.sub': 'Lo que pagas aunque no tengas trabajo: seguro, renta, software.',
    'ajustesNeg.gastos.agregar': 'Agregar gasto',
    'ajustesNeg.maquinaria.titulo': 'Costo de tu maquinaria',
    'ajustesNeg.maquinaria.agregar': 'Agregar maquinaria',
    'ajustesNeg.precios.margenMeta': 'Ganancia limpia que quieres dejar (%)',
    'clienteDet.cerrarFicha': 'Cerrar los datos del cliente',
    'clienteDet.cerrar': 'Cerrar',
    'clienteDet.editarCliente': 'Editar cliente',
    'clienteDet.borrarCliente': 'Borrar cliente',
    'clienteDet.tab.resumen': 'Resumen',
    'clienteDet.tab.proyectos': 'Proyectos',
    'clienteDet.tab.estimados': 'Estimados',
    'clienteDet.stat.totalCobrado': 'Total cobrado',
    'clienteDet.stat.porCerrar': 'Por cerrar',
    'clienteDet.stat.porCerrar.pista': { uno: '{count} estimado sin contestar', otros: '{count} estimados sin contestar' },
    'clienteDet.stat.trabajosActivos': 'Trabajos activos',
    'clienteDet.stat.trabajosActivos.pista': 'En proceso o programados',
    'clienteDet.contacto.titulo': 'Contacto y cómo entrar',
    'clienteDet.contacto.telefono': 'Teléfono',
    'clienteDet.contacto.email': 'Correo',
    'clienteDet.contacto.sinEmail': 'Sin correo',
    'clienteDet.contacto.direccion': 'Dirección',
    'clienteDet.contacto.codigoPorton': 'Código del gate',
    'clienteDet.notas.titulo': 'Notas del lugar',
    'clienteDet.notas.vacio': 'Todavía no hay notas de este lugar.',
    'clienteDet.proyectos.vacio.titulo': 'Todavía no hay proyectos',
    'clienteDet.proyectos.vacio.texto': 'Aquí aparecen los proyectos cuando el cliente acepta un estimado.',
    'clienteDet.proyectos.vacio.accion': 'Hacer un estimado',
    'clienteDet.proyectos.empezo': 'Empezó el ',
    'clienteDet.proyectos.completado': ' completado',
    'clienteDet.estimados.retomar': 'Sigue donde te quedaste',
    'clienteDet.estimados.otro': 'Hacer otro estimado',
    'clienteDet.estimados.nuevo': 'Hacer un estimado nuevo',
    'clienteDet.estimados.vacio.titulo': 'Todavía no hay estimados',
    'clienteDet.estimados.vacio.texto': 'Haz uno aquí arriba para arrancar con este cliente.',
    'clienteDet.estimados.creado': 'Creado el ',
    'clienteDet.estimados.verPropuesta': 'Ver cómo lo verá el cliente',
    'clienteDet.estimados.editarPropuesta': 'Editar el estimado del cliente',
    'clienteDet.estimados.borrarBorrador': 'Borrar borrador',
    'clienteDet.estimados.sinTitulo': 'Estimado sin nombre',
    'clienteDet.borrarCliente.titulo': 'Borrar cliente',
    'clienteDet.borrarBorrador.titulo': '¿Borrar el borrador?',
    'clienteDet.borrarBorrador.mensaje': '¿Borrar "{titulo}"? Este borrador se pierde y ya no lo puedes recuperar.',
    'clienteDet.borrarBorrador.error': 'No se pudo borrar el borrador',
    'finanzasProy.tab.numeros': 'Números',
    'finanzasProy.tab.gastos': 'Gastos',
    'finanzasProy.tab.facturas': 'Facturas',
    'finanzasProy.kpi.contrato': 'Monto del contrato',
    'finanzasProy.kpi.ganancia': 'Ganancia hasta hoy',
    'finanzasProy.kpi.bajoMeta': ' — abajo de tu meta',
    'finanzasProy.costos.titulo': 'En qué se fue el dinero',
    'finanzasProy.costos.materiales': 'Material de la lista de compra',
    'finanzasProy.costos.gastos': 'Gastos (recibos)',
    'finanzasProy.costos.total': 'Costo total del trabajo',
    'finanzasProy.porCobrar.titulo': 'Te deben: ',
    // Sin `{count}`: el número lo pinta su propio <span> con cifras tabulares.
    'finanzasProy.porCobrar.facturas': { uno: 'factura enviada esperando pago.', otros: 'facturas enviadas esperando pago.' },
    'finanzasProy.gastos.nuevo': 'Anotar gasto o recibo',
    'finanzasProy.gastos.descripcion': 'Descripción',
    'finanzasProy.gastos.monto': 'Monto ($)',
    'finanzasProy.gastos.proveedor': 'Proveedor',
    'finanzasProy.gastos.cat.material': 'Material',
    'finanzasProy.gastos.cat.maquinaria': 'Maquinaria y herramienta',
    'finanzasProy.gastos.cat.subcontratista': 'Subcontratista',
    'finanzasProy.gastos.cat.otro': 'Otro',
    'finanzasProy.gastos.foto': 'Tomar foto del recibo',
    'finanzasProy.gastos.guardar': 'Guardar gasto',
    'finanzasProy.gastos.verRecibo': 'Ver recibo',
    'finanzasProy.gastos.agregarFoto': 'Agregar foto del recibo',
    'finanzasProy.gastos.borrar': 'Borrar gasto',
    'finanzasProy.gastos.vacio.titulo': 'Todavía no hay gastos',
    'finanzasProy.gastos.vacio.texto': 'Toma la foto del recibo en la obra y el gasto se resta automáticamente de la ganancia de este trabajo.',
    'finanzasProy.facturas.crear': 'Crear factura',
    'finanzasProy.facturas.nueva': 'Nueva factura',
    'finanzasProy.facturas.numero': 'Núm. de factura',
    'finanzasProy.facturas.vence': 'Se vence el',
    'finanzasProy.facturas.conceptoDesc': 'Descripción (ej. Fase 1 – Nivelación)',
    'finanzasProy.facturas.agregarConcepto': 'Agregar concepto',
    'finanzasProy.facturas.total': 'Total de la factura',
    'finanzasProy.facturas.pdf': 'Descargar en PDF',
    'finanzasProy.facturas.borrar': 'Borrar factura',
    'finanzasProy.facturas.vacio.titulo': 'Todavía no hay facturas',
    'finanzasProy.facturas.vacio.texto': 'Haz la primera factura para llevar la cuenta de lo que ya facturaste y lo que te falta cobrar.',
    'equipo.titulo': 'Equipo y herramienta',
    'equipo.descripcion': 'Lleva el control de tu maquinaria y herramienta: qué tienes a la mano, qué anda en la obra y qué está en reparación.',
    'equipo.agregar': 'Agregar equipo',
    'equipo.tab.items': 'Equipo',
    'equipo.tab.reparacion': 'En reparación',
    'equipo.tab.mantenimiento': 'Mantenimiento',
    'equipo.buscar': 'Busca por nombre o tipo…',
    'equipo.buscar.mantenimiento': 'Busca por equipo, tipo o descripción…',
    'equipo.estado.enTaller': 'En reparación',
    'equipo.campo.ubicacion': 'Dónde está',
    'equipo.campo.proximoServicio': 'Próximo servicio',
    'equipo.vacio.titulo': 'Todavía no tienes equipo',
    'equipo.vacio.desc': 'Agrega tu maquinaria y tus trocas para saber dónde andan y cuándo les toca servicio.',
    'equipo.vacio.filtro': 'No hay nada con esa búsqueda o ese filtro.',
    'equipo.limpiarFiltros': 'Quitar filtros',
    'equipo.reparacion.vacio': 'Nada en reparación',
    'equipo.reparacion.vacio.desc': 'Aquí caen los equipos cuando marcas “Mandar a reparación” al registrar un mantenimiento. Se quedan hasta que los marques como reparados.',
    'equipo.reparacion.gastado': 'Gastado en este equipo',
    'equipo.reparacion.marcar': 'Marcar reparado',
    'equipo.mant.totalGastado': 'Total gastado',
    'equipo.mant.registrados': 'Arreglos hechos',
    'equipo.mant.enTaller': 'En reparación ahora',
    'equipo.mant.vacio': 'Todavía no registras mantenimientos',
    'equipo.mant.vacio.desc': 'Usa “Registrar mantenimiento” en un equipo para anotar una reparación o un servicio.',
    'equipo.tabla.equipo': 'Equipo',
    'equipo.tabla.tipo': 'Tipo',
    'equipo.tabla.trabajo': 'Qué se le hizo',
    'equipo.tabla.hechoPor': 'Lo hizo',
    'equipo.tabla.fecha': 'Fecha',
    'equipo.tabla.costo': 'Costo',
    'equipo.modal.agregar.titulo': 'Agregar equipo',
    'equipo.modal.editar.titulo': 'Ficha del equipo',
    'equipo.boton.borrar': 'Dar de baja',
    'equipo.editar.ok': 'Ficha actualizada',
    'equipo.guardar.error': 'No se pudo guardar la ficha',
    'equipo.borrar.titulo': '¿Dar de baja esta herramienta?',
    'equipo.borrar.texto': 'Se quita «{nombre}» del inventario. El historial de mantenimiento y lo que costó NO se borra.',
    'equipo.borrar.confirmar': 'Sí, dar de baja',
    'equipo.borrar.ok': 'Herramienta dada de baja',
    'equipo.borrar.error': 'No se pudo dar de baja',
    'equipo.campo.nombre': 'Nombre o número del equipo*',
    'equipo.guardar': 'Guardar equipo',
    'equipo.mant.campo.trabajo': '¿Qué hay que hacerle?*',
    'equipo.mant.btnEnviar': 'Mandar a reparación',
    'equipo.mant.btnGuardar': 'Guardar registro',

    // Resto de la app.
    'app.error.titulo': 'Algo salió mal',
    /* La pantalla de error se traduce SIN `t()`: vive fuera del proveedor de
       idioma a propósito, para poder cazar también los fallos del propio
       proveedor. Usa `traducir(detectarIdioma(), …)`, que son funciones puras.
       Ver components/ErrorBoundary.tsx. */
    'app.error.desc': 'Ocurrió un error inesperado. Ya se avisó al equipo para que lo arreglen.',
    'app.error.recargar': 'Recargar la página',
    'app.acceso.denegado': 'Todavía no tienes acceso',
    'app.acceso.desc': 'Todavía no te han invitado. Pídele a un Admin que agregue tu correo en la pantalla de Gente.',
    'app.acceso.reintentar': 'Reintentar',
    'app.acceso.salir': 'Cerrar sesión',
    'app.workspace.error_titulo': 'No pudimos cargar tu cuenta',
    'app.settings.error_titulo': 'No pudimos cargar tus ajustes',
    'app.setup.titulo': 'Estamos preparando tu cuenta',
    'app.setup.desc': 'Tu admin todavía está preparando la cuenta. En cuanto termine, ya entras.',
    'app.setup.salir': 'Cerrar sesión',
    'app.propuesta.link_invalido': 'Ese link de propuesta no sirve',
    'direccion.autocompletar.placeholder': 'Escribe para buscar la dirección...',
    'direccion.autocompletar.sinconfigurar': 'La búsqueda de direcciones no está configurada. Activa Places API (New) en Google Cloud Console.',
    'direccion.autocompletar.error': 'Error en la búsqueda de direcciones — revisa la consola.',
    'direccion.autocompletar.cargando': 'Cargando...',
    'clienteDet.ia.contexto': 'Cliente · ',
    'clienteDet.ia.seguimiento.label': 'Escribir un mensaje de seguimiento',
    'clienteDet.ia.siguientePaso.label': 'Qué sigue con este cliente',
    'clienteDet.ia.siguientePaso.prompt': 'Con los proyectos y estimados de este cliente, ¿qué hago para que esto avance?',
    'clienteDet.ia.resumen.label': 'Resúmeme este cliente',
    'clienteDet.borrarCliente.confirmar': 'Borrar cliente',
    'clienteDet.cancelar': 'Cancelar',
    'clienteDet.borrarBorrador.confirmar': 'Borrar borrador',
    'clienteDet.borrarBorrador.cancelar': 'Cancelar',
    'clienteDet.borrarBorrador.listo': 'Borrador borrado',
    'clienteDet.estimados.pista.toca': 'Toca ',
    'clienteDet.estimados.seguirEditando': 'Sigue editando',
    'clienteDet.estimados.pista.texto': 'para seguir armando el estimado — no tienes que empezar de nuevo.',
    'clienteDet.estimados.pista.borrar': 'Haz clic derecho en un borrador para borrarlo.',
    'clienteDet.estimados.masPartidas': ' partidas',
    'clienteDet.menu.editarCliente': 'Editar cliente',
    'clienteDet.menu.borrarCliente': 'Borrar cliente',
    'clienteDet.menu.seguirEditando': 'Sigue editando',
    'clienteDet.menu.borrarBorrador': ' Borrar borrador',
    'clientes.modal.error_guardar': 'No se pudo guardar el cliente. Inténtalo otra vez.',
    'clientes.modal.titulo_editar': 'Editar cliente',
    'clientes.modal.titulo_nuevo': 'Nuevo cliente',
    'clientes.modal.cerrar': 'Cerrar',
    'clientes.modal.nombre': 'Nombre del cliente / negocio',
    'clientes.modal.nombre_placeholder': 'ej. Juan Pérez o Acme Corp',
    'clientes.modal.correo_placeholder': 'juan@ejemplo.com',
    'clientes.modal.direccion': 'Dirección del lugar',
    'clientes.modal.direccion_placeholder': 'Empieza a escribir para buscar la dirección...',
    'clientes.modal.gate': 'Código del gate',
    'clientes.modal.contacto': 'Contacto preferido',
    'clientes.modal.notas': 'Notas del lugar',
    'clientes.modal.notas_placeholder': 'Perro en el backyard, parquearse en la calle, etc.',
    'clientes.modal.tags': 'Etiquetas',
    'clientes.modal.tag_placeholder': 'Agrega tu propia etiqueta (ej. HOA, claim de seguro, mal pagador)...',
    'clientes.modal.agregar': 'Agregar',
    'clientes.modal.cancelar': 'Cancelar',
    'clientes.modal.guardando': 'Guardando…',
    'clientes.modal.actualizar': 'Actualizar cliente',
    'clientes.modal.crear': 'Crear cliente',
    // Bloqueos por duplicado. Clientes y prospectos comparten libreta: el mismo
    // teléfono o el mismo correo no puede estar en dos fichas.
    // Etapas cerradas del embudo. Ver utils/leadStageFlow.ts.
    'prospectos.etapa.ganadoEsFinal': '{nombre} ya está en Ganado. Ganarlo creó su ficha de cliente y le pasó las propuestas, así que su etapa ya no se mueve. Lo que siga pasando con este trabajo se lleva desde Clientes.',
    'prospectos.etapa.perdidoNoSaltaAGanado': '{nombre} está marcado como Perdido. Si el trato revivió, muévelo primero a una etapa activa —Contactado, Visita— y gánalo desde ahí.',
    'prospectos.etapa.bloqueadaAria': 'Etapa cerrada: este prospecto ya está en Ganado',
    'prospecto.ganado.banner': 'Ganado. Su ficha vive ahora en Clientes: los cambios se hacen ahí.',
    'prospecto.tarjeta.ganadoCerrado': 'Ganado — ya es cliente',
    'prospecto.perdido.comoRevivir': 'Si el trato revivió, cámbiale la etapa arriba y sigue desde ahí.',
    // Quitar ganados del tablero. Ver utils/leadStageFlow.ts.
    'prospectos.borrar.soloGanados': 'Solo se pueden quitar del tablero los prospectos ganados, y «{nombre}» no lo está. No se borró ninguno.',
    'prospectos.borrar.titulo': { uno: '¿Quitar 1 ganado del tablero?', otros: '¿Quitar {count} ganados del tablero?' },
    'prospectos.borrar.mensaje': { uno: 'Su ficha de cliente se queda intacta, con sus propuestas y sus trabajos: lo que desaparece es la tarjeta de la columna Ganado. No se puede deshacer.', otros: 'Sus fichas de cliente se quedan intactas, con sus propuestas y sus trabajos: lo que desaparece son las tarjetas de la columna Ganado. No se puede deshacer.' },
    'prospectos.borrar.confirmar': 'Sí, quitar del tablero',
    'prospectos.borrar.cancelar': 'Dejarlos',
    'prospectos.borrar.listo': { uno: '1 ganado quitado del tablero', otros: '{count} ganados quitados del tablero' },
    'prospectos.borrar.error': 'No se pudieron quitar. Inténtalo otra vez.',
    'prospectos.borrar.unoTitulo': '¿Quitar a {nombre} del tablero?',
    'prospecto.borrar.uno': 'Quitar del tablero',
    'crm.duplicado.sinNombre': 'una ficha sin nombre',
    'crm.duplicado.cliente.telefono': 'Ese teléfono ya es de {nombre}, que está en Clientes. Abre su ficha en vez de crear otra, o corrige el número.',
    'crm.duplicado.cliente.email': 'Ese correo ya es de {nombre}, que está en Clientes. Abre su ficha en vez de crear otra, o corrige el correo.',
    // Dirección: solo bloquea cuando el NOMBRE es el mismo. Un edificio o dos
    // vecinos comparten dirección sin ser la misma persona.
    'crm.duplicado.cliente.direccion': 'Esa dirección ya es la de {nombre}, en Clientes. Cada dirección va en una sola ficha: abre la suya en vez de crear otra. Si es otra vivienda del mismo edificio, agrega el número de apartamento o unidad.',
    'crm.duplicado.prospecto.telefono': 'Ese teléfono ya es de {nombre}, que está en Prospectos. Abre ese prospecto en vez de crear otro, o corrige el número.',
    'crm.duplicado.prospecto.email': 'Ese correo ya es de {nombre}, que está en Prospectos. Abre ese prospecto en vez de crear otro, o corrige el correo.',
    'crm.duplicado.prospecto.direccion': 'Esa dirección ya es la de {nombre}, en Prospectos. Cada dirección va en una sola ficha: abre ese prospecto en vez de crear otro. Si es otra vivienda del mismo edificio, agrega el número de apartamento o unidad.',
    // Nombre repetido: el mensaje tiene que decir la salida, no solo el bloqueo.
    // Dos personas que se llaman igual son un caso normal, y quien lo encuentre
    // necesita saber que se resuelve escribiendo el nombre completo.
    'crm.duplicado.cliente.nombre': 'Ya tienes un cliente llamado {nombre}. Abre su ficha en vez de crear otra. Si es otra persona, escribe el nombre completo para distinguirlos (apellido, o inicial del segundo nombre).',
    'crm.duplicado.prospecto.nombre': 'Ya tienes un prospecto llamado {nombre}. Abre ese prospecto en vez de crear otro. Si es otra persona, escribe el nombre completo para distinguirlos (apellido, o inicial del segundo nombre).',
    'portal.avatar.alt': 'Foto de perfil',
    'portal.usuario.cliente': 'Cliente',
    'portal.abrirMenu': 'Abrir menú principal',
    'portal.nav.pagosFacturas': 'Pagos y facturas',
    'portal.pie.derechos': 'Todos los derechos reservados.',
    'estimados.error.noSeGuardo': 'No se guardó el último cambio. Revisa tu conexión y vuelve a intentarlo.',
    'estimados.crear.titulo': 'Empezar un Trabajo',
    'estimados.crear.alcance_general': 'Trabajo General',
    'estimados.crear.error_crear_cliente': 'No se pudo crear el cliente. Intenta otra vez.',
    'estimados.crear.cerrar': 'Cerrar',
    'estimados.crear.paso1': 'Paso 1 de 3',
    'estimados.crear.para_quien': '¿Para quién es este trabajo?',
    'estimados.crear.cliente_existente': 'Ya es Cliente',
    'estimados.crear.prospecto': 'Prospecto',
    'estimados.crear.cliente_nuevo': 'Cliente Nuevo',
    'estimados.crear.buscar_cliente': 'Busca por nombre o dirección...',
    'estimados.crear.sin_clientes_titulo': 'No aparece nadie',
    'estimados.crear.sin_clientes_texto': 'Nadie coincide con ese nombre o dirección.',
    'estimados.crear.crear_cliente': 'Crear cliente nuevo',
    'estimados.crear.prospecto_explicacion': 'No se crea ningún cliente todavía. La propuesta queda colgada del prospecto, y pasa a cliente el día que la marques como pagada.',
    'estimados.crear.buscar_prospecto': 'Busca por nombre, teléfono o dirección...',
    'estimados.crear.sin_prospectos_titulo': 'No tienes prospectos abiertos',
    'estimados.crear.sin_prospectos_texto': 'Apunta uno en el tablero de Prospectos y vuelve aquí para cotizarle.',
    'estimados.crear.sin_prospectos_filtro_titulo': 'No aparece nadie',
    'estimados.crear.sin_prospectos_filtro_texto': 'Ningún prospecto coincide con esa búsqueda.',
    'estimados.crear.prospecto_sin_datos': 'Sin teléfono ni dirección',
    'estimados.crear.prospecto_falta_direccion': '¿Dónde es el trabajo?',
    'estimados.crear.prospecto_direccion_nota': 'La propuesta la lleva impresa, y es la que se usa para planear las rutas.',
    'estimados.crear.prospecto_guardar_seguir': 'Guardar y seguir',
    // Entrada desde la ficha del prospecto: el destinatario ya está decidido, así
    // que el paso 1 no vuelve a preguntarlo — solo confirma dónde es el trabajo.
    'estimados.crear.prospecto_titulo_fijo': 'Propuesta para {nombre}',
    'estimados.crear.prospecto_sub_falta': 'Solo falta saber dónde se hace el trabajo.',
    'estimados.crear.prospecto_sub_confirma': 'Confirma dónde se hace el trabajo.',
    'estimados.crear.prospecto_continuar': 'Continuar',
    'estimados.crear.prospecto_error_direccion': 'No se pudo guardar la dirección. Intenta otra vez.',
    'estimados.crear.ejemplo_nombre': 'Juan Pérez',
    'estimados.crear.campo_direccion': 'Dirección',
    'estimados.crear.buscar_direccion': 'Escribe para buscar la dirección...',
    'estimados.crear.crear_y_seguir': 'Crear y Seguir',
    'estimados.crear.paso2': 'Paso 2 de 3',
    'estimados.crear.tipo_trabajo': 'Tipo de Trabajo',
    'estimados.crear.que_oficio': '¿Qué oficio para',
    'estimados.crear.blanco_titulo': 'Empezar en blanco',
    'estimados.crear.blanco_texto': 'Sáltate el oficio y el alcance — vas directo a armar las partidas.',
    'estimados.crear.siguiente': 'Siguiente',
    'estimados.crear.paso3': 'Paso 3 de 3',
    'estimados.crear.que_haces': '¿Qué Vas a Hacer?',
    'estimados.crear.ayuda_alcance': 'Escoge lo más parecido — así solo te salen las plantillas y las herramientas de este trabajo.',
    'estimados.crear.buscar_alcance': 'Busca el tipo de trabajo...',
    'estimados.crear.sin_alcance': 'Nada coincide con',
    'estimados.crear.ver_todo': 'Ver todo',
    'estimados.crear.armar_estimado': 'Armar Estimado',
    'empleadoDet.stat.horas14': 'Horas en 14 días',
    'empleadoDet.stat.diasTrabajados': 'Días trabajados',
    'empleadoDet.stat.proyectos': 'Proyectos',
    'empleadoDet.historial.titulo': 'Trabajo reciente',
    'empleadoDet.historial.vacio.titulo': 'Todavía no hay horas',
    'empleadoDet.historial.vacio.texto': 'No se registró nada en los últimos 14 días.',
    'empleadoDet.log.taller': 'Taller',
    'empleadoDet.log.adentro': 'Sigue adentro',
    'empleadoDet.log.entrada.aria': 'Hora de entrada',
    'empleadoDet.log.salida.aria': 'Hora de salida',
    'empleadoDet.log.guardar.aria': 'Guardar las horas',
    'empleadoDet.log.guardar.title': 'Guardar',
    'empleadoDet.log.editar.aria': 'Editar las horas',
    'empleadoDet.log.editar.title': 'Editar la hora',
    'estimado.constructor.atras': 'Atrás',
    'estimado.constructor.zona_borrada_con_partidas': { uno: 'Se borró la zona "{name}" y {count} partida.', otros: 'Se borró la zona "{name}" y {count} partidas.' },
    'estimado.constructor.zona_borrada': 'Se borró la zona "{name}" — sus partidas pasaron a Sin zona.',
    'estimado.constructor.margen_aplicado': 'Margen meta de este estimado en {value}%. Las partidas que ya están no cambiaron.',
    'estimado.constructor.markup_aplicado': 'Markup de material de este estimado en {value}%. Las partidas que ya están no cambiaron.',
    'estimado.constructor.aplicar_a_partidas': { uno: 'Aplicar a la {count} partida de este estimado', otros: 'Aplicar a las {count} partidas de este estimado' },
    'estimado.constructor.politica_aplicada': { uno: 'Se recotizó {count} partida con la política de este estimado.', otros: 'Se recotizaron {count} partidas con la política de este estimado.' },
    'estimado.constructor.precios_alcance': 'Afecta solo a este estimado. Los valores por defecto del taller están en Ajustes ▸ Precios.',
    'estimado.constructor.precios_bloqueados': 'Este estimado ya salió al cliente: los precios quedan como los vio. Para cambiarlos, crea una revisión.',
    'estimado.constructor.plantillas_guardadas': { uno: 'Guardé {count} plantilla privada.', otros: 'Guardé {count} plantillas privadas.' },
    'estimado.constructor.plantillas_fallaron': { uno: 'No se pudo guardar {count} plantilla privada.', otros: 'No se pudieron guardar {count} plantillas privadas.' },
    'estimado.constructor.titulo': 'Armar estimado',
    'estimado.constructor.contexto_ia': 'Estimado · {nombreCliente}',
    'estimado.constructor.ia_que_falta_label': '¿Qué me falta en este estimado?',
    'estimado.constructor.ia_que_falta_prompt': 'Revisa este estimado y dime qué partidas o costos se me pueden estar pasando. Sé específico y práctico.',
    'estimado.constructor.ia_margen_label': 'Ayúdame a llegar a mi margen',
    'estimado.constructor.ia_margen_prompt': 'Mi margen está abajo del objetivo. Dame cambios concretos en las partidas o en los precios para ganar más sin perder el trabajo.',
    'estimado.constructor.ia_intro_label': 'Escribe una intro para la propuesta',
    'estimado.constructor.ia_intro_prompt': 'Escribe una introducción corta y amable para la propuesta que le voy a mandar a este cliente por este trabajo. Que suene profesional y en palabras sencillas.',
    'estimado.constructor.precios_rapidos_tooltip': 'Ajustes rápidos de precio',
    'estimado.constructor.margen_objetivo': 'Margen meta %',
    'estimado.constructor.markup_material': 'Markup de material %',
    'estimado.constructor.abrir_gastos_precios': 'Abrir gastos fijos y precios →',
    'estimado.constructor.guardar_y_ver': 'Guardar y ver propuesta',
    'estimado.constructor.nombre_proyecto': 'Nombre del proyecto',
    'estimado.constructor.nombre_placeholder': 'Ponle nombre a este proyecto…',
    'estimado.constructor.ver': 'Ver',
    'estimado.constructor.aviso_precios': 'No has configurado los precios — los márgenes pueden salir mal.',
    'estimado.constructor.config_completa': 'Toda la configuración →',
    'estimado.constructor.ver_documento': 'Ver documento',
    'estimado.constructor.agregar_partidas': 'Agregar partidas',
    'estimado.constructor.condiciones_titulo': 'Condiciones de la obra y zonas',
    'estimado.constructor.resumen_zonas': ' Zonas · ',
    'estimado.constructor.resumen_dificultad': ' de dificultad · ',
    'estimado.constructor.resumen_suelo': ' (suelo)',
    'estimado.constructor.zonas_activas': 'Zonas activas',
    'estimado.constructor.borrar_zona_aria': 'Borrar la zona {name}',
    'estimado.constructor.zona_nombre_placeholder': 'Nombre de la zona…',
    'estimado.constructor.confirmar_zona': 'Confirmar zona',
    'estimado.constructor.cancelar_zona': 'Cancelar',
    'estimado.constructor.total_partidas': 'Partidas (',
    'estimado.constructor.total_cargo_viaje': 'Cargo por viaje',
    'estimado.constructor.total_recargo_emergencia': 'Recargo de emergencia',
    'estimado.constructor.total_dificultad': 'Dificultad de la obra (',
    'estimado.constructor.total_descuento': 'Descuento',
    'estimado.constructor.total_minimo': 'Mínimo por trabajo',
    'estimado.constructor.total_impuesto': 'Impuesto (',
    'estimado.constructor.total_estimado': 'Total del estimado',
    'estimado.constructor.barra_costo': 'Costo: ',
    'estimado.constructor.barra_ingreso': 'Ingreso: ',
    'estimado.constructor.barra_ganancia': 'Ganancia: ',
    'estimado.constructor.borrar_zona_titulo': '¿Borrar esta zona?',
    'estimado.constructor.borrar_zona_texto': 'Vas a borrar',
    'estimado.constructor.borrar_zona_check1': 'Borrar también las ',
    'estimado.constructor.borrar_zona_check2': ' que hay dentro de esta zona.',
    'estimado.constructor.borrar_zona_conservar': 'Desmárcalo para conservarlas — se pasan a ',
    'estimado.constructor.sin_zona': 'Sin zona',
    'estimado.constructor.borrar_zona_conservar2': '.',
    'estimado.constructor.zona_sin_partidas': 'Esta zona no tiene partidas adentro.',
    'estDetalle.cerrarPanel': 'Cerrar detalles del estimado',
    'estDetalle.cerrar': 'Cerrar',
    'estDetalle.kpi.total': 'Valor total',
    'estDetalle.kpi.margen': 'Margen estimado',
    'estDetalle.costo': 'Costo',
    'estDetalle.ganancia': 'Ganancia',
    'estDetalle.costoTip': 'Costo',
    'estDetalle.gananciaTip': 'Ganancia',
    'estDetalle.abrirEditor': 'Abrir editor',
    'estDetalle.editarPropuesta': 'Editar propuesta',
    'estDetalle.partidas': 'Partidas',
    'estDetalle.col.descripcion': 'Descripción',
    'estDetalle.col.tipo': 'Tipo',
    'estDetalle.col.cantidad': 'Cant',
    'estDetalle.cliente.titulo': 'Datos del cliente',
    'estDetalle.cliente.nombre': 'Nombre',
    'estDetalle.cliente.direccion': 'Dirección',
    'estDetalle.marcarEnviado': 'Marcar como enviado',
    'estDetalle.aprobar': 'Aprobar y ganar el trabajo',
    'estDetalle.duplicar': 'Duplicar',
    'estDetalle.cerrarBoton': 'Cerrar',
    'estDetalle.hacerCopia': 'Hacer una copia',
    'estDetalle.borrar': 'Borrar estimado',
    'estDetalle.cerrarMenu': 'Cerrar menú',
    'estDetalle.seguirEditando': 'Seguir editando',
    'estDetalle.borrarBorrador': 'Borrar borrador',
    'leads.modal.titulo.editar': 'Editar prospecto',
    'leads.modal.titulo.nuevo': 'Nuevo prospecto',
    'leads.modal.aria.cerrar': 'Cerrar',
    'leads.modal.campo.nombre': 'Nombre del prospecto',
    'leads.modal.campo.email': 'Correo (opcional)',
    'leads.modal.campo.direccion': 'Dirección',
    'leads.modal.placeholder.direccion': 'Escribe la dirección para buscarla…',
    'leads.modal.campo.servicio': 'Tipo de trabajo',
    'leads.modal.opcion.elige': 'Elige...',
    'leads.modal.campo.valor': 'Precio estimado ($)',
    'leads.modal.campo.notas': 'Notas iniciales',
    'leads.modal.placeholder.notas': '¿Cómo supieron de nosotros? ¿Piden algo en especial?',
    'leads.modal.boton.cancelar': 'Cancelar',
    'leads.modal.boton.guardando': 'Guardando…',
    'leads.modal.boton.actualizar': 'Guardar cambios',
    'leads.modal.boton.crear': 'Crear prospecto',
    'login.tagline': 'CRM profesional',
    'login.titulo': 'Qué gusto verte de nuevo',
    'login.sub': 'Entra a tu panel con tu cuenta de Google.',
    'login.verificando': 'Verificando…',
    'login.entrarGoogle': 'Entrar con Google',
    'login.soloAutorizados': 'Solo personal autorizado',
    'proyNuevo.titulo': 'Proyecto nuevo',
    'proyNuevo.cerrar': 'Cerrar',
    'proyNuevo.escogeCliente': 'Escoge al cliente para arrancar el proyecto.',
    'proyNuevo.buscar.placeholder': 'Busca un cliente…',
    'proyNuevo.vacio.titulo': 'No encontramos a nadie',
    'proyNuevo.vacio.texto': 'Prueba con otro nombre o con el teléfono.',
    'proyNuevo.campo.nombre': 'Nombre del proyecto',
    'proyNuevo.campo.inicio': 'Cuándo empieza',
    'proyNuevo.campo.fin': 'Cuándo termina',
    'proyNuevo.campo.monto': 'Monto del contrato ($)',
    'proyNuevo.campo.estatus': 'Estatus',
    'proyNuevo.estatus.programado': 'Programado',
    'proyNuevo.estatus.enProceso': 'En proceso',
    'proyNuevo.estatus.terminado': 'Terminado',
    'proyNuevo.estatus.cancelado': 'Cancelado',
    'proyNuevo.campo.descripcion': 'Descripción',
    'proyNuevo.errorDesconocido': 'Error desconocido',
    'notificaciones.tiempo.ahorita': 'ahorita',
    'notificaciones.tiempo.minutos': 'hace {min}m',
    'notificaciones.tiempo.horas': 'hace {hr}h',
    'notificaciones.tiempo.dias': 'hace {d}d',
    'notificaciones.panel.borrar_todo': 'Borrar todo',
    'notificaciones.panel.vacio': 'Ya no tienes nada pendiente.',
    'notificaciones.panel.quitar_tooltip': 'Quitar',
    'notificaciones.panel.quitar_aria': 'Quitar notificación',
    'pricebook.modal.titulo_editar': 'Editar material',
    'pricebook.modal.titulo_nuevo': 'Nuevo material',
    'pricebook.campo.nombre': 'Nombre del material',
    'pricebook.campo.nombre_placeholder': 'ej. madera de 2x4',
    'pricebook.campo.categoria': 'Categoría',
    'pricebook.campo.proveedor': 'Proveedor',
    'pricebook.campo.proveedor_placeholder': 'ej. SiteOne',
    'pricebook.campo.unidad': 'Unidad',
    'pricebook.campo.costo': 'Costo ($)',
    'pricebook.preview.precio_cliente': 'Precio al cliente',
    'pricebook.preview.ganancia': 'Ganancia',
    'pricebook.boton.cancelar': 'Cancelar',
    'pricebook.boton.actualizar': 'Guardar cambios',
    'pricebook.boton.agregar': 'Guardar material',
    'proyecto.detalle.borrar.titulo': '¿Borrar el proyecto?',
    'proyecto.detalle.borrar.mensaje': 'Esto no se puede deshacer. Se borran para siempre las tareas, los materiales, los gastos y las facturas de este proyecto.',
    'proyecto.detalle.borrar.confirmar': 'Borrar proyecto',
    'proyecto.detalle.borrar.cancelar': 'Cancelar',
    'proyecto.detalle.borrar.errordesconocido': 'Error desconocido',
    'proyecto.detalle.nota.autordefecto': 'Usuario',
    'proyecto.detalle.tab.centro': 'Resumen',
    'proyecto.detalle.tab.tareas': 'Tareas',
    'proyecto.detalle.tab.mano': 'Mano de obra',
    'proyecto.detalle.tab.materiales': 'Materiales',
    'proyecto.detalle.tab.equipo': 'Equipo',
    'proyecto.detalle.tab.finanzas': 'Finanzas',
    'proyecto.detalle.nav.atras': '"Atrás"',
    'proyecto.detalle.estado.arialabel': 'Estado del proyecto',
    'proyecto.detalle.estado.aceptado': 'Aceptado',
    'proyecto.detalle.estado.programado': 'Programado',
    'proyecto.detalle.estado.enproceso': 'En proceso',
    'proyecto.detalle.estado.completado': 'Terminado',
    'proyecto.detalle.estado.cancelado': 'Cancelado',
    'proyecto.detalle.borrar.title': 'Borrar proyecto',
    'proyecto.detalle.borrar.arialabel': 'Borrar el proyecto',
    'proyecto.detalle.vital.atrasado': 'Atrasado',
    'proyecto.detalle.vital.aldia': 'Al día',
    'proyecto.detalle.vital.barratitle': '"% de avance"',
    'proyecto.detalle.vital.preciofijo': 'Precio fijo',
    'proyecto.detalle.nota.tarea1': 'La tarea "',
    'proyecto.detalle.nota.tarea2': '" la terminó ',
    'precios.toast.guardado': 'Listo. Tus estimados van a usar estos números.',
    'precios.toast.error': 'No se pudo guardar. Inténtalo otra vez.',
    'precios.modal.titulo': 'Pon tus precios rápido',
    'precios.modal.subtitulo': 'Tres números y tus márgenes quedan donde tú quieres.',
    'precios.campo.margen': 'Ganancia limpia que quieres dejar (%)',
    'precios.campo.margen_ayuda': 'Lo que te quieres quedar después de todos los costos',
    'precios.campo.markup': 'Markup de materiales (%)',
    'precios.campo.markup_ayuda': 'Se le suma a lo que te cuesta el material',
    'precios.campo.viaje': 'Cargo por viaje / service call ($)',
    'precios.boton.guardando': 'Guardando…',
    'precios.boton.guardar': 'Guardar y empezar a cotizar',
    'precios.boton.saltar': 'Ahora no',
    'agenda.cita.titulo.editar': 'Editar cita',
    'agenda.cita.titulo.nueva': 'Agendar cita',
    'agenda.cita.cliente.pregunta': '¿A quién vas a ver?',
    'agenda.cita.cliente.buscar': 'Busca al cliente por nombre o dirección...',
    'agenda.cita.cliente.vaciotitulo': 'No encontramos clientes',
    'agenda.cita.cliente.vaciotexto': 'Prueba con otro nombre o dirección.',
    'agenda.cita.cliente.cambiar': 'Cambiar',
    'agenda.cita.campo.titulo': 'Título',
    'agenda.cita.campo.fecha': 'Fecha',
    'agenda.cita.campo.tipo': 'Tipo',
    'agenda.cita.tipo.visita': 'Visita al sitio',
    'agenda.cita.tipo.servicio': 'Servicio',
    'agenda.cita.tipo.consulta': 'Consulta',
    'agenda.cita.tipo.recorrido': 'Walkthrough final',
    'agenda.cita.campo.inicio': 'Empieza',
    'agenda.cita.campo.fin': 'Termina',
    'agenda.cita.campo.notas': 'Notas',
    'agenda.cita.notas.placeholder': 'Código del gate, instrucciones, etc...',
    'agenda.cita.proyecto.etiqueta': 'Conectar con un proyecto (opcional)',
    'agenda.cita.proyecto.ninguno': '— Ninguno —',
    'agenda.cita.boton.cancelar': 'Cancelar',
    'agenda.cita.boton.guardando': 'Guardando…',
    'agenda.cita.boton.actualizar': 'Actualizar cita',
    'agenda.cita.boton.guardar': 'Guardar cita',
    'servicios.plantilla.titulo_editar': 'Editar plantilla de servicio',
    'servicios.plantilla.titulo_nueva': 'Nueva plantilla de servicio',
    'servicios.plantilla.nombre': 'Nombre del servicio',
    'servicios.plantilla.nombre_placeholder': 'ej. Instalación estándar',
    'servicios.plantilla.categoria': 'Categoría',
    'servicios.plantilla.descripcion': 'Descripción del trabajo',
    'servicios.plantilla.descripcion_placeholder': 'ej. Instalación completa según las especificaciones del fabricante...',
    'servicios.plantilla.produccion_titulo': 'Cómo se calcula la producción',
    'servicios.plantilla.unidad': 'Unidad de medida',
    'servicios.plantilla.cantidad_hora': 'Cantidad por hora-hombre',
    'servicios.plantilla.desperdicio': '% de desperdicio estándar (materiales)',
    'servicios.plantilla.desperdicio_ayuda': 'Desperdicio que se toma en cuenta al estimar este trabajo.',
    'servicios.plantilla.ejemplo_fin': 'en 1 hora.',
    'servicios.plantilla.material_ligado': 'Ligar material automáticamente (opcional)',
    'servicios.plantilla.sin_material': '-- Sin material ligado --',
    'servicios.plantilla.material_ayuda': 'Si eliges uno, ese material se agrega solo al estimado cada vez que uses este servicio.',
    'servicios.plantilla.cancelar': 'Cancelar',
    'servicios.plantilla.actualizar': 'Actualizar plantilla',
    'servicios.plantilla.crear': 'Crear plantilla',
    'wiz.error.nombrenegocio': 'Falta el nombre del negocio.',
    'wiz.error.noterminar': 'Todavía no puedes terminar',
    'wiz.error.guardar': 'No se pudieron guardar los ajustes. Puede que no tengas permiso.',
    'wiz.perfil.titulo': 'Perfil del negocio',
    'wiz.perfil.tituloinicio': 'Vamos a empezar.',
    'wiz.perfil.subtitulo': 'Actualiza los datos básicos de tu negocio.',
    'wiz.perfil.subtituloinicio': 'Cuéntanos de tu negocio para armar tus precios.',
    'wiz.perfil.industria': 'Tipo de negocio',
    'wiz.perfil.industriaayuda': 'Define tus calculadoras, las categorías de tu lista de precios y el asistente de IA.',
    'wiz.perfil.industriaayudainicio': 'Te llenamos burden, margen y gastos fijos con valores reales de este tipo de negocio. Ajústalos si hace falta.',
    'wiz.perfil.nombre': 'Nombre del negocio',
    'wiz.perfil.descripcion': 'Descripción del negocio',
    'wiz.perfil.descripcionplaceholder': 'Describe rapidito lo que haces — así la IA escribe mejores propuestas y alcances de trabajo.',
    'wiz.perfil.moneda': 'Moneda',
    'wiz.perfil.zona': 'Zona donde trabajas',
    'wiz.perfil.zonaplaceholder': 'Empieza a escribir una ciudad, zip code o dirección...',
    'wiz.perfil.sugerencias': 'Sugerencias de dirección activadas',
    'wiz.contacto.titulo': 'Datos de contacto',
    'wiz.contacto.tituloinicio': '¿Cómo te localizan los clientes?',
    'wiz.contacto.subtitulo': 'Esto sale en tus propuestas y facturas.',
    'wiz.contacto.email': 'Correo del negocio',
    'wiz.contacto.emailplaceholder': 'hola@tunegocio.com',
    'wiz.contacto.telefono': 'Teléfono',
    'wiz.contacto.web': 'Sitio web',
    'wiz.contacto.webplaceholder': 'https://tunegocio.com',
    'wiz.logo.subido': '¡Logo subido!',
    'wiz.logo.error': 'No se pudo subir el logo.',
    'wiz.logo.titulo': 'Logo del negocio',
    'wiz.logo.tituloinicio': 'Ponle tu marca a las propuestas',
    'wiz.logo.subtitulo': 'Sube el logo de tu compañía. Va a salir en propuestas, facturas y correos.',
    'wiz.logo.alt': 'Logo del negocio',
    'wiz.logo.vacio': 'Todavía no hay logo',
    'wiz.logo.subiendo': 'Subiendo...',
    'wiz.logo.cambiar': 'Cambiar logo',
    'wiz.logo.subir': 'Subir logo',
    'wiz.logo.formatos': 'PNG, JPG o SVG. Puedes saltarte esto y ponerlo después en Ajustes.',
    'wiz.temporada.titulo': 'Tu temporada',
    'wiz.temporada.subtitulo': 'Necesitamos saber cuánto alcanzas a trabajar para repartir los gastos fijos.',
    'wiz.temporada.semanas': 'Semanas al año',
    'wiz.temporada.semanastip': '¿Cuántas semanas trabajas? No cuentes las que paras en invierno.',
    'wiz.temporada.dias': 'Días de trabajo / semana',
    'wiz.temporada.diastip': '¿Cuántos días sale la cuadrilla?',
    'wiz.temporada.horas': 'Horas de cuadrilla / día',
    'wiz.temporada.horastip': 'Lo que dura el turno (por ejemplo, de 8am a 5pm = 9 horas)',
    'wiz.empleados.puestodefecto': 'Trabajador',
    'wiz.empleados.titulo': 'Empleados',
    'wiz.empleados.subtitulo': 'Agrega a todos los que trabajan en las obras (tú incluido).',
    'wiz.empleados.agregar': 'Agregar empleado',
    'wiz.empleados.agregarvacio': 'Agregar empleado',
    'wiz.empleados.vaciotitulo': 'Todavía no hay empleados',
    'wiz.empleados.vaciotexto': 'Tu tarifa de mano de obra sale de la cuadrilla. Agrégate tú primero y luego a todos los que cobran horas.',
    'wiz.empleados.etiqueta': 'Empleado',
    'wiz.empleados.nombreplaceholder': 'Nombre',
    'wiz.empleados.puesto': 'Puesto',
    'wiz.empleados.puestoplaceholder': 'Puesto',
    'wiz.empleados.pago': 'Pago ($)',
    'wiz.empleados.carga': 'Burden %',
    'wiz.empleados.cargatip': 'FICA + Workers\' Comp + prestaciones. Lo normal: 20–30%.',
    'wiz.empleados.quitar': 'Quitar empleado',
    'wiz.empleados.facturable': 'Cobrable',
    'wiz.empleados.disponible': 'Horas disponibles %',
    'wiz.empleados.disponibletip': 'De las horas de la temporada, el % que NO se pierde por vacaciones, lluvia, enfermedad o entrenamiento.',
    'wiz.empleados.porcentajefacturable': 'Horas que sí cobras %',
    'wiz.empleados.porcentajefacturabletip': 'Del tiempo disponible, el % que de veras anda en un trabajo que paga (no manejando, preparando ni limpiando).',
    'wiz.empleados.efectivo': 'Termina cobrando el',
    'wiz.empleados.nofacturable': 'Su costo se va a los gastos fijos, no a la tarifa de mano de obra.',
    'wiz.gastos.rapidorenta': 'Renta',
    'wiz.gastos.rapidoseguro': 'Seguro',
    'wiz.gastos.rapidogasolina': 'Gasolina',
    'wiz.gastos.titulo': 'Gastos fijos',
    'wiz.gastos.subtitulo': 'Costos que pagas aunque no tengas trabajo.',
    'wiz.gastos.otro': 'Otro',
    'wiz.gastos.vaciotitulo': 'Todavía no hay gastos fijos',
    'wiz.gastos.vaciotexto': 'Renta, seguro, marketing, software — todo lo que pagas salga o no salga la cuadrilla.',
    'wiz.gastos.agregar': 'Agregar gasto',
    'wiz.gastos.nombre': 'Nombre del gasto',
    'wiz.gastos.nombreplaceholder': 'Nombre del gasto',
    'wiz.gastos.monto': 'Monto',
    'wiz.gastos.frecuencia': 'Frecuencia',
    'wiz.gastos.mensual': 'Mensual',
    'wiz.gastos.quitar': 'Quitar gasto',
    'wiz.equipo.titulo': 'Recuperar el costo de la maquinaria',
    'wiz.equipo.subtitulo': 'Trocas, podadoras, excavadoras — su depreciación y lo que cuesta operarlas tiene que salir de las horas que cobras. Sáltate esto si no tienes maquinaria propia.',
    'wiz.equipo.costoanual': 'Costo anual de la maquinaria',
    'wiz.equipo.costoanualayuda': 'Se recupera con las horas que cobras.',
    'wiz.equipo.vaciotitulo': 'Todavía no hay maquinaria',
    'wiz.equipo.vaciotexto': 'Agrega trocas, podadoras o máquinas para que su depreciación y lo que cuesta usarlas entren en tu tarifa. Puedes saltarte este paso.',
    'wiz.equipo.agregar': 'Agregar maquinaria',
    'wiz.equipo.nombreplaceholder': 'Troca, podadora, etc.',
    'wiz.equipo.compra': 'Compra $',
    'wiz.equipo.vida': 'Vida (años)',
    'wiz.equipo.gasolina': 'Gasolina $/año',
    'wiz.equipo.mantenimiento': 'Mant. $/año',
    'wiz.equipo.quitar': 'Quitar maquinaria',
    'wiz.precios.titulo': 'Cuánto quieres ganar',
    'wiz.precios.subtitulo': '¿Cuánta ganancia limpia quieres sacar arriba de tus costos?',
    'wiz.precios.sobrevivir': 'Sobrevivir',
    'wiz.precios.crecer': 'Crecer',
    'wiz.precios.fuerte': 'Fuerte',
    'wiz.precios.agresivo': 'Agresivo',
    'wiz.precios.ganancia': 'Ganancia limpia que quieres',
    'wiz.precios.gananciaaria': 'Porcentaje de ganancia limpia',
    'wiz.precios.equilibrio': 'Sin ganancia',
    'wiz.precios.premium': 'Precio alto',
    'wiz.precios.markup': 'Markup normal de materiales',
    'wiz.precios.markupayuda': 'El markup que le pones a plantas y materiales.',
    'wiz.reglas.titulo': 'Tus mínimos',
    'wiz.reglas.subtitulo': 'Pon tus mínimos para no perder dinero en trabajos chicos.',
    'wiz.reglas.minimo': 'Mínimo por visita ($)',
    'wiz.reglas.minimotip': 'Lo menos que puede costar una factura, pase lo que pase.',
    'wiz.reglas.viaje': 'Cargo por viaje ($)',
    'wiz.reglas.viajetip': 'Cargo fijo en cada visita por gasolina y desgaste.',
    'wiz.reglas.horas': 'Horas-hombre mínimas',
    'wiz.reglas.horastip': 'Lo mínimo de horas de mano de obra que cobras por visita.',
    'wiz.reglas.urgencia': 'Recargo por urgencia (%)',
    'wiz.resumen.repaso': 'Resumen',
    'wiz.resumen.completo': 'Ya quedó todo',
    'wiz.resumen.titulorepaso': 'Resumen de tus ajustes',
    'wiz.resumen.titulo': 'Ya estás listo.',
    'wiz.resumen.subtitulorepaso': 'Revisa los números de tu negocio.',
    'wiz.resumen.subtitulo': 'Ya tienes armados tus números.',
    'wiz.resumen.tarifa': 'Lo que debes cobrar por hora',
    'wiz.resumen.porhora': '/hora',
    'wiz.resumen.tarifaayuda': 'Cobra esto por cada hora-hombre en la obra.',
    'wiz.resumen.margen': 'Margen de ganancia',
    'wiz.resumen.semanas': 'Semanas activas',
    'wiz.resumen.cuadrilla': 'Gente en la cuadrilla',
    'wiz.nav.cerrar': 'Cerrar ajustes',
    'wiz.nav.ajustes': 'Ajustes del negocio',
    'wiz.nav.asistente': 'Configuración inicial',
    'equipo.modal.error.faltandatos': 'Pon el nombre y el email.',
    'equipo.modal.error.emailinvalido': 'Escribe un email válido.',
    'equipo.modal.error.emailduplicado': 'Ya hay alguien en el equipo con ese email.',
    'equipo.modal.campo.email': 'Email (cuenta de Google)',
    'equipo.modal.ayuda.emailfijo': 'El email ya no se puede cambiar.',
    'equipo.modal.ayuda.emailgoogle': 'Tiene que ser el mismo email con el que entra a Google.',
    'equipo.modal.placeholder.nombre': 'Juan Pérez',
    'equipo.modal.campo.pestanas': 'Pestañas permitidas',
    'equipo.modal.pestana.agenda': 'Agenda',
    'equipo.modal.pestana.reloj': 'Reloj',
    'equipo.modal.pestana.propuestas': 'Propuestas',
    'equipo.modal.aviso.permisosfijos': 'Los permisos de este rol están fijos por seguridad.',
    'equipo.modal.admin.titulo': 'Acceso total',
    'equipo.modal.admin.descripcion': 'Los admins entran a todo: pestañas, ajustes y usuarios.',
    'equipo.modal.boton.cancelar': 'Cancelar',
    'equipo.modal.boton.guardando': 'Guardando...',
    'equipo.modal.boton.guardarcambios': 'Guardar cambios',
    'equipo.modal.boton.invitar': 'Invitar',
    'horas.editor.error_guardar': 'No se pudo guardar el registro.',
    'horas.editor.titulo': 'Editar registro de horas',
    'horas.editor.cancelar': 'Cancelar',
    'horas.editor.guardando': 'Guardando…',
    'horas.editor.guardar': 'Guardar cambios',
    'horas.editor.proyecto': 'Proyecto asignado',
    'horas.editor.taller': 'Tiempo en el shop (general)',
    'horas.editor.entrada': 'Hora de entrada',
    'horas.editor.salida': 'Hora de salida',
    'horas.editor.aviso': 'Si cambias estas horas, los minutos netos que van a la nómina se recalculan solos.',
    'asistente.resumen.modelovivo': 'Tus números al momento',
    'asistente.resumen.tunegocio': 'Tu negocio',
    'asistente.resumen.diasproduccion': 'Días de producción',
    'asistente.resumen.dias': ' días',
    'asistente.resumen.capacidadtemporada': 'Capacidad de la temporada',
    'asistente.resumen.temporadacorta': 'Temporada corta. Los costos se van a concentrar en menos horas.',
    'asistente.resumen.metricasmanodeobra': 'Números de mano de obra',
    'asistente.resumen.horasfacturables': 'Horas facturables',
    'asistente.resumen.costomanodeobradirecta': 'Mano de obra directa',
    'asistente.resumen.manodeobraindirecta': 'Mano de obra indirecta',
    'asistente.resumen.agregaempleados': 'Agrega empleados para calcular los costos.',
    'asistente.resumen.gastosfijosequilibrio': 'Gastos fijos y punto de equilibrio',
    'asistente.resumen.gastosfijosanuales': 'Gastos fijos al año',
    'asistente.resumen.equilibriohora': 'Equilibrio / hr',
    'asistente.resumen.tarifameta': 'Tarifa meta por hora',
    'asistente.resumen.margenganancia': 'Margen de ganancia ',
    'asistente.resumen.gananciaposible': 'Ganancia posible',
    'asistente.resumen.poranio': '/año',
    'asistente.resumen.minimostrabajo': 'Mínimos por trabajo',
    'asistente.resumen.cargoviaje': 'Cargo por viaje',
    'asistente.resumen.serviciominimo': 'Servicio mínimo',
    'flujo.volver.cliente': 'Volver al cliente',
    'flujo.volver.panel': 'Volver al panel',
    'flujo.volver.estimados': 'Volver a estimados',
    'flujo.guardado.titulo': 'Estimado guardado',
    'flujo.guardado.textoInicio': 'Arma la propuesta de',
    'flujo.guardado.textoFin': 'y mándala desde ahí.',
    'flujo.guardado.abrirPropuesta': 'Abrir propuesta',
    'flujo.guardado.volverEstimados': 'Volver a estimados',
    'admin.resumen.total_negocios': 'Total de negocios',
    'admin.resumen.activos': 'Activos',
    'admin.resumen.suspendidos': 'Suspendidos',
    'admin.resumen.total_usuarios': 'Total de usuarios',
    'admin.resumen.personal_campo': 'Personal de campo',
    'admin.resumen.nuevo_negocio_titulo': 'Negocio nuevo',
    'admin.resumen.nuevo_negocio_pista': 'Crear un espacio',
    'admin.resumen.invitar_titulo': 'Invitar usuario',
    'admin.resumen.invitar_pista': 'Agregar a un espacio',
    'admin.resumen.reportes_titulo': 'Reportes del sistema',
    'admin.resumen.reportes_pista': 'Registro de errores',
    'admin.resumen.actualizar_titulo': 'Actualizar datos',
    'admin.resumen.actualizar_pista': 'Sincronizar ahora',
    'admin.resumen.distribucion': 'Negocios — usuarios por negocio',
    'admin.resumen.vacio_titulo': 'Todavía no hay negocios',
    'admin.resumen.vacio_descripcion': 'Crea tu primer espacio para dar de alta un negocio.',
    'admin.resumen.nuevo_negocio_boton': 'Negocio nuevo',
    // ── Videos educativos (panel de admin → Videos educativos) ──
    'admin.videos.titulo': 'Videos educativos',
    'admin.videos.descripcion': 'Los videos de "Cómo se usa" que ve el equipo en cada pantalla. Al subir uno se publica al instante.',
    'admin.videos.refrescar': 'Actualizar',
    'admin.videos.progreso': '{hechos} de {total} publicados',
    'admin.videos.quitar_miniatura_titulo': '¿Quitar la miniatura?',
    'admin.videos.quitar_miniatura_mensaje': 'La miniatura de {tema} se borra y el video vuelve a arrancar en negro. El video no se toca.',
    'admin.videos.err.archivo_vacio': 'Ese archivo está vacío.',
    'admin.videos.err.muy_grande': 'El archivo pesa {tamano} y el máximo es {limite}. Recorta el video o bájale la calidad.',
    'admin.videos.err.formato': 'El formato {tipo} no sirve aquí. Usa MP4 o WebM.',
    'admin.videos.err.titulo_vacio': 'El título no puede quedar vacío.',
    'admin.videos.err.titulo_largo': 'El título es muy largo (máximo {max} caracteres).',
    'admin.videos.err.sin_permiso': 'La base de datos no aceptó el cambio. Revisa que tu cuenta siga siendo admin de la plataforma.',
    'admin.videos.err.limite_servidor': 'Supabase rechazó el archivo por tamaño. Sube el tope en el panel de Supabase: Project Settings → Storage → "Upload file size limit". El del bucket ya está en 2 GB; el que manda es ese otro.',
    'admin.videos.err.sin_sesion': 'Tu sesión se venció. Vuelve a entrar y reintenta.',
    'admin.videos.cancelar': 'Cancelar',
    'admin.videos.subiendo': 'Subiendo {pct}% — {enviado} de {total}',
    'admin.videos.subiendo_aria': 'Progreso de la subida',
    'admin.videos.subiendo_aviso': 'Si se corta el internet, la subida sigue donde se quedó. Cerrar esta pestaña sí la cancela.',
    'admin.videos.ayuda_formato': 'Formatos: {formatos}. Máximo {limite} MB por video. La subida se reanuda sola si se cae el internet, pero no cierres esta pestaña mientras corre.',
    'admin.videos.sin_video': 'Sin video',
    'admin.videos.previsualizar': 'Ver el video',
    'admin.videos.estado_publicado': 'Publicado',
    'admin.videos.estado_pendiente': 'Pendiente',
    'admin.videos.campo_titulo': 'Título que ve el usuario',
    'admin.videos.guardar': 'Guardar',
    'admin.videos.subir': 'Subir video',
    'admin.videos.reemplazar': 'Reemplazar',
    'admin.videos.miniatura': 'Miniatura',
    'admin.videos.cambiar_miniatura': 'Cambiar miniatura',
    'admin.videos.quitar_miniatura': 'Quitar miniatura',
    'admin.videos.borrar': 'Borrar',
    'admin.videos.borrar_titulo': '¿Borrar este video?',
    'admin.videos.borrar_mensaje': 'El botón "Cómo se usa" de {tema} vuelve a decir "Tutorial en camino". El archivo se borra y no se puede recuperar.',
    'admin.videos.borrar_confirmar': 'Borrar video',
    'admin.videos.actualizado': 'Actualizado el {fecha} por {quien}',
    'admin.videos.desconocido': 'el sistema',
    'admin.videos.pie': 'Las miniaturas son opcionales (PNG, JPG o WebP, máximo {max}). Sin miniatura, el video arranca en negro.',
    'admin.videos.toast_subido': 'Video publicado',
    'admin.videos.toast_miniatura': 'Miniatura actualizada',
    'admin.videos.toast_miniatura_borrada': 'Miniatura quitada',
    'admin.videos.toast_borrado': 'Video borrado',
    'admin.videos.toast_titulo': 'Título actualizado',
    'admin.videos.error_subir': 'No se pudo subir el archivo.',
    'admin.videos.error_borrar': 'No se pudo borrar.',
    'admin.videos.error_titulo': 'No se pudo guardar el título.',
    'admin.videos.error_cargar_titulo': 'No se pudieron cargar los videos',
    'admin.videos.error_cargar_desc': 'La lista no llegó, así que esta pantalla no sabe cuáles hay. No es que no haya ninguno. Detalle: {detalle}',
    'admin.videos.reintentar': 'Reintentar',
    'admin.videos.aviso_mov': 'Subido, pero .mov casi nunca se ve en Chrome ni Firefox. Conviértelo a MP4 si tu equipo no usa Safari.',

    // Uso de IA. Solo porcentajes: ningún texto de esta pestaña habla de dinero
    // ni de consumo (lo vigila components/admin/AIUsageTab.test.tsx).
    'admin.uso_ia.titulo': 'Uso de IA',
    'admin.uso_ia.descripcion': 'Cuánto de su cupo de IA de este mes lleva cada empresa, en porcentaje.',
    'admin.uso_ia.periodo': 'Mes de {mes}',
    'admin.uso_ia.plataforma': 'Toda la plataforma',
    'admin.uso_ia.plataforma_pista': 'Todas las empresas juntas, contra el límite del mes.',
    'admin.uso_ia.renueva': 'Se renueva el {fecha}',
    'admin.uso_ia.bloqueado': 'Se alcanzó el límite: la IA está en pausa para todas las empresas hasta el {fecha}.',
    'admin.uso_ia.empresas': 'Empresas',
    'admin.uso_ia.leyenda_propio': 'Uso propio',
    'admin.uso_ia.leyenda_prestado': 'Usado por otras empresas',
    'admin.uso_ia.pide_prestado': 'Usa cupo de otras empresas',
    'admin.uso_ia.presto': 'Prestó {pct}',
    'admin.uso_ia.sin_uso': { uno: '{n} empresa sin uso este mes', otros: '{n} empresas sin uso este mes' },
    'admin.uso_ia.mostrar': 'Mostrar',
    'admin.uso_ia.ocultar': 'Ocultar',
    'admin.uso_ia.nadie_uso': 'Ninguna empresa ha usado IA este mes.',
    'admin.uso_ia.vacio_titulo': 'Todavía no hay empresas',
    'admin.uso_ia.fila_aria': '{nombre}: {pct} de su cupo',
    'admin.uso_ia.plataforma_aria': 'Toda la plataforma: {pct} del límite del mes',
    'admin.uso_ia.actualizar': 'Actualizar',
    'admin.uso_ia.reintentar': 'Reintentar',
    'admin.uso_ia.error_cargar_titulo': 'No se pudo cargar el uso de IA',
    'admin.uso_ia.error_cargar_desc': 'Los datos no llegaron, así que esta pantalla no sabe cuánto se ha usado. No quiere decir que no haya uso. Detalle: {detalle}',
    'admin.uso_ia.sin_permiso_titulo': 'Tu cuenta no está en la lista de admins de la plataforma',
    'admin.uso_ia.sin_permiso_desc': 'El uso de IA solo lo ve un admin de la plataforma. Agrega este correo en la pestaña Admins.',
    // Lo que ve el contratista cuando la plataforma llega al límite del mes.
    'ia.cupo.agotado': 'La IA está en pausa hasta el día 1: se alcanzó el límite de uso de este mes.',

    'admin.plataforma.erroragregar': 'Error al agregar admin: ',
    'admin.plataforma.confirmartitulo': '¿Quitar admin de la plataforma?',
    'admin.plataforma.confirmarmensaje1': '¿Quitar el acceso de súper admin a ',
    'admin.plataforma.confirmarmensaje2': 'Se queda con su acceso al espacio de trabajo, pero pierde el control de la plataforma.',
    'admin.plataforma.quitaracceso': 'Quitar acceso',
    'admin.plataforma.cancelar': 'Cancelar',
    'admin.plataforma.toastquitado': 'Acceso de admin quitado',
    'admin.plataforma.errorquitar': 'Error al quitar admin: ',
    'admin.plataforma.titulo': 'Admins de la plataforma',
    'admin.plataforma.descripcion': 'Cuentas con acceso sin restricciones a este centro de control.',
    'admin.plataforma.avisotitulo': 'Aviso sobre privilegios de súper admin',
    'admin.plataforma.avisotexto1': 'Los usuarios que agregues aquí van a tener acceso sin restricciones al Panel de Súper Admin. Pueden ver, editar y borrar todas las cuentas,',
    'admin.plataforma.avisotexto2': 'manejar los usuarios de toda la plataforma y ver los errores de la app. No des este rol a la ligera.',
    'admin.plataforma.respaldoentorno': 'Cuenta fija del sistema',
    'admin.plataforma.nosepuedeborrar': 'no se puede borrar',
    'admin.plataforma.placeholdercorreo': 'Escribe el correo de Google Workspace…',
    'admin.plataforma.daracceso': 'Dar acceso de admin',
    'admin.plataforma.vaciotitulo': 'Todavía no hay admins agregados',
    'admin.plataforma.vaciodesc': 'Por ahora solo la cuenta fija del sistema puede entrar a este panel.',
    'admin.plataforma.columnacorreo': 'Correo',
    'admin.plataforma.columnaagregadopor': 'Agregado por',
    'admin.plataforma.columnaagregadoel': 'Agregado el',
    'admin.plataforma.sistema': 'Sistema',
    'admin.plataforma.tooltipquitar': 'Quitar acceso de admin',
    'admin.plataforma.ariaquitar': 'Quitar acceso de admin a ',
    'admin.reportes.severidad_aviso': 'Aviso',
    'admin.reportes.tiempo_ahorita': 'hace un momento',
    'admin.reportes.tiempo_minutos': 'hace ${mins}m',
    'admin.reportes.tiempo_horas': 'hace ${hrs}h',
    'admin.reportes.tiempo_dias': 'hace ${days}d',
    'admin.reportes.marcado_activo': 'Marcado como activo',
    'admin.reportes.sin_permiso': 'No tienes permiso',
    'admin.reportes.marcado_resuelto': 'Marcado como resuelto',
    'admin.reportes.borrar_log_titulo': '¿Borrar este log?',
    'admin.reportes.borrar_log_mensaje': 'Este grupo de errores se borra para siempre.',
    'admin.reportes.borrar_log_confirmar': 'Borrar',
    'admin.reportes.borrado': 'Borrado',
    'admin.reportes.borrar_resueltos_titulo': '¿Borrar todos los resueltos?',
    'admin.reportes.borrar_resueltos_mensaje': 'Borra para siempre todos los errores marcados como resueltos.',
    'admin.reportes.borrar_resueltos_confirmar': 'Borrar resueltos',
    'admin.reportes.resueltos_borrados': 'Logs resueltos borrados',
    'admin.reportes.borrar_todo_titulo': '¿Borrar TODOS los logs?',
    'admin.reportes.borrar_todo_mensaje': 'Borra todos los errores y avisos. No se puede deshacer.',
    'admin.reportes.borrar_todo_confirmar': 'Borrar todo',
    'admin.reportes.todo_borrado': 'Todos los logs borrados',
    'admin.reportes.titulo': 'Reportes del sistema',
    'admin.reportes.descripcion': 'Fallos y avisos del navegador, agrupados por fingerprint.',
    'admin.reportes.limpiar_resueltos': 'Borrar resueltos',
    'admin.reportes.limpiar_todo_aria': 'Borrar todos los logs',
    'admin.reportes.limpiar_todo_tooltip': 'Borrar todos los logs',
    'admin.reportes.kpi_errores_activos': 'Errores activos',
    'admin.reportes.kpi_unicos': '{count} únicos',
    'admin.reportes.filtro_avisos': 'Avisos',
    'admin.reportes.filtro_resueltos': 'Resueltos',
    'admin.reportes.filtro_errores': 'Errores',
    'admin.reportes.buscar': 'Busca mensaje, usuario, stack, ruta…',
    'admin.reportes.filtrar_workspace': 'Filtrar por workspace',
    'admin.reportes.todos_workspaces': 'Todos los workspaces',
    'admin.reportes.vacio_titulo': 'Ningún log coincide',
    'admin.reportes.vacio_texto': 'Todo limpio, o los filtros están muy cerrados.',
    'admin.reportes.quitar_filtros': 'Quitar filtros',
    'admin.reportes.usuarios_afectados': 'Usuarios afectados (',
    'admin.reportes.navegador': 'Navegador',
    'admin.reportes.resuelto_por': 'Resuelto por {quien} {cuando}',
    'admin.negocios.titulo': 'Negocios',
    'admin.negocios.descripcion': 'Cada negocio de la plataforma y quién es el dueño.',
    'admin.negocios.buscar': 'Buscar negocios…',
    'admin.negocios.nuevo': 'Nuevo negocio',
    'admin.negocios.vacio_filtro_titulo': 'Ningún negocio coincide con esa búsqueda',
    'admin.negocios.vacio_titulo': 'Todavía no hay negocios',
    'admin.negocios.vacio_filtro_desc': 'Prueba con otro nombre, ID del negocio o correo del dueño.',
    'admin.negocios.vacio_desc': 'Crea el primer negocio para empezar a dar de alta una compañía.',
    'admin.negocios.limpiar_busqueda': 'Limpiar búsqueda',
    'admin.negocios.nuevo_vacio': 'Nuevo negocio',
    'admin.negocios.suspender': 'Suspender',
    'admin.negocios.activar': 'Activar',
    'admin.negocios.borrar_tooltip': 'Borrar el negocio y todos sus datos para siempre',
    'admin.usuarios.titulo': 'Usuarios',
    'admin.usuarios.descripcion': 'Toda la gente con acceso a un negocio de la plataforma.',
    'admin.usuarios.buscar': 'Buscar usuarios…',
    'admin.usuarios.invitar': 'Invitar usuario',
    'admin.usuarios.vacio_filtro_titulo': 'Ningún usuario coincide con esa búsqueda',
    'admin.usuarios.vacio_titulo': 'Todavía no hay usuarios',
    'admin.usuarios.vacio_filtro_desc': 'Prueba con otro nombre o correo.',
    'admin.usuarios.vacio_desc': 'Invita al primer usuario y asígnalo a un negocio.',
    'admin.usuarios.limpiar_busqueda': 'Limpiar búsqueda',
    'admin.usuarios.invitar_vacio': 'Invitar usuario',
    'admin.usuarios.borrar_tooltip': 'Borrar el usuario para siempre',
    'chat.boton.preguntar.title': 'Pregúntale a Raza — tu asistente con IA',
    'chat.boton.preguntar.aria': 'Pregúntale a Raza — tu asistente con IA',
    'chat.boton.cerrarburbuja.aria': 'Cerrar el asistente Raza AI',
    'chat.cabecera.subtitulo': 'Pregunta lo que sea de tu negocio',
    'chat.privacidad.privada.title': 'Privada — haz clic para compartirla con el equipo',
    'chat.privacidad.compartida.title': 'Compartida — haz clic para hacerla privada',
    'chat.privacidad.privada.aria': 'Conversación privada — compartirla con el equipo',
    'chat.privacidad.compartida.aria': 'Conversación compartida — hacerla privada',
    'chat.boton.nueva.title': 'Conversación nueva',
    'chat.boton.nueva.aria': 'Conversación nueva',
    'chat.boton.pantallacompleta.title': 'Abrir en pantalla completa',
    'chat.boton.pantallacompleta.aria': 'Abrir en pantalla completa',
    'chat.boton.cerrar.title': 'Cerrar',
    'chat.boton.cerrar.aria': 'Cerrar el asistente',
    'chat.estado.escribiendo': 'Raza AI está escribiendo',
    'chat.input.aria': 'Mensaje para Raza AI',
    'chat.boton.voz.title': 'Habla con Raza AI',
    'chat.boton.voz.aria': 'Habla con Raza AI',
    'chat.boton.enviar.aria': 'Enviar mensaje',
    'chat.pie.tecnologia': 'Funciona con Gemini · Raza Pro AI',
    'ai.askraza.botontarjeta': 'Pregúntale a Raza sobre esto',
    'ai.askraza.boton': 'Pregúntale a Raza',
    'ai.askraza.chatvacio': 'Abrir un chat nuevo sobre esto',
    'adjuntos.miniatura.errorsubida': 'No se pudo subir',
    'adjuntos.miniatura.fallo': 'Falló',
    'adjuntos.miniatura.archivo': 'Doc',
    'ia.sugerencias.titulo': 'Toca una para empezar',
    'calendario.encabezado.mes_anterior': 'Mes anterior',
    'calendario.encabezado.hoy': 'Hoy',
    'calendario.encabezado.mes_siguiente': 'Mes siguiente',
    'calendario.encabezado.agendar_visita': 'Agendar visita',
    'calendario.dia.escoge_dia': 'Escoge un día',
    'calendario.dia.vacio_titulo': 'Sin día escogido',
    'calendario.dia.vacio_descripcion': 'Escoge un día en el calendario para ver su agenda.',
    'calendario.dia.en_curso': 'En curso',
    'calendario.dia.agregar': 'Agregar',
    'calendario.dia.agenda_vacia_titulo': 'Nada agendado',
    'calendario.dia.agenda_vacia_descripcion': 'Este día está libre.',
    'calendario.dia.editar_cita_aria': 'Editar cita',
    'calendario.dia.editar_cita_tooltip': 'Editar cita',
    'calendario.dia.borrar_evento_aria': 'Borrar evento',
    'calendario.dia.borrar_evento_tooltip': 'Borrar evento',
    'panel.checklist.titulo': 'Deja todo listo en minutos',
    'panel.checklist.ocultar': 'Ocultar lista',
    'materiales.duplicado.inicio': 'Ya tienes "',
    'materiales.duplicado.fin': 'en tu lista de precios. Si lo guardas otra vez te va a quedar repetido.',
    'materiales.toast.agregado_inicio': 'Agregado "',
    'materiales.toast.agregado_fin': 'a tu lista de precios.',
    'materiales.toast.error_desconocido': 'error desconocido',
    'materiales.modal.titulo': 'Agregar material nuevo',
    'materiales.modal.subtitulo': 'Se guarda en tu lista de precios y se mete en la zona que tienes abierta.',
    'materiales.modal.cerrar': 'Cerrar',
    'materiales.campo.nombre': 'Nombre del material *',
    'materiales.campo.nombre_placeholder': 'ej. mulch de corteza de pino, molido de 3 pulgadas',
    'materiales.campo.costo': 'Costo / unidad *',
    'materiales.campo.unidad': 'Unidad *',
    'materiales.campo.categoria': 'Categoría *',
    'materiales.campo.markup_placeholder': '% de markup)',
    'materiales.campo.proveedor': 'Proveedor',
    'materiales.campo.opcional': 'Opcional',
    'materiales.campo.notas': 'Notas',
    'materiales.campo.notas_placeholder': 'Opcional — specs, link del proveedor, etc.',
    'materiales.boton.cancelar': 'Cancelar',
    'materiales.boton.guardando': 'Guardando…',
    'materiales.boton.guardar': 'Guardar material',
    'estimado.partidas.unidad.horas': 'HORAS-HOMBRE',
    'estimado.partidas.unidad.piezas': 'PIEZAS',
    'estimado.partidas.vacio.titulo': 'El estimado está vacío',
    'estimado.partidas.vacio.texto': 'Usa las calculadoras o tu lista de precios para agregar tus primeras partidas.',
    'estimado.partidas.moverZona': 'Mover a una zona…',
    'estimado.partidas.cambiarTipo': 'Cambiar el tipo…',
    'estimado.partidas.borrarSel.title': 'Borrar lo seleccionado',
    'estimado.partidas.borrarSel.aria': 'Borrar lo seleccionado',
    'estimado.partidas.quitarSeleccion': 'Quitar selección',
    'estimado.partidas.borrarZona.title': 'Borrar la zona',
    'estimado.partidas.seleccionarTodo': 'Seleccionar todo',
    'estimado.partidas.col.acciones': 'Acciones',
    'estimado.partidas.totalSinZona': 'Total sin zona',
    'estimado.partidas.grupoSinZona': 'Partidas sin zona',
    'estimado.partidas.agregarZona': 'Agregar otra zona',
    'comun.cancelar': 'Cancelar',
    'estimado.snapshot.hr_punto_equilibrio': '/hr de punto de equilibrio',
    'estimado.snapshot.margen_sufijo': ' de margen',
    'estimado.snapshot.tomado_el': 'Tomado el',
    'estimado.snapshot.precios_usados': 'Precios usados en este estimado',
    'estimado.snapshot.boton_actualizar': 'Actualizar',
    'estimado.snapshot.aviso_equilibrio': 'Hoy tu punto de equilibrio es',
    'estimado.snapshot.aviso_antes': '/hr (antes',
    'estimado.snapshot.equilibrio_hr': 'Punto de equilibrio / hr',
    'estimado.snapshot.meta_hr': 'Meta / hr',
    'estimado.snapshot.overhead_hr': 'Gastos fijos / hr',
    'estimado.tab.servicios': 'Plantillas',
    'estimado.tab.materiales': 'Materiales',
    'estimado.tab.calculadoras': 'Calculadoras',
    'estimado.tab.precios': 'Precios',
    'estimado.pista.escoge': 'Escoge un servicio abajo y toca',
    'estimado.pista.agregar': 'Agregar',
    'estimado.pista.automatico': '— las partidas se meten solas a tu estimado.',
    'estimado.zona.agregar': 'Agregar zona',
    'estimado.zona.confirmar': 'Confirmar zona',
    'estimado.zona.cancelar': 'Cancelar',
    'estimado.rapido.dumpfee': 'Cargo de dump',
    'estimado.rapido.entrega': 'Entrega',
    'estimado.rapido.rentaequipo': 'Renta de equipo',
    'estimado.margen.bajometa': 'Abajo de tu meta',
    'estimado.margen.enmeta': 'En tu meta',
    'estimado.grafica.manodeobra': 'Mano de obra y cargas',
    'estimado.grafica.materiales': 'Materiales y subcontratistas',
    'estimado.grafica.ganancia': 'Ganancia neta',
    'estimado.anticipo.saldo': 'Saldo',
    'estimado.anticipo.alterminar': 'al terminar',
    'estimado.impuesto.aplicararia': 'Cobrar impuesto',
    'estimado.impuesto.tasaaria': 'Porcentaje de impuesto',
    'estimado.vacio.agregarmaterial': 'Agregar material',
    'precios.siguiente.titulo': 'Esto es lo que sigue',
    'precios.siguiente.pendiente': ' costo por llenar',
    'precios.siguiente.pendientes': ' costos por llenar',
    'precios.siguiente.seguardan': ' Se guardan en tu lista de precios para la próxima.',
    'precios.siguiente.sigue': 'Siguiente:',
    'precios.siguiente.mostrar': 'Muéstrame el que sigue',
    'estimados.precios.modal.tip': 'Tip rápido',
    'estimados.precios.modal.titulo': 'Una cosita antes',
    'estimados.precios.modal.texto_inicio': 'Todavía hay',
    'estimados.precios.modal.costo_singular': ' costo',
    'estimados.precios.modal.costo_plural': ' costos',
    'estimados.precios.modal.texto_fin': ' por llenar en este estimado.',
    'estimados.precios.modal.texto_explicacion': 'Llena eso primero y se te queda guardado para el próximo trabajo.',
    'estimados.precios.modal.sigue': 'Sigue:',
    'estimados.precios.modal.pendiente': 'Falta agregar:',
    'estimados.precios.modal.boton_siguiente': 'Enséñame qué sigue',
    'estimados.precios.modal.boton_continuar': 'Agrégalo — luego le pongo precio',
    'estimados.precios.modal.cerrar': 'Cerrar',
    'plantillaPriv.error.faltaNombre': 'Ponle nombre a la plantilla.',
    'plantillaPriv.error.sinPartidas': 'Una plantilla necesita al menos una partida.',
    'plantillaPriv.manoDeObraGeneral': 'Mano de obra general',
    'plantillaPriv.cerrar': 'Cerrar',
    'plantillaPriv.nombre': 'Nombre de la plantilla',
    'plantillaPriv.traerCategoria': 'Traer materiales de una categoría',
    'plantillaPriv.traerCategoriaOpcion': 'Agrega los materiales de una categoría en $0…',
    'plantillaPriv.traerCategoriaAyuda': 'Mete los materiales de la categoría sin precio — nomás pon cada costo. Las partidas con precio también se guardan en tu lista de precios.',
    'plantillaPriv.partidas': 'Partidas',
    'plantillaPriv.descripcionPartida': 'Descripción de la partida',
    'plantillaPriv.cantidad': 'Cant',
    'plantillaPriv.costo': 'Costo $',
    'plantillaPriv.tip.manoDeObraBloqueada': 'La tarifa de mano de obra viene de tu calculadora de overhead en Ajustes.',
    'plantillaPriv.quitarPartidaAria': 'Quitar partida',
    'plantillaPriv.quitarPartidaTip': 'Quitar partida',
    'plantillaPriv.vacio.titulo': 'Todavía no hay partidas',
    'plantillaPriv.vacio.texto': 'Agrega abajo una partida de material o de mano de obra.',
    'plantillaPriv.avisoMinimo': 'Una plantilla necesita al menos una partida para guardarse.',
    'plantillaPriv.agregarMaterial': 'Agregar material',
    'plantillaPriv.agregarManoDeObra': 'Agregar mano de obra',
    'plantillaPriv.notaPie': 'Al agregar material se guarda en tu lista de precios. La mano de obra sale de tu calculadora de overhead y aquí queda bloqueada.',
    'plantillaPriv.cancelar': 'Cancelar',
    'estimado.plantillas.titulo': '¿Guardar como plantilla privada?',
    'estimado.plantillas.cambiasteprecios': 'Cambiaste precios en ',
    'estimado.plantillas.guardaconunombre': 'Guarda una copia con tu propio nombre para volver a usar los precios nuevos la próxima vez.',
    'estimado.plantillas.cerrar': 'Cerrar',
    'estimado.plantillas.basadaen': 'Basada en',
    'estimado.plantillas.guardar': 'Guardar',
    'estimado.plantillas.placeholdernombre': 'Ponle nombre a esta plantilla…',
    'estimado.plantillas.omitir': 'Solo guardar el estimado',
    'estimado.plantillas.nadaseleccionado': 'Nada seleccionado',
    'plantillas.toast.agregado': 'Agregado',
    'plantillas.toast.zonanueva': '→ zona nueva',
    'plantillas.boton.nueva': 'Nueva plantilla',
    'plantillas.vacio.titulo': 'Todavía no tienes plantillas privadas',
    'plantillas.vacio.texto': 'Usa una plantilla Global, ajusta tus precios en el estimado y guarda — te va a preguntar si la quieres guardar con tu nombre. Después la vuelves a usar con un clic.',
    'plantillas.tab.globales': 'Globales',
    'plantillas.tab.privadas': 'Privadas',
    'plantillas.toast.errorborrar': 'No se pudo borrar',
    'plantillas.boton.editar.title': 'Editar plantilla',
    'plantillas.boton.editar.aria': 'Editar plantilla',
    'plantillas.boton.borrar.title': 'Borrar plantilla privada',
    'plantillas.boton.borrar.aria': 'Borrar plantilla',
    'plantillas.boton.agregar': 'Agregar al estimado',
    'plantillas.boton.todascategorias': 'Todas las categorías',
    'calculadoras.shell.volver': 'Volver a las calculadoras',
    'estimado.concreto.forma_escalones': 'Escalones',
    'estimado.concreto.base_losa': '{length}\' × {width}\' × {thicknessIn}" de grueso',
    'estimado.concreto.base_sonotube': '{tubeCount}× {diameterIn}" Ø × {heightFt}\' de alto',
    'estimado.concreto.base_escalones': '{stepCount} escalones × {stepRunIn}" de run × {stepRiseIn}" de rise × {stepWidthFt}\' de ancho',
    'estimado.concreto.partida_readymix': 'Concreto ready-mix',
    'estimado.concreto.partida_sacos': 'Mezcla de concreto (sacos de 80 lb)',
    'estimado.concreto.basis_desperdicio': '{basis}, +{waste}% de desperdicio',
    'estimado.concreto.basis_desperdicio_total': '{basis}, +{waste}% de desperdicio, {cuYd} cu yd en total',
    'estimado.concreto.plantilla_nombre': 'Concreto — {basis}',
    'estimado.concreto.toast_readymix': 'Se agregaron {cuYd} cu yd de ready-mix',
    'estimado.concreto.toast_sacos': 'Se agregaron {bags} sacos de 80 lb',
    'estimado.concreto.campo_forma': 'Tipo de colado',
    'estimado.concreto.seccion_medidas': 'Medidas',
    'estimado.concreto.campo_largo': 'Largo',
    'estimado.concreto.campo_grueso': 'Grueso',
    'estimado.concreto.seccion_tubo': 'Datos del tubo',
    'estimado.concreto.campo_diametro': 'Diámetro',
    'estimado.concreto.campo_alto': 'Alto',
    'estimado.concreto.campo_cantidad': 'Cantidad',
    'estimado.concreto.unidad_tubos': 'tubos',
    'estimado.concreto.seccion_escalon': 'Datos del escalón',
    'estimado.concreto.campo_num_escalones': 'Cuántos escalones',
    'estimado.concreto.unidad_cada_uno': 'c/u',
    'estimado.concreto.campo_run': 'Run (fondo)',
    'estimado.concreto.campo_rise': 'Rise (alto)',
    'estimado.concreto.seccion_ocupas': 'Lo que ocupas',
    'estimado.concreto.salida_sacos_valor': '{bags} sacos de 80 lb',
    'estimado.concreto.hint_readymix': 'Incluye {waste}% de desperdicio · redondeado para arriba a 0.25 yd',
    'estimado.concreto.hint_sacos': 'Incluye {waste}% de desperdicio · {cuYd} cu yd en total',
    'estimado.concreto.salida_yardas': 'Yardas cúbicas (con desperdicio)',
    'estimado.concreto.salida_magic81': 'Atajo del "Magic 81"',
    'estimado.concreto.hint_magic81': 'Sqft del slab ÷ 81 (a 4 pulgadas) — para checar el número',
    'estimado.concreto.salida_sacos_60': 'Sacos (60 lb)',
    'estimado.concreto.salida_sacos_40': 'Sacos (40 lb)',
    'estimado.concreto.salida_peso': 'Peso aproximado',
    'estimado.concreto.boton_readymix': 'Agregar {cuYd} cu yd de ready-mix',
    'estimado.concreto.boton_sacos': 'Agregar {bags} sacos de 80 lb',
    'estimado.concreto.boton_sin_medidas': 'Pon las medidas para agregar',
    'estimados.calc.sod.zona_default': 'Yarda de enfrente',
    'estimados.calc.sod.zona_nueva': { uno: 'Área {count}', otros: 'Área {count}' },
    'estimados.calc.sod.forma_curva': 'Orillas curveadas (10%)',
    'estimados.calc.sod.forma_compleja': 'Complicado / muchos recortes (15%)',
    'estimados.calc.sod.pasto_mezcla': 'Mezcla Fescue / Bluegrass',
    'estimados.calc.sod.areas': 'Áreas de pasto',
    'estimados.calc.sod.nombre_area': 'Nombre del área',
    'estimados.calc.sod.quitar_area': 'Quitar área',
    'estimados.calc.sod.largo': 'Largo',
    'estimados.calc.sod.ancho': 'Ancho',
    'estimados.calc.sod.agregar_area': 'Agregar otra área',
    'estimados.calc.sod.forma': 'Forma / desperdicio',
    'estimados.calc.sod.tipo_pasto': 'Tipo de pasto',
    'estimados.calc.sod.necesitas': 'Vas a necesitar',
    'estimados.calc.sod.cobertura': 'Área total',
    'estimados.calc.sod.sqft_por_pallet': 'sqft por pallet',
    'estimados.calc.sod.sqft_por_slab': 'sqft por slab',
    'estimados.calc.sod.sqft_pedidos': ' sqft a pedir',
    'estimados.calc.sod.boton_sin_medidas': 'Pon las medidas para agregar',
    'estimados.calc.sod.toast_parte1': 'Se agregaron ',
    'estimados.calc.sod.toast_parte2': ' slabs de ',
    'calculadoras.mulch.nombre': 'Volumen de mulch / tierra / grava',
    'calculadoras.mulch.desc': 'Yardas cúbicas de largo × ancho × profundidad. Mulch, tierra, grava, arena, composta.',
    'calculadoras.sod.nombre': 'Sod / área de césped',
    'calculadoras.sod.desc': 'Pies cuadrados → planchas y pallets, con desperdicio según la forma del área.',
    'calculadoras.concreto.nombre': 'Concreto',
    'calculadoras.concreto.desc': 'Yardas cúbicas para losas, footings, sonotubes y escalones. 10% de desperdicio automático.',
    'calculadoras.plantas.nombre': 'Separación de plantas',
    'calculadoras.plantas.desc': 'Cuántas plantas caben según el área y la separación centro a centro. Ya viene en triángulo.',
    'estimado.categorias.vacio_titulo': 'Todavía no hay categorías',
    'estimado.categorias.vacio_descripcion': 'Este oficio todavía se está armando.',
    'estimado.plantillas.confianza.triangulado': 'Comparado',
    'estimado.plantillas.confianza.aproximado': 'Aproximado',
    'estimado.plantillas.aviso.catalogo': '⚠ no está en el catálogo:',
    'estimado.plantillas.vacio.titulo': '— todavía no hay plantillas',
    'estimado.plantillas.vacio.boton': 'Mejor abre la calculadora',
    'estimado.plantillas.ayuda.titulo': 'De dónde salen las plantillas',
    'estimado.plantillas.ayuda.texto1': 'Pon el JSON de plantillas v2 en',
    'estimado.plantillas.ayuda.texto2': 'y van a aparecer aquí.',
    'estimado.plantillas.toast.deconfiguracion': ' con precio de tus ajustes)',
    'estimado.plantillas.toast.zonafallback': 'zona',
    'estimado.plantillas.toast.zonanueva': '→ zona nueva "',
    'estimado.plantillas.conteo.servicios': ' servicios',
    'estimado.plantillas.boton.agregar': '\n                  Agregar\n',
    'estimado.plantillas.zona.title': 'Dónde caen estas partidas',
    'estimado.plantillas.zona.arialabel': 'Zona de destino',
    'estimado.plantillas.zona.auto': 'Zona nueva (auto)',
    'estimado.plantillas.boton.menos': ' Menos',
    'estimado.plantillas.meta.subcontrata': 'Muchas veces lo hace un sub',
    'estimado.plantillas.meta.calculadora': 'Hay calculadora',
    'estimado.plantillas.meta.calculadorapronto': 'Calculadora en camino',
    'estimado.plantillas.aviso.datos': 'Hay que revisar los datos',
    'estimado.plantillas.aviso.precios': 'Revisa los precios',
    'estimado.plantillas.partidas.titulo': 'Partidas (',
    'estimado.plantillas.partidas.suma': 'Suma de partidas',
    'estimado.plantillas.aviso.mediana1': 'Ojo — la suma de las partidas (',
    'estimado.plantillas.aviso.mediana2': ') no cuadra con la mediana (',
    'estimado.plantillas.aviso.mediana3': '). Se van a usar las partidas; si quieres cuadrar con la mediana, edita las filas después.',
    'estimado.plantillas.seccion.supuestos': 'Supuestos',
    'estimado.plantillas.seccion.exclusiones': 'Exclusiones',
    'estimado.plantillas.seccion.normativa': 'Normas y permisos',
    'estimado.plantillas.seccion.fuentes': 'Fuentes',
    'finanzas.trabajos.filtro.todo': 'Todo',
    'finanzas.trabajos.filtro.proyectos': 'Proyectos',
    'finanzas.trabajos.filtro.visitas': 'Visitas',
    'finanzas.trabajos.stat.trabajos': 'Trabajos',
    'finanzas.trabajos.stat.proyectosvisitas': 'Proyectos / visitas',
    'finanzas.trabajos.stat.valor': 'Cuánto suman',
    'finanzas.trabajos.placeholder.buscar': 'Busca un trabajo…',
    'finanzas.trabajos.vacio.titulo': 'No hay trabajos',
    'finanzas.trabajos.etiqueta.sinfecha': 'Sin fecha',
    'contratos.stat.activos': 'Contratos activos',
    'contratos.stat.recurrentemes': 'Lo que entra cada mes',
    'contratos.stat.soloactivos': 'Solo activos',
    'contratos.stat.visitashechas': 'Visitas hechas',
    'contratos.stat.ingresototal': 'Total cobrado en rutas',
    'contratos.stat.facturado': 'Facturado',
    'contratos.stat.facturadoAyuda': 'Pagado o no',
    'contratos.stat.cobrado': 'Cobrado',
    'contratos.stat.porcobrar': 'Falta por cobrar',
    'contratos.stat.porcobrarAyuda': 'Facturas sin pagar',
    'contratos.buscar.placeholder': 'Busca cliente o dirección…',
    'contratos.vacio.titulo': 'No hay contratos',
    'contratos.vacio.texto': 'Aquí aparecen los contratos recurrentes que creas en Rutas de servicio.',
    'contratos.filtro.texto': 'Prueba otra búsqueda o cambia el filtro de estado.',
    'contratos.tabla.cliente': 'Cliente',
    'contratos.tabla.frecuencia': 'Frecuencia',
    'contratos.tabla.estado': 'Estado',
    'contratos.tabla.visitas': 'Visitas',
    'contratos.tabla.pormes': 'Por mes',
    'contratos.tabla.total': 'Total',
    'contratos.tabla.abrirrutas': 'Abrir rutas de servicio',
    'contratos.movil.pormes': '/mes',
    'contratos.nota.rutas': 'Los contratos se manejan en Rutas de servicio — si escoges uno te lleva para allá.',
    'contratos.nota.facturacion': 'El mes que ya tiene factura vale lo que dice la factura. El mes que nadie facturó se calcula por visita, que es un estimado.',
    'finanzasNum.fuente.proyectos': 'Proyectos',
    'finanzasNum.fuente.proyectosAyuda': 'Trabajos de una sola vez, desde la propuesta aceptada hasta terminar',
    'finanzasNum.fuente.enPropuesta': 'cobrado en la propuesta',
    'finanzasNum.fuente.enFactura': 'cobrado en una factura del proyecto',
    'finanzasNum.fuente.rutas': 'Rutas de servicio',
    'finanzasNum.fuente.rutasAyuda': 'Visitas recurrentes completadas',
    'finanzasNum.titulo': 'Ingresos por fuente',
    'finanzasNum.subtitulo': 'Dinero que de veras entró, contado el día que cayó.',
    'finanzasNum.kpi.recurrente': 'Recurrente / mes',
    'finanzasNum.kpi.recurrenteAyuda': 'Contratos de servicio activos',
    'finanzasNum.kpi.sinCobrar': 'Todavía sin cobrar',
    'finanzasNum.kpi.sinCobrarAyuda': 'Valor de proyectos sin ningún pago registrado',
    'finanzasNum.pl.titulo': 'Ganancias y pérdidas de proyectos',
    'finanzasNum.pl.subtitulo': 'Todos los proyectos activos. Los cancelados no cuentan.',
    'finanzasNum.pl.materiales': 'Materiales y gastos',
    'finanzasNum.pl.manoDeObra': 'Mano de obra',
    'finanzasNum.pl.overhead': 'Tu parte de los gastos del negocio',
    'finanzasNum.pl.reparaciones': 'Reparaciones y mantenimiento',
    'finanzasNum.pl.neta': 'Ganancia neta',
    'finanzasNum.pl.netaVacia': 'Ganancia neta',
    'finanzasNum.pl.netaVaciaAyuda': 'Pon tus tarifas de mano de obra y overhead en Ajustes para ver esto',
    'finanzasNum.reparto.titulo': 'De dónde vino el dinero',
    'finanzasNum.reparto.vacioTitulo': 'No hubo ingresos en este periodo',
    'finanzasNum.reparto.vacioTexto': 'Aquí aparece el dinero cuando marcas una factura como pagada, te pagan una propuesta o se completa una visita de servicio.',
    'finanzasNum.reparto.nota1': 'Estas dos suman el Cobrado de arriba. Una propuesta aceptada se vuelve',
    'finanzasNum.reparto.nota2': 'proyecto, así que los pagos de la propuesta son dinero del proyecto — se cuentan aquí, no',
    'finanzasNum.reparto.nota3': 'como una fuente aparte.',
    'finanzasNum.libro.titulo': 'Libro de proyectos (desde el inicio)',
    'finanzasNum.libro.subtitulo': 'Solo proyectos — aquí no entra el dinero de las rutas de servicio.',
    'finanzasNum.libro.gastos': 'Gastos registrados',
    'finanzasNum.libro.bruta': 'Ganancia bruta',
    'finanzasNum.libro.cobrado': 'Cobrado',
    'finanzasNum.libro.facturadoSinPagar': 'Facturado, sin pagar',
    'finanzasNum.libro.nota1': 'La ganancia bruta es el valor del contrato menos los gastos registrados. La mano de obra se lleva aparte',
    'finanzasNum.libro.nota2': 'en las horas y aquí no se resta.',
    /* El título de esta tarjeta ya no lleva el rango: el rango se fue a la
       pastilla de alcance, que además dice si el selector de arriba la mueve o
       no. La clave con `{rango}` se borró al cerrar la Ola 1 en vez de dejarla
       muerta, porque una clave sin uso pero con marcador invita a volver a
       pegarle el rango al título y a pintarlo dos veces. Ver
       'finanzasNum.mant.tituloLlano' y 'finanzas.alcance.siempre'. */
    'finanzasNum.mant.subtitulo': 'Las reparaciones registradas contra el presupuesto de mantenimiento de tu overhead.',
    'finanzasNum.mant.presupuestado': 'Presupuestado',
    'finanzas.proyectos.buscar': 'Busca por proyecto o cliente…',
    'finanzas.proyectos.filtro_todos': 'Todos',
    'finanzas.proyectos.vacio_titulo': 'No hay proyectos',
    'finanzas.proyectos.col_proyecto': 'Proyecto',
    'finanzas.proyectos.col_estado': 'Estado',
    'finanzas.proyectos.col_terminado': 'Terminado',
    'finanzas.proyectos.col_valor': 'Monto',
    'finanzas.proyectos.col_materiales': 'Materiales',
    'finanzas.proyectos.tooltip_ganancia_bruta': 'Es el monto menos los materiales — no se resta la labor ni los gastos fijos',
    'finanzas.proyectos.col_ganancia_bruta': 'Ganancia bruta',
    'finanzas.proyectos.tooltip_labor': 'Lo que le pagas a la cuadrilla, más FICA y prestaciones, por las horas checadas en este trabajo',
    'finanzas.proyectos.tooltip_gastos_fijos': 'La parte de los gastos fijos que le toca a este trabajo: horas trabajadas × tus gastos fijos por hora',
    'finanzas.proyectos.col_gastos_fijos': 'Gastos fijos',
    'finanzas.proyectos.tooltip_ganancia_neta': 'El monto menos materiales, labor y gastos fijos',
    'finanzas.proyectos.col_ganancia_neta': 'Ganancia neta',
    'finanzas.proyectos.abrir_proyecto': 'Abrir proyecto',
    'finanzas.proyectos.tooltip_sin_tarifas': 'Pon tus tarifas de labor y gastos fijos en Ajustes para ver la ganancia neta',
    'finanzas.proyectos.tooltip_sin_horas': 'Nadie checó horas en este trabajo, así que no se pudo cargar labor ni gastos fijos. Esta no es una ganancia neta real.',
    'finanzas.proyectos.sin_horas': 'sin horas',
    'finanzas.proyectos.movil_terminado': 'Terminado',
    'finanzas.proyectos.movil_neto': 'neto',
    'finanzas.proyectos.movil_bruto': 'bruto',
    'finanzas.proyectos.nota_pie': 'Abre cualquier proyecto para ver todo: tareas, labor, materiales y las fotos.',
    // Tablero de Seguimientos. Ver views/FollowUpsView.tsx y su modal de detalle.
    'seguimientos.columna.vistas': 'Abiertas',
    'seguimientos.columna.ganadas': 'Ganadas',
    'seguimientos.columna.perdidas': 'Perdidas',
    'seguimientos.tarjeta.vista': 'Abierta',
    'seguimientos.error.titulo': 'No se pudieron cargar las propuestas',
    'seguimientos.error.descripcion': 'Revisa tu conexión e inténtalo de nuevo.',
    'seguimientos.error.reintentar': 'Reintentar',
    'seguimientos.detalle.propuesta_generica': 'Estimado',
    'seguimientos.detalle.cerrar': 'Cerrar',
    'seguimientos.detalle.creada_por': 'Lo hizo',
    'seguimientos.detalle.historial': 'Historial',
    'seguimientos.detalle.enviada_titulo': 'Estimado enviado',
    // Ya no sale ningún correo desde la app: el link lo manda el contratista.
    'seguimientos.detalle.enviada_desc': 'Le mandaste el link al cliente.',
    'seguimientos.detalle.vista_titulo': 'El cliente lo vio',
    // Cierto tanto si el cliente abrió el link como si lo marcaste tú a mano.
    'seguimientos.detalle.vista_desc': 'El cliente ya vio la propuesta.',
    'seguimientos.detalle.firma_titulo': 'Firma del cliente',
    'seguimientos.detalle.firma_alt': 'Firma del cliente',
    'seguimientos.detalle.proyecto_creado': 'Proyecto creado',
    'seguimientos.detalle.convertir_proyecto': 'Pasar a proyecto',
    'seguimientos.detalle.marcado_a_mano': 'Lo marcaste tú a mano; el cliente no tocó el link.',
    'seguimientos.mover.titulo': 'Si te contestó por otro lado',
    'seguimientos.mover.descripcion': 'Si te llamó, te escribió o te dijo en persona, márcalo aquí y la propuesta se cambia de columna.',
    'seguimientos.mover.titulo.reabrir': '¿Te volvió a buscar?',
    'seguimientos.mover.descripcion.reabrir': 'Reábrela y regresa al tablero como si la acabaras de mandar. Si se le pasó la fecha, se le dan {dias} días más de vigencia para que el cliente todavía pueda firmarla.',
    'seguimientos.mover.reabrir.sinabrir': 'Reabrir sin abrir',
    'seguimientos.mover.reabrir.abierta': 'Reabrir como ya vista',
    'seguimientos.reabrir.boton': 'Reabrir',
    'seguimientos.reabrir.aria': 'Reabrir la propuesta de {nombre}',
    'seguimientos.mover.sinabrir': 'No la ha abierto',
    'seguimientos.mover.abierta': 'Ya la vio',
    'seguimientos.mover.ganada': 'La gané',
    'seguimientos.mover.perdida': 'La perdí',
    'seguimientos.mover.moviendo': 'Moviendo…',
    'seguimientos.mover.ganadaesfinal': 'Esta ya está ganada y desde aquí no se mueve: casi siempre hay una firma y un proyecto detrás. Si hay que corregirla, cámbiale el estado en Estimados.',
    'seguimientos.mover.perdidareabrir': 'Para darla por ganada, primero reábrela.',
    'seguimientos.mover.confirmaganada.titulo': '¿La diste por ganada?',
    'seguimientos.mover.confirmaganada.mensaje': 'Desde el tablero ya no vas a poder regresarla. Enseguida te sale el botón para pasarla a proyecto.',
    'seguimientos.mover.confirmaganada.si': 'Sí, la gané',
    'seguimientos.mover.listo': 'Listo, ya la moví',
    'seguimientos.mover.listovigencia': 'Reabierta, y vigente hasta el {fecha}',
    'seguimientos.mover.error': 'No se pudo mover la propuesta',
    'seguimientos.mover.bloqueo.ganadaesfinal': 'La de {nombre} ya está ganada: desde el tablero no se regresa.',
    'seguimientos.mover.bloqueo.perdidanosalta': 'Para dar por ganada la de {nombre}, primero reábrela.',
    'seguimientos.arrastra.pista': 'Arrastra una tarjeta de una columna a otra para moverla, o ábrela y márcalo desde ahí.',
    'ayuda.video.cerrar_tooltip': 'Cerrar tutorial',
    'ayuda.video.cerrar_aria': 'Cerrar tutorial',
    'ayuda.video.reproducir_aria': 'Reproducir',
    'ayuda.video.proximamente': 'Tutorial en camino',
    'ayuda.video.sin_video_desc': 'Todavía no se ha subido el video de esta pantalla.',
    'ayuda.video.cargando': 'Cargando tutorial…',
    'ayuda.video.error_titulo': 'No se pudo cargar el video',
    'ayuda.video.error_reintentar': 'Reintentar',
    'ayuda.video.toca_para_ver': 'Toca para reproducir',
    'ayuda.video.redimensionar_aria': 'Cambiar el tamaño de la ventana',
    'ayuda.video.restablecer': 'Volver al tamaño y lugar de siempre',
    'ayuda.video.cerrar_confirma': '¿Cerrar el tutorial? Vas a perder el punto donde ibas.',
    'ayuda.video.cerrar_confirma_si': 'Cerrar',
    'ayuda.video.cerrar_confirma_no': 'Seguir viendo',
    'ayuda.video.barra_aria': 'Barra de tiempo',
    'ayuda.video.pantalla_completa_tooltip': 'Pantalla completa',
    'ayuda.video.pantalla_completa_aria': 'Pantalla completa',
    'ayuda.video.etiqueta': 'Cómo se usa',
    'ayuda.video.tooltip': 'Mira un video corto de cómo usar esta pantalla',
    'precios.catalogo.toast.guardado': 'Guardado · ',
    'precios.catalogo.toast.error': 'No se pudo guardar ',
    'precios.catalogo.grupo.sincategoria': '(sin categoría)',
    'precios.catalogo.titulo': 'Materiales del catálogo',
    'precios.catalogo.filtro.soloconprecio': 'Solo con precio',
    'precios.catalogo.filtro.soloconpreciocorto': 'Con precio',
    'precios.catalogo.buscar.placeholder': 'Busca materiales…',
    'precios.catalogo.ayuda.titulo': 'Toca cualquier precio para poner el tuyo.',
    'precios.catalogo.ayuda.texto': 'El precio que guardes aquí se pasa automáticamente a cada estimado que use ese material.',
    'precios.catalogo.vacio.titulo': 'Nada coincide con tus filtros',
    'precios.catalogo.vacio.texto': 'Prueba otra categoría o limpia la búsqueda.',
    'precios.catalogo.costo.placeholder': 'Toca para poner tu costo',
    'precios.catalogo.tabla.conprecio': 'Con precio',
    'precios.catalogo.tabla.nombre': 'Nombre',
    'precios.catalogo.tabla.unidad': 'Unidad',
    'precios.catalogo.tabla.tipo': 'Tipo',
    'precios.catalogo.tabla.tucosto': 'Tu costo',
    'cuadrilla.error.desconocido': 'Error desconocido',
    'cuadrilla.modal.titulo': 'Asignar cuadrilla',
    'cuadrilla.buscar.placeholder': 'Busca por nombre o correo…',
    'cuadrilla.filtro.incluirroles': 'Incluir Admin y Oficina',
    'cuadrilla.presets.titulo': 'Agrega rápido desde tus cuadrillas',
    'cuadrilla.vacio.titulo': 'Sin resultados',
    'cuadrilla.vacio.texto': 'No hay nadie con esa búsqueda o ese filtro.',
    'cuadrilla.boton.cancelar': 'Cancelar',
    'cuadrilla.boton.guardando': 'Guardando…',
    'proyecto.equipo.titulo': 'Equipo asignado',
    'proyecto.equipo.descripcion': 'Saca maquinaria pesada y trocas de la compañía para este proyecto.',
    'proyecto.equipo.cancelar': 'Cancelar',
    'proyecto.equipo.registrarnuevo': 'Registrar nuevo',
    'proyecto.equipo.registrorapido': 'Registro rápido de equipo',
    'proyecto.equipo.placeholdernombre': 'Nombre del equipo (ej. Excavadora CAT 330)',
    'proyecto.equipo.tipoherramienta': 'Herramienta',
    'proyecto.equipo.tipovehiculo': 'Vehículo',
    'proyecto.equipo.tipopesado': 'Equipo pesado',
    'proyecto.equipo.tipotrailer': 'Tráiler',
    'proyecto.equipo.tipootro': 'Otro',
    'proyecto.equipo.registrando': 'Registrando…',
    'proyecto.equipo.registrar': 'Registrar',
    'proyecto.equipo.enlaobra': 'En la obra',
    'proyecto.equipo.regresando': 'Regresando…',
    'proyecto.equipo.regresaraltaller': 'Regresar al taller',
    'proyecto.equipo.sinasignado': 'No hay equipo asignado',
    'proyecto.equipo.sinasignadodesc': 'Saca cosas del inventario del taller para llevar el control de lo que hay en este trabajo.',
    'proyecto.equipo.inventariotaller': 'Inventario del taller',
    'proyecto.equipo.sacando': 'Sacando…',
    'proyecto.equipo.sacar': 'Sacar',
    'proyecto.equipo.sindisponible': 'No hay equipo disponible',
    'proyecto.equipo.sindisponibledesc': 'Todo está en uso o en el taller en mantenimiento.',
    'proyecto.equipo.toastsalioatrabajo': ' salió para este trabajo',
    'proyecto.equipo.toastnosepudosacar': 'No se pudo sacar ',
    'proyecto.equipo.toastregresotaller': ' regresó al taller',
    'proyecto.equipo.toastnosepudoregresar': 'No se pudo regresar ',
    'proyecto.equipo.toastregistrado': ' se registró',
    'proyecto.equipo.toastnosepudoregistrar': 'No se pudo registrar el equipo: ',
    'finanzasProy.facturas.pagadaEl': 'Pagada el ',
    'finanzasProy.facturas.cambiarEstatus': 'Cambiar el estatus de la factura',
    'finanzasProy.gastos.guardadoSinRecibo': ' — se guardó sin el recibo.',
    'finanzasProy.gastos.agregado': 'Gasto guardado',
    'finanzasProy.gastos.reciboAgregado': 'Recibo agregado',
    'finanzasProy.pdf.negocioSinNombre': 'Tu negocio',
    'finanzasProy.pdf.comoPagar': 'Cómo pagar',
    'finanzasProy.pdf.pagarEnLinea': 'Paga en línea: ',
    'finanzasProy.pdf.zelleTelefono': 'Zelle al teléfono: ',
    'finanzasProy.pdf.cobrarA': 'Cobrar a',
    'finanzasProy.pdf.fecha': 'Fecha de la factura:',
    'finanzasProy.pdf.sinConceptos': 'Sin conceptos',
    'finanzasProy.pdf.totalPagar': 'Total a pagar',
    'finanzasProy.pdf.gracias': 'Gracias por tu preferencia.',
    'finanzasProy.pdf.popupBloqueado': 'El navegador bloqueó la ventana. Permite las ventanas emergentes para bajar el PDF.',
    'finanzasProy.kpi.facturado': 'Facturado $',
    'finanzasProy.kpi.pagado': '· Pagado $',
    'finanzasProy.kpi.margenNeto': 'Margen neto ',
    'finanzasProy.costos.manoDeObra': 'Mano de obra (',
    'finanzasProy.gastos.reciboVista': 'Foto del recibo',
    'finanzasProy.gastos.quitarFoto.title': 'Quitar la foto',
    'finanzasProy.gastos.quitarFoto.aria': 'Quitar la foto',
    'finanzasProy.gastos.subiendoRecibo': 'Subiendo el recibo…',
    'finanzasProy.gastos.verRecibo.aria': 'Ver recibo',
    'finanzasProy.gastos.agregarFoto.aria': 'Agregar foto del recibo',
    'finanzasProy.gastos.cambiarRecibo.title': 'Cambiar el recibo',
    'finanzasProy.gastos.cambiarRecibo.aria': 'Cambiar el recibo',
    'comun.cerrar': 'Cerrar',
    'finanzasProy.facturas.numero.placeholder': 'ej. INV-001',
    'finanzasProy.facturas.terminos.alRecibir': 'Al recibirla',
    'finanzasProy.facturas.conceptos': 'Conceptos',
    'finanzasProy.facturas.crearBoton': 'Crear la factura',
    'proyecto.materiales.columna.listacompras': 'Lista de compras',
    'proyecto.materiales.columna.listo': 'Listo / En sitio',
    'proyecto.materiales.item.sueltonombre': 'Material suelto',
    'proyecto.materiales.item.sueltotipo': 'Suelto',
    'proyecto.materiales.grupo.sinproveedor': 'Sin proveedor',
    'proyecto.materiales.compartir.titulo': 'Lista de compras',
    'proyecto.materiales.compartir.vacio': 'No hay nada que pedir.',
    'proyecto.materiales.imprimir.bloqueado': 'El navegador bloqueó la ventana. Permite los pop-ups para imprimir.',
    'proyecto.materiales.csv.articulo': 'Artículo',
    'proyecto.materiales.csv.proveedor': 'Proveedor',
    'proyecto.materiales.csv.tipo': 'Tipo',
    'proyecto.materiales.csv.cantidad': 'Cantidad',
    'proyecto.materiales.csv.costo': 'Costo',
    'proyecto.materiales.csv.estado': 'Estado',
    'proyecto.materiales.editar.descripcion': 'Descripción',
    'proyecto.materiales.tarjeta.editar.tooltip': 'Editar',
    'proyecto.materiales.tarjeta.quitar.tooltip': 'Quitar',
    'proyecto.materiales.tarjeta.huerfano': 'Fuera del presupuesto',
    'proyecto.materiales.tarjeta.huerfano.tooltip': 'Esta partida ya no está en el presupuesto de la obra. Sigue contando en Finanzas; puedes quitarla si ya no aplica.',
    'proyecto.materiales.tarjeta.unidades': 'unidades',
    'proyecto.materiales.tarjeta.mover': 'Mover',
    'proyecto.materiales.tarjeta.deshacer': 'Regresar',
    'proyecto.materiales.titulo': 'Tablero de materiales',
    'proyecto.materiales.boton.cancelar': 'Cancelar',
    'proyecto.materiales.boton.agregaralista': 'Agregar a la lista',
    'proyecto.materiales.form.cerrar': 'Cerrar',
    'proyecto.materiales.form.nombre': 'Nombre del material (ej. bulto de cemento de 60 lb)',
    'proyecto.materiales.form.sugerencias': 'De tu lista de precios',
    'proyecto.materiales.grupo.pedirtodo.tooltip': 'Marcar como pedido todo lo de este proveedor',
    'proyecto.materiales.grupo.pedirtodo': 'Pedir todo',
    'proyecto.materiales.columna.vacia': 'Nada aquí',
    'proyecto.resumen.cargandoEspacio': 'Todavía se está cargando tu cuenta — vuelve a intentar en un momento.',
    'proyecto.fechas.guardadas': 'Fechas guardadas',
    'proyecto.lider.asignado': '{quien} queda a cargo el {dia}.',
    'proyecto.lider.diaQuitado': 'Ese dia se quito de la agenda.',
    'proyecto.fechas.errorGuardar': 'No se pudieron guardar las fechas: ',
    'proyecto.geocerca.errorRadio': 'No se pudo guardar el radio: ',
    'proyecto.bitacora.tipo.retraso': 'Retraso',
    'proyecto.bitacora.tipo.avance': 'Avance',
    'proyecto.bitacora.tipo.seguridad': 'Seguridad',
    'proyecto.bitacora.placeholder': 'Anota el avance, un retraso o una nota…',
    'proyecto.bitacora.enviar': 'Mandar la nota',
    'proyecto.bitacora.adjunto': 'Foto',
    'proyecto.bitacora.fotoLista': 'Foto lista — dale a mandar',
    'proyecto.bitacora.quitarFoto.title': 'Quitar la foto',
    'proyecto.bitacora.quitarFoto.aria': 'Quitar la foto',
    'proyecto.bitacora.agregarFoto': 'Agregar foto',
    'proyecto.cuadrilla.actividadEnObra': 'Hay gente en la obra',
    'proyecto.cuadrilla.editar.title': 'Cambiar quién trabaja aquí',
    'proyecto.cuadrilla.editar.aria': 'Cambiar quién trabaja aquí',
    'proyecto.cuadrilla.vacio.titulo': 'Nadie asignado',
    'proyecto.cuadrilla.vacio.texto': 'Asigna a tu gente para ver aquí sus horas y quién está en la obra.',
    'proyecto.cuadrilla.asignar': 'Asignar gente',
    'proyecto.cuadrilla.distanciaObra': 'ft de la obra',
    'proyecto.cuadrilla.fueraDeObra': 'Fuera de la obra',
    'proyecto.cuadrilla.enObra': 'En la obra',
    'proyecto.cuadrilla.sinRegistro': 'Todavía no',
    'proyecto.geocerca.inactivaSinCoords': 'Inactiva (sin coordenadas)',
    'proyecto.geocerca.activa': 'Activa',
    'proyecto.geocerca.soloLectura': 'Solo la oficina puede cambiar la geocerca de la obra.',
    'proyecto.geocerca.inactiva': 'Inactiva',
    'proyecto.geocerca.direccion': 'Dirección de la obra',
    'proyecto.geocerca.direccion.placeholder': 'ej. 123 Main St, City, ST',
    'proyecto.geocerca.buscar': 'Buscar',
    'proyecto.geocerca.radio': 'Radio (pies)',
    'comun.guardar': 'Guardar',
    'proyecto.fechas.inicio': 'Empieza',
    'proyecto.fechas.fin': 'Debe terminar',
    'proyecto.fechas.guardar': 'Guardar fechas',
    'proyecto.actividad.titulo': 'Historial del trabajo',
    'proyecto.actividad.vacio.titulo': 'Todavía no hay nada',
    'proyecto.actividad.vacio.texto': 'Anota algo en la bitácora o marca entrada para empezar el historial.',
    'proyecto.actividad.marcoEntrada': 'marcó entrada',
    'proyecto.actividad.trabajando': 'Trabajando ahorita',
    'proyecto.actividad.fotoEntrada': 'Foto de la entrada',
    'proyecto.actividad.verFoto': 'Ver la foto',
    'proyecto.actividad.autorSistema': 'El sistema',
    'proyecto.actividad.abrirFoto': 'Abrir la foto de la obra',
    'proyectos.tareas.materiales_titulo': '¿Marcar los materiales como instalados?',
    'proyectos.tareas.materiales_confirmar': 'Sí, marcar instalados',
    'proyectos.tareas.materiales_cancelar': 'Solo terminar la tarea',
    'proyectos.tareas.borrar_titulo': '¿Borrar la tarea?',
    'proyectos.tareas.borrar_mensaje': 'Esto borra la tarea para siempre. No se puede deshacer.',
    'proyectos.tareas.borrar_cancelar': 'Cancelar',
    'proyectos.tareas.hecho_por': 'Hecho por',
    'proyectos.tareas.asignada_a': 'Asignada a',
    'proyectos.tareas.asignar_tooltip': 'Asignar a',
    'proyectos.tareas.asignar_aria': 'Asignar a',
    'proyectos.tareas.fecha_tooltip': 'Fecha límite',
    'proyectos.tareas.fecha_aria': 'Fecha límite',
    'proyectos.tareas.mover_prep_tooltip': 'Mover a Prep',
    'proyectos.tareas.mover_prep_aria': 'Mover a Prep',
    'proyectos.tareas.borrar_tooltip': 'Borrar la tarea',
    'proyectos.tareas.borrar_aria': 'Borrar la tarea',
    'proyectos.tareas.fase_vacia': 'No hay tareas en esta fase',
    'proyectos.tareas.poner_fase': 'Agregar tarea en',
    'proyectos.tareas.nueva_tarea_placeholder': 'Agrega una tarea…',
    'proyectos.tareas.agregar_tarea_aria': 'Agregar tarea',
    'proyectos.tareas.cualquiera_valor': 'Cualquiera (sin asignar)',
    'proyectos.tareas.cualquiera_opcion': 'Cualquiera (sin asignar)',
    'proyectos.tareas.fase_prep': 'Prep',
    'proyectos.tareas.fase_ejecucion': 'Ejecución',
    'proyectos.tareas.fase_acabado': 'Acabado',
    'proyectos.tareas.fecha_opcional_aria': 'Fecha límite (opcional)',
    'proyectos.tareas.quitar_fecha_aria': 'Quitar la fecha',
    'proyectos.tareas.quitar_fecha_tooltip': 'Quitar la fecha',
    'proyectos.tareas.vacio_titulo': 'Todavía no hay tareas',
    'proyectos.tareas.vacio_texto': 'Agrega la primera tarea aquí arriba para llevar el punch list de este trabajo.',
    'proyectos.tareas.autoetiquetar_tooltip': 'Acomoda cada tarea en su fase según lo que dice',
    'proyectos.tareas.autoetiquetar': 'Acomodar en fases',
    'proyecto.horas.csv_empleado': 'Empleado',
    'proyecto.horas.csv_fecha': 'Fecha',
    'proyecto.horas.csv_desconocido': 'Desconocido',
    'proyecto.horas.csv_activo': 'Trabajando',
    'proyecto.horas.presupuesto_titulo': 'Horas contra el presupuesto',
    'proyecto.horas.presupuesto_desc': 'Checa las horas de verdad contra las que calculaste.',
    'proyecto.horas.barra_inicio': 'Inicio',
    'proyecto.horas.barra_limite': 'Límite del presupuesto',
    'proyecto.horas.excedido_titulo': 'Te pasaste del presupuesto:',
    'proyecto.horas.excedido_texto1': 'vas',
    'proyecto.horas.excedido_texto2': 'horas arriba de lo que calculaste.',
    'proyecto.horas.eficiencia_titulo': 'Horas de la cuadrilla',
    'proyecto.horas.horas_registradas': 'horas registradas',
    'proyecto.horas.sin_horas_titulo': 'Todavía no hay horas',
    'proyecto.horas.sin_horas_desc': 'Las horas salen aquí conforme la cuadrilla checa entrada y salida en este trabajo.',
    'proyecto.horas.detalle_titulo': 'Detalle de horas',
    'proyecto.horas.exportar_csv': 'Descargar CSV',
    'proyecto.horas.col_empleado': 'Empleado',
    'proyecto.horas.col_fecha': 'Fecha',
    'proyecto.horas.col_entrada_salida': 'Entrada / Salida',
    'proyecto.horas.col_duracion': 'Duración',
    'proyecto.horas.estado_activo': 'Trabajando',
    'proyecto.horas.sin_checadas_titulo': 'No hay checadas en este trabajo',
    'proyecto.horas.sin_checadas_desc': 'Aquí salen las checadas de este trabajo.',
    'propuesta.general.etiqueta': 'Propuesta',
    'propuesta.general.botonfirmar': 'Firmar y aceptar',
    'propuesta.estado.firmada': 'firmada y aceptada',
    'propuesta.resenas.eyebrow': 'Testimonios',
    'propuesta.resenas.titulo': 'Lo que dicen nuestros clientes',
    'propuesta.resenas.dejargoogle': 'Déjanos tu reseña en Google',
    'propuesta.resenas.dejaryelp': 'Déjanos tu reseña en Yelp',
    'propuesta.social.titulo': 'Síguenos en redes',
    'propuesta.pago.deposito': 'Pagar el anticipo',
    'propuestas.editor.alerta.faltanpartidas.titulo': 'Faltan partidas',
    'propuestas.editor.alerta.faltanpartidas.mensaje': 'Agrégale partidas al estimado antes de generar el scope.',
    'propuestas.editor.alerta.fallogenerar.titulo': 'No se pudo generar',
    'propuestas.editor.tab.contenido': 'Contenido',
    'propuestas.editor.tab.diseno': 'Diseño',
    'propuestas.editor.tab.formato': 'Secciones',
    'propuestas.editor.tab.precio': 'Precio',
    'propuestas.editor.tab.compartir': 'Enviar',
    'propuestas.editor.boton.verpropuesta': 'Ver la propuesta',
    'propuestas.editor.tooltip.descargarpdf': 'Membrete, partidas, totales y las líneas de firma — sin foto de portada, sin scope ni términos legales.',
    'propuestas.editor.boton.descargarpdf': 'Descargar PDF',
    'propuestas.editor.pdf.solotarjeta.titulo': 'Cambia la forma de cobro antes de bajar el PDF',
    'propuestas.editor.pdf.solotarjeta.mensaje': 'Ahora mismo solo tienes prendido el enlace de tarjeta, y ese es lo único que no sirve en papel: impreso es una dirección larguísima que nadie va a teclear. Prende Zelle o transferencia bancaria y vuelve a bajarlo.',
    'propuestas.editor.pdf.solotarjeta.ir': 'Ir a Precio',
    'propuestas.editor.boton.vercomocliente': 'Ver como cliente',
    'propuesta.editor.esvistacliente': 'Esto es la vista de tu cliente. Aquí firmará él, no tú.',
    'propuesta.editor.editarseccion': 'Editar',
    'propuesta.editor.solotuloves': 'Solo tú ves esto',
    'propuesta.precios.contratoadjunto': 'Al aceptar, tu cliente firmará también «{contrato}».',
    'propuesta.requisito.titulo': 'Título de la propuesta',
    'propuesta.arreglo.titulo': 'Ponle un nombre que tu cliente reconozca, como «Patio trasero — casa de los García».',
    'propuesta.requisito.alcance': 'Alcance del trabajo escrito',
    'propuesta.arreglo.alcance': 'Cuéntale qué vas a hacer, o deja que la IA lo arme con tus partidas.',
    'propuesta.requisito.partidas': 'El estimado tiene partidas',
    'propuesta.arreglo.partidas': 'Sin partidas no hay precio que enseñar. Vuelve al estimado y agrégalas.',
    'propuesta.requisito.vigencia': 'Vigencia todavía válida',
    'propuesta.arreglo.vigencia': 'La fecha ya pasó o no está puesta. Dale +7, +14 o +30 días en Precio.',
    'propuesta.progreso.listo': 'Lista para enviar',
    'propuesta.progreso.cuenta': '{hechos} de {total} listos',
    'propuesta.progreso.abrir': 'Ver qué falta',
    'propuesta.progreso.arreglar': 'Arreglar',
    'propuesta.layout.resenas.sincontenido': 'Prendidas, pero tu negocio todavía no tiene enlaces ni reseñas guardadas, así que no va a salir nada.',
    'propuesta.layout.resenas.sincontenido.enlace': 'Ponerlos ahora',
    'propuesta.marca.galeria.encendidavacia': 'La galería está prendida y no tiene ni una foto: en la propuesta no va a salir nada.',
    'propuesta.marca.galeria.quitada': 'Foto quitada de la propuesta',
    'propuesta.vacio.alcance.titulo': 'Todavía no cuentas el trabajo',
    'propuesta.vacio.alcance.desc': 'Escríbelo a tu manera o deja que la IA lo arme con tus partidas. Es lo primero que lee el cliente.',
    'propuesta.vacio.galeria.titulo': 'La galería está prendida, pero sin fotos',
    'propuesta.vacio.galeria.desc': 'Sube fotos del trabajo en Diseño. Sin ellas, aquí el cliente no ve nada.',
    'propuesta.vacio.resenas.titulo': 'Las reseñas están prendidas, pero sin nada que enseñar',
    'propuesta.vacio.resenas.desc': 'Pon tus enlaces de Google, Yelp y redes en Estimados ▸ Propuesta ▸ Marketing y marca.',
    'propuesta.autoguardado.reposo': 'Todo se guarda solo mientras editas',
    'propuesta.autoguardado.guardando': 'Guardando…',
    'propuesta.autoguardado.guardado': 'Guardado',
    'propuesta.atajo.yaguardado': 'Ya está guardado — no hace falta que lo pidas',
    'propuesta.galeria.etiqueta': 'Nuestros trabajos',
    'propuesta.galeria.titulo': 'Así se va a ver el trabajo',
    'propuesta.galeria.imagenalt': 'Galería',
    'propuesta.galeria.despuesalt': 'después',
    'propuesta.portada.imagenalt': 'Portada',
    'propuesta.portada.preparadopara': 'Preparado exclusivamente para',
    'propuesta.portada.clientepredeterminado': 'Cliente',
    'propuesta.precios.upsellmantenimiento': 'Planes de mantenimiento',
    'propuesta.precios.upselltemporada': 'Servicios de temporada',
    'propuesta.precios.upsellpaquete': 'Ahorro en paquete',
    'propuesta.precios.upsellrecomendado': 'Extras recomendados',
    'propuesta.precios.desglose': 'Desglose de la inversión',
    'propuesta.precios.estimado': 'Estimado del trabajo',
    'propuesta.precios.serviciopartida': 'Servicio / Partida',
    'propuesta.precios.cantidad': 'Cant.',
    'propuesta.precios.opcional': 'Opcional',
    'propuesta.precios.upsellsactivos': 'Extras activados',
    'propuesta.precios.upsellsactivosdesc': 'Aquí van a salir las recomendaciones de la IA o los extras que agregues a mano.',
    'propuesta.precios.inversiontotal': 'Inversión total',
    'propuesta.precios.incluyeimpuestos': 'Incluye impuestos y cargos aplicables',
    'propuesta.precios.vencioel': 'Esta propuesta venció el ',
    'propuesta.precios.validahasta': 'Válida hasta el ',
    'propuesta.precios.deposito': 'Depósito (',
    'propuesta.precios.restoalterminar': 'Resto al terminar',
    'propuesta.precios.mifirma': 'Mi firma',
    'propuesta.precios.altfirma': 'Firma',
    'propuesta.precios.rechazar': 'Rechazar',
    'propuesta.precios.firmado': ' con firma',
    'propuesta.precios.pagardeposito': 'Paga el depósito ahora',
    'propuesta.alcance.titulo': 'Alcance del trabajo',
    'propuesta.empresa.logoalt': 'Logo de la empresa',
    'propuesta.empresa.textopredeterminado': 'Damos servicio de primera: trabajo bien hecho, cumplimos lo que prometemos y el cliente queda contento.',
    'firma.alerta.faltafirma.titulo': 'Falta la firma',
    'firma.alerta.faltafirma.texto': 'Firma antes de aceptar.',
    'firma.alerta.faltanombre.titulo': 'Falta el nombre',
    'firma.alerta.faltanombre.texto': 'Escribe tu nombre completo, como aparece en tu ID.',
    'firma.alerta.faltaconsentimiento.titulo': 'Falta tu confirmación',
    'firma.alerta.faltaconsentimiento.texto': 'Confirma que aceptas firmar electrónicamente.',
    'firma.modal.titulo': 'Firma para aceptar',
    'firma.modal.aviso': 'Al firmar abajo aceptas los términos y condiciones de tu',
    'firma.boton.borrar': 'Borrar',
    'firma.linea.cliente': 'Firma del cliente',
    'firma.nombre.placeholder': 'Escribe tu nombre completo',
    'firma.consentimiento.texto': 'Acepto firmar electrónicamente, y que mi firma electrónica vale igual que mi firma de puño y letra (ESIGN / UETA).',
    'firma.boton.cancelar': 'Cancelar',
    'firma.boton.firmar': 'Firmar y aceptar',
    'propuesta.impresion.etiqueta.defecto': 'Propuesta',
    'propuesta.impresion.ficha.cliente': 'Cliente',
    'propuesta.impresion.ficha.obra': 'Dirección de la obra',
    'propuesta.impresion.ficha.vigencia': 'Válida hasta',
    'propuesta.impresion.portada.fecha': 'Fecha',
    'propuesta.impresion.portada.referencia': 'Referencia',
    'propuesta.impresion.tabla.cantidad': 'Cantidad',
    'propuesta.impresion.pago.metodo.tarjeta': 'Tarjeta o pago en línea',
    'propuesta.impresion.pago.metodo.zelle': 'Zelle',
    'propuesta.impresion.pago.metodo.banco': 'Transferencia bancaria (ACH)',
    'propuesta.impresion.firmaenlinea.titulo': 'Fírmala desde el teléfono',
    'propuesta.impresion.firmaenlinea.texto': 'Apunta la cámara al código, o entra a la dirección de abajo. Ahí puedes leer la propuesta completa y firmarla sin imprimir nada.',
    'propuesta.impresion.firmaenlinea.qr.alt': 'Código QR con el enlace de la propuesta',
    'propuesta.impresion.firmaenlinea.contrato': 'Si prefieres firmar a mano, el contrato va adjunto al final de este documento.',
    'propuesta.impresion.anexo.etiqueta': 'Anexo',
    'propuesta.impresion.anexo.hoja': 'Hoja {n} de {total}',
    'propuesta.impresion.enlace.etiqueta': 'Tu propuesta en línea',
    'propuesta.impresion.tipo.recibo': 'Recibo',
    'propuesta.impresion.tipo.contrato': 'Contrato',
    'propuesta.impresion.logo.alt': 'Negocio',
    'propuesta.impresion.empresa.titulo': 'Quiénes somos',
    // Rótulos de los cuatro botones de descarga (services/proposalPrint.ts).
    'propuesta.impresion.titulo.antetitulo': 'Tu inversión',
    'propuesta.impresion.titulo.defecto': 'Estimado del proyecto',
    'propuesta.impresion.alcance.titulo': 'Alcance del trabajo',
    'propuesta.impresion.partidas.titulo': 'El trabajo, partida por partida',
    'propuesta.impresion.portada.departede': 'De parte de',
    'propuesta.impresion.tabla.encabezado': 'Servicio / Partida',
    'propuesta.impresion.extras.titulo': 'Extras opcionales — no van incluidos en el total',
    'propuesta.impresion.total.etiqueta': 'Inversión total',
    'propuesta.impresion.total.impuestos': 'Incluye impuestos y cargos que apliquen.',
    'propuesta.impresion.firma.etiqueta': 'Firmado por el cliente',
    'propuesta.impresion.firma.alt': 'Firma del cliente',
    'propuesta.impresion.total.anticipo': 'Anticipo',
    'propuesta.impresion.total.resto': 'Resto al terminar',
    'propuesta.impresion.pago.titulo': 'Cómo pagar',
    'propuesta.impresion.pago.alaceptar': 'A pagar al aceptar:',
    'propuesta.impresion.pago.anticipo': '% de anticipo)',
    'propuesta.impresion.pago.resto': '— resto ',
    'propuesta.impresion.pago.alterminar': 'al terminar.',
    'propuesta.impresion.vigencia.texto': 'Esta propuesta vence el',
    'propuesta.impresion.vigencia.dias': { uno: 'queda {dias} día', otros: 'quedan {dias} días' },
    'propuesta.impresion.vigencia.vencida': ' · vencida',
    'propuesta.impresion.pago.enlinea': 'Paga en línea:',
    'propuesta.impresion.pago.banco': 'Transferencia bancaria (ACH):',
    'propuesta.impresion.pago.banco.ruta': 'ruta',
    'propuesta.impresion.pago.banco.cuenta': 'cuenta',
    'propuesta.impresion.pago.directo': 'El pago se arregla directo con nosotros — te buscamos apenas aceptes.',
    'propuesta.impresion.contrato.adjunto': 'Contrato adjunto:',
    'propuesta.impresion.contratoadjunto.titulo': 'Contrato adjunto',
    'propuesta.impresion.contratoadjunto.hojas': { uno: '{hojas} hoja', otros: '{hojas} hojas' },
    'propuesta.impresion.contratoadjunto.hoja.alt': 'Hoja {n} del contrato',
    'propuesta.impresion.contratoadjunto.preparando': 'Preparando el contrato…',
    'propuesta.impresion.contratoadjunto.error': 'No se pudo preparar el contrato. La propuesta se imprime sin él.',
    'propuesta.impresion.contrato.firmaenlinea': '— se firma en línea desde el enlace de tu propuesta.',
    'propuesta.impresion.terminos.titulo': 'Términos y condiciones',
    'propuesta.impresion.garantia.titulo': 'Garantía',
    'propuesta.impresion.firmas.titulo': 'Acuerdo y firmas',
    'propuesta.impresion.firmas.aviso': 'Al firmar abajo, las dos partes aceptan los precios y los términos de este documento',
    'propuesta.impresion.firmas.fecha': ', con fecha ',
    'propuesta.impresion.firmas.cliente': 'Cliente',
    'propuesta.impresion.firmas.contratista': 'Contratista',
    'propuesta.impresion.firmas.lineafirma': 'Firma —',
    'propuesta.impresion.firmas.nombre': 'nombre',
    'propuesta.impresion.firmas.lineafecha': 'Fecha',
    'propuesta.marca.tema.moderno': 'Moderno',
    'propuesta.marca.tema.natural': 'Natural',
    'propuesta.marca.tema.elegante': 'Elegante',
    'propuesta.marca.tema.urlpropia': 'Tu foto',
    'propuesta.marca.tipografia.sans': 'Moderna (sans)',
    'propuesta.marca.tipografia.serif': 'Clásica (serif)',
    'propuesta.marca.tipografia.mono': 'Técnica (mono)',
    'propuesta.marca.animacion.ninguna': 'Ninguna',
    'propuesta.marca.animacion.aparecer': 'Aparecer',
    'propuesta.marca.animacion.subir': 'Deslizar',
    'propuesta.marca.animacion.zoom': 'Zoom',
    'propuesta.marca.titulo': 'Diseño y fotos',
    'propuesta.marca.enlacebiblioteca': 'Biblioteca de fotos en Propuesta',
    'propuesta.marca.colores.titulo': 'Colores de tu marca',
    'propuesta.marca.colores.principal.aria': 'Color principal de la marca',
    'propuesta.marca.colores.secundario': 'Secundario (acento)',
    'propuesta.marca.colores.secundario.aria': 'Color de acento secundario',
    'propuesta.marca.tipografia.titulo': 'Tipografía',
    'propuesta.marca.portada.guardadas': 'Tus fotos guardadas',
    'propuesta.marca.portada.errorsubida': 'No se pudo subir la foto de portada',
    'propuesta.marca.portada.subir': 'Subir',
    'propuesta.marca.portada.ayuda': 'Pulsa la que quieras, o sube la tuya.',
    'propuesta.marca.portada.guardada': 'Tu foto {n}',
    'propuesta.marca.portada.tuya': 'Tu foto',
    'propuesta.marca.portada.usar': 'Usar la portada {nombre}',
    'propuesta.marca.portada.suelta': 'Suéltala aquí',
    'propuesta.marca.portada.formatos': 'JPG, PNG o WEBP · hasta 10 MB',
    'propuesta.marca.encuadre.restablecer': 'Restablecer',
    'propuesta.marca.encuadre.altprevia': 'Vista previa de la portada',
    'propuesta.marca.encuadre.llenar': 'Llenar',
    'propuesta.marca.encuadre.llenar.ayuda': 'Recorta la foto para llenar el espacio',
    'propuesta.marca.encuadre.completa': 'Foto completa',
    'propuesta.marca.encuadre.completa.ayuda': 'Muestra la foto completa, con barras a los lados',
    'propuesta.marca.encuadre.horizontal': 'Horizontal',
    'propuesta.marca.encuadre.vertical': 'Vertical',
    'propuesta.marca.portada.opacidad': 'Oscurecer la foto',
    'propuesta.marca.galeria.ayuda': 'Fotos de trabajos que ya hiciste. Al subir la primera, la galería se prende sola.',
    'propuesta.marca.galeria.ayudaon': 'Estas fotos salen en la propuesta.',
    'propuesta.marca.galeria.ayudaoff': 'La galería está apagada: estas fotos no salen en la propuesta.',
    'propuesta.marca.galeria.mostrar': 'Enseñar la galería',
    'propuesta.marca.galeria.agregarrapido': 'Agregar rápido desde la biblioteca',
    'propuesta.marca.galeria.yaagregada': 'Ya está en esta galería',
    'propuesta.marca.galeria.errorsubida': 'No se pudo subir la foto',
    'propuesta.marca.galeria.errorvarias': { uno: 'No se pudo subir 1 foto.', otros: 'No se pudieron subir {count} fotos.' },
    'propuesta.marca.galeria.subir': 'Subir fotos',
    'propuesta.marca.galeria.pieplaceholder': 'Título (opcional)',
    'propuesta.marca.galeria.piearia': 'Título de la foto',
    'propuesta.marca.galeria.principal.tooltip': 'Ponerla como foto principal',
    'propuesta.marca.galeria.esprincipal': 'Principal',
    'propuesta.marca.galeria.hacerprincipal': 'Hacer principal',
    'propuesta.marca.galeria.altdespues': 'después',
    'propuesta.marca.galeria.imagendespues': 'Foto del después',
    'propuesta.marca.galeria.quitardespues.tooltip': 'Quitar la foto del después',
    'propuesta.marca.galeria.quitardespues.aria': 'Quitar la foto del después',
    'propuesta.marca.galeria.errordespues': 'No se pudo subir la foto del después',
    'propuesta.marca.galeria.agregardespues': 'Agrega la foto del «después» (se ve al pasar el mouse)',
    'propuesta.marca.galeria.antesdespues': 'Antes/Después',
    'propuesta.marca.galeria.subirorden': 'Mover la foto arriba',
    'propuesta.marca.galeria.bajarorden': 'Mover la foto abajo',
    'propuesta.marca.galeria.quitar.tooltip': 'Quitar',
    'propuesta.marca.galeria.quitar.aria': 'Quitar la foto',
    'propuesta.marca.animacion.titulo': 'Animación de entrada',
    'propContenido.titulo': 'Contenido y mensaje',
    'propContenido.tituloPropuesta': 'Título de la propuesta',
    'propContenido.tituloPropuesta.placeholder': 'ej. Remodelación del patio',
    'propContenido.etiquetaDoc': 'Nombre del documento',
    'propContenido.etiquetaDoc.valor': 'Propuesta',
    'propContenido.etiquetaDoc.placeholder': 'Propuesta',
    'propContenido.botonAccion': 'Botón de acción',
    'propContenido.botonAccion.valor': 'Firmar y aceptar',
    'propContenido.botonAccion.placeholder': 'Firmar y aceptar',
    'propContenido.alcance.titulo': 'Alcance del trabajo',
    'propContenido.alcance.escribiendo': 'Escribiendo…',
    'propContenido.alcance.generar': 'Generar con IA',
    'propContenido.alcance.viejo': 'Cambiaron las partidas desde que se generó esto — mejor vuélvelo a generar.',
    'propContenido.alcance.placeholder': 'Escribe una nota personal (español o inglés) o deja que la IA arme un alcance de trabajo profesional…',
    'propContenido.alcance.markdown': 'Acepta Markdown',
    'propContenido.perfil.titulo': 'Perfil del negocio',
    'propContenido.perfil.placeholder': 'Cuenta aquí la historia de tu negocio…',
    'propContenido.perfil.ayuda': 'Tu historia, tal como la lee el cliente.',
    'propContenido.perfil.mostrar': 'Mostrar el perfil del negocio',
    'propContenido.legal.titulo': 'Garantía y términos',
    'propContenido.garantia.escoger': 'Escoge la garantía',
    'propContenido.garantia.ninguna': 'Ninguna / personalizada',
    'propContenido.garantia.mostrar': 'Mostrar la garantía',
    'propContenido.terminos.titulo': 'Términos legales',
    'propContenido.terminos.placeholder': 'Los términos que ya tienes en ajustes…',
    'propContenido.contrato.ver': 'Ver ',
    'propContenido.contrato.adjunto': 'Contrato adjunto',
    'propContenido.contrato.sinadjuntar': 'Ninguno todavía',
    'propContenido.contrato.ira': 'Elegirlo en Compartir',
    'propContenido.contrato.dondeayuda': 'Se adjunta desde la pestaña Compartir, junto a la lista de lo que falta para enviar.',
    'propuesta.layout.seccion_resumen': 'Resumen ejecutivo',
    'propuesta.layout.seccion_perfil': 'Perfil de la compañía',
    'propuesta.layout.seccion_galeria': 'Galería de fotos',
    'propuesta.layout.seccion_precios': 'Precios y opciones',
    'propuesta.layout.seccion_partidas': 'Detalle por partida',
    'propuesta.layout.seccion_totales': 'Resumen de totales',
    'propuesta.layout.seccion_terminos': 'Términos y firma',
    'propuesta.layout.titulo': 'Estructura y secciones',
    'propuesta.layout.orden_label': 'Orden y visibilidad',
    'propuesta.layout.ayuda': 'Usa las flechas para reordenar las secciones. Los switches muestran o esconden secciones en la propuesta.',

    // ── Cómo se dibuja el desglose de precios ───────────────────────
    'propuesta.layout.precios.titulo': 'Cómo se enseña el precio',
    'propuesta.layout.modo.itemizado': 'Detallado',
    'propuesta.layout.modo.itemizado.sub': 'Una fila por partida',
    'propuesta.layout.modo.agrupado': 'Agrupado',
    'propuesta.layout.modo.agrupado.sub': 'Secciones que tú armas',
    'propuesta.layout.eje.titulo': 'Agrupar las partidas',
    'propuesta.layout.eje.tipo': 'Por tipo',
    'propuesta.layout.eje.tipo.ayuda': 'Mano de obra, materiales, equipo…',
    'propuesta.layout.eje.zona': 'Por área o zona',
    'propuesta.layout.eje.zona.ayuda': 'Las zonas del estimado, cada una con su total.',
    'propuesta.layout.eje.categoria': 'Por categoría',
    'propuesta.layout.eje.categoria.ayuda': 'La categoría de cada partida (Concreto, Jardinería…).',
    'propuesta.layout.eje.una': 'Todo en una',
    'propuesta.layout.eje.una.ayuda': 'Una sola sección con el proyecto completo.',
    'propuesta.layout.cobertura': 'Salen las {partidas} partidas, repartidas en {secciones} secciones.',
    'propuesta.layout.restablecer': 'Volver a auto',
    'propuesta.layout.combinar': 'Juntar en una',
    'propuesta.layout.condiciones.titulo': 'Nombre de las condiciones del terreno',
    'propuesta.layout.condiciones.ayuda': 'Cómo se llama en la propuesta el recargo por las condiciones del terreno. Por defecto, «Project Conditions».',
    'propuesta.layout.secciones.titulo': 'Secciones',
    'propuesta.layout.secciones.vacio': 'Este estimado todavía no tiene partidas que agrupar.',
    'propuesta.layout.seccion.nombre': 'Nombre de la sección',
    'propuesta.layout.seccion.descripcion': 'Una línea opcional: qué incluye',
    'propuesta.layout.seccion.modo': 'Cómo se enseña',
    'propuesta.layout.seccion.precios': 'Desglosar',
    'propuesta.layout.seccion.precios.ayuda': 'Enseñar el precio de cada partida dentro de la sección. El total de la sección se enseña siempre.',
    'propuesta.layout.partidas.titulo': { uno: 'Redactar 1 partida', otros: 'Redactar {count} partidas' },
    'propuesta.layout.partidas.ayuda': 'Cómo lee el cliente cada renglón. Vacío = lo que dice el estimado. Cambiarlo aquí no toca el estimado ni el precio.',
    'propuesta.layout.partida.descripcion': 'Cómo se llama la partida en la propuesta',
    'propuesta.layout.partida.detalle': 'La línea de abajo (cantidad, notas)',
    'propuesta.layout.seccion.subir': 'Subir la sección {seccion}',
    'propuesta.layout.seccion.bajar': 'Bajar la sección {seccion}',
    'propuesta.layout.modo.desplegable': 'Se despliega',
    'propuesta.layout.modo.abierta': 'Siempre abierta',
    'propuesta.layout.modo.resumen': 'Solo el total',
    'propuesta.precios.grupo.partidas': { uno: '1 partida — tócalo para verla', otros: '{count} partidas — tócalo para verlas' },
    'propuesta.precios.grupo.condiciones': 'Condiciones del terreno',
    'propuesta.precios.grupo.condiciones.ayuda': 'Ajuste por cómo está el sitio: acceso, terreno y pendiente.',
    'propuesta.layout.resenas.google.titulo': 'Reseñas de Google',
    'propuesta.layout.resenas.google.desc': 'Muestra tus reseñas de Google y el enlace',
    'propuesta.layout.resenas.yelp.titulo': 'Reseñas de Yelp',
    'propuesta.layout.resenas.yelp.desc': 'Muestra el botón de Yelp',
    'propuesta.layout.resenas.redes.titulo': 'Redes sociales',
    'propuesta.layout.resenas.redes.desc': 'Pone los íconos de tus redes en la propuesta',
    'propuesta.compartir.error.marcarenviada': 'No se pudo marcar como enviada',
    'propuesta.compartir.error.deshacerenviada': 'No se pudo deshacer',
    'propuesta.compartir.error.reintentar': 'Inténtalo otra vez.',
    'propuesta.compartir.copiar.manual.titulo': 'Copia el enlace a mano',
    'propuesta.compartir.copiar.manual.mensaje': 'Tu navegador no dejó copiar. Selecciona el enlace de arriba y cópialo.',
    'propuesta.compartir.guardaprimero.titulo': 'Primero guarda tu configuración',
    'propuesta.compartir.guardaprimero.mensaje': 'Tienes cambios sin guardar en la configuración de tu negocio. Guárdalos y luego recarga la página.',
    'propuesta.compartir.requisito.contratoelegido': 'Contrato elegido',
    'propuesta.compartir.requisito.contratotienefirma': 'El contrato tiene dónde firmar',
    'propuesta.compartir.requisito.contratofirmado': 'Contrato firmado por ti',
    'propuesta.compartir.arreglo.subircontrato': 'Sube uno aquí abajo — es cosa de un minuto.',
    'propuesta.compartir.arreglo.elegircontrato': 'Elige uno en la lista de Contrato de aquí abajo.',
    'propuesta.compartir.arreglo.sincamposfirma': 'Este contrato todavía no tiene campos de firma. Ábrelo aquí abajo y arrastra un campo de Firma a la página, o el cliente no va a tener dónde firmar.',
    'propuesta.compartir.arreglo.firmarcomocontratista': 'Ábrelo aquí abajo y usa "Firmar como contratista".',
    'propuesta.compartir.requisito.metodopago': 'Método de pago elegido',
    'propuesta.compartir.arreglo.metodopago': 'En la pestaña Precio: tarjeta, Zelle, transferencia o «sin pago en línea». Con uno basta.',
    'propuesta.compartir.arreglo.irprecio': 'Ir a Precio',
    'propuesta.preciotab.titulo': 'Precio y condiciones',
    'propuesta.preciotab.pagos': 'Cómo te pagan',
    'propuesta.preciotab.pagos.ayuda': 'Enciende al menos una. Es lo que el cliente ve para pagarte después de aceptar.',
    'propuesta.preciotab.bloqueada.desc': 'La propuesta ya está firmada, así que las condiciones se quedan como el cliente las aceptó.',
    'propuesta.compartir.titulo': 'Compartir y cobrar',
    'propuesta.compartir.anticipo.titulo': 'Anticipo y vigencia',
    'propuesta.compartir.anticipo.ayuda': 'Lo que el cliente paga al firmar, y hasta cuándo le respetas este precio. Los dos salen impresos en la propuesta.',
    'propuesta.compartir.anticipo.etiqueta': 'Anticipo',
    'propuesta.compartir.anticipo.sinanticipo': 'Sin anticipo — se cobra todo al terminar.',
    'propuesta.compartir.anticipo.alfirmar': 'Al firmar',
    'propuesta.compartir.anticipo.alterminar': 'Al terminar',
    'propuesta.compartir.anticipo.sliderAria': 'Porcentaje del anticipo',
    'propuesta.compartir.vigencia.etiqueta': 'Este precio vale hasta',
    'propuesta.compartir.vigencia.faltan': 'Faltan {dias} días.',
    'propuesta.compartir.vigencia.ultimodia': 'Hoy es el último día.',
    'propuesta.compartir.vigencia.vencida': 'Ya venció. El cliente ve la propuesta marcada como vencida.',
    'propuesta.compartir.listaparaenviar': 'Lista para enviar',
    'propuesta.compartir.contrato.enarchivo': ' guardados',
    'propuesta.compartir.contrato.ningunosubido': 'Todavía no has subido contratos',
    'propuesta.compartir.contrato.sinadjuntar': 'Sin contrato adjunto',
    'propuesta.compartir.contrato.necesitatufirma': 'Este contrato necesita tu firma antes de salir — ábrelo aquí abajo y usa',
    'propuesta.compartir.contrato.subir': 'Subir un contrato',
    'propuesta.compartir.contrato.administrar': 'Mis contratos',
    'propuesta.compartir.contrato.recargar.tooltip': 'Recargar la lista de contratos desde el servidor',
    'propuesta.compartir.contrato.actualizando': 'Actualizando…',
    'propuesta.compartir.contrato.recargar': '¿Ya lo configuraste? Actualiza',
    'propuesta.compartir.contrato.ayuda1': 'El cliente lo firma junto con la propuesta. Se abre en otra pestaña para que no pierdas',
    'propuesta.compartir.contrato.ayuda2': 'los cambios sin guardar.',
    'propuesta.compartir.pago.tarjeta.switch': 'Enlace de tarjeta',
    'propuesta.compartir.pago.tarjeta.aria': 'Enlace de pago del cliente',
    'propuesta.compartir.pago.tarjeta.ayudaon': 'Pega aquí el enlace de pago. Después de aceptar, al cliente le sale un botón de "Pagar con tarjeta".',
    'propuesta.compartir.pago.tarjeta.ayudaoff': 'El pago con tarjeta está apagado en esta propuesta.',
    'propuesta.compartir.pago.zelle.etiqueta': 'Transferencia Zelle',
    'propuesta.compartir.pago.zelle.pagara': 'Pagar a ',
    'propuesta.compartir.pago.zelle.sinconfigurar': 'No lo tienes configurado en tu negocio',
    'propuesta.compartir.pago.zelle.switch': 'Transferencia Zelle',
    'propuesta.compartir.pago.zelle.ayudaon': 'Muestra tus datos de Zelle en esta propuesta para que el cliente te pague por transferencia.',
    'propuesta.compartir.pago.zelle.ayudaoff1': 'Agrega tu cuenta de Zelle en ',
    'propuesta.compartir.pago.zelle.ayudaoff.ruta': 'Estimados → Propuesta → Pagos',
    'propuesta.compartir.pago.zelle.ayudaoff2': ' para poder prenderlo.',
    'propuesta.compartir.pago.banco.etiqueta': 'Transferencia bancaria',
    'propuesta.compartir.pago.banco.switch': 'Transferencia bancaria',
    'propuesta.compartir.pago.banco.depositara': 'Depositar en ',
    'propuesta.compartir.pago.banco.sinconfigurar': 'No la tienes configurada en tu negocio',
    'propuesta.compartir.pago.banco.ayudaon': 'Muestra los datos de tu cuenta en esta propuesta para que el cliente te transfiera el anticipo.',
    'propuesta.compartir.pago.banco.ayudaoff1': 'Agrega tu cuenta de banco en ',
    'propuesta.compartir.pago.banco.ayudaoff.ruta': 'Estimados → Propuesta → Pagos',
    'propuesta.compartir.pago.banco.ayudaoff2': ' para poder prenderlo.',
    'propuesta.compartir.pago.efectivo.etiqueta': 'Sin pago en línea',
    'propuesta.compartir.pago.efectivo.desc': 'Efectivo, cheque, o lo arreglan directo',
    'propuesta.compartir.pago.efectivo.switch': 'Sin pago en línea',
    'propuesta.compartir.pago.efectivo.ayudaon': 'No se muestra tarjeta ni Zelle. Cuando el cliente firma, se le dice que tú lo vas a contactar para el pago.',
    'propuesta.compartir.pago.efectivo.ayudaoff': 'Préndelo para mandar una propuesta de efectivo, sin enlace de tarjeta ni Zelle.',
    'propuesta.compartir.bloqueada.titulo': 'Propuesta firmada y cerrada',
    'propuesta.compartir.bloqueada.desc': 'Esta propuesta ya fue aceptada, así que la vista del cliente está cerrada. Ve o descarga la copia firmada en la pestaña ',
    'propuesta.compartir.bloqueada.pestana': 'Firmadas',
    'propuesta.compartir.mandala.titulo': 'Mándala tú mismo',
    'propuesta.compartir.mandala.desc1': 'Raza Pro ',
    'propuesta.compartir.mandala.desc.no': 'no',
    'propuesta.compartir.mandala.desc2': ' manda propuestas por correo.',
    'propuesta.compartir.mandala.desc3': 'Copia el enlace y mándaselo como siempre le hablas a este cliente — mensaje, WhatsApp,',
    'propuesta.compartir.mandala.desc4': 'o tu propio correo.',
    'propuesta.compartir.paso1': 'Copia el enlace',
    'propuesta.compartir.enlace.aria': 'Enlace público de la propuesta',
    'propuesta.compartir.enlace.copiar.tooltip': 'Copiar',
    'propuesta.compartir.enlace.copiar.aria': 'Copiar el enlace público',
    'propuesta.compartir.enlace.copiado': 'Copiado. Mándaselo a tu cliente.',
    'propuesta.compartir.enlace.copiado.marcada': 'Copiado y marcada como enviada con la fecha de hoy. Mándaselo a tu cliente.',
    'propuesta.compartir.enlace.aviso': 'Cualquiera con este enlace puede ver y aceptar la propuesta. Al copiarlo se marca como enviada.',
    'propuesta.compartir.paso2': 'Márcala como enviada',
    'propuesta.compartir.marcada.fecha': 'Marcada como enviada el ',
    'propuesta.compartir.marcada.ya': 'Ya está marcada como enviada',
    'propuesta.compartir.marcada.desc': 'Ya está en el tablero de Seguimientos. Ahí vas a ver cuándo la abre el cliente.',
    'propuesta.compartir.marcada.auto': 'Se marcó sola al copiar el enlace. Si solo la estabas revisando, todavía puedes echarlo para atrás.',
    'propuesta.compartir.marcada.deshacer': 'Deshacer',
    'propuesta.compartir.marcando': 'Marcando…',
    'propuesta.compartir.marcarenviada': 'Marcar como enviada',
    'propuesta.compartir.marcar.aviso1': 'Normalmente no hace falta: se marca sola al copiar el enlace. Este botón es por si lo copiaste',
    'propuesta.compartir.marcar.aviso2': 'a mano y la propuesta se quedó en borrador, fuera del tablero de Seguimientos.',
    'propuesta.compartir.fechaauto.titulo': 'La fecha se pone sola.',
    'propuesta.compartir.fechaauto.pendiente': 'Como todavía no la mandas, va con la de hoy y mañana cambia sola. Se queda fija en cuanto la marques como enviada o el cliente firme. Ahora mismo sale con el',
    'propuesta.compartir.fechaauto.enviada': 'La propuesta y el contrato llevan la fecha en que la mandaste:',
    'propuesta.compartir.fechaauto.firmada': 'Los documentos quedaron sellados con la fecha en que el cliente firmó:',
    'propuesta.compartir.abrirvistacliente': 'Ver como cliente',
    'propuesta.firma.errorpdf': 'No se pudo abrir el PDF.',
    'propuesta.firma.revisayacepta': 'Revisa y acepta',
    'propuesta.firma.suelta.explicacion': 'Este contrato no tiene casilla de firma para ti: firma en el recuadro de arriba, escribe tu nombre y acepta.',
    'nav.sinAcceso': 'No tienes acceso a {vista}',
    'propuesta.top.volver.estimados': 'Volver a estimados',
    'propuesta.top.volver.herramientas': 'Volver a las herramientas',
    'propuesta.firma.enviando': 'Enviando…',
    'propuesta.firma.firmarcontrato': 'Firmar contrato',
    'propuesta.firma.firmaryaceptar': 'Firmar y aceptar',
    'propuesta.firma.paginaanterior': 'Página anterior',
    'propuesta.firma.paginade': 'de',
    'propuesta.firma.paginasiguiente': 'Página siguiente',
    'propuesta.firma.errorcontrato': 'No se pudo abrir este contrato',
    'propuesta.firma.placeholdernombre': 'Escribe tu nombre legal completo',
    'propuesta.firma.arianombre': 'Nombre legal completo',
    'propuesta.firma.consentimiento': 'Acepto firmar este documento de forma electrónica, y que mi firma electrónica tiene el mismo valor legal que mi firma de puño y letra (ESIGN / UETA).',
    'propuesta.firma.firmadopor': 'Firmado por ',
    'propuesta.firma.editar': 'Editar',
    'propuesta.firma.tocapara': 'Toca para ',
    'propuesta.firma.accionfirmar': 'firmar',
    'propuesta.firma.accionfecha': 'poner la fecha',
    'propuesta.firma.accionllenar': 'llenar',
    'propuesta.firma.firmaaqui': 'Firma aquí',
    'propuesta.firma.iniciales': 'Iniciales',
    'propuesta.firma.fecha': 'Fecha',
    'propuesta.firma.tocaparallenar': 'Toca para llenar',
    'propuesta.firma.tufirma': 'Tu firma',
    'propuesta.firma.tusiniciales': 'Tus iniciales',
    'propuesta.firma.modo.escribir': 'Escribir',
    'propuesta.firma.modo.dibujar': 'Dibujar',
    'propuesta.firma.estilo': 'Estilo',
    'propuesta.firma.escribeparaver': 'Escribe para ver cómo queda',
    'propuesta.firma.placeholder.iniciales': 'Ej. KYC',
    'propuesta.firma.placeholder.nombre': 'Escribe tu nombre completo',
    'propuesta.firma.cargandofuente': 'Cargando la tipografía…',
    'propuesta.firma.dibujatufirma': 'Dibuja tu firma',
    'propuesta.firma.dibujatusiniciales': 'Dibuja tus iniciales',
    'propuesta.firma.ayudadibujo': 'Usa el dedo o el mouse para dibujar arriba.',
    'propuesta.firma.borrar': 'Borrar',
    'propuesta.firma.cancelarcanvas': 'Cancelar',
    'propuesta.firma.guardarcanvas': 'Guardar',
    'propuesta.firma.confirmafecha': 'Confirma la fecha',
    'propuesta.firma.llenar': 'Llena el campo',
    'propuesta.firma.fechaauto.titulo': 'La fecha se pone sola.',
    'propuesta.firma.fechaauto.cuerpo': 'Nunca la escribes: cada propuesta sale con la fecha del día en que la mandes o descargues su PDF, y la copia del cliente lleva la de su firma. Por eso esta casilla está reservada.',
    'propuesta.firma.fechaauto.campo': 'Al enviar',
    'servicios.ruta.sin_cuadrillas_titulo': 'Todavía no hay cuadrillas',
    'servicios.ruta.sin_cuadrillas_descripcion': 'Para armar rutas primero necesitas cuadrillas. Con ellas organizas al equipo y le asignas sus paradas.',
    'servicios.ruta.ir_cuadrillas': 'Ir a Cuadrillas',
    'servicios.ruta.tiempo_estimado': 'Tiempo aprox.',
    'servicios.ruta.abrir_crew_builder': 'Abrir la pestaña de Cuadrillas',
    'servicios.ruta.administrar_cuadrillas': 'Manejar cuadrillas',
    'servicios.ruta.deshacer': 'Deshacer',
    'servicios.ruta.guardando': 'Guardando…',
    'servicios.ruta.guardar_orden': 'Guardar el orden',
    'servicios.ruta.todas_paradas': 'Todas las paradas',
    'servicios.ruta.orden_ruta': 'Orden de la ruta',
    'servicios.ruta.ayuda_todas': 'Se ven todas las cuadrillas. Escoge una arriba para reacomodar su ruta.',
    'servicios.ruta.ayuda_arrastrar': 'Arrastra las paradas para cambiar el orden de la ruta.',
    'servicios.ruta.sin_paradas_titulo': 'No hay paradas que mostrar',
    'servicios.ruta.sin_paradas_dia_1': 'No hay contratos activos para el día ',
    'servicios.ruta.sin_paradas_dia_2': '. Revisa el día preferido, la frecuencia y la fecha de inicio.',
    'servicios.ruta.sin_paradas_todas': 'Todavía no hay contratos asignados a ninguna cuadrilla.',
    'servicios.ruta.sin_paradas_cuadrilla': 'Asígnale contratos a esta cuadrilla para armarle su ruta.',
    'servicios.ruta.sin_asignar': 'Sin cuadrilla',
    'servicios.ruta.mapa_cargando': 'Cargando el mapa…',
    'servicios.ruta.mapa_sin_paradas': 'No hay paradas con coordenadas',
    'servicios.ruta.mapa_ayuda': 'Las paradas salen en el mapa cuando sus direcciones tengan coordenadas.',
    'servicios.analiticas.frecuencia_otra': 'Otra',
    'servicios.analiticas.vacio_titulo': 'Todavía no hay datos',
    'servicios.analiticas.vacio_desc': 'Los números van saliendo conforme agregues contratos y completes visitas.',
    'servicios.analiticas.kpi_ingreso_mensual': 'Ingreso mensual',
    'servicios.analiticas.kpi_cumplimiento': 'Visitas completadas',
    'servicios.analiticas.kpi_visitas_hint': 'visitas (30 días)',
    'servicios.analiticas.kpi_duracion': 'Duración promedio',
    'servicios.analiticas.kpi_duracion_hint': 'Por visita completada',
    'servicios.analiticas.kpi_clientes': 'Clientes activos',
    'servicios.analiticas.top_clientes': 'Clientes que más dejan',
    'servicios.analiticas.sin_clientes': 'Todavía no hay clientes',
    'servicios.analiticas.resumen_contratos': 'Resumen de contratos',
    'servicios.analiticas.estado_activos': 'Activos',
    'servicios.analiticas.estado_pausados': 'En pausa',
    'servicios.analiticas.estado_cancelados': 'Cancelados',
    'servicios.analiticas.actividad_30_dias': 'Actividad de los últimos 30 días',
    'servicios.analiticas.visitas_completadas': 'completadas',
    'servicios.analiticas.visitas_saltadas': 'saltadas',
    'contratoServ.frec.semanal': 'Semanal',
    'contratoServ.frec.quincenal': 'Quincenal',
    'contratoServ.frec.mensual': 'Mensual',
    'contratoServ.paso': 'Paso',
    'contratoServ.pasoDe': 'de',
    'contratoServ.cerrar': 'Cerrar',
    'contratoServ.buscarClientes': 'Busca clientes…',
    'contratoServ.sinClientes': 'No se encontraron clientes.',
    'contratoServ.direccion': 'Dirección del servicio',
    'contratoServ.direccionPlaceholder': 'Empieza a escribir para buscar la dirección…',
    'contratoServ.frecuencia': 'Frecuencia',
    'contratoServ.diaPreferido': 'Día preferido',
    'contratoServ.cualquierDia': 'Cualquier día',
    'contratoServ.horaPreferida': 'Hora preferida',
    'contratoServ.opcional': '(opcional)',
    'contratoServ.horaAyuda': 'Las visitas con hora agarran ese horario. Las que no tienen hora se acomodan por cercanía.',
    'contratoServ.fechaInicio': 'Fecha de inicio',
    'contratoServ.cobro': 'Cobro',
    'contratoServ.asignarCuadrilla': 'Asignar cuadrilla',
    'contratoServ.notas': 'Notas',
    'contratoServ.notasPlaceholder': 'Alguna instrucción especial…',
    'contratoServ.serviciosIncluidos': 'Servicios incluidos',
    'contratoServ.servicioNuevo': 'Agrega otro servicio…',
    'contratoServ.agregar': 'Agregar',
    'contratoServ.quitarServicio': 'Quitar {servicio}',
    'contratoServ.duracion': 'Duración estimada',
    'contratoServ.cuatroHoras': '4 h',
    'contratoServ.resumen': 'Resumen',
    'contratoServ.minPorVisita': 'min por visita',
    'contratoServ.atras': 'Atrás',
    'contratoServ.continuar': 'Continuar',
    'servicios.equipo.titulo': 'Equipo de la ruta',
    'servicios.equipo.descripcion': 'Saca herramientas y trocas para la cuadrilla de la ruta de hoy, y recíbelas cuando regresen.',
    'servicios.equipo.cancelar': 'Cancelar',
    'servicios.equipo.registrarnuevo': 'Registrar nuevo',
    'servicios.equipo.registrorapido': 'Registro rápido de equipo',
    'servicios.equipo.placeholdernombre': 'Nombre del equipo (ej. Blower de mochila)',
    'servicios.equipo.tipoherramienta': 'Herramienta',
    'servicios.equipo.tipovehiculo': 'Vehículo',
    'servicios.equipo.tipopesado': 'Equipo pesado',
    'servicios.equipo.tipotrailer': 'Tráiler',
    'servicios.equipo.tipootro': 'Otro',
    'servicios.equipo.registrando': 'Registrando…',
    'servicios.equipo.registrar': 'Registrar',
    'servicios.equipo.selollleva': 'Se lo lleva',
    'servicios.equipo.sincuadrilla': 'Sin cuadrilla específica',
    'servicios.equipo.enlaruta': 'En la ruta',
    'servicios.equipo.regresando': 'Regresando…',
    'servicios.equipo.regresaraltaller': 'Regresar al taller',
    'servicios.equipo.vacioruta': 'No hay nada en la ruta',
    'servicios.equipo.vaciorutadesc': 'Saca cosas del taller para llevar el control de lo que salió con la cuadrilla hoy.',
    'servicios.equipo.inventariotaller': 'Inventario del taller',
    'servicios.equipo.sacando': 'Sacando…',
    'servicios.equipo.sacar': 'Sacar',
    'servicios.equipo.sindisponible': 'No hay equipo disponible',
    'servicios.equipo.sindisponibledesc': 'Todo está en un trabajo, en la ruta, o en el taller en reparación.',
    'servicios.equipo.toastcargadopara': ' se cargó para ',
    'servicios.equipo.toastsalioruta': ' salió a la ruta',
    'servicios.equipo.toastnosepudosacar': 'No se pudo sacar ',
    'servicios.equipo.toastregresotaller': ' regresó al taller',
    'servicios.equipo.toastnosepudoregresar': 'No se pudo regresar ',
    'servicios.equipo.toastregistrado': ' se registró',
    'servicios.equipo.toastnosepudoregistrar': 'No se pudo registrar el equipo: ',
    'servicios.visita.cerrar': 'Cerrar la visita',
    'servicios.visita.arrancar': 'Voy en camino',
    'servicios.visita.llegue': 'Ya llegué',
    'servicios.visita.terminar': 'Terminar visita',
    'servicios.visita.saltar': 'Saltar',
    'servicios.visita.terminada': 'Visita terminada',
    'servicios.visita.marcadaPor': 'Marcada por',
    'servicios.visita.saltada': 'Visita saltada',
    'servicios.visita.motivo_titulo': 'Por qué se salta',
    'servicios.visita.motivo_placeholder': '¿Por qué se salta esta visita? (ej. el gate con candado, lluvia, el cliente lo pidió)',
    'servicios.visita.cancelar': 'Cancelar',
    'servicios.visita.confirmar_saltar': 'Sí, saltarla',
    'servicios.visita.motivo_guardado': 'Motivo: ',
    'servicios.visita.contrato': 'Datos del contrato',
    'servicios.visita.por_mes': '/mes',
    'servicios.visita.por_visita': '/visita',
    'servicios.visita.checklist': 'Checklist del servicio',
    'servicios.visita.notas_titulo': 'Notas de la cuadrilla',
    'servicios.visita.notas_placeholder': 'Notas de esta visita…',
    'servicios.visita.empezo': 'Empezó a las',
    'servicios.visita.total_min': ' min en total',
    'servicios.visita.guardar': 'Guardar notas y checklist',
    'visitas.csv.fecha': 'Fecha',
    'visitas.csv.cliente': 'Cliente',
    'visitas.csv.direccion': 'Dirección',
    'visitas.csv.estatus': 'Estatus',
    'visitas.csv.duracion': 'Duración (min)',
    'visitas.csv.notas': 'Notas',
    'visitas.vacio.titulo': 'Todavía no hay visitas',
    'visitas.vacio.texto': 'Aquí van a salir las visitas hechas y las que se saltaron.',
    'visitas.kpi.total': 'Visitas en total',
    'visitas.kpi.hechas': 'Hechas',
    'visitas.kpi.saltadas': 'Saltadas',
    'visitas.kpi.duracionPromedio': 'Tiempo promedio',
    'visitas.buscar.placeholder': 'Busca por cliente, dirección o nota…',
    'visitas.filtro.todos': 'Todos los estatus',
    'visitas.filtro.desde': 'Desde',
    'visitas.filtro.hasta': 'Hasta',
    'visitas.filtroVacio.titulo': 'No hay visitas con esos filtros',
    'visitas.filtroVacio.texto': 'Prueba con otra búsqueda, otro estatus u otras fechas.',
    'visitas.col.fecha': 'Fecha',
    'visitas.col.cliente': 'Cliente',
    'visitas.col.tiempo': 'Tiempo',
    'visitas.col.estatus': 'Estatus',
    'visitas.col.detalles': 'Detalles',
    'visitas.borrar.aria': 'Borrar la visita de {cliente}',
    'visitas.borrar.titulo': '¿Borrar esta visita del historial?',
    'visitas.borrar.texto': '{cliente} — {fecha}. Si estaba completada y el contrato cobra por visita, los ingresos de ese mes bajan. No se puede deshacer.',
    'visitas.borrar.sinCliente': 'Sin cliente',
    'visitas.borrar.confirmar': 'Sí, borrarla',
    'visitas.borrar.ok': 'Visita borrada del historial',
    'visitas.borrar.error': 'No se pudo borrar la visita',
    'visitas.detalle.porMes': '/mes',
    'visitas.detalle.porVisita': '/visita',
    'visitas.paginacion.mostrando': 'Mostrando',
    'comun.anterior': 'Atrás',
    'comun.siguiente': 'Siguiente',
    'servicios.visitas.vacio.titulo': 'No hay paradas para este día',
    'servicios.visitas.vacio.descripcion': 'Crea un contrato de servicio con el día que le toca y aquí aparecen las paradas.',
    'servicios.visitas.boton.ruta': 'Ruta',
    'servicios.visitas.etiqueta.inicio': 'Inicio',
    'servicios.visitas.etiqueta.empezo': 'Empezó',
    'servicios.visitas.opcion.asignarcrew': 'Asignar crew…',
    'servicios.visitas.tooltip.empezaraqui': 'Empezar la ruta desde esta parada',
    'servicios.visitas.boton.empezaraqui': 'Empezar aquí',
    'servicios.calendario.semanaanterior': 'Semana anterior',
    'servicios.calendario.semanasiguiente': 'Semana siguiente',
    'servicios.calendario.estasemana': 'Esta semana',
    'servicios.calendario.paradas': 'Paradas',
    'servicios.calendario.tiempoest': 'Tiempo est.',
    'servicios.calendario.lunes': 'Lunes',
    'servicios.calendario.martes': 'Martes',
    'servicios.calendario.miercoles': 'Miércoles',
    'servicios.calendario.jueves': 'Jueves',
    'servicios.calendario.viernes': 'Viernes',
    'servicios.calendario.sabado': 'Sábado',
    'servicios.calendario.domingo': 'Domingo',
    'servicios.calendario.sinparadas': 'Sin paradas',
    'servicios.calendario.cargasemanal': 'Carga de la semana por cuadrilla',
    'ajustes.burden.titulo': 'Calculadora de burden',
    'ajustes.burden.usar': 'Usar la calculadora',
    'ajustes.burden.info_parte2': 'FICA + FUTA + SUTA del estado + Workers\' Comp + prestaciones. Mucho más exacto que adivinar.',
    'ajustes.burden.estado': 'Tu estado',
    'ajustes.burden.estado_nota': '(de aquí salen los valores de SUTA y Workers\' Comp)',
    'ajustes.burden.estado_placeholder': '— Escoge tu estado —',
    'ajustes.burden.fica': 'FICA (Seguro Social + Medicare)',
    'ajustes.burden.futa': 'FUTA (tasa efectiva)',
    'ajustes.burden.seguro_medico_unidad': '($/empleado/mes)',
    'ajustes.burden.retiro': 'Retiro / match del 401k',
    'ajustes.burden.otros': 'Otras prestaciones / bonos',
    'ajustes.burden.total': 'Burden total',
    'ajustes.burden.nota_parte1': 'Este total reemplaza el campo Burden % de cada empleado. Si quieres un burden distinto para un',
    'ajustes.burden.nota_parte2': 'empleado en especial, apaga la calculadora y edítalos uno por uno.',
    'ajustes.burden.restablecer': 'usar el sugerido',
    'ajustes.gastos.descripcion': 'Escoge de las categorías comunes o agrega un gasto tuyo.',
    'ajustes.gastos.cerrar': 'Cerrar',
    'ajustes.gastos.buscar': 'Busca gastos (ej. internet, gasolina, seguro)…',
    'ajustes.gastos.sin_resultados_titulo': 'Sin resultados',
    'ajustes.gastos.sin_resultados_1': 'Nada en el catálogo coincide con “',
    'ajustes.gastos.sin_resultados_2': '”. De todos modos lo puedes agregar como gasto propio.',
    'ajustes.gastos.agregar_propio': 'Agregar un gasto propio',
    'ajustes.gastos.pie': 'Toca un gasto para agregarlo. Puedes escoger varios.',
    'ajustes.gastos.gasto_propio': 'Gasto propio',
    'ajustes.metricas.titulo.manodeobra': 'Mano de obra directa (gente que sí cobras)',
    'ajustes.metricas.vacio.facturables': 'Todavía no hay empleados facturables.',
    'ajustes.metricas.texto.repartidoen': ' entre ',
    'ajustes.metricas.titulo.gastosfijos': 'Gastos fijos (al año)',
    'ajustes.metricas.vacio.gastosfijos': 'No hay gastos fijos capturados.',
    'ajustes.metricas.etiqueta.manodeobraindirecta': 'Mano de obra indirecta:',
    'ajustes.metricas.etiqueta.equipo': 'Maquinaria (depreciación + gasolina + mantenimiento)',
    'ajustes.metricas.titulo.cuentas': 'Las cuentas',
    'metricas.error.titulo': 'Todavía no confíes en lo que te cuesta la hora',
    'metricas.aviso.titulo': 'Ojo — con esto el número te puede salir chueco',
    'ajustes.guardar.exito': '¡Listo, se guardó la configuración!',
    'ajustes.guardar.error': 'No se pudo guardar la configuración.',
    'ajustes.guardar.faltaColumna': 'A esta base de datos le falta una columna ({detalle}). Hay que correr las migraciones de supabase/migrations. No se guardó ningún ajuste.',
    'ajustes.guardar.region': 'Cambios sin guardar',
    'ajustes.guardar.titulo': 'Tienes cambios sin guardar',
    'ajustes.guardar.nota': 'Nada se guarda hasta que le des Guardar.',
    'ajustes.guardar.guardando': 'Guardando…',
    'ajustes.guardar.boton': 'Guardar cambios',
    'ui.lightbox.alt_default': 'Foto',
    'ui.lightbox.abrir_original': 'Abrir original',
    'ui.lightbox.cerrar_foto': 'Cerrar foto',
    'ui.lightbox.ayuda_cerrar': 'Aprieta Esc o haz clic afuera para cerrar',
    'tabla.acciones.encabezado': 'Acciones',
    'rastreo.banner.activo': 'Rastreo de ubicación: Prendido',
    'rastreo.banner.info_tooltip': '¿Qué información se guarda?',
    'rastreo.banner.info_titulo': 'Lo que rastreamos mientras estás en turno:',
    'rastreo.banner.info_gps': 'Tu ubicación GPS cuando checas entrada y salida',
    'rastreo.banner.info_foto': 'Tu foto de verificación al empezar y al terminar el turno',
    'rastreo.banner.info_app': 'La ubicación solo se actualiza mientras la app está abierta',
    'rastreo.banner.info_promesa': 'En cuanto checas salida, el rastreo se apaga por completo. Nunca te rastreamos fuera de turno.',
    'voz.orbe.parar': 'Toca para parar',
    'voz.orbe.escucho': 'Te escucho',
    'voz.orbe.hablar': 'Ya puedes hablar',
    'voz.error.sinmicrofono': 'No hay micrófono disponible.',
    'voz.orbe.arialabel': 'Voz de IA {effectiveOrbState} — toca para cerrar',
    'equipo.mapa.leyenda.todavia_trabajando': 'todavía trabajando',
    'equipo.mapa.aviso_gps': 'La ubicación se guarda una vez al entrar y otra al salir — no se rastrea a nadie todo el día.',
    'equipo.mapa.tooltip.entro': 'Entró',
    'equipo.mapa.tooltip.salio': 'Salió',
    'equipo.mapa.tooltip.lonche': 'En el lonche',
    'equipo.mapa.tooltip.trabajado': 'trabajadas',
    'equipo.mapa.tooltip.fuera_sitio': 'Fuera de la obra',
    'equipo.mapa.lista.titulo': 'Turnos',
    'equipo.mapa.lista.con_ubicacion': 'con ubicación',
    'equipo.mapa.lista.vacio_titulo': 'No hay turnos con ubicación',
    'equipo.mapa.lista.vacio_descripcion': 'Los pines salen cuando tu gente checa entrada o salida con el GPS prendido.',
    'equipo.mapa.fila.lonche': 'En el lonche',
    'equipo.mapa.fila.trabajando': 'Trabajando',
    'equipo.mapa.fila.sin_ubicacion_salida': 'No se guardó la ubicación de salida',
    'equipo.mapa.fila.salio_fuera_sitio': 'Salió fuera de la obra',
    'cuadrillas.actualizada': 'Cuadrilla guardada',
    'cuadrillas.creada': 'Cuadrilla creada',
    'cuadrillas.errorGuardar': 'No se pudo guardar la cuadrilla: ',
    'cuadrillas.borrar.titulo': '¿Borrar la cuadrilla?',
    'cuadrillas.borrar.mensaje': ' se borra para siempre. Tu gente no se borra, nomás se deshace la cuadrilla.',
    'cuadrillas.borrar.confirmar': 'Borrar cuadrilla',
    'cuadrillas.borrada': 'Cuadrilla borrada',
    'cuadrillas.errorBorrar': 'No se pudo borrar la cuadrilla: ',
    'comun.desconocido': 'Desconocido',
    'cuadrillas.crear': 'Crear cuadrilla',
    'cuadrillas.editar.titulo': 'Editar la cuadrilla',
    'cuadrillas.nueva.titulo': 'Cuadrilla nueva',
    'cuadrillas.nombre.placeholder': 'ej. Cuadrilla del este',
    'cuadrillas.lider.placeholder': '— Escoge al líder —',
    'cuadrillas.escogerTrabajadores': 'Escoge a los trabajadores',
    'cuadrillas.sinTrabajadoresDisponibles': 'No hay trabajadores ni técnicos de servicio. Primero agrega a tu gente.',
    'comun.guardando': 'Guardando…',
    'cuadrillas.creando': 'Creando…',
    'cuadrillas.guardarCambios': 'Guardar cambios',
    'cuadrillas.crear.boton': 'Crear cuadrilla',
    'cuadrillas.vacio.titulo': 'Todavía no hay cuadrillas',
    'cuadrillas.vacio.texto': 'Arma tu primera cuadrilla para empezar a asignar gente a las rutas.',
    'cuadrillas.editar.title': 'Editar la cuadrilla',
    'cuadrillas.editar.aria': 'Editar la cuadrilla',
    'cuadrillas.borrar.title': 'Borrar la cuadrilla',
    'cuadrillas.borrar.aria': 'Borrar la cuadrilla',
    'reloj.foto.alt': 'Foto de verificación',
    'reloj.foto.lista': 'Foto lista',
    'reloj.foto.quitar.title': 'Quitar la foto',
    'reloj.foto.quitar.aria': 'Quitar la foto',
    'reloj.destino.general': 'Taller',
    'reloj.error.turnoAbierto': 'Ya tienes un turno abierto.',
    'reloj.error.perfilCargando': 'Todavía estamos cargando tu perfil — inténtalo en un momento.',
    'reloj.error.sinInternet.entrada': 'No tienes internet — para marcar entrada necesitas conexión. Conéctate y vuelve a intentar.',
    'reloj.error.sinInternet.salida': 'No tienes internet — para marcar salida necesitas conexión. Conéctate y vuelve a intentar.',
    'reloj.error.sinInternet.traslado': 'No tienes internet — para mover tu reloj necesitas conexión. Sigues checado donde estabas.',
    'reloj.error.sinInternet.lunch': 'No tienes internet — el lunch no se puede guardar sin conexión. Conéctate y vuelve a intentar.',
    'reloj.salida.confirmar.titulo': '¿Marcar salida?',
    'reloj.salida.confirmar.no': 'Sigo trabajando',
    'reloj.estado.enLunch': 'En el lunch',
    'reloj.estado.entradaALas': 'Entraste a las ',
    'reloj.estado.fueraPor': 'Fuera de la obra por ',
    'reloj.destino.pregunta': '¿En qué vas a trabajar?',
    'reloj.destino.tallerGeneral': 'Taller / horas generales',
    'reloj.destino.grupo.proyectos': 'Proyectos',
    'reloj.destino.grupo.rutas': 'Rutas de servicio',
    'reloj.destino.sinAsignar': 'Todavía no te asignan nada — pídele al admin que te meta a un proyecto o a una ruta, o marca entrada en el taller.',
    'reloj.destino.sinNada': 'Todavía no hay proyectos ni rutas activas aquí. De todos modos puedes marcar entrada en el taller.',
    'reloj.destino.verTodo': 'Mostrar todas las obras del negocio',
    /* La primera opción del desplegable enseña el NOMBRE del taller cuando hay
       dirección configurada, y el fichaje escribe ese destino. Sin dirección
       sigue siendo 'reloj.destino.tallerGeneral', que no escribe destino
       ninguno — que es lo que hay hoy en los 165 talleres. Este aviso solo lo
       ve quien puede arreglarlo: mandar a un peón a Ajustes no sirve de nada. */
    'reloj.destino.sinTaller': 'El taller todavía no tiene dirección, así que estas horas quedan sin clasificar. Ponla en Ajustes → Oficina.',
    'reloj.geofence.activo': 'Geofence activo (',
    'reloj.foto.boton.salida': 'Agregar foto de salida (opcional)',
    'reloj.foto.boton.entrada': 'Agregar foto de verificación (opcional)',
    'reloj.boton.entrada': 'Marcar entrada',
    'reloj.boton.empezarLunch': 'Empezar lunch',
    'reloj.boton.terminarLunch': 'Terminar lunch',
    'reloj.lunch.tomado': 'Lunch tomado (',
    /* El lunch se cuenta por TURNO, no por tramo: cuando lo tomó antes de mover
       su reloj, las horas del lunch están en el tramo anterior y esta pantalla
       ya no tiene los sellos que enseñar. */
    'reloj.lunch.tomadoTurno': 'Ya tomaste tu lunch en este turno.',
    'reloj.lunch.terminaPrimero': 'Termina tu lunch antes de marcar salida.',
    'reloj.horas.hoy': 'Horas de hoy',
    'reloj.horas.semana': 'Esta semana',
    'reloj.gente.titulo': 'Checados ahora',
    'reloj.gente.vacio.titulo': 'Nadie ha checado entrada',
    'reloj.gente.vacio.texto': 'Aquí aparece tu gente cuando empiece su turno.',
    'reloj.gente.fueraObraDistancia': { uno: 'Fuera de obra · {count} ft', otros: 'Fuera de obra · {count} ft' },
    'reloj.gente.fueraObra': 'Fuera de obra',
    'reloj.gente.desde': ' • Desde ',
    'equipo.directorio.quitar_titulo': '¿Quitar a esta persona del equipo?',
    'equipo.directorio.quitar_confirmar': 'Quitar',
    'equipo.directorio.quitar_cancelar': 'Cancelar',
    'equipo.directorio.buscar': 'Busca por nombre o correo…',
    'equipo.directorio.invitar': 'Invitar a alguien',
    'equipo.directorio.vacio_titulo': 'No hay nadie en la lista',
    'equipo.directorio.col_nombre': 'Nombre',
    'equipo.directorio.col_correo': 'Correo',
    'equipo.directorio.col_rol': 'Puesto',
    'equipo.directorio.col_cuadrilla': 'Cuadrilla',
    'equipo.directorio.col_agregado': 'Desde',
    'equipo.directorio.editar_tooltip': 'Editar a esta persona',
    'equipo.directorio.editar_aria': 'Editar a esta persona',
    'equipo.directorio.quitar_tooltip': 'Quitar a esta persona',
    'equipo.directorio.quitar_aria': 'Quitar a esta persona',
    'reloj.titulo': 'Reloj checador',
    'reloj.descripcion': 'Checa tu entrada, tu lonche y dónde andas cada día.',
    'personal.horas.taller': 'Taller / general',
    'personal.horas.rango_fechas': 'Rango de fechas',
    'personal.horas.ultimos_7': 'Últimos 7 días',
    'personal.horas.ultimos_14': 'Últimos 14 días',
    'personal.horas.ultimos_30': 'Últimos 30 días',
    'personal.horas.ultimos_90': 'Últimos 90 días',
    'personal.horas.vista_por_empleado': 'Por Empleado',
    'personal.horas.vista_compacta': 'Lista',
    'personal.horas.abrir_todo': 'Abrir todo',
    'personal.horas.cerrar_todo': 'Cerrar todo',
    'personal.horas.exportar_csv': 'Exportar CSV',
    'personal.horas.buscar': 'Busca nombre, email, proyecto…',
    'personal.horas.filtro_empleado': 'Filtro de empleado',
    'personal.horas.todos_empleados': 'Todos los empleados',
    'personal.horas.filtro_estatus': 'Filtro de estatus',
    'personal.horas.todos_estatus': 'Todos los estatus',
    'personal.horas.opcion_activo': 'Activos (no han checado salida)',
    'personal.horas.filtro_cuadrilla': 'Filtro de cuadrilla',
    'personal.horas.todas_cuadrillas': 'Todas las cuadrillas',
    'personal.horas.resumen_empleado_uno': 'empleado',
    'personal.horas.resumen_empleado_varios': 'empleados',
    'personal.horas.vacio_titulo': 'No hay horas registradas',
    'personal.horas.vacio_texto': 'Abre más el rango de fechas o quita los filtros.',
    'personal.horas.tarjeta_turnos': 'Turnos',
    'personal.horas.editar_turno_tabla_tooltip': 'Editar turno',
    'personal.horas.editar_turno_tabla_aria': 'Editar turno',
    'personal.horas.editar_turno_fila_tooltip': 'Editar turno',
    'personal.horas.editar_turno_fila_aria': 'Editar turno',
    'equipo.tabs.checar': 'Checar',
    'equipo.tabs.equipo': 'Gente',
    'equipo.tabs.horas': 'Horas',
    'equipo.tabs.mapa': 'Mapa',
    'equipo.encabezado.titulo': 'Gente',
    'equipo.encabezado.descripcion': 'Checa tu entrada, maneja a tu gente y revisa las horas. Las crews se arman en Rutas de servicio.',
    'equipo.kpi.miembros': 'Tu gente',
    'equipo.kpi.crews_activas': 'Crews activas',
    'equipo.kpi.de_total': { uno: 'de {count}', otros: 'de {count}' },
    'equipo.kpi.trabajando': 'Trabajando ahora',
    'chat.errores.iniciar_conversacion': 'No se pudo abrir la conversación — revisa tu internet y vuelve a intentar.',
    'chat.errores.crear_conversacion_voz': 'No se pudo crear la conversación',
    'chat.errores.voz_stream': 'Falló la conexión de voz',
    'dialogo.boton.confirmar': 'Confirmar',
    'dialogo.boton.ok': 'OK',
    'dialogo.boton.enviar': 'Enviar',
    'dialogo.input.placeholder': 'Escribe los detalles...',
    'ajustes.terminos.predeterminados': '1. Forma de pago: Se pide un anticipo del 50% para agendar el trabajo. El resto se paga al terminar.\n\n2. Fechas: Las fechas que damos son aproximadas y pueden cambiar.',
    'ajustes.garantia.nombre': 'Garantía de 1 año en mano de obra',
    'ajustes.garantia.texto': 'Garantizamos la mano de obra por 1 año desde que terminamos el trabajo. Las plantas se garantizan 30 días, a menos que se instale un sistema de riego automático.',
    'ajustes.email.asunto': 'Propuesta: {{proposalTitle}}',
    'ajustes.email.cuerpo': 'Hola,\n\nAquí puedes revisar tu propuesta:\n{{publicLink}}\n\nGracias,\n{{businessName}}',
    'ajustes.error.divergencia': 'Al volver a leer los ajustes guardados, lo que te cuesta la hora salió distinto (${remoto} en vez de ${local}). Refresca la página y revisa otra vez.',
    'espacio.nombrePorDefecto': 'Nombre del negocio',
    'espacio.nombreCorto': 'Negocio',
    'espacio.cargando': 'Cargando…',
    'chat.tiempo.ahorita': 'ahorita',
    'chat.historial.titulo': 'Historial de chat',
    'chat.historial.subtitulo': 'Conversaciones pasadas con Raza AI',
    'chat.historial.buscar': 'Buscar conversaciones…',
    'chat.historial.vacio.titulo': 'Todavía no hay conversaciones',
    'chat.historial.vacio.sinresultados': 'No hay ninguna conversación con esa búsqueda.',
    'chat.historial.vacio.descripcion': 'Empieza un chat nuevo y aparecerá aquí.',
    'chat.historial.borrar.tooltip': 'Borrar conversación',
    'chat.encabezado.volver.tooltip': 'Volver al panel',
    'chat.encabezado.volver.aria': 'Volver al panel',
    'chat.encabezado.conversaciones.tooltip': 'Conversaciones',
    'chat.encabezado.conversaciones.aria': 'Ver las conversaciones',
    'chat.encabezado.nuevaconversacion': 'Chat nuevo',
    'chat.encabezado.compartidaequipo': 'Compartida con el equipo',
    'chat.voz.completa.tooltip': 'Voz completa: la IA te contesta hablando',
    'chat.voz.dictado.tooltip': 'Dictado: solo pasa tu voz a texto',
    'chat.voz.completa.aria': 'Modo voz completa — cambiar a dictado',
    'chat.voz.dictado.aria': 'Modo dictado — cambiar a voz completa',
    'chat.voz.boton.voz': 'Voz',
    'chat.voz.boton.dictado': 'Dictado',
    'chat.privacidad.privada.tooltip': 'Privada — tócala para compartirla con el equipo',
    'chat.privacidad.compartida.tooltip': 'Compartida — tócala para hacerla privada',
    'chat.mensajes.escribiendo': 'Raza AI está escribiendo',
    'chat.entrada.aria': 'Escríbele a Raza AI',
    'chat.entrada.micro.tooltip': 'Habla con Raza AI',
    'chat.entrada.micro.aria': 'Habla con Raza AI',
    'chat.entrada.enviar': 'Enviar mensaje',
    'chat.audio.hablando': 'Raza AI está hablando…',
    'cliente.facturacion.errorcuenta.titulo': 'Error de cuenta',
    'cliente.facturacion.errorcuenta.descripcion': 'No pudimos conectar tu cuenta con tus datos. Comunícate con soporte.',
    'cliente.facturacion.titulo': 'Facturas y pagos',
    'cliente.facturacion.descripcion': 'Tus facturas y tu historial de pagos.',
    'cliente.facturacion.saldo.titulo': 'Total por pagar',
    'cliente.facturacion.saldo.comopagar': 'Para pagar una factura o preguntar por tu saldo, responde el correo de la factura o habla directo con tu contratista.',
    'cliente.facturacion.porpagar.titulo': 'Facturas por pagar',
    'cliente.facturacion.porpagar.vacio': 'Ahorita no debes nada.',
    'cliente.facturacion.historial.titulo': 'Historial de pagos',
    'cliente.facturacion.historial.vacio.titulo': 'Todavía no hay pagos',
    'cliente.facturacion.historial.vacio.descripcion': 'Cuando pagues una factura, aparece aquí.',
    'cliente.facturacion.tabla.factura': 'Factura',
    'cliente.facturacion.tabla.fecha': 'Fecha',
    'cliente.facturacion.tabla.estado': 'Estado',
    'cliente.facturacion.tabla.monto': 'Monto',
    'cliente.facturacion.tabla.recibo': 'Recibo',
    'cliente.panel.error_cuenta_titulo': 'Error con la cuenta',
    'cliente.panel.error_cuenta_descripcion': 'No pudimos conectar tu cuenta con ningún cliente. Habla con soporte.',
    'cliente.panel.bienvenida': 'Qué gusto verte',
    'cliente.panel.bienvenida_descripcion': 'Aquí ves tus proyectos activos, revisas los estimados pendientes y sigues tus próximas visitas de servicio.',
    'cliente.panel.proyectos_vacio_titulo': 'Sin proyectos activos',
    'cliente.panel.proyectos_vacio_descripcion': 'Ahorita no tienes ningún proyecto activo.',
    'cliente.panel.estimados_titulo': 'Tus estimados',
    'cliente.panel.estimado_para': 'Estimado para ',
    'cliente.panel.creado': 'Creado el ',
    'cliente.panel.estimados_vacio_titulo': 'Todavía no hay estimados',
    'cliente.panel.estimados_vacio_descripcion': 'Los estimados que te compartan salen aquí.',
    'cliente.panel.servicios_titulo': 'Próximos servicios',
    'cliente.panel.visita_hoy': 'Agendada para hoy',
    'cliente.panel.visita_proxima': 'Próximo mantenimiento',
    'cliente.panel.servicios_vacio_titulo': 'Nada agendado',
    'cliente.panel.servicios_vacio_descripcion': 'No hay visitas de servicio agendadas.',
    'cliente.panel.pedir_servicio': 'Pedir servicio',
    'clientes.toast.actualizado': 'Cliente actualizado',
    'clientes.toast.creado': 'Cliente creado',
    'clientes.borrar.titulo': 'Borrar cliente',
    'clientes.borrar.confirmar': 'Sí, borrar',
    'clientes.borrar.cancelar': 'Cancelar',
    'clientes.fila.acciones': 'Acciones del cliente',
    'clientes.tab.clientes': 'Clientes',
    'clientes.tab.prospectos': 'Prospectos',
    'clientes.tab.cerrados': 'Cerrados',
    // Archivo de cerrados. Ver utils/leadArchive.ts y views/ClosedLeadsView.tsx.
    'cerrados.tab.ganados': 'Ganados',
    'cerrados.tab.perdidos': 'Perdidos',
    'cerrados.buscar': 'Buscar por nombre, dirección, teléfono o servicio…',
    'cerrados.valorTotal': 'Valor:',
    'cerrados.limpiar': { uno: 'Quitar 1', otros: 'Quitar {count}' },
    'cerrados.sinFecha': 'Sin fecha',
    'cerrados.sinDireccion': 'Sin dirección',
    'cerrados.fecha.ganado': 'Ganado el {fecha}',
    'cerrados.fecha.apuntado': 'Apuntado el {fecha}',
    'cerrados.vacio.ganados': 'Todavía no has ganado ninguno',
    'cerrados.vacio.perdidos': 'No hay prospectos perdidos',
    'cerrados.vacio.descripcion': 'Aquí se van guardando los tratos que ya se cerraron, agrupados por mes.',
    'cerrados.vacio.busqueda': 'Nada con esa búsqueda',
    'cerrados.vacio.busquedaDesc': 'Prueba con otro nombre, otra dirección o parte del teléfono.',
    'cerrados.error.titulo': 'No se pudieron cargar los cerrados',
    'cerrados.error.descripcion': 'Revisa tu conexión e inténtalo de nuevo.',
    'cerrados.error.reintentar': 'Reintentar',
    'prospectos.columna.verTodos': 'Ver todos',
    'prospectos.columna.masOcultos': { uno: '1 más en Cerrados', otros: '{count} más en Cerrados' },
    'clientes.vacio.boton.nuevo': 'Nuevo cliente',
    'clientes.vacio.titulo': 'Todavía no hay clientes',
    'clientes.vacio.desc': 'Agrega tu primer cliente y aquí van a salir sus trabajos, estimados e ingresos.',
    'clientes.filtro.sinresultados': 'Sin resultados',
    'clientes.filtro.sinresultados.desc': 'No hay clientes con ese filtro.',
    'clientes.filtro.limpiar': 'Quitar filtros',
    'clientes.tabla.direccion': 'Dirección',
    'clientes.tabla.trabajosactivos': 'Trabajos activos',
    'clientes.menu.cerrar': 'Cerrar menú',
    'clientes.menu.editar': 'Editar cliente',
    'clientes.menu.borrar': 'Borrar cliente',
    'clientes.buscar': 'Busca por nombre, dirección o teléfono…',
    'clientes.stat.total': 'Clientes en total',
    'clientes.stat.contrabajo': 'Con trabajos activos',
    'clientes.filtro.todos': 'Todos',
    'camara.errores.elegirproyecto': 'Primero elige un proyecto.',
    'camara.subida.usuariodesconocido': 'Usuario desconocido',
    'camara.subida.exito': '¡Foto subida!',
    'camara.subida.error': 'No se pudo subir la foto. Intenta otra vez.',
    'camara.borrar.confirmar': '¿Seguro que quieres borrar esta foto?',
    'camara.borrar.mensaje': 'La foto se quita del trabajo y ya no se puede recuperar.',
    'camara.borrar.aceptar': 'Borrar foto',
    'camara.borrar.cancelar': 'Cancelar',
    'camara.borrar.error': 'No se pudo borrar la foto.',
    'camara.foreman.descripcion': 'Toma fotos y súbelas directo a tus proyectos activos.',
    'camara.foreman.opcionvacia': 'Elige un proyecto…',
    'camara.foreman.subiendo': 'Subiendo…',
    'camara.foreman.tomarfoto': 'Tomar o subir foto',
    'camara.admin.titulo': 'Cámara de la empresa',
    'camara.admin.descripcion': 'Revisa, descarga y borra las fotos del proyecto.',
    'camara.admin.opcionvacia': 'Elige un proyecto…',
    'camara.vacio.sinproyecto.titulo': 'Elige un proyecto',
    'camara.vacio.sinproyecto.descripcion': 'Elige un proyecto en la lista de arriba para ver sus fotos.',
    'camara.vacio.sinfotos.titulo': 'Todavía no hay fotos',
    'camara.vacio.sinfotos.descripcion': 'Este proyecto no tiene fotos subidas. Usa el botón de subir para agregar.',
    'camara.galeria.altfoto': 'Foto del proyecto',
    'camara.visor.descargar.aria': 'Descargar foto',
    'camara.visor.descargar.tooltip': 'Descargar',
    'camara.visor.borrar.aria': 'Borrar foto',
    'camara.visor.borrar.tooltip': 'Borrar',
    'camara.visor.cerrar.aria': 'Cerrar foto',
    'camara.visor.cerrar.tooltip': 'Cerrar',
    'camara.visor.altcompleta': 'Vista completa',
    'panel.stat.pipeline': 'Por cerrar + anual',
    'panel.stat.pipeline.desc': 'Estimados por cerrar + contratos de servicio',
    'panel.stat.proyectosactivos': 'Proyectos activos',
    'panel.stat.verproyectos': 'Ver proyectos ',
    'panel.stat.reloj': 'Reloj checador',
    'panel.stat.reloj.trabajando': 'trabajando ahorita',
    'panel.stat.reloj.administrar': 'Ver las horas ',
    'panel.ingresos.anioanterior': 'Año anterior',
    'panel.ingresos.aniosiguiente': 'Año siguiente',
    'panel.ingresos.cobradoen': 'cobrado en ',
    'panel.ingresos.sinbase': 'no hay con qué comparar',
    'panel.ingresos.vacio.titulo': 'No se cobró nada en ',
    'panel.ingresos.vacio.desc': 'Aquí aparece el dinero cuando marcas pagada una factura o una propuesta.',
    'panel.ingresos.tooltip.cobrado': 'Cobrado',
    'panel.proyectosrecientes.titulo': 'Proyectos recientes',
    'panel.proyectosrecientes.vacio': 'Todavía no hay proyectos activos.',
    'panel.prospectos.titulo': 'Prospectos nuevos',
    'panel.prospectos.vacio': 'No hay prospectos nuevos.',
    'designLab.nav.panel': 'Panel',
    'designLab.nav.clientes': 'Clientes',
    'designLab.nav.estimados': 'Estimados',
    'designLab.nav.precios': 'Precios',
    'designLab.nav.seguimientos': 'Seguimientos',
    'designLab.nav.proyectos': 'Proyectos',
    'designLab.nav.rutas': 'Rutas de servicio',
    'designLab.nav.finanzas': 'Finanzas',
    'designLab.nav.gente': 'Gente',
    'designLab.nav.agenda': 'Agenda',
    'designLab.nav.grupo.ventas': 'Ventas',
    'designLab.nav.grupo.operaciones': 'Operaciones',
    'designLab.nav.grupo.equipo': 'Equipo',
    'designLab.nav.chat': 'Historial de chat',
    'designLab.nav.tienda': 'Tienda Raza',
    'designLab.boton.contorno': 'Contorno',
    'designLab.boton.chico': 'Chico',
    'designLab.boton.mediano': 'Mediano',
    'designLab.boton.grande': 'Grande',
    'disenos.presentacion.vacio.titulo': 'No hay diseño',
    'disenos.presentacion.vacio.descripcion': 'Primero agrégale un diseño a un prospecto.',
    'disenos.presentacion.boton.volver': 'Volver a los prospectos',
    'disenos.presentacion.etiqueta.presentando': 'Presentando a:',
    'disenos.presentacion.tooltip.salir': 'Salir de la presentación',
    'disenos.presentacion.aria.salir': 'Salir de la presentación',
    'disenos.presentacion.titulo.iframe3d': 'Vista del diseño en 3D',
    'disenos.presentacion.aria.anterior': 'Imagen anterior',
    'disenos.presentacion.aria.siguiente': 'Imagen siguiente',
    'disenos.presentacion.tab.antes': 'Antes',
    'disenos.presentacion.tab.propuesta': 'Cómo va a quedar',
    'disenos.presentacion.tab.tresd': '3D interactivo',
    'equipo.error.guardarmantenimiento': 'No se pudo guardar el registro de mantenimiento.',
    'equipo.error.regresartaller': 'No se pudo marcar el equipo como reparado.',
    'equipo.filtro.todos': 'Todos',
    'equipo.filtro.disponible': 'Disponible',
    'equipo.filtro.entrabajo': 'En la obra',
    'equipo.reparacion.sinresultados.desc': 'No hay equipo en reparación con esa búsqueda.',
    'equipo.reparacion.desde': 'En reparación desde ',
    'equipo.reparacion.sinregistros': 'Está marcado en reparación, pero todavía no tiene ningún mantenimiento anotado.',
    'equipo.reparacion.registro.uno': 'arreglo',
    'equipo.reparacion.registro.varios': 'arreglos',
    'equipo.reparacion.regresando': 'Marcando…',
    'equipo.mant.sinresultados.desc': 'No hay nada con esa búsqueda.',
    'equipo.tabla.equipoborrado': 'Equipo borrado',
    'equipo.vacio.agregar': 'Agregar equipo',
    'equipo.campo.nombre.placeholder': 'ej. Skid Steer #3',
    'equipo.campo.ubicacionactual': 'Ubicación actual',
    'equipo.campo.ubicacion.placeholder': 'Taller o nombre del proyecto',
    'equipo.campo.proximoservicio.label': 'Próximo servicio',
    'equipo.campo.notas': 'Notas internas',
    'equipo.campo.notas.placeholder': 'Cómo cuidarlo, qué le ha pasado, cualquier detalle...',
    'equipo.boton.verdetalles': 'Ver detalles',
    'equipo.boton.registrarmantenimiento': 'Registrar mantenimiento',
    'equipo.modal.cerrar': 'Cerrar',
    'equipo.mant.campo.fecha': 'Fecha*',
    'equipo.mant.campo.trabajo.placeholder': 'ej. Cambiar el filtro hidráulico y rellenar el aceite',
    'equipo.mant.campo.costo': 'Costo ($)',
    'equipo.mant.campo.costo.aria': 'Costo en dólares',
    'equipo.mant.campo.hechopor': 'Quién lo hizo',
    'equipo.mant.campo.hechopor.placeholder': 'Nombre o taller',
    'equipo.mant.yaentaller': 'Ya está en ',
    'equipo.mant.yaentaller.cola': ' — ahí se queda hasta que lo marques reparado.',
    'equipo.mant.mandartaller': 'Mandar a ',
    'equipo.mant.historial': 'Historial reciente',
    'equipo.mant.guardando': 'Guardando…',
    'estimados.lista.borrar_varios_titulo': { uno: '¿Borrar {count} estimado?', otros: '¿Borrar {count} estimados?' },
    'estimados.lista.borrar_varios_mensaje': { uno: 'Esto borra para siempre {count} estimado. Los proyectos ligados (si hay) se quedan con sus datos, pero se desligan del estimado. Esto no se puede deshacer.', otros: 'Esto borra para siempre {count} estimados. Los proyectos ligados (si hay) se quedan con sus datos, pero se desligan del estimado. Esto no se puede deshacer.' },
    'estimados.lista.borrar_varios_confirmar': 'Borrar {count}',
    'estimados.lista.borrar_varios_cancelar': 'Cancelar',
    'estimados.lista.borrados_ok': { uno: 'Borré {count} estimado.', otros: 'Borré {count} estimados.' },
    'estimados.lista.borrados_error': { uno: 'No se pudo borrar {count} estimado y se restauró.', otros: 'No se pudieron borrar {count} estimados y se restauraron.' },
    'estimados.lista.borrar_con_proyecto_titulo': '¿Borrar el estimado Y el proyecto?',
    'estimados.lista.borrar_con_proyecto_mensaje': 'Este estimado creó el proyecto "{name}". ¿Borro los dos? Si le das Cancelar, el proyecto se queda pero se desliga de este estimado.',
    'estimados.lista.borrar_los_dos': 'Borrar los dos',
    'estimados.lista.dejar_proyecto': 'Dejar el proyecto (desligarlo)',
    'estimados.lista.error_borrar': 'No se pudo borrar: {error}',
    'estimados.lista.borrar_mensaje': '¿Seguro que quieres borrar este estimado? Esto no se puede deshacer.',
    'estimados.lista.borrar_confirmar': 'Borrar',
    'estimados.lista.borrar_cancelar': 'Cancelar',
    'estimados.lista.error_borrar_estimado': 'No se pudo borrar el estimado: {error}',
    'estimados.lista.modo_estimados': 'Estimados',
    'estimados.lista.modo_propuesta': 'Propuesta',
    'estimados.lista.modo_firmadas': 'Firmadas',
    'estimados.lista.kpi_borradores': 'En borrador',
    'estimados.lista.kpi_por_aprobar': 'Por aprobar',
    'estimados.lista.kpi_ganado': 'Dinero ganado',
    'estimados.lista.kpi_cierre': 'Tasa de cierre',
    'estimados.lista.kpi_cierre_nota': 'Según los trabajos ya cerrados',
    'estimados.lista.tab_todos': 'Todos',
    'estimados.lista.tab_borrador': 'Borrador',
    'estimados.lista.tab_enviado': 'Enviado',
    'estimados.lista.tab_aceptado': 'Aceptado',
    'estimados.lista.tab_rechazado': 'Rechazado',
    'estimados.lista.tab_vencido': 'Vencido',
    'estimados.lista.tab_archivado': 'Archivado',
    'estimados.lista.buscar': 'Busca estimados…',
    'estimados.lista.quitar_seleccion_todo': 'Quitar selección',
    'estimados.lista.seleccionar_todo': 'Seleccionar todo',
    'estimados.lista.sin_titulo': 'Estimado sin nombre',
    'estimados.lista.cambiar_estatus': 'Cambiar estatus',
    'estimados.lista.sin_proyecto_tooltip': 'Este estimado aceptado todavía no tiene proyecto. Dale clic para crearlo.',
    'estimados.lista.sin_proyecto_crear': 'Sin proyecto — crear',
    'estimados.lista.vacio_filtro_titulo': 'Sin resultados',
    'estimados.lista.vacio_filtro_busqueda': 'Nada coincide con “{searchQuery}”.',
    'estimados.lista.vacio_filtro_estatus': 'No hay estimados en este estatus.',
    'estimados.lista.quitar_filtros': 'Quitar filtros',
    'estimados.lista.vacio_titulo': 'Todavía no hay estimados',
    'estimados.lista.vacio_texto': 'Empieza un trabajo para armar tu primer estimado — el precio, el margen y la propuesta al cliente salen de aquí.',
    'estimados.lista.cerrar_menu': 'Cerrar menú',
    'estimados.lista.seguir_editando': 'Seguir editando',
    'estimados.lista.borrar_borrador': 'Borrar borrador',
    'finanzas.encabezado.titulo': 'Finanzas',
    'finanzas.encabezado.desc': 'Lo que entra, los trabajos y los contratos de todo el negocio.',
    'finanzas.restringido.titulo': 'No puedes ver los números',
    'finanzas.restringido.desc': 'Con tu puesto puedes ver los trabajos y los contratos, pero no el dinero.',
    'seguimientos.cabecera.titulo': 'Seguimientos',
    'seguimientos.cabecera.descripcion': 'Mira cómo va cada propuesta que mandaste',
    'seguimientos.estado.proyectocreado': 'Proyecto creado',
    'seguimientos.estado.pagado': 'Pagado',
    'seguimientos.estado.completado': 'Terminado',
    'seguimientos.estado.ganado': 'Ganado',
    'seguimientos.columna.enviadas': 'Enviadas (sin abrir)',
    'seguimientos.vacio.enviadas': 'No hay propuestas sin abrir',
    'seguimientos.vacio.enviadaspista': 'Las propuestas llegan aquí cuando las marcas como',
    'seguimientos.vacio.enviadasmarca': 'Enviada',
    'seguimientos.vacio.enviadasdonde': 'hazlo en la pestaña Share de la propuesta, justo después de mandar el link.',
    'seguimientos.tarjeta.hacepoco': 'Hace poco',
    'seguimientos.vacio.abiertas': 'Nadie ha abierto una propuesta todavía',
    'seguimientos.vacio.ganadas': 'No hay propuestas ganadas',
    'seguimientos.estado.vencida': 'Vencida',
    'seguimientos.estado.rechazada': 'Rechazada',
    'seguimientos.vacio.perdidas': 'No hay propuestas perdidas',
    'seguimientos.dia.sinfecha': 'Sin fecha',
    'prospectos.convertir.titulo': 'Pasar a cliente',
    'prospectos.convertir.confirmar': 'Pasar a cliente',
    'prospectos.convertir.cancelar': 'Cancelar',
    'prospectos.convertir.error': 'No se pudo pasar este prospecto a cliente. Inténtalo de nuevo.',
    'prospectos.buscar.placeholder': 'Buscar en el pipeline…',
    'prospectos.filtro.todos': 'Todos',
    'prospectos.filtro.vencidos': 'Vencidos',
    'prospectos.boton.nuevo': 'Nuevo prospecto',
    'prospectos.pista.arrastrar': 'Arrastra la tarjeta a otra columna para cambiarle la etapa.',
    'marketing.vista.titulo': 'Marketing',
    'marketing.vista.descripcion': 'Campañas, reseñas y redes sociales.',
    'marketing.vista.vacio_titulo': 'El marketing todavía no está conectado',
    'marketing.vista.vacio_descripcion': 'Aquí van a salir Unified Inbox, Reputation y Social Planner en cuanto se conecte el sistema de marketing a tu cuenta. Mientras tanto, Raza Pro no manda nada.',
    'precios.titulo': 'Lista de precios',
    'precios.sub': 'Materiales, plantillas y tus propios precios, todo en un solo lugar.',
    'precios.tab.materiales': 'Materiales',
    'precios.tab.plantillas': 'Plantillas',
    'precios.tab.materialesPlantilla': 'Materiales del catálogo',
    'precios.tab.mios': 'Mis materiales',
    'precios.aviso.titulo': 'Los precios se mueven',
    'precios.aviso.texto': 'El material sube y baja todos los días por la gasolina y porque a veces no hay. Antes de cerrar un contrato grande, confirma el precio con tu proveedor.',
    'precios.exportarCsv': 'Bajar CSV',
    'precios.nuevoMaterial': 'Material nuevo',
    'precios.frescura.fresco': 'Al día',
    'precios.frescura.medio': 'Ya tiene rato',
    'precios.frescura.viejo': 'Viejo',
    'precios.borrar.titulo': 'Borrar material',
    'precios.borrar.mensaje1': '¿Seguro que quieres borrar',
    'precios.borrar.mensaje2': 'Esto no cambia los estimados que ya hiciste, pero sí puede afectar las plantillas que lo usan.',
    'precios.borrar.confirmar': 'Borrar',
    'precios.borrar.cancelar': 'Cancelar',
    'precios.importar.errorTamano': 'El archivo pasa de 10 MB.',
    'precios.importar.leyendo': 'La IA está leyendo tu lista de materiales…',
    'precios.importar.sinMateriales': 'La IA no encontró ningún material en este archivo.',
    'precios.importar.sinNombre': 'Material sin nombre',
    'precios.importar.avisoCosto': 'El costo viene en $0',
    'precios.importar.listo': { uno: 'La IA sacó {count} material', otros: 'La IA sacó {count} materiales' },
    'precios.importar.preciosActualizados': { uno: '{count} precio del catálogo actualizado', otros: '{count} precios del catálogo actualizados' },
    'precios.importar.nuevosAgregados': { uno: '{count} material nuevo agregado', otros: '{count} materiales nuevos agregados' },
    'precios.importar.resumen': 'Listo —',
    'precios.importar.nada': 'No había nada que importar.',
    'precios.importar.error': 'No se pudieron importar algunos materiales. Inténtalo otra vez.',
    'precios.exportar.listo': '¡Listo! Ya se bajó tu lista de precios.',
    'precios.importar.titulo': 'Estamos leyendo tu archivo',
    'precios.importar.sub': 'Sacando materiales, categorías y precios. Si el archivo está grande, tarda de 10 a 30 segundos.',
    'precios.vacio.titulo': 'No hay materiales',
    'precios.vacio.texto': 'Sube tu lista de precios para arrancar, o agrega los materiales a mano.',
    'precios.vacio.filtro': 'Cambia el filtro o busca otra cosa.',
    'precios.tabla.material': 'Material',
    'precios.tabla.precioVenta': 'Precio de venta',
    'precios.editar': 'Editar',
    'precios.borrarFila': 'Borrar',
    'precios.preview.titulo': 'Revisa antes de importar',
    'precios.preview.llenanCatalogo': 'van a actualizar un precio del catálogo',
    'precios.cerrar': 'Cerrar',
    'precios.preview.formato': 'Formato detectado:',
    'precios.preview.total': 'Total de materiales:',
    'precios.preview.valor': 'Valor estimado:',
    'precios.preview.confianza.alta': 'La IA está segura',
    'precios.preview.confianza.media': 'La IA está más o menos segura (revísalo)',
    'precios.preview.confianza.baja': 'La IA no está segura (revísalo bien)',
    'precios.preview.confianza.desconocida': 'No se sabe qué tan segura está',
    'precios.tabla.coincidencia': 'Coincide en el catálogo',
    'precios.preview.usarPrecio': 'Usar este precio',
    'precios.preview.agregarNuevo': 'Agregar nuevo',
    'precios.preview.sinCoincidencia': 'No coincide → agregar nuevo',
    'proyectos.tarjeta.vaTarde': 'Va tarde',
    'proyectos.tarjeta.arranque.poner': 'Poner fecha de arranque',
    'proyectos.tarjeta.arranque.fecha': 'Arranca el {fecha}',
    'proyectos.tarjeta.arranque.ayuda': 'Cuando llegue el día, la obra pasa sola a En proceso',
    'proyectos.tarjeta.cambiarEstatus': 'Cambiar estatus',
    'proyectos.estatus.aceptado': 'Aceptado',
    'proyectos.estatus.programado': 'Programado',
    'proyectos.estatus.enProceso': 'En proceso',
    'proyectos.estatus.terminado': 'Terminado',
    'proyectos.estatus.cancelado': 'Cancelado',
    'proyectos.tarjeta.sinPendientes': 'Ya no falta nada',
    'proyectos.tarjeta.sinGente': 'Sin gente',
    'proyectos.columna.vacia': 'Nada por aquí',
    'proyectos.columna.masViejos': 'terminados de antes',
    'proyectos.columna.dondeQuedaron': 'Están en Finanzas → Proyectos',
    'proyectos.cargando': 'Abriendo el proyecto…',
    'proyectos.titulo': 'Proyectos',
    'proyectos.vista.tablero': 'Tablero',
    'proyectos.vista.agenda': 'Agenda',
    'proyectos.agenda.porColocar': 'Trabajos por colocar',
    'proyectos.agenda.ayudaArrastre': '— arrastra un trabajo al día de alguien, o arrástralo de vuelta aquí para quitarlo',
    'proyectos.agenda.todoColocado': 'Todo está agendado.',
    'proyectos.agenda.hoy': 'Hoy',
    'proyectos.agenda.imprimir': 'Imprimir',
    'proyectos.agenda.dia': 'Día',
    'proyectos.agenda.semana': 'Semana',
    'proyectos.agenda.agendados': 'agendados',
    'proyectos.agenda.cuadrilla': 'Cuadrilla',
    'proyectos.agenda.sinAsignar': 'Sin asignar',
    'proyectos.agenda.sinEquipo': 'Aún no hay nadie en el equipo — agrega gente en Equipo para armar la agenda.',
    'proyectos.agenda.agregarAqui': 'Poner un trabajo aquí',
    'proyectos.agenda.agregarAria': 'Poner un trabajo a {persona} el {dia}',
    'proyectos.agenda.elegirTitulo': '{persona} — {dia}',
    'proyectos.agenda.sinTrabajos': 'No hay trabajos que agendar. Los terminados y cancelados no salen aquí.',
    'proyectos.agenda.errorAgendar': 'No se pudo agendar ese trabajo.',
    'proyectos.agenda.errorQuitar': 'No se pudo quitar ese trabajo de la agenda.',
    'proyectos.agenda.quitado': '{trabajo} quitado de ese día.',
    'proyectos.agenda.quitarBoton': 'Quitar de este día',
    'proyectos.agenda.quitarAria': 'Quitar {trabajo} de este día',
    'proyectos.agenda.anterior': 'Anterior',
    'proyectos.agenda.siguiente': 'Siguiente',
    'proyectos.descripcion': 'Los trabajos activos y cómo va la producción.',
    'proyectos.buscar.placeholder': 'Busca un proyecto…',
    'proyectos.nuevo': 'Proyecto nuevo',
    'proyectos.stat.dineroActivo': 'Dinero activo',
    'proyectos.stat.trabajosActivos': 'Trabajos activos',
    'proyectos.columna.aceptado': 'Aceptado',
    'proyectos.columna.programado': 'Programado',
    'proyectos.columna.enProceso': 'En proceso',
    'proyectos.columna.terminado': 'Terminado',
    'proyectos.columna.cancelado': 'Cancelado',
    'propuesta.aceptar.titulo': 'Propuesta aceptada',
    'propuesta.aceptar.mensaje': 'La propuesta quedó marcada como aceptada.',
    'propuesta.aceptar.error': 'No se pudo aceptar / No se pudo actualizar el estado de la propuesta. Inténtalo otra vez.',
    'propuesta.rechazar.titulo': 'Rechazar propuesta',
    'propuesta.rechazar.motivo': 'Motivo del rechazo (opcional):',
    'propuesta.rechazar.placeholder': 'Escribe el motivo...',
    'propuesta.rechazada.titulo': 'Propuesta rechazada',
    'propuesta.rechazada.mensaje': 'La propuesta quedó marcada como rechazada.',
    'propuesta.rechazar.error': 'No se pudo rechazar / No se pudo actualizar el estado de la propuesta. Inténtalo otra vez.',
    'propuesta.top.titulo_preview': 'Así la ve el cliente',
    'propuesta.top.titulo_editor': 'Editor de propuesta',
    'propuesta.top.sub_movil': 'Toca ← para volver a las herramientas',
    'propuesta.top.sub_editor': 'Armando la de ',
    'propuesta.publica.error.noencontrada': 'No encontramos la propuesta. Puede que la hayan borrado o que el enlace esté mal.',
    'propuesta.publica.error.nocargo': 'No se pudo cargar la propuesta. Inténtalo más tarde.',
    'propuesta.publica.error.abrircontrato.titulo': 'No se pudo abrir el contrato',
    'propuesta.publica.error.abrircontrato.mensaje': 'Pídele una copia al contratista.',
    'propuesta.publica.error.descargarfirmado': 'No se pudo abrir el contrato firmado. Habla con el contratista.',
    'propuesta.publica.tarea.recorrido': 'Recorrido de la obra y revisión de seguridad',
    'propuesta.publica.tarea.material': 'Revisar el material',
    'propuesta.publica.tarea.equipo': 'Preparar el equipo',
    'propuesta.publica.proyecto.nombre': 'Proyecto para ',
    'propuesta.publica.proyecto.desc': 'Proyecto creado automáticamente',
    'propuesta.publica.error.aceptar.mensaje': 'Hubo un error al aceptar la propuesta. Comunícate directo con el contratista.',
    'propuesta.publica.error.aceptar.titulo': 'Error al aceptar',
    'propuesta.publica.rechazar.titulo': 'Rechazar la propuesta',
    'propuesta.publica.rechazar.mensaje': 'Cuéntanos por qué la rechazas (opcional):',
    'propuesta.publica.rechazar.placeholder': 'Motivo del rechazo',
    'propuesta.publica.rechazada.titulo': 'Propuesta rechazada',
    'propuesta.publica.rechazada.mensaje': 'Propuesta rechazada.',
    'propuesta.publica.error.rechazar.mensaje': 'Hubo un error al rechazar la propuesta. Inténtalo otra vez.',
    'propuesta.publica.error.sincronizacion': 'Error de sincronización',
    'propuesta.publica.error.nodisponible': 'Propuesta no disponible',
    'propuesta.publica.default.intro': 'Revisa el estimado aquí abajo.',
    'propuesta.publica.imprimir.volver': '← Volver a la propuesta',
    'propuesta.publica.imprimir.guardarpdf': 'Imprimir / Guardar en PDF',
    'propuesta.publica.banner.rechazada': 'Esta propuesta fue rechazada y ya no está activa.',
    'propuesta.publica.banner.firmada': 'Esta propuesta ya está firmada y cerrada — no se le pueden hacer más cambios.',
    'propuesta.publica.banner.vistaprevia': 'Modo vista previa — así la ve tu cliente. Los clientes firman desde el enlace que compartes; tú no puedes firmar tu propia propuesta.',
    'propuesta.publica.contrato.titulo': 'El contrato que vas a firmar',
    'propuesta.publica.contrato.yafirmadopor': ' — ya lo firmó ',
    'propuesta.publica.contrato.leelocompleto': 'Léelo completo antes de aceptar.',
    'propuesta.publica.contrato.abriendo': 'Abriendo…',
    'propuesta.publica.contrato.leer': 'Leer el contrato',
    'propuesta.publica.exito.contratofirmado': 'Contrato firmado',
    'propuesta.publica.exito.propuestaaceptada': 'Propuesta aceptada',
    'propuesta.publica.exito.sinpagoenlinea': 'Gracias por firmar. Te contactamos pronto para el pago.',
    'propuesta.publica.exito.sigueelpago': 'Gracias por firmar. Lo que sigue es el pago.',
    'propuesta.publica.exito.graciasaceptar': 'Gracias por aceptar esta propuesta.',
    'propuesta.publica.pago.titulo': 'Listos para recibir tu pago',
    'propuesta.publica.pago.zelle.titulo': 'Transferencia Zelle',
    'propuesta.publica.pago.zelle.correo': 'Correo',
    'propuesta.publica.pago.zelle.telefono': 'Teléfono',
    'propuesta.publica.pago.zelle.nota': 'En cuanto llegue la transferencia, agendamos tu trabajo.',
    'propuesta.publica.pago.banco.titulo': 'Transferencia bancaria (ACH)',
    'propuesta.publica.pago.banco.titular': 'Titular',
    'propuesta.publica.pago.banco.banco': 'Banco',
    'propuesta.publica.pago.banco.tipo': 'Tipo de cuenta',
    'propuesta.publica.pago.banco.tipo.corriente': 'Corriente',
    'propuesta.publica.pago.banco.tipo.ahorro': 'Ahorros',
    'propuesta.publica.pago.banco.ruta': 'Número de ruta',
    'propuesta.publica.pago.banco.cuenta': 'Número de cuenta',
    'propuesta.publica.pago.banco.nota': 'Manda el pago desde tu banco con estos datos. Las transferencias ACH tardan de 1 a 3 días hábiles.',
    'propuesta.publica.pago.tarjeta': 'Pagar con tarjeta ',
    'propuesta.publica.imprimir.contratocompleto': 'Imprimir el contrato completo',
    'propuesta.publica.copia.preparando': 'Preparando tu copia…',
    'propuesta.publica.error.generarpdf': 'No se pudo generar el PDF del contrato firmado. Habla con el contratista.',
    'propuesta.publica.exito.verpropuesta': 'Ver la propuesta',
    'propuesta.publica.exito.imprimirrecibo': 'Imprimir el recibo',
    'servicios.rutas.sin_paradas_con_direccion': 'No hay paradas con dirección para {crewLabel}.',
    'servicios.rutas.limite_paradas': 'Te muestro las primeras 23 paradas en Google Maps (es el máximo).',
    'servicios.rutas.ruta_de_hoy': 'la ruta de hoy',
    'servicios.rutas.pane_paradas': 'Paradas',
    'servicios.rutas.pane_mapa': 'Mapa',
    'servicios.tab_rutas': 'Rutas',
    'servicios.tab_contratos': 'Contratos',
    'servicios.tab_armar_ruta': 'Armar ruta',
    'servicios.tab_cuadrillas': 'Cuadrillas',
    'servicios.tab_historial': 'Historial',
    'servicios.tab_analisis': 'Análisis',
    'servicios.tab_calendario': 'Calendario',
    'servicios.tab_equipo': 'Maquinaria',
    'servicios.tab_cobros': 'Cobros',
    'cobros.mes.anterior': 'Mes anterior',
    'cobros.mes.siguiente': 'Mes siguiente',
    'cobros.generar.nada': 'Nada que generar',
    'cobros.generar.boton': { uno: 'Generar {count} factura', otros: 'Generar {count} facturas'},
    'cobros.generar.ok': { uno: '{count} factura generada para {periodo}.', otros: '{count} facturas generadas para {periodo}.'},
    'cobros.generar.yaEstaba': '{periodo} ya estaba generado.',
    'cobros.generar.error': 'No se pudieron generar las facturas.',
    'cobros.actualizar.error': 'No se pudo actualizar la factura.',
    'cobros.total.facturado': 'Facturado',
    'cobros.total.cobrado': 'Cobrado',
    'cobros.total.pendiente': 'Pendiente',
    'cobros.total.vencidas': 'Vencidas',
    'cobros.vacio.titulo': 'Sin facturas de {periodo}',
    'cobros.vacio.hayQueGenerar': { uno: '{count} contrato se puede cobrar este mes — pulsa Generar arriba.', otros: '{count} contratos se pueden cobrar este mes — pulsa Generar arriba.'},
    'cobros.vacio.sinContratos': 'Ningún contrato activo cubre este mes.',
    'cobros.factura.vence': 'Vence {fecha}',
    'cobros.factura.pagada': 'pagada {fecha}',
    'cobros.factura.contratoBorrado': 'Contrato borrado',
    'cobros.estado.Draft': 'Borrador',
    'cobros.estado.Sent': 'Enviada',
    'cobros.estado.Paid': 'Pagada',
    'cobros.estado.Overdue': 'Vencida',
    'cobros.borrar.titulo': '¿Borrar esta factura?',
    'cobros.borrar.texto': '{cliente} — {periodo}, {importe}. Puedes volver a generar el mes después.',
    'cobros.borrar.confirmar': 'Borrar factura',
    'cobros.borrar.ok': 'Factura borrada.',
    'cobros.borrar.error': 'No se pudo borrar la factura.',
    'cobros.borrar.aria': 'Borrar factura',
    'cobros.descargar.aria': 'Descargar factura en PDF',
    'cobros.descripcion.editar': 'Cambia lo que dice la factura',
    'cobros.descripcion.vacia': 'Sin descripción del trabajo',
    'cobros.descripcion.placeholder': 'Qué se hizo este mes…',
    'cobros.descripcion.aria': 'Descripción del trabajo en la factura',
    'facturaDoc.titulo': 'Factura',
    'facturaDoc.facturarA': 'Facturar a',
    'facturaDoc.periodo': 'Periodo',
    'facturaDoc.emitida': 'Emitida',
    'facturaDoc.vence': 'Vence',
    'facturaDoc.pagada': 'Pagada',
    'facturaDoc.tabla.descripcion': 'Descripción',
    'facturaDoc.tabla.importe': 'Importe',
    'facturaDoc.sinDescripcion': 'Servicio de ruta',
    'facturaDoc.totalDebido': 'Total a pagar',
    'facturaDoc.totalPagado': 'Total pagado',
    'facturaDoc.comoPagar': 'Cómo pagar',
    'facturaDoc.pago.zelle': 'Zelle',
    'facturaDoc.pago.banco': 'Transferencia bancaria',
    'facturaDoc.pago.enlace': 'Pagar en línea',
    'facturaDoc.pie': 'Gracias por su preferencia — {negocio}',

    'servicios.rutas.sin_contrato': 'No encontré el contrato de esta parada.',
    'servicios.rutas.sin_ruta': 'No encontré la ruta de esta parada.',
    'servicios.rutas.primera_parada': '"{cliente}" ahora es la primera parada.',
    'servicios.rutas.error_primera_parada': 'No se pudo poner como primera parada: {error}',
    'servicios.rutas.visita_marcada': 'Visita marcada "{newStatus}"',
    'servicios.rutas.error_actualizar_visita': 'No se pudo actualizar la visita: {error}',
    'servicios.rutas.notas_guardadas': 'Notas de la visita guardadas',
    'servicios.rutas.error_guardar_notas': 'No se pudieron guardar las notas: {error}',
    'servicios.rutas.contratos_otro_dia_pero': ' — pero ',
    'servicios.rutas.contratos_otro_dia_revisa': '. Revisa esos contratos.',
    'servicios.rutas.dia_anterior': 'Día anterior',
    'servicios.rutas.dia_siguiente': 'Día siguiente',
    'servicios.rutas.ver_cuadrillas_tooltip': 'Mira las cuadrillas que trabajan hoy',
    'servicios.rutas.tiempo_estimado': 'Tiempo est.',
    'servicios.rutas.geocodificando': 'Buscando direcciones',
    'servicios.rutas.sin_paradas_tooltip': 'No hay paradas para armar la ruta',
    'servicios.rutas.abrir_maps_tooltip': 'Abre todas las paradas como una ruta en Google Maps',
    'servicios.contratos.buscar': 'Busca por cliente o dirección…',
    'servicios.contratos.actualizado': '¡Contrato actualizado!',
    'servicios.contratos.creado': '¡Contrato creado!',
    'servicios.contratos.error_guardar': 'No se pudo guardar el contrato',
    'servicios.contratos.cancelar_titulo': '¿Cancelar este contrato?',
    'servicios.contratos.cancelar_mensaje': '¿Paramos el servicio recurrente de {cliente} en {direccion}? Deja de contar para el ingreso mensual y deja de generar visitas. Las visitas pasadas se quedan, y lo puedes reactivar después.',
    'servicios.contratos.cancelar_confirmar': 'Cancelar contrato',
    'servicios.contratos.cancelar_negar': 'Dejarlo activo',
    'servicios.contratos.reactivado': 'Contrato reactivado',
    'servicios.contratos.pausado': 'Contrato pausado — ya no se generan visitas',
    'servicios.contratos.cancelado': 'Contrato cancelado',
    'servicios.contratos.error_actualizar': 'No se pudo actualizar el contrato',
    'servicios.contratos.borrado': 'Contrato borrado',
    'servicios.contratos.error_borrar': 'No se pudo borrar el contrato',
    'servicios.contratos.vacio_filtro_titulo': 'Ningún contrato coincide con tus filtros',
    'servicios.contratos.vacio_titulo': 'Todavía no hay contratos de servicio',
    'servicios.contratos.vacio_filtro_texto': 'Prueba otra búsqueda o quita el filtro de estatus.',
    'servicios.contratos.vacio_texto': 'Crea tu primer contrato para empezar a manejar los servicios recurrentes.',
    'servicios.contratos.editar': 'Editar contrato',
    'servicios.contratos.borrar_tooltip': 'Borrar el contrato para siempre',
    'servicios.contratos.tarifa_mensual': '${tarifa}/mes',
    'servicios.contratos.tarifa_visita': '${tarifa}/visita',
    'servicios.contratos.asignar_cuadrilla': 'Asignar cuadrilla…',
    'servicios.titulo': 'Rutas de servicio',
    'servicios.descripcion': 'Maneja los contratos de servicio recurrente, las rutas y el historial de visitas.',
    'servicios.rutas.sin_cuadrillas': 'Todavía no hay cuadrillas',
    'servicios.rutas.sin_miembros': 'Sin gente asignada',
    'servicios.contratos.borrar_titulo': '¿Borrar este contrato?',
    'servicios.contratos.borrar_texto': 'Vas a borrar para siempre el contrato de servicio de',
    'servicios.contratos.borrar_aviso': 'Esto no se puede deshacer.',
    'servicios.contratos.borrando': 'Borrando…',
    'servicios.contratos.borrar_confirmar': 'Borrar contrato',
    'ajustes.nav.titulo': 'Ajustes',
    'ajustes.nav.mi_cuenta': 'Mi cuenta',
    'ajustes.nav.overhead': 'Gastos fijos y precios',
    'ajustes.nav.equipo': 'Gente',
    'propFirmadas.error.plantillaBorrada': 'La plantilla del contrato de esta propuesta ya no existe.',
    'propFirmadas.ok.contratoGuardado': 'Copia del contrato firmado guardada.',
    'propFirmadas.titulo': 'Propuestas firmadas',
    'propFirmadas.descripcion': 'Propuestas aceptadas y firmadas — míralas o baja una copia para tu archivo.',
    'propFirmadas.buscar': 'Busca cliente o título…',
    'propFirmadas.vacio.titulo': 'Todavía no hay propuestas firmadas',
    'propFirmadas.limpiarBusqueda': 'Limpiar búsqueda',
    'propFirmadas.col.cliente': 'Cliente',
    'propFirmadas.col.propuesta': 'Propuesta',
    'propFirmadas.col.monto': 'Monto',
    'propFirmadas.col.firmada': 'Firmada',
    'propFirmadas.col.contrato': 'Contrato',
    'propFirmadas.col.estado': 'Estado',
    'propFirmadas.col.acciones': 'Acciones',
    'propFirmadas.clienteSinNombre': 'Cliente',
    'propFirmadas.propuestaSinTitulo': 'Propuesta',
    'propFirmadas.estado.guardado': 'Con copia',
    'propFirmadas.tip.sinGuardar': 'Firmado, pero todavía no hay copia permanente — dale a Guardar copia',
    'propFirmadas.estado.sinGuardar': 'Falta copia',
    'propFirmadas.sinContrato': 'Sin contrato',
    'propFirmadas.verPropuesta': 'Ver propuesta',
    'propFirmadas.pdfPropuestaLink': 'PDF de la propuesta',
    'propFirmadas.tip.guardarCopia': 'Guarda una copia permanente para que aguante los cambios en la plantilla del contrato',
    'propFirmadas.guardarCopia': 'Guardar copia',
    'propFirmadas.abrirContrato': 'Abrir contrato',
    'propFirmadas.pdfPropuestaBoton': 'PDF de la propuesta',
    'propFirmadas.tip.pdfPropuesta': 'Copia de la propuesta lista para imprimir',
    'superAdmin.error.agregarEmpresa': 'Error al agregar la empresa: ',
    'superAdmin.error.invitar': 'Error al invitar al usuario',
    'superAdmin.error.invitarToast': 'Error al invitar al usuario: ',
    'superAdmin.error.borrarUsuario': 'Error al borrar al usuario',
    'superAdmin.error.borrarUsuarioToast': 'Error al borrar al usuario: ',
    'superAdmin.error.borrarEmpresa': 'Error al borrar la empresa',
    'superAdmin.error.borrarEmpresaToast': 'Error al borrar la empresa: ',
    'superAdmin.estado.activo': 'Activo',
    'superAdmin.estado.suspendido': 'Suspendido',
    'superAdmin.rol.tecnicoEtiqueta': 'Técnico de servicio',
    'superAdmin.tab.resumen': 'Resumen',
    'superAdmin.tab.empresas': 'Empresas',
    'superAdmin.tab.usuarios': 'Usuarios',
    'superAdmin.tab.admins': 'Admins',
    'superAdmin.tab.reportes': 'Reportes',
    'superAdmin.tab.videos': 'Videos educativos',
    'superAdmin.tab.uso_ia': 'Uso de IA',
    'superAdmin.tab.clases': 'Clases',

    // ── Clases (vista del equipo + pestaña de admin) ──
    'nav.clases': 'Clases',
    'clases.titulo': 'Clases',
    'clases.descripcion': 'Las clases grabadas y la junta de preguntas de cada viernes. Míralas cuando puedas.',
    'clases.buscar': 'Buscar una clase…',
    'clases.reproducir': 'Reproducir',
    'clases.borrador': 'Borrador',
    'clases.sin_grabacion': 'Todavía sin grabación',
    'clases.vacio_titulo': 'Todavía no hay clases',
    'clases.vacio_desc': 'Cuando se suba la primera grabación, aparece aquí.',
    'clases.sin_resultados': 'Ninguna clase coincide',
    'clases.sin_resultados_desc': 'Prueba con otra palabra o borra la búsqueda.',
    'clases.error_titulo': 'No se pudieron cargar las clases',
    'clases.error_desc': 'La lista no llegó, así que esta pantalla no sabe cuáles hay. No es que no haya ninguna. Detalle: {detalle}',
    'clases.error_reproducir': 'No se pudo abrir este video. Vuelve a intentarlo; si sigue igual, avísale al equipo.',
    'clases.reintentar': 'Reintentar',

    'clases.admin_titulo': 'Clases',
    'clases.admin_desc': 'Sube aquí la grabación de cada clase. Nadie la ve hasta que le des Publicar.',
    'clases.admin_vacio': 'Todavía no has creado ninguna clase',
    'clases.admin_vacio_desc': 'Crea la clase primero, y después súbele el video.',
    'clases.progreso': '{publicadas} de {total} publicadas',
    'clases.actualizar': 'Actualizar',
    'clases.nueva': 'Nueva clase',
    'clases.crear': 'Crear clase',
    'clases.crear_aviso': 'Se crea como borrador. El video se sube después, desde la tarjeta de la clase.',
    'clases.titulo_placeholder': 'Junta del viernes — preguntas y respuestas',
    'clases.desc_placeholder': 'De qué se habló, en dos renglones.',
    'clases.campo_titulo': 'Título',
    'clases.campo_fecha': 'Fecha de la clase',
    'clases.campo_desc': 'Descripción',
    'clases.ayuda': 'Formatos: MP4 o WebM, hasta {limite}. La subida se reanuda sola si se cae el internet, pero no cierres esta pestaña mientras corre.',
    'clases.subir': 'Subir video',
    'clases.reemplazar': 'Reemplazar video',
    'clases.miniatura': 'Miniatura',
    'clases.cambiar_miniatura': 'Cambiar miniatura',
    'clases.publicar': 'Publicar',
    'clases.ocultar': 'Ocultar',
    'clases.borrar': 'Borrar',
    'clases.estado_publicada': 'Publicada',
    'clases.estado_borrador': 'Borrador',
    'clases.meta': '{peso} · {duracion}',
    'clases.subiendo': 'Subiendo {pct}% — {enviado} de {total}',
    'clases.subiendo_aria': 'Progreso de la subida',
    'clases.subiendo_aviso': 'Si se corta el internet, la subida sigue donde se quedó. Cerrar esta pestaña sí la cancela.',
    'clases.cancelar': 'Cancelar',
    'clases.cancelar_titulo': '¿Cancelar la subida?',
    'clases.cancelar_mensaje': 'Se pierde lo que se lleva subido y hay que empezar de nuevo.',
    'clases.cancelar_confirmar': 'Sí, cancelar',
    'clases.cancelar_seguir': 'Seguir subiendo',
    'clases.borrar_titulo': '¿Borrar esta clase?',
    'clases.borrar_mensaje': 'Se borra «{titulo}» y su grabación. No se puede recuperar.',
    'clases.borrar_confirmar': 'Borrar clase',
    'clases.toast_creada': 'Clase creada',
    'clases.toast_subida': 'Video subido',
    'clases.toast_miniatura': 'Miniatura actualizada',
    'clases.toast_publicada': 'Clase publicada',
    'clases.toast_oculta': 'Clase oculta',
    'clases.toast_borrada': 'Clase borrada',
    'clases.err.archivo_vacio': 'Ese archivo está vacío.',
    'clases.err.muy_grande': 'El archivo pesa {tamano} y el máximo es {limite}.',
    'clases.err.formato': 'El formato {tipo} no sirve aquí. Usa MP4 o WebM.',
    'clases.err.titulo_vacio': 'La clase necesita un título.',
    'clases.err.sin_permiso': 'La base de datos no aceptó el cambio. Revisa que tu cuenta siga siendo admin de la plataforma.',
    'clases.err.sin_sesion': 'Tu sesión se venció. Vuelve a entrar y reintenta.',
    'clases.err.sin_video': 'No se puede publicar una clase sin grabación.',
    'clases.comprimir.titulo': 'Comprimir aquí mismo',
    'clases.comprimir.ya_esta_bien': 'Este archivo está bien como está. Si quieres, puedes comprimirlo de todas formas para que pese menos.',
    'clases.convertidor.abrir': 'Convertir un video',
    'clases.convertidor.titulo': 'Convertidor de video',
    'clases.convertidor.intro': 'Achica un video usando tu propia computadora. El archivo no se sube a ningún lado: se convierte aquí y te queda guardado.',
    'clases.convertidor.elegir': 'Elegir un video',
    'clases.convertidor.salida': 'El archivo convertido se descarga a tu computadora. Después lo subes a la clase que quieras.',
    'clases.convertidor.descargar': 'Descargar el video',
    'clases.convertidor.otro': 'Convertir otro',
    'clases.convertidor.guarda_ya': 'Descárgalo antes de cerrar esta ventana: si la cierras sin guardar, el archivo se borra.',
    'clases.convertidor.descartar': 'Descartar',
    'clases.convertidor.pendiente_titulo': 'Tienes un video convertido sin guardar',
    'clases.convertidor.pendiente_desc': 'Se convirtió antes y quedó guardado aquí porque se recargó la página. Descárgalo o descártalo.',
    'clases.comprimir.intro': 'Tu computadora puede arreglar este video sin instalar nada. Elige qué hacer:',
    'clases.comprimir.opcion_comprimir': 'Comprimir el video',
    'clases.comprimir.opcion_comprimir_desc': 'Lo achica y le arregla el arranque. Tarda un rato y usa la tarjeta de video.',
    'clases.comprimir.opcion_rapido': 'Solo arreglar el arranque',
    'clases.comprimir.opcion_rapido_desc': 'Segundos. No cambia nada de la imagen, solo hace que empiece de inmediato.',
    'clases.comprimir.opcion_subir': 'Subirlo tal cual',
    'clases.comprimir.archivo': 'Archivo: {peso} · {bitrate}',
    'clases.comprimir.no_soportado': 'Tu navegador no puede comprimir aquí. Usa Chrome, o comprime el archivo antes de subirlo.',
    'clases.comprimir.listo': 'Listo: de {antes} a {despues}',
    'clases.comprimir.error': 'No se pudo comprimir el video.',
    'clases.comprimir.cancelar': 'Cancelar',
    'clases.comprimir.etapa.leyendo': 'Leyendo el archivo…',
    'clases.comprimir.etapa.midiendo': 'Midiendo tu computadora…',
    'clases.comprimir.etapa.trabajando': 'Comprimiendo…',
    'clases.comprimir.etapa.cerrando': 'Cerrando el archivo…',
    'clases.comprimir.aviso_tiempo': 'No cierres esta pestaña. Puedes seguir usando el resto del panel.',
    'clases.comprimir.err.no_soportado': 'Tu navegador no puede comprimir aquí. Usa Chrome.',
    'clases.comprimir.err.sin_pista': 'Ese archivo no trae pista de video.',
    'clases.comprimir.err.sin_audio': 'Se habría perdido el audio, así que se canceló. Sube el archivo tal cual o conviértelo con ffmpeg.',
    'clases.comprimir.err.sin_espacio': 'No hay espacio en el disco para el archivo comprimido.',
    'clases.comprimir.err.sin_ahorro': 'El resultado no salió más chico, así que no se subió. El video ya estaba bien comprimido.',
    'clases.comprimir.err.indice': 'El archivo comprimido salió mal armado y no se subió.',
    'clases.comprimir.err.decodificar': 'Chrome no pudo leer este video para volver a comprimirlo. Usa «Solo arreglar el arranque»: ese no lo vuelve a codificar y sí funciona.',
    'clases.comprimir.err.cancelado': 'Compresión cancelada.',
    'clases.aviso.titulo': 'Este video va a dar problemas',
    'clases.aviso.faststart': 'El índice del video está al FINAL del archivo, así que el navegador lo baja completo antes del primer cuadro: se ve roto, no lento. Se arregla en segundos, sin perder calidad.',
    'clases.aviso.bitrate': 'Está a {actual}. Una grabación de pantalla se ve igual a {sugerido}, o sea unas cuatro veces más chico: se sube más rápido, se ve más rápido y gasta mucho menos.',
    'clases.aviso.comando': 'Corre esto antes de subirlo (necesitas ffmpeg):',
    'clases.aviso.subir_igual': 'Subirlo así',
    'clases.aviso.mejor_no': 'Mejor lo arreglo',
    'clases.err.limite_servidor': 'Supabase rechazó el archivo por tamaño. Sube el tope en el panel de Supabase: Project Settings → Storage → "Upload file size limit". El límite del bucket ya está en 5 GB; el que manda es ese otro.',
    'clases.err.crear': 'No se pudo crear la clase.',
    'clases.err.subir': 'No se pudo subir el archivo.',
    'clases.err.publicar': 'No se pudo cambiar el estado.',
    'clases.err.borrar': 'No se pudo borrar.',
    'clases.err.guardar': 'No se pudo guardar el cambio.',
    'superAdmin.subtitulo': 'Centro de control de la plataforma',
    'superAdmin.refrescarAria': 'Actualizar datos',
    'superAdmin.refrescarTip': 'Actualizar datos',
    'superAdmin.salir': 'Cerrar sesión',
    'superAdmin.empresaNueva': 'Empresa nueva',
    'superAdmin.crearEmpresa': 'Crear empresa',
    'superAdmin.nombreNegocio': 'Nombre del negocio',
    'superAdmin.nombreNegocioPlaceholder': 'p. ej., Raza Pro HVAC',
    'superAdmin.idAuto': 'Con este nombre se crea automáticamente un ID único de espacio de trabajo.',
    'superAdmin.correoDueno': 'Correo del dueño',
    'superAdmin.nombreCorto': 'Nombre corto',
    'superAdmin.nombreCortoPlaceholder': 'p. ej., RPH',
    'superAdmin.colorMarca': 'Color de marca',
    'superAdmin.colorMarcaAria': 'Color de marca',
    'superAdmin.invitarTitulo': 'Invitar usuario',
    'superAdmin.invitarBoton': 'Invitar usuario',
    'superAdmin.correo': 'Correo electrónico',
    'superAdmin.nombreCompleto': 'Nombre completo',
    'superAdmin.nombrePlaceholder': 'Juan Pérez',
    'superAdmin.rol': 'Rol',
    'superAdmin.rol.admin': 'Admin (gerente del espacio de trabajo)',
    'superAdmin.rol.oficina': 'Personal de oficina',
    'superAdmin.rol.trabajador': 'Trabajador',
    'superAdmin.rol.tecnico': 'Técnico de servicio',
    'superAdmin.asignarEspacios': 'Asignar a espacios de trabajo',
    'superAdmin.borrarUsuarioTitulo': 'Borrar usuario para siempre',
    'superAdmin.borrarParaSiempre': 'Borrar para siempre',
    'superAdmin.borrarUsuarioAviso': 'Esto va a borrar para siempre:',
    'superAdmin.borrarUsuarioDetalle': 'El usuario se borra del sistema de autenticación y de todos los accesos a espacios de trabajo.',
    'superAdmin.borrarEmpresaTitulo': 'Borrar empresa',
    'superAdmin.borrarEmpresaBoton': 'Borrar empresa',
    'superAdmin.borrarEmpresaAviso': 'Esto va a destruir para siempre:',
    'superAdmin.borrarEmpresaUsuarios': 'usuario(s) pierden el acceso',
    'superAdmin.borrarEmpresaDatos': 'Se borran todos los clientes, estimados, proyectos, equipo, horas, citas, artículos de la lista de precios y ajustes',
    'superAdmin.borrarEmpresaDetalle': 'Todos los datos del espacio de trabajo se destruyen para siempre.',
    'superAdmin.derechos': 'Todos los derechos reservados',
    'plantillas.vista.toast_usar': 'se agrega desde la pestaña Services cuando estás haciendo un estimado — abre uno o empieza uno nuevo para meterla ahí. Aquí puedes ver sus materiales y manejar tus propias plantillas de partidas.',
    'plantillas.vista.titulo': 'Plantillas',
    'plantillas.vista.descripcion': 'Revisa las plantillas de servicio ya hechas y sus materiales, y crea o edita las tuyas. Son las mismas que salen en la pestaña Services cuando haces un estimado.',
    'ajustesNeg.perfil.email': 'Correo',
    'ajustesNeg.perfil.emailEjemplo': 'contacto@tunegocio.com',
    'ajustesNeg.perfil.telefono': 'Teléfono',
    'ajustesNeg.perfil.sitioWeb': 'Sitio web',
    'ajustesNeg.perfil.sitioEjemplo': 'www.tunegocio.com',
    'ajustesNeg.temporada.semanasAnio.ayuda': 'Semanas que sí trabajas (sin contar el invierno).',
    'ajustesNeg.campo.salarioAnual': 'Sueldo al año ($)',
    'ajustesNeg.campo.pagoPorHora': 'Pago por hora ($)',
    'ajustesNeg.empleados.burden.auto': '(lo calcula el Burden Builder de arriba)',
    'ajustesNeg.empleados.noCobrable': 'Su costo se va a gastos fijos',
    'ajustesNeg.empleados.disponiblePct.ayuda': '(ya quitando vacaciones y días de lluvia)',
    'ajustesNeg.empleados.cobrablePct.ayuda': '(de las horas disponibles)',
    'ajustesNeg.empleados.efectivo': 'Lo que de verdad cobras:',
    'ajustesNeg.empleados.horasTemporada': 'hrs de la temporada',
    'ajustesNeg.empleados.horasCobrables': 'hrs cobrables al año',
    'ajustesNeg.empleados.vacio.texto': 'Agrega a tu gente para que su mano de obra entre en tu tarifa — tú incluido si andas en las obras.',
    'ajustesNeg.stat.gastosAnuales': 'Gastos fijos al año',
    'ajustesNeg.stat.gastosAnuales.pista': 'Todo lo que pagas al año',
    'ajustesNeg.stat.gastoMensual': 'Gasto al mes',
    'ajustesNeg.stat.gastoMensual.pista': 'Promedio al mes',
    'ajustesNeg.gastos.nombre': 'Nombre del gasto',
    'ajustesNeg.gastos.frecuencia': 'Cada cuándo',
    'ajustesNeg.gastos.quitar': 'Quitar gasto',
    'ajustesNeg.gastos.vacio.titulo': 'Todavía no hay gastos fijos',
    'ajustesNeg.gastos.vacio.texto': 'Aquí va la renta, el seguro, el software y las trocas — eso es lo que tu tarifa tiene que pagar.',
    'ajustesNeg.gastos.vacio.accion': 'Agregar el primer gasto',
    'ajustesNeg.stat.costoMaquinaria': 'Costo de la maquinaria al año',
    'ajustesNeg.stat.costoMaquinaria.pista': 'Se suma a tus gastos fijos',
    'ajustesNeg.maquinaria.nombreEjemplo': 'Troca, excavadora, podadora...',
    'ajustesNeg.maquinaria.precioCompra': 'Lo que costó ($)',
    'ajustesNeg.maquinaria.vidaUtil': 'Años que te va a durar',
    'ajustesNeg.maquinaria.gasolina': 'Gasolina ($/año)',
    'ajustesNeg.maquinaria.mantenimiento': 'Mantenimiento ($/año)',
    'ajustesNeg.maquinaria.quitar': 'Quitar maquinaria',
    'ajustesNeg.maquinaria.desglose.gasolina': '/año + gasolina $',
    'ajustesNeg.maquinaria.desglose.manten': '+ manten. $',
    'ajustesNeg.maquinaria.vacio.titulo': 'Todavía no hay maquinaria',
    'ajustesNeg.maquinaria.vacio.texto': 'Si no tienes maquinaria propia, sáltate esto. Si sí, aquí van las trocas y las máquinas para que su costo entre en tu tarifa.',
    'ajustesNeg.maquinaria.vacio.accion': 'Agregar la primera máquina',
    'ajustesNeg.precios.titulo': 'Precios y ganancia',
    'ajustesNeg.precios.margenMeta.ayuda': 'Lo que quieres que te quede después de costos',
    'ajustesNeg.precios.margenTope': 'Máximo 80% — de ahí para arriba la fórmula ya no cuadra.',
    'ajustesNeg.precios.markupMateriales': 'Markup normal de materiales (%)',
    'ajustesNeg.precios.markupMateriales.ayuda': 'Se le pone automáticamente a cada material nuevo.',
    'ajustesNeg.precios.impuestoMO': '¿Cobrar impuesto a la mano de obra?',
    'ajustesNeg.precios.impuestoMO.ayuda1': 'Si lo prendes, el impuesto se calcula sobre todo el subtotal (mano de obra + materiales).',
    'ajustesNeg.precios.impuestoMO.ayuda2': 'Si lo apagas, solo se cobra sobre materiales y maquinaria.',
    'ajustesNeg.reglas.titulo': 'Mínimos y reglas del trabajo',
    'ajustesNeg.reglas.minimoVisita': 'Mínimo por service call ($)',
    'ajustesNeg.reglas.cargoViaje': 'Cargo por viaje ($)',
    'ajustesNeg.reglas.horasMinimas': 'Mínimo de horas de trabajo',
    'ajustesNeg.reglas.recargoEmergencia': 'Recargo por emergencia (%)',
    'ajustesNeg.reglas.recargoEmergencia.ayuda': 'Solo se le aplica a la mano de obra.',
    'ajustesNeg.resumen.titulo': 'Resumen de tus números',
    'ajustesNeg.resumen.sub': 'Tus tarifas por hora, sacadas de tu temporada, tu gente y tus gastos fijos.',
    'ajustesNeg.resumen.tarifaBase': 'Lo que pagas de mano de obra',
    'ajustesNeg.resumen.tarifaBase.pista': 'Pago promedio por hora',
    'ajustesNeg.resumen.cargaGastos': 'Lo que cargas de gastos fijos',
    'ajustesNeg.resumen.cargaGastos.pista': 'Por cada hora-hombre',
    'ajustesNeg.resumen.puntoEquilibrio': 'Lo que te cuesta la hora',
    'ajustesNeg.resumen.puntoEquilibrio.pista': 'Costo de operar por hora',
    'ajustesNeg.resumen.tarifaMeta': 'Lo que debes cobrar por hora',
    'ajustesNeg.resumen.tarifaMeta.pista1': 'Ya incluye',
    'ajustesNeg.resumen.tarifaMeta.pista2': '% de ganancia limpia',
    'ajustesNeg.rapido.puntoEquilibrio.pista': 'Sin ganancia, nomás para no perder',
    'ajustesNeg.rapido.tarifaMeta.pista': '% de ganancia que quieres dejar',
    'ajustesNeg.tab.rapido': 'De un vistazo',
    'ajustesNeg.tab.negocio': 'Mi negocio',
    'ajustesNeg.tab.costos': 'Costos',
    'ajustesNeg.tab.precios': 'Precios',
    'ajustesNeg.tab.diagnostico': 'Diagnóstico',
    'contrato.editor.pdfNoExiste': 'El PDF de este contrato ya no está guardado. Borra el contrato y súbelo otra vez.',
    'contrato.editor.errorAbrir': 'No se pudo abrir el PDF.',
    'contrato.editor.sinCampos': 'Se guardó sin campos. El cliente no va a tener dónde firmar — arrastra un campo de Firma a la página antes de mandar este contrato.',
    'contrato.editor.sinFirmaCliente': 'Ojo: este contrato no tiene campo de firma del cliente.',
    'contrato.editor.errorGuardar': 'No se pudieron guardar los campos. Ahí siguen — inténtalo otra vez.',
    'contrato.editor.sub': 'Pon los campos de firma, iniciales, fecha y texto. Se guardan donde los dejes, junto con el contrato.',
    'contrato.editor.fechaauto.titulo': 'Esa fecha es una vista previa.',
    'contrato.editor.fechaauto.cuerpo': 'No la llena nadie a mano. Cada propuesta que lleve este contrato sale con la fecha del día en que la mandas, y si el cliente firma, con la fecha de su firma.',
    'contrato.editor.cancelar': 'Cancelar',
    'contrato.editor.guardando': 'Guardando…',
    'contrato.editor.guardar': 'Guardar campos',
    'contrato.editor.errorPdfTitulo': 'No se pudo abrir este PDF',
    'contrato.editor.cargandoPdf': 'Cargando PDF…',
    'ajustes.contratos.campos.errorguardar': 'No se pudo guardar dónde van los campos. Intenta otra vez.',
    'ajustes.contratos.firma.exito': 'Firmado como contratista.',
    'ajustes.contratos.firma.errorguardar': 'Se firmó, pero no se guardó. Dale a Guardar cambios para no perderlo.',
    'ajustes.contratos.subir.solopdf': 'Solo se aceptan archivos PDF.',
    'ajustes.contratos.subir.muypesado': 'El archivo pasa de 10 MB. Comprímelo e intenta otra vez.',
    'ajustes.contratos.subir.exito': 'Contrato subido y guardado.',
    'ajustes.contratos.borrar.bloqueado.titulo': 'Todavía no puedes borrar este contrato',
    'ajustes.contratos.borrar.bloqueado.evidencia': 'Si lo borras ahora, se pierde esa prueba.',
    'ajustes.contratos.borrar.exito': 'Contrato quitado. Dale a Guardar cambios para confirmar.',
    'ajustes.contratos.titulo': 'Contratos',
    'ajustes.contratos.descripcion': 'Sube los PDF de contrato que quieras adjuntar a los estimados. Los que elijas aparecen en Propuesta → Contenido → Contrato adjunto.',
    'ajustes.contratos.subir.titulo': 'Sube un contrato',
    'ajustes.contratos.subir.limite': 'Solo PDF · máx. 10 MB',
    'ajustes.contratos.subir.subiendo': 'Subiendo…',
    'ajustes.contratos.subir.elegir': 'Elegir PDF',
    'ajustes.contratos.vacio.titulo': 'Todavía no hay contratos',
    'ajustes.contratos.vacio.descripcion': 'Sube tu primer PDF para adjuntarlo a un estimado y juntar firmas.',
    'ajustes.contratos.fila.sincampos': 'Nada que firmar todavía',
    'ajustes.contratos.fila.firmadoporti': 'Firmado por ti',
    'ajustes.contratos.fila.faltatufirma': 'Falta tu firma',
    'ajustes.contratos.fila.camposporfirmar': 'campos tuyos sin firmar',
    'ajustes.contratos.fila.completarfirma': 'Completar firma',
    'ajustes.contratos.fila.firmar.tooltip': 'Firma este contrato como contratista',
    'ajustes.contratos.fila.volverafirmar': 'Volver a firmar',
    'ajustes.contratos.fila.firmarcomocontratista': 'Firmar como contratista',
    'ajustes.contratos.fila.configurar.tooltip': 'Colocar los campos de firma',
    'ajustes.contratos.fila.abrirpdf': 'Abrir PDF',
    'ajustes.contratos.fila.borrar': 'Borrar contrato',
    'ajustes.contratos.pregunta.despues': 'Decidir después',
    'ajustes.contratos.pregunta.titulo': '¿Este contrato ya está firmado?',
    'ajustes.contratos.pregunta.cuerpo1': '¿Ya firmaste',
    'ajustes.contratos.limpio.titulo': 'Sube también el contrato en blanco',
    'ajustes.contratos.limpio.porque': 'El que subiste lleva tu firma dentro, y el ejemplar que se imprime saldría con ella. Con una copia limpia el cliente lo recibe en blanco y ambos firman a mano.',
    'ajustes.contratos.limpio.elegir': 'Elegir el PDF en blanco',
    'ajustes.contratos.limpio.omitir': 'No la tengo',
    'ajustes.contratos.limpio.exito': 'Copia en blanco guardada.',
    'ajustes.contratos.fila.limpio.falta': 'Falta el limpio',
    'ajustes.contratos.fila.limpio.falta.tooltip': 'Sube el contrato sin firmar: es el que se imprime con la propuesta para firmar a mano.',
    'ajustes.contratos.fila.limpio.puesto': 'Limpio',
    'ajustes.contratos.fila.limpio.reemplazar.tooltip': 'Reemplazar la copia sin firmar.',
    'ajustes.contratos.pregunta.yafirmado.titulo': 'Ya está firmado',
    'ajustes.contratos.pregunta.yafirmado.ayuda': 'Solo vas a poner los campos que llena tu cliente.',
    'ajustes.contratos.pregunta.firmarahora.titulo': 'No — lo firmo ahora',
    'ajustes.legal.terminos_descripcion': 'Estos términos se agregan solos a toda propuesta nueva. Los puedes cambiar después en cada propuesta.',
    'ajustes.legal.terminos_placeholder': 'Escribe aquí las reglas que van en todas tus propuestas...',
    'ajustes.legal.terminos_markdown': 'Acepta formato Markdown.',
    'ajustes.legal.garantias_descripcion': 'Arma garantías listas para escogerlas mientras haces una propuesta.',
    'ajustes.legal.garantia_agregar': 'Agregar garantía',
    'ajustes.legal.garantia_quitar': 'Quitar garantía',
    'ajustes.legal.garantia_nombre_placeholder': 'ej. 1 año en mano de obra',
    'ajustes.legal.garantia_falta_nombre': 'Primero ponle nombre a la garantía',
    'ajustes.legal.garantia_generar_tooltip': 'Generar el texto de la garantía con IA',
    'ajustes.legal.garantia_texto_placeholder': 'Detalles de la garantía...',
    'ajustes.legal.garantias_vacio_titulo': 'Sin garantías propias',
    'ajustes.legal.garantias_vacio_descripcion': 'Arma garantías listas para meterlas en las propuestas sin volver a escribirlas.',
    'ajustes.legal.garantia_agregar_vacio': 'Agregar garantía',

    // ── Estimados ▸ Propuesta ▸ Pagos ───────────────────────────
    // Las formas de cobro vivían sueltas al final de «Legal y comunicación».
    // Zelle y la cuenta de banco son el mismo trabajo —decir cómo te pagan— y
    // no tienen nada que ver con los términos legales ni con las garantías.
    'ajustes.pagos.encabezado': 'Cómo te van a pagar',
    'ajustes.pagos.descripcion': 'Se configura una vez para todo el negocio. En cada propuesta eliges cuál de estas formas se le enseña al cliente, desde su pestaña Compartir.',
    'ajustes.pagos.zelle.titulo': 'Zelle',
    'ajustes.pagos.zelle.descripcion': 'Lo más rápido para cobrar el anticipo. Al cliente le salen estos datos cuando prendes Zelle en una propuesta.',
    'ajustes.pagos.zelle.mostrar': 'Aceptar pagos por Zelle',
    'ajustes.pagos.zelle.correo': 'Correo de Zelle',
    'ajustes.pagos.zelle.correo_placeholder': 'pagos@negocio.com',
    'ajustes.pagos.zelle.telefono': 'Teléfono de Zelle (opcional)',
    'ajustes.pagos.zelle.falta': 'Pon el correo o el teléfono. Sin uno de los dos, Zelle no se puede prender en una propuesta.',
    'ajustes.pagos.banco.titulo': 'Transferencia bancaria (ACH)',
    'ajustes.pagos.banco.descripcion': 'Deja que el cliente mande el anticipo directo a tu cuenta. Solo se le enseña cuando eliges «Transferencia bancaria» en una propuesta.',
    'ajustes.pagos.banco.mostrar': 'Aceptar transferencias bancarias',
    'ajustes.pagos.banco.titular': 'Nombre del titular de la cuenta',
    'ajustes.pagos.banco.titular_placeholder': 'Alex Landscaping LLC',
    'ajustes.pagos.banco.banco': 'Banco',
    'ajustes.pagos.banco.banco_placeholder': 'Chase',
    'ajustes.pagos.banco.tipo': 'Tipo de cuenta',
    'ajustes.pagos.banco.tipo.corriente': 'Corriente (checking)',
    'ajustes.pagos.banco.tipo.ahorro': 'Ahorros (savings)',
    'ajustes.pagos.banco.ruta': 'Número de ruta (routing)',
    'ajustes.pagos.banco.ruta_placeholder': '9 dígitos',
    'ajustes.pagos.banco.ruta_error': 'El número de ruta lleva 9 dígitos.',
    'ajustes.pagos.banco.cuenta': 'Número de cuenta',
    'ajustes.pagos.banco.cuenta_placeholder': 'Solo números',
    'ajustes.pagos.banco.cuenta_error': 'El número de cuenta lleva solo dígitos (de 4 a 17).',
    'ajustes.pagos.banco.instrucciones': 'Instrucciones de pago (opcional)',
    'ajustes.pagos.banco.instrucciones_placeholder': 'ej. Pon el número de propuesta en el memo',
    'ajustes.pagos.banco.falta': 'Faltan datos. Sin titular, ruta y número de cuenta, la transferencia no se puede prender en una propuesta.',
    'ajustes.pagos.banco.aviso': 'La cuenta solo viaja en las propuestas donde prendes la transferencia — no en todas.',
    'ajustes.marketing.quitar_imagen': 'Quitar de la biblioteca',
    'ajustes.marketing.quitar_nota': 'Quitarla solo la saca de esta lista. Las propuestas que ya la usan la siguen enseñando.',
    'ajustes.marketing.portadas_cupo': 'Llevas {usadas} de {max}. Salen para escoger en cada propuesta.',
    'ajustes.marketing.portadas_lleno': 'Ya tienes las {max}. Quita una para subir otra.',
    'ajustes.marketing.sin_imagenes_titulo': 'Todavía no hay imágenes',
    'ajustes.marketing.subir_imagen': 'Subir imagen',
    'ajustes.marketing.portadas_desc': 'Tus portadas fijas: salen para escoger en cada propuesta, junto a las tres de siempre. Lo que subas dentro de una propuesta se queda solo en esa propuesta.',
    'ajustes.marketing.galeria_desc': 'Sube aquí tus imágenes y las agregas rápido a la galería del estimado.',
    'ajustes.marketing.redes_desc': 'Agrega tus redes. Si las activas, salen en tus estimados.',
    'ajustes.marketing.resenas_titulo': 'Tus reviews',
    'ajustes.marketing.google_enlace': 'Link de tus reviews de Google',
    'ajustes.marketing.google_destacada': 'Tu mejor review de Google',
    'ajustes.marketing.google_autor_placeholder': 'Nombre del cliente (ej. Juan D.)',
    'ajustes.marketing.google_texto_placeholder': 'Pega aquí tu mejor review de Google...',
    'ajustes.marketing.yelp_enlace': 'Link de tus reviews de Yelp',
    'ajustes.marketing.yelp_destacada': 'Tu mejor review de Yelp',
    'ajustes.marketing.yelp_autor_placeholder': 'Nombre del cliente (ej. Sara M.)',
    'ajustes.marketing.yelp_texto_placeholder': 'Pega aquí tu mejor review de Yelp...',
    'ajustes.cuenta.toast.fotook': 'Ya se cambió tu foto de perfil.',
    'ajustes.cuenta.toast.fotoerror': 'No se pudo subir la foto de perfil.',
    'ajustes.cuenta.toast.nombreerror': 'No se pudo cambiar el nombre en todos lados. Revisa la consola.',
    'ajustes.cuenta.titulo': 'Mi cuenta',
    'ajustes.cuenta.descripcion': 'Cambia tu perfil y tus datos para entrar.',
    'ajustes.cuenta.foto.arialabel': 'Cambiar foto de perfil',
    'ajustes.cuenta.foto.alt': '"Perfil"',
    'ajustes.cuenta.foto.boton': 'Cambiar foto',
    'ajustes.cuenta.nombre.etiqueta': 'Nombre que se ve',
    'ajustes.cuenta.nombre.placeholder': 'Tu nombre',
    'ajustes.cuenta.nombre.guardar': '\n                Guardar\n',
    'ajustes.cuenta.nombre.ayuda': 'Se cambia en las horas, los proyectos y la lista de tu gente.',
    'ajustes.cuenta.correo.etiqueta': 'Correo',
    'ajustes.cuenta.correo.ayuda': 'El correo no se puede cambiar.',
    'ajustes.cuenta.rol.etiqueta': 'Rol y permisos',
    'ajustes.cuenta.rol.defecto': 'Usuario',
    'ajustes.cuenta.grupo.perfil': 'Tu perfil',
    'ajustes.cuenta.grupo.perfilSub': 'Tu foto y tu nombre, como te ve tu equipo.',
    'ajustes.cuenta.grupo.acceso': 'Cómo entras',
    'ajustes.cuenta.nombre.seGuardaSolo': 'Se guarda solo al salir del campo.',
    /* Los permisos no se escriben a mano: se preguntan a las mismas funciones
       de constants/roles.ts que la app usa para permitir o bloquear. Una lista
       escrita aparte se desincroniza el día que cambie una regla. */
    'ajustes.cuenta.permisos.titulo': 'Lo que puedes hacer',
    'ajustes.cuenta.permisos.sub': 'Te lo da tu rol. Para cambiarlo, habla con quien administra la cuenta.',
    'ajustes.cuenta.permisos.dinero': 'Ver el dinero: precios, ganancias y facturas',
    'ajustes.cuenta.permisos.gente': 'Invitar gente y cambiar sus roles',
    'ajustes.cuenta.permisos.clientes': 'Crear y editar clientes y prospectos',
    'ajustes.cuenta.permisos.agenda': 'Planear la agenda y las rutas',
    'ajustes.cuenta.permisos.proyectos': 'Configurar proyectos y asignar encargados',
    'ajustes.cuenta.permisos.costos': 'Apuntar costos y horas en un proyecto',
    'ajustes.cuenta.permisos.ia': 'Usar el asistente de IA',
    'ajustes.cuenta.permisos.no': 'No disponible con tu rol',
    'ajustes.cuenta.idioma.titulo': 'Idioma',
    'ajustes.cuenta.idioma.sub': 'Solo cambia cómo ves tú la app. Tu equipo y tus clientes no se enteran.',
    'ajustes.equipo.quitar_titulo': 'Quitar usuario',
    'ajustes.equipo.quitar_mensaje_1': '¿Seguro que quieres quitar a ',
    'ajustes.equipo.quitar_mensaje_2': ' del equipo? Ya no va a poder entrar.',
    'ajustes.equipo.quitar_confirmar': 'Quitar usuario',
    'ajustes.equipo.cancelar': 'Cancelar',
    'ajustes.equipo.error_quitar': 'Error al quitar al usuario: ',
    'ajustes.equipo.titulo': 'Usuarios y permisos',
    'ajustes.equipo.descripcion': 'Controla quién entra a tus datos y quién checa horas.',
    'ajustes.equipo.agregar': 'Agregar usuario',
    'ajustes.equipo.acceso_total': 'Acceso total',
    'ajustes.equipo.editar_permisos': 'Editar permisos',
    'ajustes.equipo.quitar_tooltip': 'Quitar usuario',
    'ajustes.equipo.vacio_titulo': 'Todavía no hay nadie en el equipo',
    'ajustes.equipo.vacio_descripcion': 'Invita a la gente que necesita checar horas o entrar a tus datos.',
    'ajustes.equipo.vacio_agregar': 'Agregar usuario',
    'contrato.campo.firma': 'Firma',
    'contrato.campo.iniciales': 'Iniciales',
    'contrato.campo.fecha': 'Fecha',
    'contrato.campo.texto': 'Texto',
    'contrato.campo.cambiarRol': 'Cambiar quién firma',
    'contrato.campo.quitar': 'Quitar campo',
    'contrato.campo.fecha.auto': 'auto',
    'contrato.campo.fecha.auto.tooltip': 'La fecha que ves es de muestra. La de verdad se pone al mandar la propuesta.',
    'contratos.editor.campo.firma': 'Firma',
    'contratos.editor.campo.iniciales': 'Iniciales',
    'contratos.editor.campo.fecha': 'Fecha',
    'contratos.editor.campo.texto': 'Texto',
    'contratos.editor.titulo.tipocampo': 'Tipo de campo',
    'ajustes.contratos.campos.guardados': 'Guardado:',
    'ajustes.contratos.campos.delcliente': 'del cliente',
    'ajustes.contratos.campos.tuyos': 'tuyos',
    'contrato.editor.previa': 'Vista previa',
    'contrato.editor.previa.tooltip': 'Mira el contrato como lo verá tu cliente, con tu firma ya puesta.',
    'contratos.previa.titulo': 'Vista previa del cliente',
    'contratos.previa.sub': 'Así llega el contrato al cliente: tu firma estampada y sus recuadros por llenar.',
    'contratos.previa.cerrar': 'Volver a editar',
    'contratos.previa.leyenda.tuyo': 'Tu firma (ya puesta)',
    'contratos.previa.leyenda.cliente': 'Lo llena el cliente',
    'contratos.previa.tuyosinfirmar': 'Sin firmar',
    'contratos.previa.aviso.sinfirmar': 'campos tuyos saldrán en blanco. Guarda y fírmalos antes de enviar.',
    'contratos.previa.fechaauto': 'Se sella con la fecha de envío de la propuesta.',
    'contratos.previa.cliente.firma': 'Firma del cliente',
    'contratos.previa.cliente.iniciales': 'Iniciales',
    'contratos.previa.cliente.fecha': 'Fecha',
    'contratos.previa.cliente.texto': 'Texto',
    'contratos.editor.ayuda.arrastra': 'Arrastra un campo hasta el punto del documento donde debe ir.',
    'contratos.editor.ayuda.suelta': 'Suelta sobre la página para colocarlo.',
    'contratos.editor.titulo.rol': 'Rol del siguiente campo',
    'contratos.editor.rol.solocliente': 'Solo cliente',
    'contratos.editor.ayuda.yafirmado': 'Este contrato ya viene firmado por ti, así que solo faltan los campos del cliente.',
    'contratos.editor.ayuda.cambiarrol': 'Para cambiarle el rol a un campo ya puesto, haz clic en su etiqueta.',
    'contratos.editor.boton.anterior': '← Anterior',
    'contratos.editor.boton.siguiente': 'Siguiente →',
    'contratos.editor.conteo.campo': 'campo',
    'contratos.editor.conteo.campos': 'campos',

    // Resto de la app.
    'clienteDet.contacto.prefiere': 'Prefiere: ',
    'clientes.modal.contacto_texto': 'Mensaje',
    'clientes.modal.correo': 'Correo',
    'clientes.modal.telefono': 'Teléfono',
    'estimados.crear.campo_email': 'Email',
    'estimados.crear.campo_telefono': 'Teléfono',
    'estimados.crear.campo_nombre': 'Nombre',
    'estimado.constructor.borrar_zona_check_partida': { uno: 'partida', otros: 'partidas' },
    'estimado.constructor.barra_partidas': ' partidas',
    'estimado.constructor.precios_rapidos_titulo': 'Precio rápido',
    'estimado.constructor.ganancia': 'Ganancia',
    'estimado.constructor.margen': 'Margen',
    'leads.modal.campo.direccionopcional': '(opcional)',
    'leads.modal.campo.telefono': 'Teléfono',
    'notificaciones.panel.titulo': 'Notificaciones',
    'pricebook.preview.por_unidad': 'por ',
    'wiz.gastos.anual': 'Anual',
    'wiz.gastos.rapido': 'Agregar rápido',
    'wiz.empleados.quitarescritorio': 'Quitar empleado',
    'equipo.modal.campo.rol': 'Rol',
    'equipo.modal.campo.nombre': 'Nombre',
    'admin.reportes.detalle': 'Detalle',
    'admin.reportes.primera_vez': 'primera vez ',
    'admin.reportes.kpi_avisos': 'Avisos',
    'admin.reportes.kpi_resueltos': 'Resueltos',
    'admin.reportes.filtro_activos': 'Activos',
    'admin.negocios.col_suscripcion': 'Suscripción',
    'admin.negocios.col_creado': 'Creado',
    'admin.negocios.col_usuarios': 'Usuarios',
    'admin.negocios.col_estatus': 'Estatus',
    'admin.negocios.col_dueno': 'Dueño',
    'admin.usuarios.sin_workspaces': 'Ninguno',
    'admin.usuarios.col_agregado': 'Agregado',
    'admin.usuarios.col_rol': 'Rol',
    'admin.usuarios.col_correo': 'Correo',
    'admin.usuarios.col_usuario': 'Usuario',
    'materiales.toast.fallo': 'Falló: ',
    'estimado.partidas.conteo': { uno: 'partida', otros: 'partidas' },
    'estimado.partidas.zonaVacia.texto': 'Agrega partidas desde tu lista de precios o desde una calculadora.',
    'estimado.partidas.zonaVacia.titulo': 'No hay partidas en esta zona',
    'estimado.partidas.quitar': 'Quitar la partida',
    'estimado.partidas.sinZona.opcion': 'Sin zona',
    'estimado.partidas.descripcion.placeholder': 'Descripción de la partida…',
    'estimado.partidas.tipo.cargo': 'Cargo',
    'estimado.partidas.tipo.maquinaria': 'Maquinaria',
    'estimado.partidas.tipo.manoDeObra': 'Mano de obra',
    'estimado.partidas.col.margen': 'Margen',
    'estimado.partidas.campo.precio': 'Precio',
    'estimado.partidas.campo.costo': 'Costo',
    'estimado.partidas.campo.cantidad': 'Cant.',
    'estimado.partidas.col.descripcion': 'Descripción',
    'estimado.partidas.col.tipo': 'Tipo',
    'estimado.partidas.borrarConfirm.titulo': '¿Borrar',
    'estimado.partidas.conteoZona': 'partidas',
    'estimado.partidas.seleccionadas': 'seleccionadas',
    'estimado.snapshot.margen': 'Margen',
    'estimado.plantillas.botonplantilla': 'plantilla',
    'estimado.plantillas.botonguardar': 'Guardar',
    'estimado.plantillas.partidas': { uno: 'partida', otros: 'partidas' },
    'estimado.plantillas.conteoPartidas': { uno: '{count} partida', otros: '{count} partidas' },
    'estimado.plantillas.horasHombre': { uno: '{count} hora-hombre', otros: '{count} horas-hombre' },
    'estimado.partida.horasViejas': 'Esta cantidad está en unidades de cliente (día de cuadrilla, visita), no en horas-hombre, así que la mano de obra se está costeando por debajo. El estimado se queda como se envió; para cobrarlo por horas vuelve a soltar la plantilla.',
    'estimado.partida.horasViejas.corto': 'en unidades de cliente',
    'estimado.concreto.campo_ancho_losa': 'Ancho',
    'estimados.calc.sod.desperdicio': '% de desperdicio = ',
    'estimado.plantillas.zona.prefijo': 'Zona: ',
    'estimado.plantillas.boton.agregar2': '\n                  Agregar\n',
    'finanzasNum.pl.bruta': 'Ganancia bruta',
    'finanzasNum.pl.contrato': 'Valor del contrato',
    'finanzasNum.reparto.deEso': 'de eso,',
    'seguimientos.detalle.cliente_estado': 'El cliente marcó el estimado como {estado}.',
    'seguimientos.detalle.propuesta_estado': 'Estimado',
    'finanzasProy.gastos.recibo.alt': 'Recibo',
    'finanzasProy.facturas.venceEl': 'Vence',
    'finanzasProy.facturas.terminos': 'Términos de pago',
    'finanzasProy.pdf.col.monto': 'Monto',
    'finanzasProy.pdf.col.descripcion': 'Descripción',
    'finanzasProy.pdf.vence': 'Se vence:',
    'finanzasProy.pdf.trabajo': 'Trabajo: ',
    'finanzasProy.pdf.encabezado': 'FACTURA',
    'finanzasProy.facturas.etiqueta': 'Factura',
    'finanzasProy.gastos.errorSubirRecibo': 'No se pudo subir el recibo: ',
    'proyecto.materiales.form.titulo': 'Agregar a la lista de compras',
    'proyecto.materiales.editar.guardar': 'Guardar',
    'proyecto.materiales.editar.cancelar': 'Cancelar',
    'proyecto.actividad.fotoObra': 'Foto de la obra',
    'proyecto.visitas.titulo': 'Visitas programadas',
    'proyecto.fechas.titulo': 'Fechas del trabajo',
    'proyecto.geocerca.activaCon': 'Activa: ',
    'proyecto.geocerca.estado': 'Estatus',
    'proyecto.geocerca.titulo': 'Ajustes de la geocerca',
    'proyecto.geocerca.ayudaDireccion': 'Si la dirección del cliente no es donde se hace el trabajo, cámbiala aquí.',
    'proyecto.cuadrilla.titulo': 'Cuadrilla',
    'proyecto.bitacora.titulo': 'Bitácora de la obra',
    'proyectos.tareas.opcional': 'Opcional',
    'proyectos.tareas.vence_etiqueta': 'Vence',
    'proyectos.tareas.fase_etiqueta': 'Fase',
    'proyectos.tareas.asignar_etiqueta': 'Asignar a',
    'proyectos.tareas.sin_asignar_opcion': 'Sin asignar',
    'proyectos.tareas.borrar_confirmar': 'Borrar',
    'propuesta.resenas.pieyelp': 'Reseña de Yelp',
    'propuesta.resenas.piegoogle': 'Reseña de Google',
    'propuesta.galeria.pista': 'Pasa el cursor: antes → después',
    'propuesta.marca.galeria.despues': 'Después',
    'propuesta.marca.galeria.activa': 'Galería activa',
    'propuesta.marca.galeria.titulo': 'Galería de fotos',
    'propuesta.marca.encuadre.titulo': 'Encuadre',
    'propuesta.marca.portada.titulo': 'Foto de portada',
    'propuesta.marca.colores.principal': 'Principal',
    'propuesta.compartir.bloqueada.pestana.cola': '.',
    'propuesta.compartir.pago.tarjeta.etiqueta': 'Enlace de tarjeta',
    'propuesta.firma.de': 'de',
    'servicios.ruta.sin_gps_todas': 'Sin GPS',
    'servicios.ruta.paradas': 'Paradas',
    'servicios.ruta.todos_dias': 'Todos los días',
    'servicios.ruta.todas_cuadrillas': 'Todas las cuadrillas',
    'servicios.analiticas.cliente_contratos': { uno: 'contrato', otros: 'contratos' },
    'contratoServ.servicio': { uno: 'servicio', otros: 'servicios' },
    'contratoServ.porVisita': '/visita',
    'contratoServ.porMes': '/mes',
    'contratoServ.error.cliente': 'Elige el cliente del contrato.',
    'contratoServ.error.direccion': 'Falta la dirección donde se da el servicio.',
    'contratoServ.error.tarifaVisita': 'Pon el precio por visita. En cero, la cuadrilla iría a trabajar sin que nadie cobre.',
    'contratoServ.error.tarifaMensual': 'Pon la tarifa mensual. En cero, la cuadrilla iría a trabajar sin que nadie cobre.',
    'contratoServ.error.servicios': 'Marca al menos un servicio que se va a hacer en cada visita.',
    'geocoder.error.rechazado': 'Google Maps rechazó la geocodificación. Verifica que la «Geocoding API» esté habilitada y que la API key permita este dominio.',
    'geocoder.error.noEncontrado': 'No se encontró ubicación en el mapa para {cuantas} dirección(es). Revisa que estén bien escritas.',
    'crm.convertir.otroDispositivo': 'Este prospecto se está convirtiendo en otro dispositivo. Espera unos segundos y vuelve a intentarlo.',
    'servicios.visita.avance': 'Avance',
    'visitas.paginacion.de': 'de',
    'visitas.detalle.notas': 'Notas',
    'visitas.detalle.checklist': 'Checklist',
    'visitas.detalle.laHizo': 'La hizo',
    'visitas.detalle.contrato': 'Contrato',
    'visitas.filtro.hecha': 'Hecha',
    'visitas.detalle.empezo': 'Empezó',
    'visitas.col.cuadrilla': 'Cuadrilla',
    'visitas.col.direccion': 'Dirección',
    'visitas.filtro.enCamino': 'En camino',
    'visitas.filtro.enObra': 'En la obra',
    'visitas.filtro.saltada': 'Saltada',
    'ajustes.burden.valor_default': 'sugerido',
    'ajustes.burden.seguro_medico': 'Seguro médico',
    'ajustes.gastos.ya_agregado_busqueda': 'Ya lo agregaste',
    'ajustes.gastos.titulo': 'Agregar gasto',
    'ajustes.metricas.sufijo.anual.gasto': '/año',
    'ajustes.metricas.boton.comosecalculo': 'De dónde salen estos números',
    'rastreo.banner.apagado': 'Rastreo: APAGADO — No estás en turno',
    'equipo.mapa.leyenda.salio': 'Salió',
    'equipo.mapa.leyenda.entro': 'Entró',
    'cuadrillas.tarjeta.sinTrabajadores': 'Sin trabajadores',
    'cuadrillas.tarjeta.trabajadores': 'Trabajadores',
    'cuadrillas.tarjeta.sinLider': 'Sin asignar',
    'cuadrillas.tarjeta.lider': 'Líder',
    'cuadrillas.vacio.boton': 'Crear cuadrilla',
    'comun.cancelar.boton': 'Cancelar',
    'cuadrillas.lider': 'Líder de la cuadrilla',
    'cuadrillas.nombre': 'Nombre de la cuadrilla',
    'reloj.estado.taller': 'Taller / general',
    'reloj.salida.confirmar.si': 'Marcar salida',
    'personal.horas.opcion_en_obra': 'En obra',
    'personal.horas.opcion_fuera_obra': 'Fuera de obra',
    'personal.horas.opcion_auto_cerrado': 'Auto-cerrado',
    'personal.horas.estatus_activo': 'Activo',
    'personal.horas.col_estatus': 'Estatus',
    'personal.horas.col_proyecto': 'Proyecto',
    'personal.horas.tarjeta_horas': 'Horas',
    'personal.horas.col_lonche': 'Lonche',
    'personal.horas.col_salida': 'Salida',
    'personal.horas.col_entrada': 'Entrada',
    'personal.horas.col_fecha': 'Fecha',
    'personal.horas.col_trabajador': 'Trabajador',
    'personal.horas.tarjeta_overtime': 'Overtime',
    'personal.horas.tarjeta_auto': 'Auto',
    'dialogo.boton.cancelar': 'Cancelar',
    'chat.mensajes.soloautor': 'Estás viendo una conversación compartida de {activeConversation.user_name}. Solo el autor puede escribir en ella.',
    'chat.encabezado.privada': 'Privada',
    'cliente.facturacion.tabla.pagada': 'Pagada',
    'cliente.panel.avance': 'Avance',
    'clientes.tabla.ingresostotales': 'Total cobrado',
    'clientes.tabla.cliente': 'Cliente',
    'camara.foreman.elegirproyecto': 'Elige el proyecto',
    'camara.foreman.titulo': 'Cámara de la empresa',
    'panel.tabla.avance': 'Avance',
    'panel.tabla.estado': 'Estado',
    'panel.tabla.cliente': 'Cliente',
    'panel.tabla.proyecto': 'Proyecto',
    'equipo.modal.mantenimiento.titulo': 'Registrar mantenimiento',
    'equipo.estado.entrabajo': 'En la obra',
    'equipo.estado.disponible': 'Disponible',
    'equipo.campo.estado': 'Estado',
    'estimados.lista.col_actualizado': 'Actualizado',
    'estimados.lista.col_estatus': 'Estatus',
    'estimados.lista.col_margen': 'Margen',
    'estimados.lista.num_partidas': ' partidas',
    'estimados.lista.col_proyecto': 'Proyecto',
    'estimados.lista.col_cliente': 'Cliente',
    'estimados.lista.boton_seleccionar': 'Seleccionar',
    'estimados.lista.boton_cancelar': 'Cancelar',
    'estimados.lista.boton_borrar': 'Borrar ',
    'estimados.lista.borrar_titulo': 'Borrar estimado',
    'estimados.borrar.noEncontrado': 'Ese estimado ya no está en la lista. Recarga y vuelve a intentarlo.',
    'estimados.borrar.bloqueado.ganado': 'Este estimado ya lo aceptó el cliente, así que no se borra. Archívalo si quieres sacarlo de la lista — el documento se queda.',
    'estimados.borrar.bloqueado.firmado': 'Este estimado está firmado. La firma y el contrato son el comprobante de lo que se acordó, así que no se borra. Archívalo para sacarlo de la lista.',
    'estimados.borrar.bloqueado.convertido': 'De este estimado salió un proyecto que sigue abierto. Borrarlo dejaría el proyecto sin saber qué se acordó cobrar. Archívalo para sacarlo de la lista.',
    'estimados.borrar.protegido.titulo': 'Este estimado no se puede borrar',
    'estimados.borrar.archivar': 'Archivar',
    'estimados.borrar.entendido': 'Entendido',
    'estimados.borrar.archivado': 'Archivado. Lo encuentras en la pestaña Terminados.',
    'estimados.borrar.saltados': { uno: 'Se borró lo que se podía. {saltados} no se borró: ya está aceptado o firmado.', otros: 'Se borró lo que se podía. {saltados} no se borraron: ya están aceptados o firmados.' },
    'estimados.borrar.ningunoBorrable': { uno: 'No se borró nada: ese estimado ya está aceptado o firmado.', otros: 'No se borró nada: esos estimados ya están aceptados o firmados.' },
    'precios.preview.proveedor': 'Proveedor:',
    'precios.tabla.actualizado': 'Actualizado:',
    'precios.mios.sub': 'Los materiales que agregaste tú y que no están en el catálogo general.',
    'propuesta.default.titulo_para': 'para',
    'propuesta.publica.copia.descargar': 'Descargar el contrato firmado',
    'propuesta.publica.copia.abriendo': 'Abriendo…',
    'propuesta.publica.default.titulo': 'Propuesta',
    'servicios.contratos.filtro_cancelados': 'Cancelados',
    'servicios.contratos.filtro_pausados': 'Pausados',
    'servicios.contratos.filtro_activos': 'Activos',
    'servicios.contratos.filtro_todos': 'Todos',
    'servicios.rutas.paradas': 'Paradas',
    'ajustesNeg.maquinaria.sub': 'Trocas, trailas, podadoras, excavadoras. Costo al año = (lo que costó ÷ años que te dura) + gasolina + mantenimiento + seguro. Todo se va a gastos fijos.',
    'ajustesNeg.empleados.sub': 'Aquí agregas a tu gente para sacar tu tarifa por hora. Para darles acceso a la app, usa la pestaña «Gente».',
    'ajustes.contratos.biblioteca.titulo': 'Biblioteca',
    'ajustes.legal.garantia_texto': 'Texto de la garantía',
    'ajustes.legal.garantia_nombre': 'Nombre de la garantía',
    'ajustes.legal.garantias_titulo': 'Tus garantías',
    'ajustes.legal.terminos_titulo': 'Términos legales estándar',
    'ajustes.marketing.resenas_desc': 'Pon los links de tus páginas de reviews y, si quieres, agrega tu mejor review para que salga en el estimado.',
    'ajustes.marketing.redes_titulo': 'Redes sociales',
    'ajustes.marketing.galeria_titulo': 'Imágenes de galería',
    'ajustes.marketing.portadas_titulo': 'Imágenes de portada',
    'ajustes.equipo.mas_vistas': 'más',
    'ajustes.equipo.columna_permisos': 'Permisos',
    'ajustes.equipo.columna_rol': 'Rol',
    'ajustes.equipo.columna_usuario': 'Usuario',
    'contratos.editor.conteo.puestos': ' en el contrato',
    'contratos.editor.titulo.pagina': 'Página',

    // Resto de la app.
    'estimado.plantillas.boton.agregar3': '\n                  Agregar\n',

    'tarifas.grupo.diseno': 'Diseño',
    'tarifas.grupo.arborista': 'Arborista',
    'tarifas.grupo.campo': 'Campo',
    'tarifas.grupo.cuadrilla': 'Cuadrilla (por día)',
    'tarifas.puesto.senior-designer-la': 'Diseñador senior / Arquitecto paisajista',
    'tarifas.puesto.designer': 'Diseñador',
    'tarifas.puesto.designer-horticulturist': 'Diseñador (horticultor)',
    'tarifas.puesto.archviz-specialist': 'Especialista en 3D / ArchViz',
    'tarifas.puesto.project-administrator': 'Administrador de proyecto',
    'tarifas.puesto.structural-engineer': 'Ingeniero estructural',
    'tarifas.puesto.climber-arborist': 'Climber / Arborista certificado',
    'tarifas.puesto.senior-climber-arborist': 'Climber senior',
    'tarifas.puesto.isa-traq-arborist': 'Arborista ISA TRAQ',
    'tarifas.puesto.groundman': 'Groundman (ayudante de piso)',
    'tarifas.puesto.equipment-operator': 'Operador de maquinaria',
    'tarifas.puesto.laborer': 'Ayudante general',
    'tarifas.puesto.field-technician': 'Técnico de campo',
    'tarifas.puesto.survey-technician': 'Técnico de topografía',
    'tarifas.puesto.demolition-crew': 'Cuadrilla de demolición',
    'tarifas.puesto.grading-crew': 'Cuadrilla de grading',
    'tarifas.puesto.clearing-crew': 'Cuadrilla de limpieza de terreno',
    'tarifas.puesto.planting-crew': 'Cuadrilla de siembra',
    'tarifas.puesto.hedge-crew': 'Cuadrilla de setos',
    'tarifas.puesto.sod-removal-crew': 'Cuadrilla para quitar sod',
    'tarifas.puesto.tree-handling-crew': 'Cuadrilla de árboles (specimen)',

    'finanzasNum.encabezado.cobrado': 'Cobrado — {rango}',
    'finanzasNum.pl.horasRegistradas': '{horas} h registradas',
    'finanzasNum.pl.sinHoras': 'Todavía no hay horas registradas',
    /* La pista decía LO MISMO que la etiqueta de su renglón
       ('finanzasNum.pl.overhead'), así que la escalera imprimía la frase dos
       veces, una encima de otra, y la línea chica —que existe para explicar—
       no explicaba nada. Ahora dice de qué está hecho el número y cómo se
       reparte, que es lo que faltaba saber. */
    'finanzasNum.pl.overhead.pista': 'Renta, seguros, teléfono, camionetas: repartido entre las horas fichadas',
    'finanzasNum.pl.overhead.pistaMenosMant': 'Tu parte de los gastos del negocio, menos el mantenimiento presupuestado',
    'finanzasNum.pl.reparaciones.pista': 'Registrado en Inventario › Mantenimiento',
    'finanzasNum.pl.reparaciones.sinNada': 'Todavía no hay reparaciones registradas',
    'finanzasNum.pl.margen': '{margen}% de margen',

    'finanzasNum.reparto.nota': 'Estas dos suman el Cobrado de arriba. Una propuesta aceptada se vuelve proyecto, así que el dinero de la propuesta es dinero del proyecto: se cuenta aquí, no como una fuente aparte.',
    'finanzasNum.libro.soloProyectos': 'Solo proyectos — aquí no entra el dinero de las rutas de servicio.',
    'finanzasNum.pl.gananciaNeta': 'Ganancia neta',

    'finanzasNum.libro.nota': 'La ganancia bruta es el valor del contrato menos los gastos registrados. La mano de obra se lleva aparte, en las horas, y aquí no se resta.',
    'finanzasNum.mant.real': 'Real ({cuantas} registradas)',
    'finanzasNum.mant.arriba': 'Arriba del presupuesto',
    'finanzasNum.mant.abajo': 'Debajo del presupuesto',
    'finanzasNum.mant.sinPresupuesto': 'Todavía no le pones presupuesto de mantenimiento a tu equipo.',
    'finanzasNum.mant.noComparable': 'Un presupuesto anual no se compara con un total de siempre — escoge Este mes o Este año.',

    // Resto de la app.
    'estimado.plantillas.etiqueta.permiso': '>Permit ',
    'estimado.plantillas.etiqueta.estado': '>Estado ',
    'estimado.plantillas.boton.agregar4': '\n                  Agregar\n',

    'frecuencia.weekly': 'Semanal',
    'frecuencia.weekly.desc': 'Cada semana',
    'frecuencia.biweekly': 'Quincenal',
    'frecuencia.biweekly.desc': 'Cada 2 semanas',
    'frecuencia.monthly': 'Mensual',
    'frecuencia.monthly.desc': 'Una vez al mes',
    'dia.monday': 'Lunes',
    'dia.tuesday': 'Martes',
    'dia.wednesday': 'Miércoles',
    'dia.thursday': 'Jueves',
    'dia.friday': 'Viernes',
    'dia.saturday': 'Sábado',
    'dia.sunday': 'Domingo',
    'servicio.mowing': 'Cortar el pasto',
    'servicio.edging': 'Orillar',
    'servicio.blowing': 'Soplar',
    'servicio.trimming': 'Podar',
    'servicio.weeding': 'Quitar hierba',
    'servicio.mulching': 'Poner mulch',
    'servicio.cleanup': 'Limpieza',
    'servicio.trashremoval': 'Sacar la basura',
    'servicio.windowcleaning': 'Limpiar ventanas',
    'servicio.pressurewashing': 'Lavado a presión',

    'contratoServ.error.guardar': 'No se pudo guardar el contrato: {motivo}',
    'contratoServ.errorDesconocido': 'error desconocido',
    'contratoServ.titulo.editar': 'Editar contrato',
    'contratoServ.titulo.nuevo': 'Contrato de servicio nuevo',
    'contratoServ.sinDireccion': 'Sin dirección registrada',
    'contratoServ.cobro.mensual': 'Fijo mensual',
    'contratoServ.vencimiento.etiqueta': 'Pago vence',
    'contratoServ.vencimiento.dia': 'día',
    'contratoServ.vencimiento.defecto': '10',
    'contratoServ.vencimiento.ayudaMensual': 'de cada mes (se cobra por adelantado)',
    'contratoServ.vencimiento.ayudaPorVisita': 'del mes siguiente (se cobra al cerrar el mes)',
    'contratoServ.cobro.porVisita': 'Por visita',
    'contratoServ.tarifa.mensual': 'Tarifa mensual',
    'contratoServ.tarifa.porVisita': 'Tarifa por visita',
    'contratoServ.resumen.losDias': 'los {dia}',
    'contratoServ.boton.guardando': 'Guardando…',
    'contratoServ.boton.actualizar': 'Actualizar contrato',
    'contratoServ.boton.crear': 'Crear contrato',

    'proyecto.fechas.faltan': 'Pon las dos fechas.',
    'proyecto.fechas.ordenMal': 'La fecha de fin no puede ser antes de la de inicio.',
    'comun.errorDesconocido': 'error desconocido',

    /* La pantalla de dirección no encontrada. En cristiano y sin culpar a
       nadie: quien llega aquí normalmente pinchó un enlace viejo. */
    'error404.titulo': 'Esa página no existe',
    'error404.desc': 'El enlace que abriste no lleva a ningún lado. Puede que la dirección esté mal escrita, o que sea de una versión vieja de la app.',
    'error404.direccion': 'Dirección que pediste',
    'error404.irAlPanel': 'Ir al panel',
    'error404.volver': 'Volver',

    /* El aviso de sin conexión. Dice lo que de verdad pasa: hoy, sin red, solo
       el reloj de checadas sobrevive — lo demás se pierde al guardar. */
    'conexion.sinRed': 'Sin conexión — lo que hagas ahora puede perderse',
    'conexion.reintentar': 'Reintentar',

    /* El nombre del negocio, editable después del asistente inicial.
       Antes solo se podía escribir UNA vez, en la configuración inicial: quien
       se equivocaba se quedaba con el error para siempre, y ese nombre sale en
       cada propuesta y cada contrato que manda a sus clientes. */
    'ajustes.negocio.titulo': 'Tu negocio',
    'ajustes.negocio.ayuda': 'Es el nombre que ven tus clientes en las propuestas, los contratos y los correos.',
    'ajustes.negocio.guardar': 'Guardar',
    'ajustes.negocio.guardado': 'Listo, ya se cambió el nombre de tu negocio.',
    'ajustes.negocio.error': 'No se pudo guardar. Revisa tu conexión y vuelve a intentar.',
    'ajustes.negocio.soloAdmin': 'Solo el dueño o la oficina pueden cambiar el nombre del negocio.',
    'ajustes.nav.negocio': 'Negocio',
    'ajustes.negocio.dondeElResto': 'Los sueldos, los gastos fijos, la maquinaria y los precios están en Estimados → Gastos fijos.',
    'ajustes.negocio.irAGastos': 'Ir a Gastos fijos',
    /* Los dos botones flotantes del calendario en el teléfono. Estaban
       escritos a mano en el JSX, en inglés. */
    'calendario.modo.calendario': 'Calendario',
    'calendario.modo.agenda': 'Agenda',
    /* Se quedaron escritas a mano en el JSX y en los avisos, así que salían en
       inglés con la app en español. El diccionario está sincronizado al 100%
       —4692 claves de cada lado—, o sea que las fugas ya no son claves que
       falten: son textos que nunca pasaron por `t()`. */
    'comun.opcional': '(Opcional)',
    /* El enlace sin `?t=` ya no lee la tabla por detrás: se pide uno nuevo. */
    'propuesta.publica.error.sinToken': 'Este enlace ya no sirve. Pídele a tu contratista que te mande uno nuevo.',
    /* Los estados que faltaban para poder nombrar el enum completo. Los otros
       cinco ya existían como pestañas del filtro (`estimados.lista.tab_*`) —
       estaban traducidos ARRIBA de la tabla y en inglés DENTRO de ella. */
    'estimado.estado.revision': 'Falta revisar',
    'estimado.estado.completado': 'Terminado',
    'estimado.estado.pagado': 'Pagado',
    /* Valores que viven en la base de datos y se pintaban crudos. El dato que
       se guarda NO cambia — solo cómo se lee. Ver i18n/nombresDeDatos.ts. */
    'dato.proyecto.aceptado': 'Aceptado',
    'dato.proyecto.agendado': 'Agendado',
    'dato.proyecto.encurso': 'En curso',
    'dato.proyecto.terminado': 'Terminado',
    'dato.proyecto.cancelado': 'Cancelado',
    'dato.trabajo.activo': 'Activo',
    'dato.trabajo.pausado': 'Pausado',
    'dato.trabajo.encamino': 'En camino',
    'dato.trabajo.ensitio': 'En el sitio',
    'dato.trabajo.saltado': 'Saltada',
    'dato.rol.admin': 'Admin',
    'dato.rol.dueno': 'Dueño',
    'dato.rol.oficina': 'Oficina',
    'dato.rol.capataz': 'Capataz',
    'dato.rol.trabajador': 'Trabajador',
    'dato.rol.servicio': 'Técnico de servicio',
    'dato.partida.material': 'Material',
    'dato.partida.mano': 'Mano de obra',
    'dato.partida.equipo': 'Equipo',
    'dato.partida.equiporenta': 'Equipo rentado',
    'dato.partida.equipopropio': 'Equipo propio',
    'dato.partida.otro': 'Otro',
    'estimado.partidas.tipo.material': 'Material',
    'estimado.partidas.tipo.subcontratista': 'Subcontratista',
    'contratos.visitas.hechas': {
        uno: '1 visita',
        otros: '{count} visitas',
    },
    'calc.plantas.agregadas': {
        uno: 'Se agregó 1 planta — pon el costo por planta en el estimado',
        otros: 'Se agregaron {count} plantas — pon el costo por planta en el estimado',
    },
    'chat.adjunto.fallo': 'No se pudo subir: {archivo}',
    'chat.adjunto.omitidos': {
        uno: 'Se omitió 1 archivo (todavía subiendo o falló).',
        otros: 'Se omitieron {count} archivos (todavía subiendo o fallaron).',
    },

    /* El perfil se agrupa en dos bloques: quién eres y cómo te encuentran.
       Antes eran seis campos en fila, todos del mismo peso. */
    'ajustesNeg.perfil.grupo.identidad': 'Identidad',
    'ajustesNeg.perfil.grupo.identidadSub': 'Tu logo y tu nombre, tal como aparecen firmando el trabajo.',
    'ajustesNeg.perfil.grupo.contacto': 'Contacto',
    'ajustesNeg.perfil.grupo.contactoSub': 'Por dónde te busca un cliente que ya tiene tu propuesta en la mano.',
    'ajustesNeg.perfil.logoAyuda': 'PNG, JPG o SVG. Cuadrado y de 512 px o más se ve nítido impreso.',
    'ajustesNeg.perfil.cambiarLogo': 'Cambiar logo',
    /* La previa: esta pantalla decide lo que ve el cliente y no lo enseñaba. */
    'ajustesNeg.perfil.previa.titulo': 'Así te ven tus clientes',
    'ajustesNeg.perfil.previa.sub': 'El encabezado de tus propuestas y contratos.',
    'ajustesNeg.perfil.previa.sinNombre': 'Tu negocio',
    'ajustesNeg.perfil.previa.sinLogo': 'Sin logo',
    'ajustesNeg.perfil.previa.documento': 'Propuesta',

    /* Tarjeta «Quién manda hoy» del detalle de proyecto. Estaba ENTERA en
       inglés: el componente nunca llamó a `useIdioma`, así que ni las pruebas
       de traducción ni yo lo vimos hasta buscar texto suelto en el JSX. */
    'proyLider.titulo': 'Quién manda hoy',
    'proyLider.asignarDia': 'Apuntar un día',
    'proyLider.cancelar': 'Cancelar',
    'proyLider.quitarDia': 'Quitar este día',
    'proyLider.quitarDiaAria': 'Quitar el {dia}',
    'proyLider.capataz': 'Capataz',
    'proyLider.nadie': 'Hoy no hay nadie al frente de esta obra',
    'proyLider.siguiente': 'Le toca a {quien} el {dia}',
    'proyLider.puedesApuntar': 'Apunta un día aquí, o arrastra la obra sobre un capataz en la Agenda.',
    'proyLider.sinDias': 'Todavía no hay días programados.',
    'proyLider.sinCapataces': 'Todavía no hay capataces en el equipo. Ponle el puesto de Capataz a alguien en Gente.',
    'proyLider.dia': 'Día',
    'proyLider.elegir': 'Escoge…',
    'proyLider.guardando': 'Guardando…',
    'proyLider.ponerAlFrente': 'Ponerlo al frente ese día',
    'proyLider.proximos': 'Lo que viene',
    'proyLider.anteriores': 'Estuvieron al frente',
    'proyLider.deLaAgenda': 'Sale de la Agenda semanal — apuntar aquí reserva el mismo día en el tablero.',

    /* Rótulos y etiquetas sueltas que nunca pasaron por el diccionario. Los de
       inglés rompían el modo español; los de español rompían el modo inglés.
       Ninguno lo veían las pruebas, que vigilan el diccionario y no el JSX. */
    'aria.abrirMenu': 'Abrir menú',
    'aria.cerrarMenu': 'Cerrar menú',
    'aria.colapsarMenu': 'Colapsar menú',
    'aria.expandirMenu': 'Expandir menú',
    'aria.limpiarBusqueda': 'Limpiar búsqueda',
    'comun.buscarPlaceholder': 'Buscar…',
    'aria.cerrarAviso': 'Cerrar aviso',
    'aria.seccionesProyecto': 'Secciones del proyecto',
    'aria.faltaEnPaso': 'Falta información en este paso',
    'calc.medidasDelArriate': 'Medidas del arriate',
    'calc.loQueNecesitas': 'Lo que necesitas',
    'obra.dificultadAcceso': 'Qué tan difícil es entrar a la obra',
    'clientes.error.cargar': 'No se pudieron cargar los clientes',
    'clientes.error.reintenta': 'Revisa tu conexión e inténtalo de nuevo.',
    'prospectos.error.cargar': 'No se pudieron cargar los prospectos',
    'estimado.yaTieneProyecto': 'Te abro el proyecto que ya salió de este estimado.',
    'conexion.yaSubio': 'Ya se subió',
    'conexion.pendientes': { uno: '{count} checada por subir', otros: '{count} checadas por subir' },
    'errores.guardarCambio': 'No se pudo guardar el cambio: {motivo}',
    'proyecto.geo.faltaLlave': 'Falta la llave de Google Maps.',
    'proyecto.geo.sinCoordenadas': 'No se encontró esa dirección.',
    'proyecto.geo.error': 'Hubo un error al buscar la dirección.',
    'proyecto.geocerca.radioPuesto': 'La geocerca quedó en {radio} ft',
    'proyecto.actividad.movimientos': 'movimientos',
    'proyecto.actividad.turno': 'Turno:',
    'proyecto.actividad.horas': 'hrs',
    'proyecto.nota.tipo.retraso': 'Retraso',
    'proyecto.nota.tipo.avance': 'Avance',
    'proyecto.nota.tipo.seguridad': 'Seguridad',
    'proyecto.nota.tipo.nota': 'Nota',

    'propuesta.tema.modern': 'Moderno',
    'propuesta.tema.natural': 'Natural',
    'propuesta.tema.elegant': 'Elegante',
    'propuesta.tema.custom': 'Tu foto',
    'propuesta.fuente.sans': 'Moderna (sans)',
    'propuesta.fuente.serif': 'Clásica (serif)',
    'propuesta.fuente.mono': 'Técnica (mono)',
    'propuesta.anim.none': 'Ninguna',
    'propuesta.anim.fade': 'Aparecer',
    'propuesta.anim.slide': 'Deslizar',
    'propuesta.anim.zoom': 'Zoom',
    'propuesta.marca.usarPortada': 'Usar la foto de portada guardada {n}',
    'propuesta.marca.agregarFoto': 'Agregar la foto {n} de la biblioteca',
    'propuesta.marca.subiendo': 'Subiendo…',

    'finanzasProy.facturas.estatusAria': 'Estatus de la factura {id}',
    'finanzasProy.facturas.pagadaElDia': 'Pagada el {fecha}',
    'finanzasProy.facturas.cambiarEstatus2': 'Cambiar el estatus de la factura',
    'finanzasProy.gastos.borrarAria': 'Borrar el gasto {que}',
    'finanzasProy.factura.conceptoDesc': 'Concepto {n}: descripción',
    'finanzasProy.factura.conceptoMonto': 'Concepto {n}: monto',
    'finanzasProy.factura.quitarConcepto': 'Quitar el concepto {n}',
    'finanzasProy.facturas.pdfAria': 'Descargar la factura {id} en PDF',
    'finanzasProy.facturas.borrarAria': 'Borrar la factura {id}',

    'estimado.partidas.manoObraBloqueada2': 'El precio de la mano de obra sale de tu calculadora de gastos fijos, en Ajustes.',
    'estimado.partidas.sinCosto': 'Ponle costo — se guarda para tu próximo trabajo.',
    'estimado.partidas.seleccionar': 'Seleccionar',
    'estimado.partidas.sobreCosto': 'sobre el costo',
    'estimado.partidas.sobrePrecio': 'sobre el precio',
    'estimado.partidas.teLlevas': 'te llevas',
    'estimado.partidas.deMargen': 'de margen',
    'estimado.partidas.deMarkup': 'de markup',
    'estimado.partidas.fijadoAMano': 'Fijado a mano',
    'estimado.partidas.volverAlTaller': 'Volver a la tarifa del taller',
    'estimado.partidas.factorNeto': 'Factor de margen neto del estimado',
    'estimado.partidas.markup': 'Markup %',
    'estimado.partidas.margen': 'Margen %',
    'estimado.partidas.campo.unidad': 'Unidad',
    'estimado.partidas.campo.zona': 'Zona',
    'estimado.partidas.unidadBloqueada': 'La mano de obra se cotiza en horas-hombre; su unidad no se cambia.',

    'propuesta.seccion.scope': 'Resumen ejecutivo',
    'propuesta.seccion.companyProfile': 'Perfil de la compañía',
    'propuesta.seccion.gallery': 'Galería de fotos',
    'propuesta.seccion.pricing': 'Precios y opciones',
    'propuesta.seccion.lineItemPricing': 'Detalle por partida',
    'propuesta.seccion.totals': 'Resumen de totales',
    'propuesta.seccion.terms': 'Términos y firma',
    'propuesta.seccion.reviews': 'Reseñas y redes',
    'propuesta.seccion.movida': '{seccion}, posición {pos} de {total}',
    'propuesta.seccion.subir': 'Subir {seccion}',
    'propuesta.seccion.bajar': 'Bajar {seccion}',
    'propuesta.seccion.mostrar': 'Mostrar {seccion}',

    'reloj.foto.quitar': 'Quitar la foto',
    'reloj.foto.errorEntrada': 'No se subió la foto — te marcamos entrada sin ella.',
    'reloj.foto.errorSalida': 'No se subió la foto — te marcamos salida sin ella.',
    'reloj.entrada.sinGPS': 'Entraste sin ubicación — aquí no agarra el GPS.',
    'reloj.salida.confirmar.mensaje': 'Llevas {tiempo} en este turno. ¿Marcamos salida?',
    'reloj.salida.fueraDeObra': 'Saliste — a {pies} ft de la obra.',
    'reloj.lunch.errorEmpezar': 'No se pudo empezar el lunch: {motivo}',
    'reloj.lunch.errorTerminar': 'No se pudo terminar el lunch: {motivo}',
    'reloj.aviso.masDe10h': 'Llevas más de 10 horas adentro — no se te olvide marcar salida.',
    'reloj.boton.ubicando': 'Buscando ubicación…',
    'reloj.boton.salida': 'Marcar salida',

    'plantillaPriv.yaEstan': 'Todos los materiales de esa categoría ya están en esta plantilla.',
    'plantillaPriv.agregados': { uno: 'Agregué {cuantos} material de {categoria} en $0 — ponles precio.', otros: 'Agregué {cuantos} materiales de {categoria} en $0 — ponles precio.' },
    'plantillaPriv.guardada': { uno: '{verbo} «{nombre}» — {cuantas} partida', otros: '{verbo} «{nombre}» — {cuantas} partidas' },
    'plantillaPriv.verbo.creada': 'Creada',
    'plantillaPriv.verbo.guardada': 'Guardada',
    'plantillaPriv.errorGuardar': 'No se pudo guardar: {motivo}',
    'plantillaPriv.modo.crear': 'Crear plantilla de partidas',
    'plantillaPriv.modo.editar': 'Editar plantilla privada',
    'plantillaPriv.nombreVacio': 'Plantilla de partidas nueva',
    'plantillaPriv.ponPrecio': 'Pon un precio para que este material se guarde en tu lista de precios.',
    'plantillaPriv.boton.creando': 'Creando…',
    'plantillaPriv.boton.guardando': 'Guardando…',
    'plantillaPriv.boton.crear': 'Crear plantilla',
    'plantillaPriv.boton.guardar': 'Guardar cambios',

    'chat.hace.ahorita': 'ahorita',
    'chat.hace.minutos': 'hace {n}m',
    'chat.hace.horas': 'hace {n}h',
    'chat.hace.dias': 'hace {n}d',
    'chat.historial.de': 'de {quien}',
    'chat.historial.borrarAria': 'Borrar la conversación {titulo}',
    'chat.encabezado.de': '· de {quien}',
    'chat.visibilidad.privada': 'Privada',
    'chat.visibilidad.compartida': 'Compartida',
    'rutas.sinAsignar': 'Sin asignar',

    'horas.resumen.total': 'total',
    'horas.resumen.fueraDeObra': 'fuera de obra',
    'horas.resumen.autoCerrados': 'auto-cerrados',
    'horas.resumen.overtime': 'de overtime',
    'horas.tallerGeneral': 'Taller / general',
    'horas.sinNombre': 'Sin nombre',
    'horas.lonche': 'Lonche:',
    'horas.totalTurnos': { uno: 'Total — {cuantos} turno', otros: 'Total — {cuantos} turnos' },

    'horas.destino.todos': 'Todos los destinos',
    'horas.destino.aria': 'Filtrar por destino',
    'horas.destino.enObra': 'En obra',
    'horas.destino.taller': 'Taller',
    'horas.destino.sinClasificar': 'Sin clasificar',
    'horas.destino.eliminada': 'Obra eliminada',
    'horas.resumen.turnos': { uno: 'turno', otros: 'turnos' },
    'horas.resumen.enObra': 'en obra',
    'horas.resumen.taller': 'en el taller',
    'horas.resumen.sinClasificar': 'sin clasificar',
    'horas.resumen.eliminadas': 'en obras eliminadas',
    'horas.turno.tramos': { uno: '{cuantos} tramo', otros: '{cuantos} tramos' },
    'horas.turno.verTramosAria': 'Ver los tramos de este turno',
    'horas.revisarTooltip': 'El cierre automático no supo a qué hora terminó este turno: las horas están incompletas.',

    'rutas.asignada': 'Contrato asignado a {cuadrilla}',
    'rutas.cuadrillaGenerica': 'la cuadrilla',
    'rutas.errorAsignar': 'No se pudo asignar la cuadrilla: {motivo}',
    'frecuencia.otros': 'Otros',
    'rutas.boton.reanudar': 'Reanudar',
    'rutas.boton.reactivar': 'Reactivar',
    'rutas.miembroDesconocido': 'Desconocido',

    'calc.mulch.nombre': 'Volumen de mulch / tierra / grava',
    'calc.mulch.desc': 'Yardas cúbicas de largo × ancho × profundidad. Mulch, tierra, grava, arena, composta.',
    'calc.sod.nombre': 'Sod / área de césped',
    'calc.sod.desc': 'Pies cuadrados → planchas y pallets, con desperdicio según la forma.',
    'calc.concreto.nombre': 'Concreto',
    'calc.concreto.desc': 'Yardas cúbicas para losas, footings, sonotubos y escalones. Agrega 10% de desperdicio solo.',
    'calc.plantas.nombre': 'Separación de plantas',
    'calc.plantas.desc': 'Cuántas plantas caben según el área y la separación de centro a centro. Triangular por defecto.',
    'calc.badge.diario': 'Diario',
    'calc.badge.porTrabajo': 'Por trabajo',

    'analitica.sinAsignar': 'Sin asignar',
    'analitica.enPausa': '{n} en pausa',
    'analitica.canceladas': '{n} canceladas',
    'analitica.porMes': '/mes',
    'admin.severidad.error': 'Error',
    'admin.severidad.aviso': 'Aviso',
    'admin.severidad.info': 'Info',
    'admin.hace.ahorita': 'hace un momento',
    'admin.hace.minutos': 'hace {n}m',
    'admin.hace.horas': 'hace {n}h',
    'admin.hace.dias': 'hace {n}d',
    'admin.boton.reabrir': 'Reabrir',
    'admin.boton.resolver': 'Resolver',

    'gente.rol.desc.admin': 'Acceso total a todas las pestañas, ajustes y usuarios.',
    'gente.rol.desc.office': 'Todas las pestañas prendidas por defecto. Se puede ajustar cuáles ve.',
    'gente.rol.desc.foreman': 'Solo Proyectos y Reloj. No puede tocar los ajustes.',
    'gente.rol.desc.laborer': 'Solo el Reloj. Lo mínimo para la gente de campo.',
    'gente.rol.desc.maintenance': 'Reloj y Rutas. Para las cuadrillas de mantenimiento.',
    'gente.errorGuardar': 'Error al guardar el usuario: {motivo}. Puede que no tengas permiso para hacer esto.',
    'gente.modal.editar': 'Editar permisos del usuario',
    'gente.modal.invitar': 'Invitar a alguien nuevo',

    'comun.sinNombre': 'Sin nombre',
    'tareas.yaEtiquetadas': 'Todas las tareas ya estaban en su fase.',
    'tareas.fase1': 'Fase 1: Preparación y arranque',
    'tareas.fase2': 'Fase 2: Ejecución',
    'tareas.fase3': 'Fase 3: Acabado y cierre',
    'propFirmadas.errorAbrir': 'No se pudo abrir el contrato firmado.',
    'propFirmadas.errorGuardarCopia': 'No se pudo guardar la copia de este contrato.',
    'propFirmadas.vacio.busqueda': 'Ninguna propuesta firmada coincide con tu búsqueda.',
    'propFirmadas.vacio.aun': 'Cuando un cliente firme y acepte una propuesta, aparecerá aquí.',
    'propFirmadas.copiaGuardada': 'Copia firmada guardada el {fecha} — las dos firmas más el certificado',
    'propFirmadas.abrirGuardado': 'Abre el contrato guardado — las dos firmas más el certificado',
    'propFirmadas.rearmar': 'Rearma el contrato firmado desde la plantilla (todavía no hay copia guardada)',

    'tareas.materialesLigados': { uno: 'Esta tarea usa {n} material que está en Compras. ¿Lo marcamos como Instalado también?', otros: 'Esta tarea usa {n} materiales que están en Compras. ¿Los marcamos como Instalados también?' },
    'estimado.confianza.sourced': 'Con fuente',
    'estimado.confianza.triangulated': 'Comparado',
    'estimado.confianza.estimated': 'Aproximado',
    'estimado.plantillas.sinCatalogo': '⚠ no está en el catálogo:',
    'estimado.plantillas.sinPlantillas': 'Esta categoría todavía no tiene plantillas de servicio cargadas.',
    'estimado.plantillas.zonaGenerica': 'zona',
    'estimado.plantillas.avisoMediana': 'Ojo — la suma de las partidas (',
    'estimado.plantillas.avisoMediana.cierre': '). Se van a usar las partidas; si quieres cuadrar con la mediana, edita los renglones después.',
    'seguimientos.sistema': 'Sistema',

    'equipo.mant.enviadoAReparacion': '{nombre} se fue a reparación.',
    'equipo.mant.registrado': 'Mantenimiento registrado para {nombre}.',
    'equipo.mant.reparado': '{nombre} ya está reparado y otra vez en el taller.',
    'comun.sinResultados': 'Sin resultados',
    'materiales.estado.porComprar': 'Lista de compras',
    'materiales.estado.pedido': 'Pedido',
    'materiales.estado.listo': 'Listo / En sitio',
    'materiales.estado.instalado': 'Instalado',
    'materiales.editarAria': 'Editar {que}',
    'materiales.quitarAria': 'Quitar {que}',
    'gente.quitar.mensaje': '{nombre} va a perder el acceso a la app. Sus horas y su historial se quedan.',
    'gente.quitado': 'Ya quitaste a {nombre}.',
    'gente.errorQuitar': 'No se pudo quitar a esta persona: {motivo}',
    'gente.vacio.invita': 'Invita a tu primer trabajador para empezar.',
    'gente.vacio.busqueda': 'No hay nadie con esa búsqueda — prueba otra cosa.',
    'gente.estaPersona': 'Esta persona',
    'gente.guard.dueno': 'Al dueño del negocio no se le puede quitar ni cambiar de puesto. Para traspasar el negocio a otra persona hay que pedirlo a soporte.',
    'gente.guard.otroAdmin': 'Un Admin no puede quitar ni cambiar de puesto a otro Admin. Solo el dueño del negocio puede hacerlo.',
    'gente.guard.ultimoAdmin': 'Es el único Admin que queda. Nombra a otro Admin antes de quitar a esta persona.',
    'gente.dueno.tooltip': 'Es el dueño del negocio: no se puede quitar ni cambiar de puesto.',

    'ajustes.terminosPorDefecto': '1. Forma de pago: Se pide un anticipo del 50% para agendar el trabajo. El resto se paga al terminar.\n\n2. Fechas: Las fechas que damos son aproximadas y pueden cambiar.',
    'ajustes.correo.asunto': 'Propuesta: {{proposalTitle}}',
    'ajustes.correo.cuerpo': 'Hola,\n\nAquí puedes revisar tu propuesta:\n{{publicLink}}\n\nGracias,\n{{businessName}}',
    'ajustes.error.noCargan': 'No pudimos cargar tus ajustes. Revisa tu conexión e inténtalo otra vez.',
    'ajustes.error.recargados': 'Cargamos tus ajustes más recientes — revísalos y vuelve a guardar.',

    'avisos.hace.ahorita': 'ahorita',
    'avisos.hace.minutos': 'hace {n}m',
    'avisos.hace.horas': 'hace {n}h',
    'avisos.hace.dias': 'hace {n}d',
    'avisos.sinLeer': { uno: '{n} aviso sin leer', otros: '{n} avisos sin leer' },
    'avisos.titulo': 'Avisos',
    'contratos.campo.firma': 'Firma',
    'contratos.campo.iniciales': 'Iniciales',
    'contratos.campo.fecha': 'Fecha',
    'contratos.campo.texto': 'Texto',
    'contratos.editor.arrastraArmado': 'Arrástralo a la hoja, o haz clic donde quieras el campo de {tipo}.',
    'contratos.editor.arrastra': 'Arrastra un campo a la hoja, o haz clic en uno de aquí y luego en la hoja.',
    'finanzas.trabajos.vacio.aun': 'Aquí se van juntando los proyectos y las visitas que terminas.',
    'finanzas.trabajos.vacio.busqueda': 'Prueba con otra búsqueda u otro filtro.',

    'ajustes.contratos.sinCamposContratista': 'No pusiste campos para el contratista, así que no hay nada que firmes tú. Si agregas alguno, usa después «Firmar como contratista».',
    'ajustes.contratos.errorSubir': 'No se pudo subir: {motivo}',
    'ajustes.contratos.dependen': { uno: '{n} propuesta firmada todavía depende de esta plantilla para rearmar su copia firmada. ', otros: '{n} propuestas firmadas todavía dependen de esta plantilla para rearmar sus copias firmadas. ' },
    'ajustes.contratos.archivaPrimero': { uno: 'Abre Estimados → Firmados y dale a Guardar copia en esa propuesta — una vez guardada ya no necesita este archivo.', otros: 'Abre Estimados → Firmados y dale a Guardar copia en esas propuestas — una vez guardadas ya no necesitan este archivo.' },
    'estimado.concreto.forma.slab': 'Slab (patio, pad, driveway)',
    'estimado.concreto.forma.footing': 'Footing (corrido)',
    'estimado.concreto.forma.sonotube': 'Sonotube / pier redondo',
    'estimado.concreto.forma.steps': 'Escalones',
    'estimado.concreto.pedirReadyMix': 'Pedir (ready-mix)',
    'estimado.concreto.mezclarEnObra': 'Mezclar en obra',
    'direccion.placeholderBuscar': 'Escribe para buscar la dirección...',
    'propuesta.default.intro': 'Gracias por darnos la oportunidad de trabajar en su propiedad. Preparamos esta propuesta con cuidado, poniendo materiales de calidad y mano de obra de primera.',

    'estimado.partidas.totalZona': 'Total de {zona}',
    'estimado.partidas.borrarSeleccion': { uno: 'La partida que escogiste se va a quitar de este estimado. Esto no se puede deshacer.', otros: 'Las partidas que escogiste se van a quitar de este estimado. Esto no se puede deshacer.' },
    'estimado.sinTitulo': 'Estimado sin título',
    'estimado.margen.debajo': 'Debajo del objetivo de {objetivo}%',
    'estimado.margen.objetivo': 'Objetivo {objetivo}%',
    'estimado.margen.avisoDebajo': 'El margen está debajo del objetivo de {objetivo}%.',
    // Las DOS lecturas del margen. El trabajo propio va primero —es sobre el
    // que se decide— y el facturado solo se nombra cuando difiere, o sea
    // cuando hay pase directo. Mismas palabras en la lista, en el cajón y en
    // la cabecera del constructor: el mismo estimado no puede leerse distinto
    // según por dónde se entre.
    'estimado.margen.propio': '{valor} de tu trabajo',
    'estimado.margen.facturado': '{valor} facturado',
    'estimado.margen.paseDirecto': 'Incluye {monto} de permisos y trámites cobrados a costo, que no dejan ganancia.',
    'superadmin.sincronizado': 'Actualizado',
    'superadmin.hace.ahorita': 'ahora mismo',
    'superadmin.hace.minutos': 'hace {n}m',
    'superadmin.errorDueno': 'No se pudo crear al dueño automáticamente',
    'marca.errorSubir': 'No se pudo subir la imagen.',
    'marca.subiendo': 'Subiendo…',
    'marca.subirPortada': 'Subir imagen de portada',
    'marca.subirLogo': 'Subir logo',
    'marca.sinImagen': 'Sube una imagen para poder escogerla aquí.',
    'legal.generarIA': 'Generar con IA',
    'legal.escribiendo': 'Escribiendo…',

    'marca.subirPortada2': 'Subir imagen de portada',
    'marca.subirGaleria': 'Subir imagen a la galería',
    'marca.sinImagenPortada': 'Sube una imagen de portada para poder escogerla en el editor de propuestas.',
    'marca.sinImagenGaleria': 'Sube una imagen a la galería para poder escogerla en el editor de propuestas.',

    'proyecto.nombrePorDefecto': 'Trabajo de {cliente}',
    'proyecto.boton.creando': 'Creando…',
    'proyecto.boton.crear': 'Crear proyecto',
    'mapa.enLonche': 'En el lonche',
    'mapa.trabajando': 'Trabajando',
    'mapa.sinUbicacionSalida': 'No se guardó la ubicación de salida',
    'mapa.salioFuera': 'Salió fuera de la obra',
    'estimado.sod.forma.rectangular': 'Rectangular (5%)',
    'estimado.sod.forma.curveada': 'Orillas curveadas (10%)',
    'estimado.sod.forma.complicada': 'Complicado / muchos recortes (15%)',
    'direccion.sinResultados': 'Sin resultados — escribe la dirección más completa.',
    'video.pausa': 'Pausa',
    'video.reproducir': 'Reproducir',
    'video.ponerSonido': 'Poner sonido',
    'video.quitarSonido': 'Quitar sonido',

    'wiz.gastos.porAno': '/año',
    'finanzasNum.pl.ponTarifas': 'Pon tus tarifas de mano de obra y gastos fijos en Ajustes para ver esto',
    'finanzasNum.mant.nota.arriba': 'Las reparaciones van arriba del presupuesto de tu tarifa de gastos fijos — cada estimado que mandas se está quedando corto en el costo del equipo. Sube el mantenimiento en Ajustes › Equipo.',
    'finanzasNum.mant.nota.normal': 'Las reparaciones reales se restan en el estado de arriba, y la parte presupuestada se saca primero de los gastos fijos, así la misma reparación nunca se cobra dos veces.',
    'precios.catalogo.de': 'de',
    'precios.catalogo.conPrecio': 'con precio',
    'proyecto.encabezado.avance': 'Avance',
    'proyecto.encabezado.horasPresupuestadas': 'Horas presupuestadas',
    'proyecto.encabezado.valorContrato': 'Valor del contrato',
    'proyecto.horas.csv_entrada': 'Entrada',
    'proyecto.horas.csv_salida': 'Salida',
    'proyecto.horas.csv_duracion': 'Duración (hrs)',

    'finanzas.proyectos.sinFechaFin': 'No quedó guardada la fecha en que se terminó — se enseña la fecha de fin planeada',
    'finanzas.proyectos.vacio.aun': 'Los proyectos salen aquí en cuanto creas el primer trabajo.',
    'finanzas.proyectos.vacio.busqueda': 'Prueba con otra búsqueda u otro filtro de estatus.',
    'estimado.rapido.entregaBoton': 'Entrega',
    'estimado.rapido.rentaBoton': 'Renta',
    'estimado.anticipo': 'Anticipo',
    'metricas.puntoEquilibrio': 'Lo que te cuesta la hora = ',
    'metricas.gastosPorHora': 'Gastos fijos por hora = ',
    'metricas.loQueDebesCobrar': 'Lo que debes cobrar = ',
    'volver.alCliente': 'Volver al cliente',
    'volver.alProspecto': 'Volver a prospectos',
    'volver.alPanel': 'Volver al panel',
    'volver.aEstimados': 'Volver a estimados',
    'estimado.snapshot.tarifas': 'Tarifas:',
    'estimado.snapshot.actualizar': ' Actualiza para recalcular con los precios de ahora.',
    'estimado.snapshot.bloqueado': ' Bloqueado — el estimado ya se mandó.',

    'comun.total': 'Total',
    'comun.borrar': 'Borrar',
    'comun.nombre': 'Nombre',
    'comun.costo': 'Costo',
    'comun.atras': 'Atrás',
    'comun.vence': 'Vence',
    'comun.cliente': 'Cliente',
    'comun.prospecto': 'Prospecto',
    'comun.aviso': 'Aviso',
    'comun.quitar': 'Quitar',
    'comun.monto': 'Monto',
    'comun.seleccionar': 'Seleccionar',
    'comun.quitarSeleccion': 'Quitar selección',
    'comun.cerrarMenu': 'Cerrar menú',
    'comun.sinConexionSeguro': 'Revisa tu conexión e inténtalo otra vez. Tus datos están a salvo.',
    'ajustes.gastos.porHoraHombre': 'Se suma a cada hora-hombre',
    'plantillas.borrar.pregunta': '¿Borrar esta plantilla?',
    'plantillas.borrar.si': 'Borrar plantilla',
    'ajustes.contratos.borrar.pregunta': '¿Borrar este contrato?',
    'ajustes.contratos.borrar.si': 'Borrar contrato',
    'analitica.sinContratos': 'No hay contratos activos',
    'chat.sinVoz': 'No se escuchó nada',
    'propuesta.noSePudoRechazar': 'No se pudo rechazar',
    'propuesta.noSePudoAceptar': 'No se pudo aceptar',
    'comun.proyectosActivos': 'Proyectos activos',
    'analitica.ingresosPorCuadrilla': 'Ingresos por cuadrilla',
    'precios.misMateriales': 'Mis materiales',
    'propuesta.errorDescarga': 'Error al descargar',
    'rutas.totalClientes': 'Total de clientes',
    'empresa.nombre': 'Nombre de la compañía',
    'rutas.contratoNuevo': 'Contrato nuevo',
    'ajustes.empleados.agregar': 'Agregar empleado',
    'ajustes.gastos.nombre': 'Nombre del gasto',
    'estimado.zona.borrar': 'Borrar zona',
    'metricas.disponiblePct': '% disponible',
    'panel.empezarTrabajo2': 'Empezar un trabajo',
    'precios.importarArchivo': 'Importar archivo',
    'clientes.nuevo': 'Cliente nuevo',
    'ajustes.contratos.escogerPDF': 'Escoger PDF',
    'chat.nuevo': 'Chat nuevo',
    'comun.verTodos': 'Ver todos',
    'finanzasProy.factura': 'Factura',
    'propuesta.clienteDesconocido': 'Cliente desconocido',
    'superadmin.estaAccionEs': 'Esta acción es',
    // -- Landing (pagina publica) ---------------------------------
    // El espanol es la fuente; el ingles se REDACTO, no se tradujo palabra por
    // palabra: es copia de marca y una version literal la aplana.
    'lp.nav.laApp': 'La app',
    'lp.nav.comoEmpieza': 'Cómo empieza',
    'lp.nav.academy': 'Academy',
    'lp.nav.tienda': 'Tienda',
    'lp.nav.preguntas': 'Preguntas',
    'lp.nav.principal': 'Principal',
    'lp.nav.marca': 'Raza Pro, inicio',
    'lp.nav.abrirMenu': 'Abrir menú',
    'lp.nav.menuPrincipal': 'Menú principal',
    'lp.nav.cerrarMenu': 'Cerrar menú',
    'lp.nav.saltar': 'Saltar al contenido',
    'lp.nav.deAcademy': 'De Raza Perrona Academy',
    'lp.cta.gratis': 'Empieza gratis',
    'lp.cta.dashboard': 'Ir al dashboard',
    'lp.cta.crearCuenta': 'Crear mi cuenta gratis',

    'lp.hero.titular1': 'Tu negocio.',
    'lp.hero.titular2': 'Una sola app.',
    'lp.hero.sub': 'Estimados, cuadrillas, rutas y cobros. Todo en español y en un solo lugar.',

    'lp.reemplaza.titular': 'Hoy tu negocio vive en diez lugares distintos.',
    'lp.reemplaza.remate': 'Raza Pro los reemplaza todos.',
    'lp.reemplaza.enlace': 'Ver la app por dentro',
    'lp.reemplaza.h.excel': 'Excel',
    'lp.reemplaza.h.whatsapp': 'WhatsApp',
    'lp.reemplaza.h.cuaderno': 'El cuaderno',
    'lp.reemplaza.h.word': 'Word',
    'lp.reemplaza.h.recibos': 'Recibos en la troca',
    'lp.reemplaza.h.fotos': 'Fotos del celular',
    'lp.reemplaza.h.calendario': 'El calendario de la pared',
    'lp.reemplaza.h.llamadas': 'Llamadas perdidas',
    'lp.reemplaza.h.notas': 'Notas del teléfono',
    'lp.reemplaza.h.servilletas': 'Servilletas',
    'lp.muro.lectura': 'Hoy tu negocio vive en {lista}. {remate} los reemplaza todos.',

    'lp.producto.titular': 'Así se ve por dentro.',
    'lp.producto.lead': 'Cuatro pantallas que reemplazan diez herramientas. Sigue bajando y se van cambiando solas.',
    'lp.producto.1.titulo': 'Estimados que sí cierran',
    'lp.producto.1.texto': 'Describe el trabajo con tus palabras y Raza AI redacta el alcance completo. Sale un PDF con tu logo y el cliente lo firma desde su teléfono, sin imprimir nada.',
    'lp.producto.2.titulo': 'Tu gente, a la vista',
    'lp.producto.2.texto': 'Reloj de tiempo con geocerca: sabes quién entró, a qué hora y en qué obra. Las horas se van solas a la nómina, sin que nadie apunte nada en un cuaderno.',
    'lp.producto.3.titulo': 'El día, ya ordenado',
    'lp.producto.3.texto': 'Las paradas del día en orden, con dirección, trabajo y estado. Tu cuadrilla sabe a dónde va sin llamarte, y tú ves el avance desde donde estés.',
    'lp.producto.4.titulo': 'Una IA que sabe de',
    'lp.producto.4.texto': 'No es un robot genérico: conoce los materiales, los tiempos y las trampas de tu oficio. Te escribe alcances, correos de seguimiento y respuestas a clientes.',
    'lp.producto.oficio.techos': 'techos',
    'lp.producto.oficio.pintura': 'pintura',
    'lp.producto.oficio.concreto': 'concreto',
    'lp.producto.oficio.jardineria': 'jardinería',
    'lp.producto.oficio.remodelacion': 'remodelación',

    'lp.pasos.titular': 'De cero a operando, hoy mismo.',
    'lp.pasos.lead': 'Sin instalar nada, sin contrato y sin que nadie te tenga que capacitar.',
    'lp.pasos.1.titulo': 'Crea tu cuenta',
    'lp.pasos.1.texto': 'Con tu correo de Google. Treinta segundos y ya estás adentro. No pedimos tarjeta.',
    'lp.pasos.2.titulo': 'Haz tu primer estimado',
    'lp.pasos.2.texto': 'Dile a Raza AI de qué trabajo se trata. Ella redacta el alcance; tú pones el precio.',
    'lp.pasos.3.titulo': 'Sube a tu gente',
    'lp.pasos.3.texto': 'Cada quien con su reloj de tiempo en el teléfono. Tú ves las horas al momento.',
    'lp.pasos.4.titulo': 'Cobra sin perseguir',
    'lp.pasos.4.texto': 'Facturas, recordatorios y pagos salen solos. Tú te dedicas a vender el siguiente trabajo.',
    'lp.pasos.oficios': 'Hecha para techado, pintura, concreto, jardinería, remodelación, drywall, pisos, plomería, eléctrico y cercas.',

    'lp.banda.oficio': 'Tú ya sabes trabajar. Nosotros ponemos el sistema.',
    'lp.banda.cita': '«Yo quiero, yo puedo, y yo lo voy a lograr.»',
    'lp.banda.autorRol': ', fundador de Raza Perrona Academy',
    'lp.banda.historia': 'Conoce su historia',

    'superadmin.esIrreversible': 'Esta acción es',
    'empresa.nombrePorDefecto': 'Nombre de la compañía',
    'empresa.cortoPorDefecto': 'Compañía',
    'lp.academy.titular': 'La app es la mitad. La otra mitad es el oficio.',
    'lp.academy.parrafo1': 'Raza Pro nace de ',
    'lp.academy.parrafoMarca': 'Raza Perrona Academy',
    'lp.academy.parrafo2': ', la escuela de Aurelio Olivera. Más de 500 empresarios se han formado ahí en veinte años de oficio: cómo cobrar, cómo cerrar un trato y cómo dirigir gente sin perder la camisa.',
    'lp.academy.s1.titulo': 'Marca profesional',
    'lp.academy.s1.texto': 'Logotipo, identidad y todo lo que te hace ver serio frente al cliente.',
    'lp.academy.s2.titulo': 'Página web',
    'lp.academy.s2.texto': 'Moderna, hecha para el teléfono y lista para recibir campañas.',
    'lp.academy.s3.titulo': 'Educación',
    'lp.academy.s3.texto': 'Aprende a cobrar bien, cerrar tratos y dirigir a tu gente.',
    'lp.academy.s4.titulo': 'Documentos legales',
    'lp.academy.s4.texto': 'Contratos y cartas que protegen tu dinero cuando algo se tuerce.',
    'lp.academy.s5.titulo': 'Publicidad',
    'lp.academy.s5.texto': 'Google Ads, Facebook Ads y material impreso que sí trae llamadas.',
    'lp.academy.s6.titulo': 'Comunidad',
    'lp.academy.s6.texto': 'Acompañamiento constante de gente que ya recorrió el camino.',
    'lp.academy.programa.titulo': 'De Chalán a Empresario',
    'lp.academy.programa.texto': 'El programa con el que muchos empezaron: crea tu negocio en menos de noventa días. Si ya sabes trabajar duro, tienes lo más difícil resuelto.',
    'lp.academy.programa.boton': 'Conocer el programa',

    'lp.tienda.titular': 'Documentos que protegen tu dinero.',
    'lp.tienda.lead': 'Contratos y cartas escritos para el oficio, listos para usar hoy mismo. Se pagan una vez y son tuyos.',
    'lp.tienda.enlace': 'Ver toda la tienda',
    'lp.tienda.d1.nombre': 'Contratos residenciales',
    'lp.tienda.d1.texto': 'Para trabajo en casas. Alcance, pagos y plazos por escrito.',
    'lp.tienda.d1.nota': 'El más vendido',
    'lp.tienda.d2.nombre': 'Contratos comerciales',
    'lp.tienda.d2.texto': 'Para obra comercial, con cláusulas de orden de cambio.',
    'lp.tienda.d3.nombre': 'Cartas residenciales',
    'lp.tienda.d3.texto': 'Cobranza, aviso de retraso y cierre de trabajo.',
    'lp.tienda.d4.nombre': 'Cartas comerciales',
    'lp.tienda.d4.texto': 'Reclamaciones, prórrogas y trato con contratistas generales.',

    'lp.faq.titular': 'Lo que más nos preguntan.',
    'lp.faq.1.q': '¿De verdad es gratis para empezar?',
    'lp.faq.1.a': 'Sí. Creas tu cuenta y trabajas sin poner tarjeta. Solo pagas cuando ya estás creciendo y necesitas más usuarios, IA sin límite o integraciones.',
    'lp.faq.2.q': '¿Necesito saber de computadoras?',
    'lp.faq.2.a': 'No. Si sabes usar WhatsApp, sabes usar Raza Pro. Está hecha para contratistas, no para oficinistas, y Raza AI te va guiando cuando te atoras.',
    'lp.faq.3.q': '¿Funciona en mi teléfono?',
    'lp.faq.3.a': 'Sí, sin instalar nada. Tu gente marca su tiempo desde su celular y tú revisas todo desde donde estés — teléfono, tablet o computadora.',
    'lp.faq.4.q': '¿Mi información está segura?',
    'lp.faq.4.a': 'Vive en infraestructura con certificación SOC 2 y viaja encriptada. Solo tú y la gente que autorices ven los datos de tu negocio.',
    'lp.faq.5.q': '¿Hay quién me atienda en español?',
    'lp.faq.5.a': 'Todo el soporte es en español y con personas de verdad, que entienden el negocio del contratista. Por WhatsApp, por llamada o por chat.',
    'lp.faq.6.q': '¿Puedo cancelar cuando quiera?',
    'lp.faq.6.a': 'Cuando quieras, con un clic. Sin contrato, sin penalidad, y te devolvemos lo que no usaste del mes.',
    'lp.faq.duda': '¿Te quedó otra duda?',
    'lp.faq.whatsapp': 'Pregúntanos por WhatsApp',

    'lp.cierre.titular': 'Juntos hasta la cima.',
    'lp.cierre.lead': 'Cientos de contratistas latinos ya llevan su negocio desde aquí. Empieza hoy — te toma menos de lo que tarda un café.',
    'lp.cierre.whatsapp': 'O escríbenos por WhatsApp',
    'lp.cierre.nota': 'Empiezas gratis y sin tarjeta. Cancelas cuando quieras. Tus datos viven en infraestructura con certificación SOC 2 y el soporte lo dan personas que hablan español.',

    'lp.pie.descripcion': 'La app de Raza Perrona Academy para contratistas latinos en Estados Unidos.',
    'lp.pie.academy': 'Academy',
    'lp.pie.contacto': 'Contacto',
    'lp.pie.a1': 'Sitio principal',
    'lp.pie.a2': 'Tienda de documentos',
    'lp.pie.a3': 'De Chalán a Empresario',
    'lp.pie.a4': 'Aurelio Olivera',
    'lp.pie.derechos': '© {anio} Raza Perrona Academy. Todos los derechos reservados.',

    'lp.acceso.bienvenido': 'Bienvenido',
    'lp.acceso.sub': 'Entra con tu cuenta de Google. No pedimos tarjeta.',
    'lp.acceso.google': 'Continuar con Google',
    'lp.acceso.verificando': 'Verificando…',
    'lp.acceso.seguro': 'Inicio de sesión seguro',
    'lp.acceso.cerrar': 'Cerrar',

    'lp.idioma.etiqueta': 'Idioma',
    'lp.idioma.es': 'Español',
    'lp.idioma.en': 'English',
    'lp.idioma.bienvenida.titulo': '¿En qué idioma prefieres verla?',
    'lp.idioma.bienvenida.sub': 'Puedes cambiarlo cuando quieras desde el menú de arriba.',

    'precios.tabla.vendedor': 'Vendedor',
    'precios.tabla.unidad': 'Unidad',
    'ajustesNeg.stat.recuperacionGastos': 'Recuperación de gastos fijos',

    'chat.voz.sinVoz': 'No se escuchó nada. Intenta otra vez.',

    'visitas.fotoAlt': 'Foto de la visita {n}',
    'cuadrillas.colorAria': 'Color de cuadrilla {color}',
    'contratoServ.sinDireccionCorta': 'Sin dirección',
    'camara.abrirFotoDe': 'Abrir la foto que subió {quien}',
    'camara.subidaPor': 'La subió {quien} el',
    'facturas.vence': 'Vence {fecha}',
    'facturas.descargarRecibo': 'Descargar el recibo {id}',
    'propuesta.terminosGenericos': 'Aplican los términos normales del negocio. Comunícate con nosotros si tienes preguntas.',
    'cuenta.nombreActualizado': 'El nombre quedó como «{nombre}» en todo el sistema.',
    'admin.usuariosCuenta': { uno: '{n} usuario', otros: '{n} usuarios' },
    'plantillas.borrada': 'Se borró «{nombre}»',
    'prospectos.convertir.mensaje': '¿Convertir a {nombre} en cliente? Se crea un registro permanente y el prospecto se quita de esta lista.',
    'prospectos.yaEsCliente': '{nombre} ya es cliente.',
    'prospectos.errorGuardar': 'No se pudo guardar el prospecto. Inténtalo otra vez.',
    'rutas.abrirEnMaps': 'Abrir la ruta de {cuadrilla} en Google Maps',
    'admin.borrarAria': 'Borrar {quien}',
    'comun.quitarAria': 'Quitar {que}',
    'comun.nombreObligatorio': 'Pon un nombre',
    'app.errorGrave': 'Algo salió mal',
    'ui.fotoAlt': 'Foto',
    'ajustes.burden.explicacion': 'Préndelo para cambiar el «Burden %» que escribiste en cada empleado por un desglose partida por partida — nómina, seguros y prestaciones.',
    'ajustes.burden.ayudaPatron': '% del sueldo que pone el patrón. Lo normal es 3–6%.',
    'ajustes.burden.ayudaOtros': 'Para bonos de fin de año, ayuda para el carro, cursos, etc.',
    'panel.checklist.progreso': '{hechos} de {total} listos — termínalos para que tus estimados salgan bien.',
    'direccion.buscadorNoDisponible': 'La búsqueda de direcciones no está disponible (estatus: {estatus}). Revisa Google Cloud → Places API.',

    'prospectos.convertir.mensajeLargo': '¿Convertir a {nombre} en cliente? Se crea un registro permanente y el trato queda como GANADO. La tarjeta del prospecto desaparece del pipeline 24 horas después — los datos siguen en Clientes.',
    'direccion.buscadorNoDisponibleLargo': 'La búsqueda de direcciones no está disponible (estatus: {estatus}). Revisa que Google Cloud → Places API esté prendida y que la llave la permita.',

    'propuesta.terminosGenericos2': 'Aplican los términos normales del negocio. Comunícate con nosotros para más detalles.',
    'lp.alt.oficio': 'Carpintero clavando el armazón de madera de una casa nueva, visto desde abajo contra el cielo',
    'lp.alt.casco': 'Trabajador de espaldas con casco amarillo al final de la jornada, con las luces de la obra desenfocadas detrás',
    'lp.alt.cuadrilla': 'Cuadrilla de siete trabajadores parada frente a una losa de concreto recién armada, vista desde arriba',
    'lp.alt.cap.propuesta': 'Propuesta de Raza Pro vista por el cliente: portada con el nombre del trabajo, el logotipo del contratista y el total',
    'lp.alt.cap.partidas': 'Desglose de partidas del estimado con el total del proyecto y los botones de aceptar o rechazar',
    'lp.alt.cap.firma': 'Ventana de firma electrónica: el cliente firma con el dedo y confirma la aceptación del estimado',
    'lp.alt.cap.reloj': 'Pantalla de reloj de tiempo de Raza Pro con la lista de quién está trabajando ahora, su puesto y las horas',
    'lp.alt.cap.mapa': 'Mapa de fichajes: los puntos donde cada trabajador entró y salió del reloj durante el día',
    'lp.alt.cap.rutas': 'Pantalla de rutas de servicio con el mapa del día y las paradas de cada cuadrilla en orden, con dirección',
    'lp.alt.cap.parada': 'Detalle de una parada: dirección, progreso del trabajo, datos del contrato y notas para la cuadrilla',
    'lp.alt.cap.ia': 'Raza AI respondiendo en la app: el contratista pregunta por su cartera de estimados y la IA le contesta',

    'panel.ingresos.titulo': 'Ingresos',
    'mes.1': 'Ene',
    'mes.2': 'Feb',
    'mes.3': 'Mar',
    'mes.4': 'Abr',
    'mes.5': 'May',
    'mes.6': 'Jun',
    'mes.7': 'Jul',
    'mes.8': 'Ago',
    'mes.9': 'Sep',
    'mes.10': 'Oct',
    'mes.11': 'Nov',
    'mes.12': 'Dic',
    'panel.ingresos.semana': 'Semana {n}',

    'rango.esteMes': 'Este mes',
    'rango.esteAno': 'Este año',
    'rango.siempre': 'Todo el tiempo',
    'rango.hoy': 'Hoy',
    'rango.ultimos7': 'Últimos 7 días',

    'propuesta.descargarFirmado': 'Descargar el contrato firmado',
    'analitica.ingresosPorFrecuencia': 'Ingresos por frecuencia',
    'rutas.contratosActivos': 'Contratos activos',
    'rutas.ingresoMensual': 'Ingreso mensual',
    'rutas.verRuta': 'Ver la ruta',
    'comun.markupPct': 'Markup %',
    'comun.markup': 'Markup',
    'preciolista.markup.delDial': 'del dial',
    'comun.descargar': 'Descargar',
    'comun.vistaPrevia': 'Vista previa',
    'comun.deshacer': 'Deshacer',
    'comun.activo': 'Activo',
    'comun.subir': 'Subir',
    'comun.hoy': 'Hoy',
    'comun.ayer': 'Ayer',
    'comun.enviado': 'Enviado',
    'comun.agregar': 'Agregar',
    // ── Estimados ▸ Gastos fijos (vista unificada) ──────────────
    'gastos.tab': 'Gastos fijos',
    'gastos.titulo': 'Gastos fijos y precios',
    'gastos.sub': 'Todo lo que decide tu precio por hora, en orden y en un solo lugar.',
    'gastos.nav.titulo': 'Los pasos',
    'gastos.nav.sub': 'De arriba hacia abajo. Cada paso alimenta al siguiente.',
    'gastos.paso': 'Paso {n}',
    'gastos.listo': 'Listo',
    'gastos.faltante': 'Falta',

    // Números de arriba
    'gastos.stat.recuperacion': 'Recuperación de gastos',
    'gastos.stat.recuperacion.pista': 'Se suma a cada hora-hombre',
    'gastos.stat.equilibrio': 'Lo que te cuesta la hora',
    'gastos.stat.equilibrio.pista': 'Sin ganancia, nomás para no perder',
    'gastos.stat.meta': 'Lo que debes cobrar',
    'gastos.stat.meta.pista': 'Ya incluye {margen}% de ganancia limpia',

    // Paso 1 — Temporada y capacidad
    'gastos.p1.titulo': 'Temporada y capacidad',
    'gastos.p1.sub': 'Cuánto trabaja tu gente al año y dónde operas. De aquí sale todo lo demás.',
    'gastos.p1.semanas': 'Semanas de trabajo al año',
    'gastos.p1.semanas.ayuda': 'Cuenta solo las semanas que de veras trabajas, no las 52 del calendario.',
    'gastos.p1.dias': 'Días por semana',
    'gastos.p1.horas': 'Horas por día',
    'gastos.p1.estado': 'Estado donde operas',
    'gastos.p1.estado.ayuda': 'Fija solo el SUTA y el Workers\' Comp — son de ley estatal.',
    'gastos.p1.estado.placeholder': 'Escoge tu estado',
    'gastos.p1.oficio': 'Tu oficio',
    'gastos.p1.oficio.ayuda': 'Fija las cargas de nómina y las horas cobrables típicas de tu giro.',
    'gastos.p1.total': 'Horas de temporada por persona',
    'gastos.p1.total.pista': '{semanas} semanas × {dias} días × {horas} horas',

    // Paso 2 — Tu gente
    'gastos.p2.titulo': 'Tu gente',
    'gastos.p2.sub': 'Por puesto, no por nombre. Un renglón puede ser toda una cuadrilla.',
    'gastos.p2.agregar': 'Agregar puesto',
    'gastos.p2.dueno.pregunta': '¿Tú también andas en la obra?',
    'gastos.p2.dueno.texto': 'Si no cuentas lo que vale tu hora, tu punto de equilibrio sale más bajo de lo que es y terminas cotizando por debajo del costo.',
    'gastos.p2.dueno.boton': 'Contarme a mí',
    'gastos.p2.nombre': 'Nombre',
    'gastos.p2.nombre.placeholder': '¿Quién es?',
    'gastos.p2.puesto': 'Puesto',
    'gastos.p2.puesto.placeholder': 'Escoge un puesto',
    'gastos.p2.puesto.otro': 'Otro — lo escribo yo',
    'gastos.p2.puesto.propio': 'Nombre del puesto',
    'gastos.puesto.general': 'General',
    'gastos.puesto.general.ayuda': 'Quien anda en la obra haciendo el trabajo.',
    'gastos.puesto.foreman': 'Foreman',
    'gastos.puesto.foreman.ayuda': 'Dirige la cuadrilla y también trabaja.',
    'gastos.puesto.office': 'Oficina',
    'gastos.puesto.office.ayuda': 'Administración y contabilidad. No factura horas.',
    'gastos.p2.cuantos': '¿Cuántos?',
    'gastos.p2.cuantos.ayuda': 'Personas en este puesto',
    'gastos.p2.tipoPago': 'Cómo le pagas',
    'gastos.p2.porHora': 'Por hora',
    'gastos.p2.salario': 'Salario',
    'gastos.p2.pago': 'Pago por hora',
    'gastos.p2.pagoAnual': 'Salario anual',
    'gastos.p2.enCampo': '¿Trabaja en el campo?',
    'gastos.p2.enCampo.si': 'Sus horas se cobran a los clientes',
    'gastos.p2.enCampo.no': 'Su costo entra a gastos fijos, no a la tarifa',
    'gastos.p2.carga': 'Cargas de nómina',
    'gastos.p2.carga.auto': 'Automático',
    'gastos.p2.carga.manual': 'A mano',
    'gastos.p2.carga.propio': 'Poner mi número',
    'gastos.p2.carga.ver': 'Ver de dónde sale',
    'gastos.p2.carga.ocultar': 'Ocultar el desglose',
    'gastos.p2.cobrables': 'Horas cobrables al año',
    'gastos.p2.cobrables.auto': 'Calculado con tu oficio',
    'gastos.p2.ajustar': 'Ajustar',
    'gastos.p2.cerrarAjuste': 'Listo',
    'gastos.p2.disponible': 'Disponible %',
    'gastos.p2.disponible.ayuda': 'Lo que queda después de vacaciones, días feriados, lluvia y capacitación.',
    'gastos.p2.facturable': 'Cobrable %',
    'gastos.p2.facturable.ayuda': 'De esas horas disponibles, las que caen en un trabajo que paga.',
    'gastos.p2.costoHora': 'Costo real de una hora cobrada',
    'gastos.p2.quitar': 'Quitar este puesto',
    'gastos.p2.vacio.titulo': 'Todavía no hay puestos',
    'gastos.p2.vacio.texto': 'Agrega los puestos de tu cuadrilla. Con uno basta para que salgan los números.',

    // Paso 3 — Gastos fijos
    'gastos.p3.titulo': 'Gastos fijos',
    'gastos.p3.sub': 'Lo que pagas aunque no tengas ni un trabajo esa semana.',
    'gastos.frecuencia.mensual': 'Cada mes',
    'gastos.frecuencia.anual': 'Cada año',

    // Paso 4 — Maquinaria
    'gastos.p4.titulo': 'Recuperación de maquinaria',
    'gastos.p4.sub': 'Lo que te cuesta al año tener y operar tus máquinas.',
    'gastos.p4.nombre': 'Máquina',
    'gastos.p4.seguro': 'Seguro ($/año)',
    'gastos.p4.depreciacion': 'Se gasta $',

    // Paso 5 — Ganancia
    'gastos.p5.titulo': 'Ganancia y markup',
    'gastos.p5.sub': 'Lo único de esta página que SUBE tu precio. Todo lo de arriba es costo.',

    // Paso 6 — Mínimos
    'gastos.p6.titulo': 'Mínimos y reglas del trabajo',
    'gastos.p6.sub': 'Las reglas que protegen los trabajos chiquitos.',

    // Revisión
    'gastos.p7.titulo': 'Revisión',
    'gastos.p7.sub': 'De dónde salió cada número, por si quieres comprobarlo.',

    'chat.rapido.hoy.etiqueta': '¿Qué hago hoy?',
    'chat.rapido.hoy.pista': 'Las 3 cosas más importantes',
    'chat.rapido.hoy.prompt': 'Mira mis prospectos, seguimientos, agenda y estimados abiertos. Dame una lista numerada simple con las 3 cosas más importantes que debo hacer hoy. Habla claro, sin palabras de oficina. Responde en español.',
    'chat.rapido.mensaje.etiqueta': 'Escríbele a un cliente',
    'chat.rapido.mensaje.pista': 'Un texto o correo listo para copiar',
    'chat.rapido.mensaje.prompt': 'Ayúdame a escribir un mensaje corto y amable para un cliente (texto o correo). Si te falta el nombre o el tema, pregúntame una sola cosa primero. Luego dame algo listo para copiar y mandar. Responde en español.',
    'chat.rapido.dinero.etiqueta': '¿Cómo va mi dinero?',
    'chat.rapido.dinero.pista': 'Trabajos abiertos y qué perseguir',
    'chat.rapido.dinero.prompt': 'Explícame mi pipeline y mis estimados abiertos en palabras simples. ¿Cuánto trabajo posible tengo y en qué me conviene enfocarme para cerrarlo? Corto y práctico, sin palabras de contador. Responde en español.',
    'chat.bienvenida.titulo': '¿Te echo la mano?',
    'chat.bienvenida.cuerpo': 'Pícale a un botón de abajo — o escribe tu pregunta como si le mandaras un mensaje a un amigo.',
    'chat.entrada.placeholder': 'Escribe tu pregunta aquí…',

    'clienteDet.ia.seguimiento.prompt': 'Escribe un texto o correo amable de seguimiento para {cliente} sobre su trabajo pendiente. Corto y profesional. Responde en español.',
    'clienteDet.ia.resumen.prompt': 'Dame un resumen rápido de {cliente}: cómo va la relación, qué trabajo tiene abierto y cualquier cosa que deba recordar antes de llamarle. Responde en español.',
    'estimado.constructor.contexto_focus': 'Constructor de estimados para el cliente «{cliente}».{oficio}{alcance} {partidas} partidas, total {total}, margen {margen}%. Ayuda al contratista a poner precio a este trabajo, sugiere partidas que falten o redacta texto para la propuesta. Responde en español.',

    'finanzas.tab.numeros': 'Números',
    'finanzas.tab.proyectos': 'Proyectos',
    'finanzas.tab.contratos': 'Contratos',
    'finanzas.tab.trabajos': 'Todo el trabajo',

    'metricas.aviso.utilizacionAlta': '{quien} tiene utilización arriba del 100% ({pct}%). Se topa en 100% para el cálculo.',
    'metricas.aviso.burdenBajo': {
        uno: 'Un puesto tiene las cargas de nómina por debajo de {piso}% — lo normal en el giro es 20–30% (FICA + workers comp + prestaciones).',
        otros: '{count} puestos tienen las cargas de nómina por debajo de {piso}% — lo normal en el giro es 20–30% (FICA + workers comp + prestaciones).',
    },
    'metricas.aviso.sueldoHoraRaro': 'El pago por hora de {quien} es ${monto}. Lo normal va entre ${min} y ${max} — ¿no será el pago del día o de la semana?',
    'metricas.aviso.sueldoAnualRaro': 'El salario anual de {quien} es ${monto}. Lo normal va entre ${min} y ${max} — ¿no será el pago por hora?',
    'metricas.aviso.gastoRaro': '«{nombre}» suma ${anual} al año ({frecuencia}). Revisa si es una cifra anual metida en un renglón mensual.',
    'metricas.aviso.tarifaAlta': 'Te sale un costo de ${tarifa} por hora. Arriba de ${max} casi siempre es un dato mal tecleado — revisa sueldos y gastos.',
    'metricas.aviso.tarifaBaja': 'Te sale un costo de ${tarifa} por hora. Abajo de ${min} no alcanza a cubrir el trabajo — te faltan gastos por capturar.',
    'metricas.aviso.combustibleDoble': 'La gasolina está contada dos veces: ${gastos} al año en gastos fijos y ${maquinaria} guardados en las máquinas. Súmalos en el renglón «Combustible (toda la flota)» y deja las máquinas en cero.',
    'metricas.aviso.combustibleEnMaquina': 'Tienes ${maquinaria} al año de gasolina guardados en las máquinas. Ya se está contando, pero pásalo al renglón «Combustible (toda la flota)» de gastos fijos: la gasolina ahora va en un solo lugar.',
    'metricas.aviso.seguroDoble': 'Tienes ${gastos} al año de seguro en gastos fijos, y el workers comp ya va dentro de las cargas de nómina. Si tu póliza los junta, deja aquí solo la parte de general liability.',
    'metricas.aviso.datoInvalido': 'El campo «{campo}» de {quien} no tiene un número válido, así que no se está contando.',
    'metricas.aviso.sinVidaUtil': 'A «{nombre}» no le pusiste vida útil — se salta su depreciación.',
    'metricas.aviso.margenImposible': 'Un margen objetivo de {pct}% es imposible — déjalo abajo de {tope}%.',
    'metricas.aviso.sinEmpleados': 'Agrega al menos un empleado para sacar la tarifa de punto de equilibrio. Sin gente, la fórmula no tiene horas cobrables entre las cuales dividir.',
    'metricas.aviso.sinHorasCobrables': 'Tienes empleados pero ninguna hora cobrable. Revisa que al menos uno esté marcado como cobrable y con utilización arriba de 0%.',
    'metricas.aviso.sinGastos': 'No pusiste gastos fijos. Casi todo contratista paga renta, seguro, gasolina y programas — sin eso tu punto de equilibrio sale más bajo de lo real.',
    'metricas.aviso.equilibrioBajoSueldo': 'Tu punto de equilibrio (${equilibrio}/hr) está por debajo del sueldo promedio (${sueldo}/hr). Revisa el burden % y los gastos fijos — algo falta.',

    'propuesta.ajustes.titulo': 'Ajustes de la propuesta',
    'propuesta.ajustes.sub': 'Lo que se aplica a todas tus propuestas',

    'propuesta.seccion.legal': 'Legal y avisos',
    'propuesta.seccion.pagos': 'Pagos',
    'propuesta.seccion.contratos': 'Contratos',
    'propuesta.seccion.marca': 'Marca e imagen',

    'legal.errorTerminos': 'No se pudieron generar los términos. Inténtalo otra vez.',
    'legal.errorGarantia': 'No se pudo generar el texto de la garantía. Inténtalo otra vez.',

    /* ══════════════════════════════════════════════════════════════════════
       CAMPAÑA DE FINANZAS · BLOQUES ANCLA (Ola 0 — 2026-08-10)

       Este archivo es el conflicto GARANTIZADO de la campaña: 9.300 líneas,
       toda pantalla nueva escribe aquí, y en DOS sitios separados por ~4.600
       líneas, porque EN está tipado `Record<Clave, Texto>` y una clave sin su
       gemela rompe `tsc` para TODO el equipo, no sólo para quien la escribió.

       La solución no es coordinarse, es geometría: cada agente escribe SÓLO
       dentro de su bloque. Dos agentes no comparten ni una línea, así que el
       merge a tres bandas sale limpio por construcción, sin cuello de botella.

       REGLAS
         1. Escribe únicamente dentro de TU bloque, y en los DOS idiomas.
         2. No muevas, renombres ni borres un delimitador.
         3. No escribas fuera de los bloques mientras dure la campaña.
         4. Toda clave que añadas aquí necesita su gemela en el bloque del mismo
            nombre del objeto EN, o `tsc` falla.

       El desorden temático que esto deja se paga con un pase de ordenación del
       integrador al cerrar cada ola, que vuelve a correr las CINCO guardas de
       i18n (idioma, limpieza, llamadas, marcadores, textoSuelto) — porque
       cortar y pegar claves es justo cuando se pierde una.

       Ver docs/campana-finanzas/02-propiedad.md
       ══════════════════════════════════════════════════════════════════════ */

    /* ─── ANCLA A · INICIO ─── agente 1 · motor de cálculo ─── */

    /* PESTAÑA CONTRATOS · el rótulo que mentía y el margen que faltaba.
       La cifra grande decía «Total cobrado en rutas» y NUNCA fue lo cobrado:
       metía borradores, facturas sin pagar y trabajo que nadie ha facturado.
       Se parte en las tres que ya existían por separado —cobrado, por cobrar,
       por facturar— y la suma vieja sobrevive en la nota, con su nombre bueno. */
    'contratos.dinero.cobradoAyuda': 'Facturas pagadas. Esto sí entró.',
    'contratos.dinero.porFacturar': 'Trabajado, sin facturar',
    'contratos.dinero.porFacturarAyuda': {
        uno: '1 mes de trabajo que nadie ha facturado',
        otros: '{meses} meses de trabajo que nadie ha facturado',
    },
    'contratos.dinero.nota': 'Las tres cifras de arriba suman {total}: eso vale todo el trabajo de rutas que llevas hecho, no lo que entró al banco. Lo que entró es lo cobrado, y sólo eso.',
    'contratos.margen.filaPorFacturar': '+{monto} por facturar',

    /* El margen de cada ruta. El MRR se pintaba como si fuera ganancia limpia:
       mandar la cuadrilla cuesta, y ese costo estaba guardado desde siempre.
       Con 34 visitas cronometradas en toda la base, casi toda ruta se costea
       con los minutos que DICE el contrato — y eso se dice en voz alta, porque
       un margen sobre un tiempo que nadie cronometró no es un hecho. */
    'contratos.margen.statCosto': 'Lo que cuesta servirlas',
    'contratos.margen.statCostoAyuda': 'Cuadrilla y gastos fijos de un mes de visitas',
    'contratos.margen.statMargen': 'Lo que te queda al mes',
    'contratos.margen.statMargenSinTarifas': 'Pon tu tarifa de mano de obra en Ajustes y aquí verás lo que deja cada ruta.',
    'contratos.margen.baseMedida': {
        uno: 'Con cronómetro en 1 de {rutas} rutas activas',
        otros: 'Con cronómetro en {medidas} de {rutas} rutas activas',
    },
    'contratos.margen.baseEstimada': 'Sobre los minutos que dice el contrato: todavía nadie ha cronometrado una visita.',
    'contratos.margen.visitasHechas': {
        uno: '1 visita hecha',
        otros: '{visitas} visitas hechas',
    },
    'contratos.margen.colCosto': 'Cuesta al mes',
    'contratos.margen.colCostoAyuda': 'Los minutos de una visita por tu costo de hora-hombre, y por las visitas que llevas al mes. Cuenta el tiempo en la obra: ni el viaje ni el material.',
    'contratos.margen.colMargen': 'Te deja al mes',
    'contratos.margen.colMargenAyuda': 'Lo que cobras al mes menos lo que cuesta ir. Por esto se decide qué ruta conservar, no por cuál es la más grande.',
    'contratos.margen.minutosMedidos': {
        uno: '{minutos} min medidos en 1 visita',
        otros: '{minutos} min medidos en {visitas} visitas',
    },
    'contratos.margen.minutosContrato': '{minutos} min que dice el contrato',
    'contratos.margen.estimado': 'estimado',
    'contratos.margen.estimadoAyuda': 'Nadie ha cronometrado esta ruta. El costo sale de los minutos que dice el contrato, no de lo que de verdad se tarda la cuadrilla, así que este margen es una idea, no un hecho.',
    'contratos.margen.sinTarifas': 'Tu negocio no tiene tarifa de mano de obra, así que no hay con qué saber lo que cuesta una hora de cuadrilla. No es que servir esta ruta salga gratis.',
    'contratos.margen.sinDuracion': 'falta la duración',
    'contratos.margen.sinDuracionAyuda': 'Este contrato no dice cuánto dura una visita y nadie la ha cronometrado. Sin minutos no hay costo que sacar.',
    'contratos.margen.descartadasAyuda': {
        uno: 'Se dejó fuera 1 visita que se abrió y se cerró en el mismo minuto: eso es un toque en la pantalla, no una visita.',
        otros: 'Se dejaron fuera {descartadas} visitas que se abrieron y se cerraron en el mismo minuto: eso es un toque en la pantalla, no una visita.',
    },
    'contratos.margen.nota': 'El costo de una ruta son los minutos de la visita por tu costo de hora-hombre, que ya trae sueldo, cargas y gastos fijos. No incluye el material ni el viaje, así que lo que deja de verdad es un poco menos.',
    'contratos.margen.notaSinDuracion': {
        uno: 'Hay 1 ruta activa sin duración anotada y sin cronometrar: no suma nada al costo de arriba, así que sale dejando más de lo que deja.',
        otros: 'Hay {rutas} rutas activas sin duración anotada y sin cronometrar: no suman nada al costo de arriba, así que salen dejando más de lo que dejan.',
    },
    'contratos.movil.margen': 'de margen',
    /* ─── ANCLA A · FIN ─── */

    /* ─── ANCLA B · INICIO ─── agente 2 · interfaz de Finanzas ─── */

    /* El vocabulario de la incertidumbre. Ver components/finance/financeUi.tsx:
       hasta hoy un cero MEDIDO y un cero POR FALTA DE CAPTURA se escribían
       igual, «$0.00», y quien lo leía entendía «no costó nada». Estos textos
       son lo que se escribe en su lugar. Registro: se le habla a un
       contratista, así que «anotar», no «registrar contablemente». */
    'finanzas.dato.sinCapturar': 'sin capturar',
    'finanzas.dato.sinCapturarAyuda': 'Está en cero porque nadie lo ha anotado, no porque no haya costado nada.',
    'finanzasNum.pl.materialesSinCapturar': 'Nadie ha anotado materiales ni gastos en los proyectos. El trabajo costó algo; lo que falta es apuntarlo.',
    'finanzasNum.libro.gastosSinCapturar': 'Sin capturar',
    'finanzasNum.libro.gastosSinCapturarAyuda': 'No hay ni un gasto anotado en tus proyectos. No es que hayan salido gratis — es que nadie los ha apuntado.',
    'finanzasNum.libro.notaSinGastos': 'Sin gastos anotados, la ganancia bruta de arriba es el contrato entero: no hay nada que restarle. Apunta los materiales de cada trabajo y empezará a ser una ganancia de verdad.',

    /* LO QUE PROMETISTE AL VENDER. Sale del presupuesto: el precio de venta y
       el costo que el estimador calculó, partida por partida. Es una promesa,
       no un resultado — el texto no puede insinuar que sea lo que se ganó. */
    'finanzasNum.promesa.titulo': 'Lo que prometiste al vender',
    'finanzasNum.promesa.subtitulo': {
        uno: 'El margen que tu presupuesto calculó para 1 obra, el día que la vendiste.',
        otros: 'El margen que tus presupuestos calcularon para {obras} obras, el día que las vendiste.',
    },
    'finanzasNum.promesa.vendido': 'Vendido en',
    'finanzasNum.promesa.costoCotizado': 'Costo cotizado',
    'finanzasNum.promesa.ganancia': 'Ganancia prometida',
    'finanzasNum.promesa.margen': 'Margen prometido',
    'finanzasNum.promesa.bajoObjetivo': {
        uno: '1 obra por {monto} se vendió por debajo de tu objetivo del {objetivo}%. Eso no se arregla trabajando mejor: se arregla al cotizar.',
        otros: '{obras} obras por {monto} se vendieron por debajo de tu objetivo del {objetivo}%. Eso no se arregla trabajando mejor: se arregla al cotizar.',
    },
    'finanzasNum.promesa.nota': 'Esto es lo que dijiste que ibas a ganar, no lo que ganaste. Compáralo con la escalera de arriba: la diferencia entre las dos es lo que te está costando de más ejecutar el trabajo.',
    'finanzasNum.promesa.notaSinCotizar': {
        uno: 'Esto es lo que dijiste que ibas a ganar, no lo que ganaste. Hay además 1 obra vendida sin un presupuesto con costos detrás, así que no entra en esta cuenta.',
        otros: 'Esto es lo que dijiste que ibas a ganar, no lo que ganaste. Hay además {obras} obras vendidas sin un presupuesto con costos detrás, así que no entran en esta cuenta.',
    },

    /* El tercer motivo por el que la ganancia neta de una obra no se sostiene
       (`netProfitBlockers` → 'sin-costo'). Los otros dos ya tenían texto
       —'tooltip_sin_tarifas' y 'sin_horas'—; éste nació sin él porque el motor
       se escribió sin poder tocar i18n. Todavía no lo pinta nadie: lo usará
       quien sustituya el interruptor `laborCosted` de la pestaña Proyectos. */
    'finanzas.proyectos.sin_costo': 'sin capturar',
    'finanzas.proyectos.tooltip_sin_costo': 'A este trabajo no le han anotado ni materiales ni horas. La ganancia neta saldría igual al monto entero, y eso no es ganancia.',

    /* El alcance de cada tarjeta. El selector de rango vive arriba del todo y se
       lee como si mandara sobre la pantalla entera; de las cinco tarjetas mueve
       DOS. Que cada una diga a qué periodo pertenece es más barato que hacer que
       el rango gobierne de verdad, y quita la mentira, que es lo urgente. */
    'finanzas.alcance.siempre': 'Desde el inicio',
    'finanzas.alcance.ayudaSiempre': 'Esta tarjeta cuenta todo desde que abriste el negocio en la app. El selector de arriba no la mueve.',
    'finanzas.alcance.ayudaRango': 'Esta tarjeta sí cambia con el selector de arriba.',
    'finanzasNum.kpi.recurrenteAlcance': 'Contratos de servicio activos ahora mismo. El selector de arriba no lo mueve.',
    'finanzasNum.kpi.sinCobrarAlcance': 'Valor de proyectos sin ningún pago registrado, desde el inicio. El selector de arriba no lo mueve.',

    /* La escalera del P&L cuenta sólo trabajo ejecutado desde la Ola 1. */
    'finanzasNum.pl.tituloEjecutado': 'Ganancias y pérdidas del trabajo ejecutado',
    'finanzasNum.pl.subtituloEjecutado': 'Sólo obras en marcha o terminadas. La cartera firmada que nadie ha empezado va aparte, aquí abajo.',

    /* Lo que se escribe donde iba la ganancia neta cuando no se sostiene. Es más
       largo que el aviso de la tira de arriba a propósito: ocupa el sitio exacto
       de una cifra, así que tiene que explicar por qué esa cifra saldría MAL, no
       sólo que falta un dato. */
    'finanzasNum.pl.netaSinTarifas': 'Tu negocio todavía no tiene tarifa de mano de obra ni de gastos fijos. Sin ellas no hay forma de saber qué costó una hora de cuadrilla.',
    'finanzasNum.pl.netaSinHoras': 'Nadie ha fichado horas contra estas obras. Sin horas, la mano de obra entra como cero y la ganancia neta saldría igual a la bruta.',
    'finanzasNum.pl.netaSinCosto': 'Estas obras no tienen anotado ni un peso de gasto ni una hora de trabajo. La ganancia neta saldría igual al contrato entero, y eso no es ganancia.',
    'finanzasNum.pl.netaSinDato': 'No hay con qué calcular la ganancia neta todavía. El motivo está a la izquierda.',
    'finanzasNum.pl.sinEjecutado': 'Todavía no hay ninguna obra en marcha ni terminada, así que no hay trabajo del que sacar una ganancia.',

    /* La mitad que salió de la escalera. Sale aparte, NO desaparece: son
       $140.259,89 de contrato firmado y borrarlos de la pantalla sería cambiar
       una mentira por otra. */
    'finanzasNum.cartera.titulo': 'Cartera firmada, todavía sin empezar',
    'finanzasNum.cartera.subtitulo': 'Trabajo aceptado o programado. Va fuera de la cuenta de arriba a propósito: nadie ha movido una pala, así que no hay costo que restarle.',
    'finanzasNum.cartera.valor': 'Valor firmado',
    'finanzasNum.cartera.obras': 'Obras',
    'finanzasNum.cartera.cobrado': 'Cobrado por adelantado',
    'finanzasNum.cartera.nota': 'Si esto cuenta como ganancia el día que se firma o el día que se trabaja lo decide tu contabilidad, no la app. Aquí va separado y con su nombre, que es cierto de las dos maneras.',
    'finanzasNum.libro.conCartera': {
        uno: 'Solo proyectos. Aquí sí entra la cartera firmada que todavía no empieza: 1 obra por {monto} a la que no hay costo que restar.',
        otros: 'Solo proyectos. Aquí sí entra la cartera firmada que todavía no empieza: {obras} obras por {monto} a las que no hay costo que restar.',
    },

    /* Un veredicto necesita las dos mitades de la comparación. Con presupuesto
       puesto y cero reparaciones anotadas, la tarjeta escribía «Debajo del
       presupuesto» en verde a 13 de 13 talleres que nunca apuntaron una. */
    'finanzasNum.mant.tituloLlano': 'Mantenimiento de equipo',
    'finanzasNum.mant.sinReparaciones': 'Todavía no hay ni una reparación anotada, así que no hay nada que comparar contra el presupuesto.',
    'finanzasNum.mant.nota.sinNada': 'Sin una sola reparación anotada, esta tarjeta no puede decirte si vas bien o mal. Apunta las reparaciones en Inventario › Mantenimiento y la comparación empezará a significar algo.',

    /* La tira de confianza: lo que falta capturar, ANTES que ninguna cifra. De
       15 obras ejecutadas en toda la base, 1 tiene un gasto y 5 un fichaje — el
       vacío es el estado dominante, no la excepción de la letra chica. */
    'finanzas.confianza.titulo': 'Faltan datos para que estas cifras sean ciertas',
    /* Dos claves y no una porque hay DOS números que concuerdan por su cuenta
       —las bloqueadas y el total— y el diccionario sólo sabe pluralizar con uno.
       Cuando están bloqueadas todas, el total sobra y desaparece el problema;
       cuando no, `total` es 2 o más por aritmética (si fuera 1 estarían todas) y
       basta con pluralizar por `obras`. Sin esto sale «1 de las 1 obras». */
    'finanzas.confianza.resumen': {
        uno: 'Una de tus {total} obras en marcha o terminadas no tiene con qué calcular una ganancia: {monto} de facturación.',
        otros: '{obras} de tus {total} obras en marcha o terminadas no tienen con qué calcular una ganancia: {monto} de facturación.',
    },
    'finanzas.confianza.resumenTodas': {
        uno: 'Tu única obra en marcha o terminada no tiene con qué calcular una ganancia: {monto} de facturación.',
        otros: 'Ninguna de tus {obras} obras en marcha o terminadas tiene con qué calcular una ganancia: {monto} de facturación.',
    },
    'finanzas.confianza.sinTarifas': 'Tu negocio no tiene tarifa de mano de obra, así que no hay forma de saber qué costó una hora de cuadrilla.',
    'finanzas.confianza.sinTarifasAccion': 'Poner las tarifas',
    'finanzas.confianza.sinHoras': 'Hay obras sin una sola hora fichada contra ellas.',
    'finanzas.confianza.sinHorasDetalle': {
        uno: 'Hay 1 obra por {monto} con tarifa puesta y cero horas fichadas: su ganancia neta sale idéntica a la bruta.',
        otros: 'Hay {obras} obras por {monto} con tarifa puesta y cero horas fichadas: su ganancia neta sale idéntica a la bruta.',
    },
    'finanzas.confianza.sinHorasAccion': 'Ir al reloj de horas',
    'finanzas.confianza.sinCosto': 'Hay obras sin un solo gasto anotado: ni materiales, ni renta de equipo, ni subcontratos.',
    'finanzas.confianza.sinCostoAccion': 'Abrir los proyectos',

    /* EN QUÉ SE FUERON LAS HORAS. De 407,9 horas fichadas en producción, el
       costeo de obra ve el 2,5 %: 90 de 98 fichajes no traen obra y se caen de
       la cuenta sin que nadie lo diga. El contratista las paga igual. */
    'finanzasNum.horas.titulo': 'En qué se fueron las horas',
    'finanzasNum.horas.sub': {
        uno: '{horas} h fichadas en 1 turno cerrado',
        otros: '{horas} h fichadas en {turnos} turnos cerrados',
    },
    'finanzasNum.horas.vacioTitulo': 'Todavía no hay horas fichadas',
    'finanzasNum.horas.vacioTexto': 'En cuanto tu gente empiece a fichar, aquí verás cuántas de esas horas le suben el costo a una obra y cuántas no.',
    /* El veredicto va ANTES del reparto: es lo único que hay que leer si se lee
       una sola línea de la tarjeta. */
    'finanzasNum.horas.veredictoPoco': 'La mayoría de tus horas no está atribuida a ninguna obra: sólo el {pct}% llega a un trabajo.',
    'finanzasNum.horas.veredictoBien': 'El {pct}% de tus horas está atribuido a una obra.',
    'finanzasNum.horas.consecuencia': 'Las horas sin obra las pagas igual, pero no le suben el costo a ningún trabajo. Por eso el costo de mano de obra de arriba está incompleto y tu ganancia se ve mejor de lo que es.',
    'finanzasNum.horas.obra': 'En una obra',
    'finanzasNum.horas.obraAyuda': 'Se eligió la obra al fichar. Son las únicas horas que hoy le suben el costo a un trabajo.',
    'finanzasNum.horas.huerfanas': 'En una obra que ya no existe',
    'finanzasNum.horas.huerfanasAyuda': 'Se eligió una obra al fichar y esa obra se borró después.',
    'finanzasNum.horas.general': 'Sin obra',
    'finanzasNum.horas.generalAyuda': 'Nadie eligió obra al fichar: taller, traslados, ruta, lo que sea.',
    'finanzasNum.horas.h': '{horas} h',
    'finanzasNum.horas.fichajesPct': {
        uno: '1 fichaje · {pct}%',
        otros: '{turnos} fichajes · {pct}%',
    },
    'finanzasNum.horas.enRuta': 'De esas, {horas} h sí saben a qué ruta de servicio fueron. No están perdidas: es que el costeo de obra sólo sabe preguntar por obras.',
    /* Las huérfanas son un DATO ROTO, no una decisión de nadie, y por eso llevan
       mención aparte: no se arreglan fichando mejor de aquí en adelante. */
    'finanzasNum.horas.huerfanasNota': {
        uno: 'Un fichaje por {horas} h apunta a una obra borrada. No es una decisión, es un dato roto: alguien sí lo atribuyó y se perdió al borrar la obra.',
        otros: '{turnos} fichajes por {horas} h apuntan a obras borradas. No es una decisión, es un dato roto: alguien sí los atribuyó y se perdió al borrar la obra.',
    },
    'finanzasNum.horas.abiertos': {
        uno: 'Hay 1 turno abierto ahora mismo. No entra en estas cuentas hasta que se cierre.',
        otros: 'Hay {turnos} turnos abiertos ahora mismo. No entran en estas cuentas hasta que se cierren.',
    },

    /* EL DESGLOSE DE LOS GASTOS FIJOS. Ni una de estas cifras se calcula en la
       pantalla: salen de `metrics.breakdown`, lo mismo que Ajustes lleva
       enseñando desde siempre en «cómo se calculó». Lo que faltaba era leerlo
       donde se ve el renglón. */
    'finanzasNum.gf.verDesglose': 'Ver de qué se compone',
    'finanzasNum.gf.titulo': 'Lo que te cuesta tener el negocio abierto, al año',
    'finanzasNum.gf.manoIndirecta': 'Sueldo que no se le cobra a ningún cliente: {puesto}',
    'finanzasNum.gf.equipo': 'Camionetas y maquinaria: depreciación, gasolina y mantenimiento',
    'finanzasNum.gf.totalAnual': 'Total al año',
    'finanzasNum.gf.reparto': 'Ese total se reparte entre las {horas} horas cobrables que calculas al año: {tarifa} por cada hora fichada.',
    'finanzasNum.gf.sinHorasCobrables': 'Tu negocio todavía no tiene horas cobrables calculadas, así que este total no se reparte entre nada y el renglón de arriba sale en cero.',
    'finanzasNum.gf.cargado': 'A las obras en marcha o terminadas se les cargaron {horas} h fichadas, y de ahí sale el renglón de arriba.',
    'finanzasNum.gf.sinHoras': 'Nadie ha fichado horas contra estas obras, así que de todo esto no se le ha cargado ni un peso a ningún trabajo.',
    'finanzasNum.gf.menosMant': 'Del renglón de arriba ya salieron {monto} de mantenimiento presupuestado: se cuentan abajo, con las reparaciones que de verdad se pagaron.',

    /* ─── ANCLA B · FIN ─── */

    /* ─── ANCLA C · INICIO ─── agente 3 · captura de gastos ─── */
    /* ─── ANCLA C · FIN ─── */
    // ── Texto que estaba escrito a mano en el JSX ────────────────────────────
    //  Rescatado el 11 de agosto de 2026. Todo esto se quedaba en su idioma
    //  cuando alguien cambiaba el de la app: los diálogos de confirmación
    //  —que son justo donde no puedes permitirte no entender lo que aceptas—,
    //  el aviso del panel Enviar y la ficha de condiciones del sitio.

    // Panel Enviar de la propuesta.
    'propuesta.compartir.pendientes.resto': {
        uno: ' cosa que arreglar antes de poder enviarla',
        otros: ' cosas que arreglar antes de poder enviarla',
    },
    // Antes decía siempre «falta el contrato», y la advertencia puede ser el
    // título, el alcance, las partidas o la vigencia. Ahora cuenta, no adivina.
    'propuesta.compartir.puedesenviar': {
        uno: 'Puedes enviarla — falta 1 cosa',
        otros: 'Puedes enviarla — faltan {count} cosas',
    },

    // Borrar una cita desde la agenda.
    'agenda.cita.borrar.titulo': '¿Borrar esta cita?',
    'agenda.cita.borrar.mensaje': '«{titulo}» se quita de la agenda y no se puede recuperar.',

    // Salir del editor de estimados con cambios sin guardar.
    'estimado.salir.titulo': 'Tienes cambios sin guardar',
    'estimado.salir.mensaje': 'Si sales ahora se pierden las partidas y los ajustes que hiciste. Cancela y pulsa Guardar para conservarlos.',
    'estimado.salir.confirmar': 'Salir sin guardar',

    // Convertir un prospecto en cliente sin dirección.
    'prospectos.convertir.faltadireccion.titulo': 'Falta la dirección',
    'prospectos.convertir.faltadireccion.mensaje': 'Antes de crear el cliente, escribe la dirección de la propiedad de {nombre}. Es la que se usa para las propuestas y para planificar las rutas.',
    'prospectos.convertir.faltadireccion.confirmar': 'Guardar y convertir',
    'prospectos.convertir.faltadireccion.placeholder': 'Calle y número, ciudad',
    'prospectos.convertir.faltadireccion.invalida': 'Escribe la dirección completa, con el número de la calle.',
    // Aviso, no bloqueo: al convertir, la dirección repetida se deja pasar.
    'prospectos.convertir.direccion.tambienEnCliente': 'Ojo: esa dirección ya la tiene {nombre}, en Clientes. Si es la misma propiedad, sigue adelante. Si es otra vivienda del mismo edificio, agrega el número de apartamento o unidad.',
    'prospectos.convertir.direccion.tambienEnProspecto': 'Ojo: esa dirección ya la tiene {nombre}, en Prospectos. Si es la misma propiedad, sigue adelante. Si es otra vivienda del mismo edificio, agrega el número de apartamento o unidad.',

    // Sacar un proyecto de un estado terminal (terminado / cancelado).
    'proyecto.estado.guardia.titulo': '¿Cambiar un proyecto {estado}?',
    'proyecto.estado.guardia.mensaje': '«{nombre}» está marcado como {estado}. ¿Lo pasamos a «{nuevo}»?',
    'proyecto.estado.guardia.confirmar': 'Sí, cambiar el estado',
    'proyecto.estado.guardia.cancelar': 'Dejarlo como está',

    'panel.errorCarga': 'No se pudieron cargar tus datos',
    'contratoServ.faltaprecio': ' · falta el precio',

    // Ayudas de la calculadora de burden.
    'ajustes.burden.ayudaFica': 'Lo que aportas al Seguro Social y Medicare por cada empleado. Lo fija el gobierno federal; no se cambia.',
    'ajustes.burden.ayudaFuta': 'Impuesto federal de desempleo. También es fijo.',
    'ajustes.burden.ayudaSuta': 'Impuesto ESTATAL de desempleo. Tu porcentaje llega por carta de tu estado cada año y cambia según tu historial de despidos. Lo normal está entre 1% y 6%.',
    'ajustes.burden.ayudaWc': 'Seguro de accidentes de trabajo. El porcentaje exacto viene en tu póliza; depende del oficio (techos y concreto pagan bastante más que jardinería), del estado y de tu historial.',

    // Condiciones del sitio. Estaba entera en inglés.
    'obra.titulo': 'Condiciones del sitio',
    'obra.sub': 'Lista informativa — solo el multiplicador de dificultad de abajo cambia el precio',
    'obra.anchoAcceso': 'Ancho de la reja / del acceso',
    'obra.pulgadas': 'Pulgadas',
    'obra.avisoAccesoEstrecho': 'Acceso estrecho — el trabajo a mano va más lento; súbele al multiplicador de dificultad de abajo.',
    'obra.tipoSuelo': 'Tipo de suelo',
    'obra.suelo.arena': 'Arena',
    'obra.suelo.arcilla': 'Arcilla',
    'obra.suelo.rocoso': 'Rocoso',
    'obra.avisoArcilla': 'Suelo de arcilla — puede que necesites geotextil y ~30% extra por esponjamiento; agrégalos como partidas.',
    'obra.distanciaAcarreo': 'Distancia de acarreo',
    'obra.pies': 'Pies',
    'obra.avisoBomba': 'Considera bomba de concreto o carretilla motorizada',
    'obra.pendiente': 'Desnivel / pendiente',
    'obra.avisoPendiente': 'Terreno con pendiente — puede que haya que renivelar; agrégalo al alcance o a las partidas.',
    'obra.clima': 'Clima',
    'obra.clima.frio': 'Frío',
    'obra.clima.normal': 'Normal',
    'obra.clima.calor': 'Calor',
    'obra.avisoCalor': 'El concreto fragua rápido — usa retardante, más cuadrilla y programa temprano',
    'obra.avisoFrio': 'Fraguado lento — puede que hagan falta calentadores o cobijas térmicas',
    'obra.dificultad.estandar': 'Estándar',
    'obra.dificultad.dificil': 'Difícil',
    'obra.dificultad.extremo': 'Extremo',

    // Documento premium de la propuesta. Estaba entero en inglés y es lo que ve
    // el CLIENTE: un contratista que trabaja en español mandaba encabezados en
    // inglés a un cliente que quizá no lo lee.
    'propuesta.premium.terminos.titulo': 'Términos y condiciones',
    'propuesta.premium.terminos.pordefecto': 'Aplican los términos comerciales estándar. Contáctanos para más detalles.',
    'propuesta.premium.garantia.titulo': 'Información de la garantía',
    'propuesta.premium.zelle.titulo': 'Aceptamos pago por Zelle',
    'propuesta.premium.zelle.desc': 'Puedes pagar cómodamente por Zelle con estos datos:',
    'propuesta.premium.banco.titulo': 'Aceptamos transferencia bancaria',
    'propuesta.premium.banco.desc': 'Puedes mandar el pago directo desde tu banco con estos datos:',
    'propuesta.premium.banco.ahorros': ' · Ahorros',
    'propuesta.premium.banco.cheques': ' · Cheques',
    'propuesta.premium.banco.numeros': 'Ruta {ruta} · Cuenta {cuenta}',

    'app.sesion.como': 'Sesión iniciada como',
    'prospectos.columna.vacia': 'Aquí no hay nada',
    'rutas.mapa.cargando': 'Cargando el mapa…',
    'calc.campo.titulo': 'Calculadoras de campo',
    'calc.campo.sub': 'Cuentas rápidas de medición — escoge una calculadora, mete las medidas y pasa el resultado directo a tu estimado.',
    'metricas.facturablePct': '% facturable',
    'metricas.ajustesNegocio': 'Ajustes → Negocio',
    'metricas.sinHorasFacturables': 'No se puede calcular el punto de equilibrio: no hay horas facturables. En {donde}, marca al menos un empleado como facturable y ponle un {disponible} y un {facturable} mayores que cero.',

    // Tipos de equipo. El value que se guarda sigue en inglés.
    'equipo.tipo.minicargador': 'Minicargador',
    'equipo.tipo.excavadora': 'Excavadora',
    'equipo.tipo.podadora': 'Podadora',
    'equipo.tipo.camion': 'Camión',
    'equipo.tipo.remolque': 'Remolque',
    'equipo.tipo.herramienta': 'Herramienta de mano (gas/eléctrica)',
    'equipo.tipo.otro': 'Otro',

    'equipo.estado.enreparacion': 'En reparación',
    'equipo.mant.tipo.aceite': 'Cambio de aceite',
    'equipo.mant.tipo.reparacion': 'Reparación',
    'equipo.mant.tipo.inspeccion': 'Inspección',
    'equipo.mant.tipo.otro': 'Otro',
    // Faltaba el cierre de la frase de «Mandar a [En reparación]»: iba en inglés.
    'equipo.mant.mandartaller.cola': ' — ahí se queda hasta que lo marques reparado.',

    'calc.patron.triangular': 'Triangular (más denso)',
    'calc.patron.cuadrado': 'Cuadrícula',

    // ── En qué idioma le llega la propuesta al cliente ───────────────────────
    //  `idioma.es` / `idioma.en` ya existen para el SELECTOR, donde el nombre va
    //  suelto y por eso en mayúscula («Español»). Dentro de una frase el español
    //  los escribe en minúscula —«se manda en español»— y el inglés no cambia,
    //  así que hacen falta las dos versiones.
    'idioma.enfrase.es': 'español',
    'idioma.enfrase.en': 'inglés',
    'propuesta.compartir.idioma.titulo': 'Idioma de la propuesta',
    'propuesta.compartir.idioma.aviso': 'Tu cliente la va a leer en {idioma}.',
    'propuesta.compartir.idioma.explica': 'Las propuestas se entregan siempre en inglés. Lo que escribiste no cambia hasta que lo traduzcas aquí abajo.',
    'propuesta.compartir.idioma.escritaen': 'La escribiste en {idioma}.',
    'propuesta.compartir.idioma.traducir': 'Traducir el contenido a {idioma}',
    'propuesta.compartir.idioma.traduciendo': 'Traduciendo…',
    'propuesta.compartir.idioma.retraducir': 'Volver a traducir',
    'propuesta.compartir.idioma.falta': 'El contenido sigue en {idioma}: el título, el alcance y las partidas le van a llegar así.',
    'propuesta.compartir.idioma.obsoleta': 'Cambiaste el contenido después de traducirlo. Tu cliente leería la versión vieja.',
    'propuesta.compartir.idioma.lista': 'Contenido traducido y revisado.',
    'propuesta.compartir.idioma.original': 'Tu texto original en {idioma} no se toca.',
    'propuesta.compartir.idioma.error': 'No se pudo traducir. Inténtalo otra vez.',
    'propuesta.compartir.idioma.errorvacio': 'La traducción llegó vacía. Inténtalo otra vez.',

    // Revisión del texto legal.
    'propuesta.legal.pendiente': 'Falta que revises los términos y la garantía.',
    'propuesta.legal.porque': 'Una cláusula mal traducida cambia lo que prometes. Léelas antes de mandarla.',
    'propuesta.legal.revisar': 'Revisar términos y garantía',
    'propuesta.legal.terminosnegocio': 'Los términos que va a leer tu cliente salen de Ajustes, tal y como los escribiste.',
    'propuesta.legal.terminosnegocio.porque': 'Esta propuesta está escrita en {idioma} y la IA no traduce los términos a propósito: un texto legal tiene que salir siempre redactado igual. Si los tuyos no están en inglés, tu cliente los va a leer en {idioma}.',
    'propuesta.legal.terminosnegocio.ir': 'Revisarlos en Ajustes',
    'propuesta.legal.titulo': 'Revisa el texto legal traducido',
    'propuesta.legal.intro': 'Esto es un compromiso con tu cliente, así que lo traduce la IA pero lo apruebas tú. Puedes corregir lo que quieras aquí mismo.',
    'propuesta.legal.terminos': 'Términos y condiciones',
    'propuesta.legal.garantia': 'Garantía',
    'propuesta.legal.original': 'Lo que escribiste ({idioma})',
    'propuesta.legal.traducido': 'Lo que va a leer tu cliente ({idioma})',
    'propuesta.legal.aprobar': 'Lo he leído y lo apruebo',
    'propuesta.legal.aprobado': 'Aprobado',

    // Términos del negocio sin versión en el idioma de entrega.

    // Idioma del contrato adjunto.
    'contrato.idioma.etiqueta': 'Idioma del contrato',
    'contrato.idioma.ayuda': 'No se traduce: es el documento que subiste. Se guarda para avisarte si no coincide con el de la propuesta.',
    'propuesta.contrato.desajuste': 'La propuesta va en {propuesta} y el contrato adjunto está en {contrato}.',
    'propuesta.contrato.desajuste.porque': 'Tu cliente leería la propuesta en un idioma y firmaría en otro. Cambia el contrato por uno en {propuesta}, o manda la propuesta en {contrato}.',

    // ── El taller: dirección y geocerca de la oficina central ───────────────
    // El destino de fichaje que no es una obra. Hasta ahora las horas de taller
    // caían en «general», sin sitio contra el que medir la distancia.
    'ajustes.nav.oficina': 'Taller',
    'ajustes.oficina.titulo': 'Tu taller',
    'ajustes.oficina.sub': 'La dirección donde tu gente ficha cuando no está en una obra, y qué tan lejos de ella sigue contando como «en el taller».',
    'ajustes.oficina.cargando': 'Cargando…',
    'ajustes.oficina.errorCargar': 'No se pudo cargar la ficha del taller. Revisa tu conexión y vuelve a intentar.',
    'ajustes.oficina.nombre': 'Cómo se llama',
    'ajustes.oficina.nombre.placeholder': 'Taller / Oficina central',
    'ajustes.oficina.nombre.ayuda': 'Es lo que ve tu gente al elegir dónde ficha.',
    'ajustes.oficina.direccion': 'Dirección',
    'ajustes.oficina.direccion.placeholder': 'Escribe y elige una de la lista',
    'ajustes.oficina.direccion.ayuda': 'Elige una sugerencia: así la dirección viene normalizada por Google y la geocerca cae donde debe.',
    'ajustes.oficina.buscar': 'Guardar',
    'ajustes.oficina.guardando': 'Guardando…',
    'ajustes.oficina.radio': 'Radio de la geocerca (pies)',
    'ajustes.oficina.radio.ayuda': 'Entre {min} y {max} pies. Por debajo de {min} nadie ficha nunca dentro: el GPS de un teléfono ya yerra decenas de pies.',
    'ajustes.oficina.estado': 'Geocerca del taller',
    'ajustes.oficina.activa': 'Puesta',
    'ajustes.oficina.inactiva': 'Sin poner',
    'ajustes.oficina.activaCon': 'Puesta en: ',
    'ajustes.oficina.sinCoords': 'Todavía no hay coordenadas: los fichajes en el taller no se miden contra nada.',
    'ajustes.oficina.faltaLlave': 'Falta la llave de Google Maps, así que no se puede ubicar la dirección.',
    'ajustes.oficina.geoFallo': 'No se encontró esa dirección, así que no se guardó nada. La geocerca del taller sigue como estaba.',
    'ajustes.oficina.sinGuardar': 'Lo que hay escrito todavía no es la geocerca. Pulsa Guardar para ubicar esta dirección.',
    'ajustes.oficina.guardado': 'Listo. La geocerca del taller quedó en {radio} pies.',
    'ajustes.oficina.errorGuardar': 'No se pudo guardar: {motivo}',
    'ajustes.oficina.actualizado': 'Última vez que se tocó: {cuando}',
    'ajustes.oficina.actualizadoPor': 'Última vez que se tocó: {cuando}, por {quien}',
    // El candado es lo que decide si a un peón se le deja fichar. Se explica
    // entero aquí porque cuando esté encendido, la llamada que llega es «no me
    // deja fichar» y nadie relaciona las dos cosas.
    'ajustes.oficina.candado.titulo': 'Solo se ficha en la obra asignada',
    'ajustes.oficina.candado.etiqueta': 'Exigir que la obra esté asignada',
    'ajustes.oficina.candado.ayuda': 'Con esto encendido, quien no esté asignado a una obra no puede fichar en ella. Enciéndelo solo cuando tus obras tengan gente asignada: si no, tu cuadrilla se queda sin poder fichar.',
    'ajustes.oficina.candado.inerte': 'Todavía no hace nada. Se guarda tu decisión, pero la comprobación entra en una versión próxima.',
    'ajustes.oficina.candado.guardado': 'Guardado.',

    /* Pasar el reloj de un destino a otro. Una clave por cada marcador de
       `TRANSFER_MARKERS` (services/database.ts), con el nombre del marcador tal
       cual: así se ve de un vistazo si la base aprendió a rechazar algo nuevo y
       nadie escribió la frase, que es como el trabajador acaba leyendo
       «ON_LUNCH» en la pantalla.
       Cada frase dice QUÉ HACER, no qué falló: quien las lee está de pie en una
       obra con el teléfono en la mano y no puede hacer nada con un diagnóstico. */
    'reloj.transferir.SHIFT_NOT_FOUND': 'Ese turno ya no está. Actualiza la pantalla y vuelve a intentarlo.',
    'reloj.transferir.SHIFT_NOT_OPEN': 'Ese turno ya está cerrado. Marca entrada otra vez para empezar uno nuevo.',
    'reloj.transferir.NOT_YOUR_SHIFT': 'Ese turno es de otra persona. Solo puedes mover el tuyo.',
    'reloj.transferir.ON_LUNCH': 'Termina tu lunch primero y luego mueve tu reloj.',
    'reloj.transferir.ALREADY_ON_TARGET': 'Ya estás checado ahí. Escoge otro lugar para seguir trabajando.',
    'reloj.transferir.INVALID_TARGET_KIND': 'Ese destino no se puede usar. Escoge una obra, una ruta de servicio o el taller.',
    'reloj.transferir.TARGET_NOT_FOUND': 'Esa obra ya no existe. Escoge otra o pásate a horas de taller.',
    'reloj.transferir.NO_OFFICE_CONFIGURED': 'Todavía no hay dirección del taller. Pídele a la oficina que la agregue en Ajustes.',
    'reloj.transferir.NOT_ASSIGNED_TO_PROJECT': 'No estás asignado a esa obra. Pídele al admin que te agregue.',
    'reloj.transferir.TRANSFER_BEFORE_CLOCK_IN': 'No puedes mover el reloj a una hora anterior a tu entrada. Revisa la hora de tu teléfono.',
    'reloj.transferir.TRANSFER_IN_FUTURE': 'La hora de tu teléfono va adelantada. Ajústala y vuelve a intentarlo.',
    /* Respaldo para lo que no es ninguno de los anteriores. Dice dónde quedó la
       persona porque el traspaso es de todo o nada: si falló, sigue checada
       donde estaba y no tiene que volver a marcar entrada. */
    'reloj.transferir.error': 'No se pudo mover tu reloj. Sigues checado donde estabas — inténtalo otra vez.',
    /* El botón que hace la faena. Con el turno abierto SUSTITUYE a «Marcar
       entrada»: es el mismo desplegable y el mismo botón, y lo único que cambia
       es que en vez de abrir un turno lo mueve. */
    'reloj.transferir.boton': 'Mover mi reloj',
    'reloj.transferir.listo': 'Listo. Tu reloj está ahora en {destino}.',
} as const;

export type Clave = keyof typeof ES;

export const EN: Record<Clave, Texto> = {
    'nav.grupo.ventas': 'Sales',
    'nav.grupo.operaciones': 'Operations',
    'nav.grupo.equipo': 'Team',
    'nav.panel': 'Dashboard',
    'nav.clientes': 'Clients',
    'nav.estimados': 'Estimates',
    'nav.precios': 'Pricing Library',
    'nav.seguimientos': 'Follow-ups',
    'nav.proyectos': 'Projects',
    'nav.rutas': 'Service Routes',
    'nav.inventario': 'Inventory',
    'nav.finanzas': 'Finance',
    'nav.gente': 'Team',
    'nav.reloj': 'Time Clock',
    'nav.agenda': 'Schedule',
    'nav.chat': 'Chat history',
    'nav.tienda': 'Raza Store',
    'nav.ajustes': 'Settings',
    'nav.salir': 'Sign out',
    'nav.abrirMenu': 'Open menu',
    'nav.cerrarMenu': 'Close menu',
    'nav.colapsar': 'Collapse menu',
    'nav.expandir': 'Expand menu',

    'idioma.titulo': 'Language',
    'idioma.es': 'Spanish',
    'idioma.en': 'English',

    'estado.cargando': 'Loading Raza Pro…',
    'estado.reintentar': 'Try again',
    'estado.sinConexion': 'Check your connection and try again.',
    'estado.errorClientes': "Couldn’t load your clients",
    'estado.errorProspectos': "Couldn’t load your leads",

    'panel.bienvenida': 'Welcome back, {nombre}',
    'panel.clientes': { uno: '{count} client', otros: '{count} clients' },
    'panel.prospectosNuevos': { uno: '{count} new lead', otros: '{count} new leads' },
    'panel.proyectosActivos': { uno: '{count} active project', otros: '{count} active projects' },
    'panel.empezarTrabajo': 'Start a Job',
    'panel.clientesRecientes': 'Recent clients',
    'panel.verTodos': 'View all',
    'panel.sinClientes': 'No clients yet.',
    'panel.sinClientesPista': 'Tap Client above to add your first one.',

    'wiz.paso.perfil': 'Your business',
    'wiz.paso.contacto': 'Contact',
    'wiz.paso.logo': 'Logo',
    'wiz.paso.temporada': 'Season',
    'wiz.paso.empleados': 'Employees',
    'wiz.paso.gastos': 'Overhead',
    'wiz.paso.equipo': 'Equipment',
    'wiz.paso.precios': 'Pricing',
    'wiz.paso.reglas': 'Rules',
    'wiz.paso.resumen': 'Summary',
    'wiz.atras': 'Back',
    'wiz.cancelar': 'Cancel',
    'wiz.siguiente': 'Next',
    'wiz.siguienteLargo': 'Next step',
    'wiz.guardando': 'Saving…',
    'wiz.guardarCerrar': 'Save & close',
    'wiz.arrancando': 'Starting…',
    'wiz.terminar': 'Finish',
    'wiz.terminarLargo': 'Finish & launch',
    'wiz.falta': 'To finish setup you still need: {que}',
    'wiz.faltaMas': { uno: '(and {count} more thing)', otros: '(and {count} more things)' },
    'prospecto.nombre.aria': 'Lead name',
    'prospecto.nombre.placeholder': 'Lead name',
    'prospecto.etapa.aria': 'Pipeline stage',
    'prospecto.creado': 'Created ',
    'prospecto.cerrar.aria': 'Close lead panel',
    'prospecto.proximoPaso.titulo': ' Next Action',
    'prospecto.proximoPaso.vence': 'Due ',
    'prospecto.proximoPaso.hecho.title': 'Mark this step done',
    'prospecto.proximoPaso.hecho': 'Done',
    'prospecto.proximoPaso.aria': 'Next step',
    'prospecto.proximoPaso.placeholder': 'What’s the next step?',
    'prospecto.proximoPaso.fecha.aria': 'Next step date',
    'prospecto.contacto.titulo': 'Contact Information',
    'prospecto.telefono.aria': 'Phone number',
    'prospecto.telefono.placeholder': 'Phone number',
    'prospecto.correo.aria': 'Email address',
    'prospecto.correo.placeholder': 'Email address',
    'prospecto.direccion.placeholder': 'Start typing to search the address…',
    'prospecto.valor.etiqueta': 'Est. Value',
    'prospecto.tipoTrabajo.etiqueta': 'Type of Work',
    'prospecto.tipoTrabajo.sinDefinir': 'Not set',
    'prospecto.notas.etiqueta': 'Notes',
    'prospecto.notas.placeholder': 'Customer preferences, gate codes, site details…',
    'prospecto.marcarPerdido': 'Mark Lost',
    'prospecto.convertir': 'Convert to Client',
    'prospecto.yaEsCliente': ' Client Converted',
    'prospecto.crearPropuesta': 'Build a proposal',
    'prospecto.propuestas.titulo': ' Proposals',
    'prospecto.propuestas.nueva': 'New',
    'prospecto.propuestas.vacio': 'Nothing quoted yet. Building a proposal creates no client: they become one the day you mark it paid.',
    'prospecto.propuestas.sinTitulo': 'Untitled proposal',

    // ── Proposals built for a lead ──────────────────────────────
    'propuestaProspecto.insignia': 'Lead',
    'propuestaProspecto.insigniaTitulo': 'Proposal built for a lead. Marking it paid or accepted creates the client record.',
    'propuestaProspecto.convertido': '{nombre} is now a client',
    'propuestaProspecto.errorConvertir': 'Could not create the client record for {nombre}. Please try again.',
    'herrEst.zona.destino': 'Add items to zone',
    'herrEst.zona.sinZona': 'No zone',
    'herrEst.zona.nombrePlaceholder': 'Zone name…',
    'herrEst.rapido.titulo': 'Quick Add',
    'herrEst.item.manoDeObraGeneral': 'General Labor',
    'herrEst.buscar.placeholder': 'Search your pricebook…',
    'herrEst.vacio.titulo': 'Your pricebook is empty',
    'herrEst.vacio.texto': 'Add your first material and you can reuse it on every estimate from now on.',
    'herrEst.grafica.centro': 'Cost',
    'herrEst.margen.titulo': 'Net Margin',
    'herrEst.margen.ganancia': 'Est. Profit',
    'herrEst.margen.sliderAria': 'Target margin',
    'herrEst.margen.meta': 'Goal (',
    'herrEst.margen.bloqueado': 'This estimate already went to the client — the margin stays as they saw it.',
    'herrEst.margen.propio': 'on your work',
    'herrEst.margen.facturado': 'billed',
    'herrEst.margen.objetivoReal': 'Target {objetivo} · actual {real}',
    'herrEst.margen.pedido': 'You asked for {pedido} and no price moved.',
    'herrEst.margen.motivo.minimoDeServicio': 'Your minimum service call sets this price, not the line items: below that minimum the customer pays the minimum, so moving the rates changes nothing you charge.',
    'herrEst.margen.motivo.sinTrabajoPropio': 'There is no work of your own to scale here: everything on this estimate is pass-through, and pass-through bills at cost.',
    'herrEst.margen.motivo.objetivoInvalido': 'Margin is measured on the price, so it has to be under 100%.',
    'herrEst.margen.motivo.margenIndependiente': 'What this estimate charges does not respond to the dial — usually a flat discount that eats the whole subtotal — so the margin comes out the same at any factor.',
    'herrEst.margen.motivo.techoInalcanzable': 'At these costs the most this job can return is {techo}. To go past that you have to lower the cost, not raise the rate.',
    'herrEst.margen.motivo.fueraDeRango': 'That margin is outside what this estimate can reach by raising or lowering its prices.',
    'herrEst.margen.motivo.codo': 'The factor could not be confirmed — the minimum service call or a discount that eats the subtotal got in the way — so no price is moved blindly.',
    'herrEst.desglose.titulo': 'Where the money goes',
    'herrEst.desglose.total': 'Revenue (Pre-Tax)',
    'herrEst.descuento.titulo': 'Discount',
    'herrEst.descuento.dolaresAria': 'Flat discount',
    'herrEst.descuento.porcentajeAria': 'Percent discount',
    'herrEst.descuento.pasaSubtotal': 'Discount is bigger than the subtotal — capped at ',
    'herrEst.anticipo.sliderAria': 'Deposit percent',
    'herrEst.anticipo.alFirmar': 'Due at signing',
    'herrEst.cargos.titulo': 'Fees & Tax',
    'herrEst.cargos.viaje': 'Trip Charge (',
    'herrEst.impuesto.titulo': 'Charge tax',
    'herrEst.impuesto.tasa': 'Tax rate %',
    'herrEst.impuesto.manoDeObra': 'Charge tax on labor too?',
    'ajustesNeg.rapido.titulo': 'At a Glance',
    'ajustesNeg.rapido.sub': 'The three numbers that drive every estimate.',
    'ajustesNeg.stat.puntoEquilibrio': 'Break-Even',
    'ajustesNeg.stat.tarifaMeta': 'Target Rate',
    'ajustesNeg.rapido.verQueArreglar': 'Tap to see what needs fixing →',
    'ajustesNeg.perfil.titulo': 'Business Profile',
    'ajustesNeg.perfil.sub': 'Your logo and contact details — what shows up on your proposals.',
    'ajustesNeg.perfil.subirLogo': 'Upload Logo',
    'ajustesNeg.perfil.nombreNegocio': 'Business Name',
    'ajustesNeg.perfil.moneda': 'Currency',
    'ajustesNeg.perfil.zonaServicio': 'Service Area',
    'ajustesNeg.perfil.errorLogo': 'Failed to upload logo.',
    'ajustesNeg.temporada.titulo': 'Season & Capacity',
    'ajustesNeg.temporada.sub': 'Define your working year so we can figure out how much overhead you have to recover per hour.',
    'ajustesNeg.temporada.semanasAnio': 'Weeks per Year',
    'ajustesNeg.temporada.diasSemana': 'Days per Week',
    'ajustesNeg.temporada.horasDia': 'Hours per Day',
    'ajustesNeg.empleados.titulo': 'Employees for Job Costing',
    'ajustesNeg.empleados.dueno.pregunta': 'Are you working in the field?',
    'ajustesNeg.empleados.dueno.texto': 'Most owner-operators forget to add themselves. If you spend time on jobs, your labor cost needs to be in here — otherwise your break-even comes out lower than it really is.',
    'ajustesNeg.empleados.dueno.boton': 'Add owner ($60k/yr)',
    'ajustesNeg.campo.puesto': 'Role',
    'ajustesNeg.empleados.burden.ayuda': '(FICA + WC + benefits, typ. 20–30%)',
    'ajustesNeg.empleados.cobrable': 'Billable (does field work)',
    'ajustesNeg.empleados.disponiblePct': 'Available %',
    'ajustesNeg.empleados.cobrablePct': 'Billable %',
    'ajustesNeg.empleados.vacio.titulo': 'No employees yet',
    'ajustesNeg.gastos.titulo': 'Overhead Expenses',
    'ajustesNeg.gastos.sub': 'What you pay whether or not you have work: insurance, rent, software.',
    'ajustesNeg.gastos.agregar': 'Add Expense',
    'ajustesNeg.maquinaria.titulo': 'Equipment Cost Recovery',
    'ajustesNeg.maquinaria.agregar': 'Add Equipment',
    'ajustesNeg.precios.margenMeta': 'Target Net Profit Margin (%)',
    'clienteDet.cerrarFicha': 'Close client details',
    'clienteDet.cerrar': 'Close',
    'clienteDet.editarCliente': 'Edit client',
    'clienteDet.borrarCliente': 'Delete client',
    'clienteDet.tab.resumen': 'Overview',
    'clienteDet.tab.proyectos': 'Projects',
    'clienteDet.tab.estimados': 'Estimates',
    'clienteDet.stat.totalCobrado': 'Total Earned',
    'clienteDet.stat.porCerrar': 'Open Pipeline',
    'clienteDet.stat.porCerrar.pista': { uno: '{count} open quote', otros: '{count} open quotes' },
    'clienteDet.stat.trabajosActivos': 'Active Jobs',
    'clienteDet.stat.trabajosActivos.pista': 'In progress or scheduled',
    'clienteDet.contacto.titulo': 'Contact & Access',
    'clienteDet.contacto.telefono': 'Phone',
    'clienteDet.contacto.email': 'Email',
    'clienteDet.contacto.sinEmail': 'No email',
    'clienteDet.contacto.direccion': 'Address',
    'clienteDet.contacto.codigoPorton': 'Gate code',
    'clienteDet.notas.titulo': 'Property Notes',
    'clienteDet.notas.vacio': 'No notes for this property yet.',
    'clienteDet.proyectos.vacio.titulo': 'No projects yet',
    'clienteDet.proyectos.vacio.texto': 'Projects show up here once the client accepts an estimate.',
    'clienteDet.proyectos.vacio.accion': 'Start a new estimate',
    'clienteDet.proyectos.empezo': 'Started ',
    'clienteDet.proyectos.completado': ' complete',
    'clienteDet.estimados.retomar': 'Continue where you left off',
    'clienteDet.estimados.otro': 'Start Another Estimate',
    'clienteDet.estimados.nuevo': 'Create New Estimate',
    'clienteDet.estimados.vacio.titulo': 'No estimates yet',
    'clienteDet.estimados.vacio.texto': 'Create one above to get this client started.',
    'clienteDet.estimados.creado': 'Created ',
    'clienteDet.estimados.verPropuesta': 'Preview Proposal',
    'clienteDet.estimados.editarPropuesta': 'Edit Proposal',
    'clienteDet.estimados.borrarBorrador': 'Delete Draft',
    'clienteDet.estimados.sinTitulo': 'Untitled Estimate',
    'clienteDet.borrarCliente.titulo': 'Delete Client',
    'clienteDet.borrarBorrador.titulo': 'Delete draft?',
    'clienteDet.borrarBorrador.mensaje': 'Remove "{titulo}"? This draft will be gone for good.',
    'clienteDet.borrarBorrador.error': 'Could not delete draft',
    'finanzasProy.tab.numeros': 'Analysis',
    'finanzasProy.tab.gastos': 'Expenses',
    'finanzasProy.tab.facturas': 'Invoices',
    'finanzasProy.kpi.contrato': 'Contract value',
    'finanzasProy.kpi.ganancia': 'Profit so far',
    'finanzasProy.kpi.bajoMeta': ' — below your target',
    'finanzasProy.costos.titulo': 'Where the money went',
    'finanzasProy.costos.materiales': 'Materials purchased',
    'finanzasProy.costos.gastos': 'Expenses (receipts)',
    'finanzasProy.costos.total': 'Total job cost',
    'finanzasProy.porCobrar.titulo': 'Outstanding receivables: ',
    'finanzasProy.porCobrar.facturas': { uno: 'invoice awaiting payment.', otros: 'invoices awaiting payment.' },
    'finanzasProy.gastos.nuevo': 'Log expense or receipt',
    'finanzasProy.gastos.descripcion': 'Description',
    'finanzasProy.gastos.monto': 'Amount ($)',
    'finanzasProy.gastos.proveedor': 'Vendor',
    'finanzasProy.gastos.cat.material': 'Material',
    'finanzasProy.gastos.cat.maquinaria': 'Equipment',
    'finanzasProy.gastos.cat.subcontratista': 'Subcontractor',
    'finanzasProy.gastos.cat.otro': 'Other',
    'finanzasProy.gastos.foto': 'Snap receipt photo',
    'finanzasProy.gastos.guardar': 'Save expense',
    'finanzasProy.gastos.verRecibo': 'View receipt',
    'finanzasProy.gastos.agregarFoto': 'Attach receipt photo',
    'finanzasProy.gastos.borrar': 'Delete expense',
    'finanzasProy.gastos.vacio.titulo': 'No expenses logged yet',
    'finanzasProy.gastos.vacio.texto': 'Snap a receipt on site and it lands here, against this job’s profit.',
    'finanzasProy.facturas.crear': 'Create invoice',
    'finanzasProy.facturas.nueva': 'New invoice',
    'finanzasProy.facturas.numero': 'Invoice #',
    'finanzasProy.facturas.vence': 'Due date',
    'finanzasProy.facturas.conceptoDesc': 'Description (e.g. Phase 1 – Grading)',
    'finanzasProy.facturas.agregarConcepto': 'Add line item',
    'finanzasProy.facturas.total': 'Invoice total',
    'finanzasProy.facturas.pdf': 'Download as PDF',
    'finanzasProy.facturas.borrar': 'Delete invoice',
    'finanzasProy.facturas.vacio.titulo': 'No invoices yet',
    'finanzasProy.facturas.vacio.texto': 'Create the first invoice to track what this job has billed and collected.',
    'equipo.titulo': 'Inventory',
    'equipo.descripcion': 'Track tools and equipment — what’s in the shop and what’s out on a job.',
    'equipo.agregar': 'Add Item',
    'equipo.tab.items': 'Items',
    'equipo.tab.reparacion': 'In Repair',
    'equipo.tab.mantenimiento': 'Maintenance',
    'equipo.buscar': 'Search by name or type…',
    'equipo.buscar.mantenimiento': 'Search by item, type or description…',
    'equipo.estado.enTaller': 'In Shop',
    'equipo.campo.ubicacion': 'Location',
    'equipo.campo.proximoServicio': 'Next service',
    'equipo.vacio.titulo': 'No equipment yet',
    'equipo.vacio.desc': 'Add your machines and vehicles to track where they are and when they need service.',
    'equipo.vacio.filtro': 'Nothing matches this search or filter.',
    'equipo.limpiarFiltros': 'Clear filters',
    'equipo.reparacion.vacio': 'Nothing in repair',
    'equipo.reparacion.vacio.desc': 'Items land here when you tick “Send to In Repair” while logging maintenance. They stay until you mark them repaired.',
    'equipo.reparacion.gastado': 'Spent on this item',
    'equipo.reparacion.marcar': 'Mark repaired',
    'equipo.mant.totalGastado': 'Total spend',
    'equipo.mant.registrados': 'Repairs logged',
    'equipo.mant.enTaller': 'Currently in shop',
    'equipo.mant.vacio': 'No maintenance logged yet',
    'equipo.mant.vacio.desc': 'Use “Log Maintenance” on an item to record a repair or service.',
    'equipo.tabla.equipo': 'Item',
    'equipo.tabla.tipo': 'Type',
    'equipo.tabla.trabajo': 'Work needed',
    'equipo.tabla.hechoPor': 'Performed by',
    'equipo.tabla.fecha': 'Date',
    'equipo.tabla.costo': 'Cost',
    'equipo.modal.agregar.titulo': 'Add New Equipment',
    'equipo.modal.editar.titulo': 'Equipment details',
    'equipo.boton.borrar': 'Remove',
    'equipo.editar.ok': 'Details updated',
    'equipo.guardar.error': 'Could not save the details',
    'equipo.borrar.titulo': 'Remove this tool?',
    'equipo.borrar.texto': 'Takes "{nombre}" out of the inventory. Its maintenance history and what it cost are NOT deleted.',
    'equipo.borrar.confirmar': 'Yes, remove it',
    'equipo.borrar.ok': 'Tool removed',
    'equipo.borrar.error': 'Could not remove it',
    'equipo.campo.nombre': 'Equipment Name / ID*',
    'equipo.guardar': 'Save Equipment',
    'equipo.mant.campo.trabajo': 'What needs to be done?*',
    'equipo.mant.btnEnviar': 'Send to Repair',
    'equipo.mant.btnGuardar': 'Save Log',
    'app.error.titulo': 'Something went wrong',
    'app.error.desc': 'An unexpected error occurred. The team has been notified so it can be fixed.',
    'app.error.recargar': 'Reload the page',
    'app.acceso.denegado': 'Access Denied',
    'app.acceso.desc': 'Your account has not been invited yet. Ask an Admin to add your email in the Workforce tab.',
    'app.acceso.reintentar': 'Try Again',
    'app.acceso.salir': 'Sign Out',
    'app.workspace.error_titulo': 'Couldn’t load your workspace',
    'app.settings.error_titulo': 'Couldn’t load your settings',
    'app.setup.titulo': 'Setting Up Your Workspace',
    'app.setup.desc': 'Your admin is still configuring this workspace. You’ll have access once setup is complete.',
    'app.setup.salir': 'Sign Out',
    'app.propuesta.link_invalido': 'Invalid Proposal Link',
    'direccion.autocompletar.placeholder': 'Start typing to search address...',
    'direccion.autocompletar.sinconfigurar': 'Address search not configured. Enable Places API (New) in Google Cloud Console.',
    'direccion.autocompletar.error': 'Address search error — see console for details.',
    'direccion.autocompletar.cargando': 'Loading address search...',
    'clienteDet.ia.contexto': 'Client · ',
    'clienteDet.ia.seguimiento.label': 'Write a follow-up message',
    'clienteDet.ia.siguientePaso.label': 'Suggest next steps',
    'clienteDet.ia.siguientePaso.prompt': 'Based on this client’s projects and estimates, what should I do next to move them forward?',
    'clienteDet.ia.resumen.label': 'Summarize this client',
    'clienteDet.borrarCliente.confirmar': 'Delete Client',
    'clienteDet.cancelar': 'Cancel',
    'clienteDet.borrarBorrador.confirmar': 'Delete draft',
    'clienteDet.borrarBorrador.cancelar': 'Cancel',
    'clienteDet.borrarBorrador.listo': 'Draft deleted',
    'clienteDet.estimados.pista.toca': 'Tap ',
    'clienteDet.estimados.seguirEditando': 'Continue Editing',
    'clienteDet.estimados.pista.texto': 'to jump back into the estimate builder — no need to start over.',
    'clienteDet.estimados.pista.borrar': 'Right-click any draft to delete it.',
    'clienteDet.estimados.masPartidas': ' lines',
    'clienteDet.menu.editarCliente': 'Edit client',
    'clienteDet.menu.borrarCliente': 'Delete client',
    'clienteDet.menu.seguirEditando': 'Continue editing',
    'clienteDet.menu.borrarBorrador': ' Delete draft',
    'clientes.modal.error_guardar': 'Could not save the client. Please try again.',
    'clientes.modal.titulo_editar': 'Edit Client',
    'clientes.modal.titulo_nuevo': 'New Client',
    'clientes.modal.cerrar': 'Close',
    'clientes.modal.nombre': 'Client Name / Business',
    'clientes.modal.nombre_placeholder': 'e.g. John Doe or Acme Corp',
    'clientes.modal.correo_placeholder': 'john@example.com',
    'clientes.modal.direccion': 'Property Address',
    'clientes.modal.direccion_placeholder': 'Start typing to search address...',
    'clientes.modal.gate': 'Gate Code',
    'clientes.modal.contacto': 'Preferred Contact',
    'clientes.modal.notas': 'Property Notes',
    'clientes.modal.notas_placeholder': 'Dog in backyard, park on street, etc.',
    'clientes.modal.tags': 'Tags',
    'clientes.modal.tag_placeholder': 'Add custom tag (e.g. HOA, Insurance Claim, Bad Payer)...',
    'clientes.modal.agregar': 'Add',
    'clientes.modal.cancelar': 'Cancel',
    'clientes.modal.guardando': 'Saving…',
    'clientes.modal.actualizar': 'Update Client',
    'clientes.modal.crear': 'Create Client',
    'prospectos.etapa.ganadoEsFinal': '{nombre} is already Won. Winning created their client record and moved the proposals across, so the stage no longer changes. Anything else about this job is handled from Clients.',
    'prospectos.etapa.perdidoNoSaltaAGanado': '{nombre} is marked Lost. If the deal came back, move it to an active stage first — Contacted, Site Visit — and win it from there.',
    'prospectos.etapa.bloqueadaAria': 'Stage closed: this lead is already Won',
    'prospecto.ganado.banner': 'Won. Their record lives in Clients now — make changes there.',
    'prospecto.tarjeta.ganadoCerrado': 'Won — already a client',
    'prospecto.perdido.comoRevivir': 'If the deal came back, change the stage above and carry on from there.',
    'prospectos.borrar.soloGanados': 'Only won leads can be cleared from the board, and “{nombre}” is not one. Nothing was deleted.',
    'prospectos.borrar.titulo': { uno: 'Clear 1 won lead off the board?', otros: 'Clear {count} won leads off the board?' },
    'prospectos.borrar.mensaje': { uno: 'Their client record stays untouched, with its proposals and jobs — what goes is the card in the Won column. This cannot be undone.', otros: 'Their client records stay untouched, with their proposals and jobs — what goes are the cards in the Won column. This cannot be undone.' },
    'prospectos.borrar.confirmar': 'Yes, clear them',
    'prospectos.borrar.cancelar': 'Keep them',
    'prospectos.borrar.listo': { uno: '1 won lead cleared off the board', otros: '{count} won leads cleared off the board' },
    'prospectos.borrar.error': 'Could not clear them. Please try again.',
    'prospectos.borrar.unoTitulo': 'Clear {nombre} off the board?',
    'prospecto.borrar.uno': 'Clear off the board',
    'crm.duplicado.sinNombre': 'an unnamed record',
    'crm.duplicado.cliente.telefono': 'That phone number already belongs to {nombre}, who is in Clients. Open that client instead of creating another, or fix the number.',
    'crm.duplicado.cliente.email': 'That email already belongs to {nombre}, who is in Clients. Open that client instead of creating another, or fix the email.',
    'crm.duplicado.cliente.direccion': 'That address already belongs to {nombre}, in Clients. Each address goes on one record only. If this is another home in the same building, add the apartment or unit number.',
    'crm.duplicado.prospecto.telefono': 'That phone number already belongs to {nombre}, who is in Leads. Open that lead instead of creating another, or fix the number.',
    'crm.duplicado.prospecto.email': 'That email already belongs to {nombre}, who is in Leads. Open that lead instead of creating another, or fix the email.',
    'crm.duplicado.prospecto.direccion': 'That address already belongs to {nombre}, in Leads. Each address goes on one record only. If this is another home in the same building, add the apartment or unit number.',
    'crm.duplicado.cliente.nombre': 'You already have a client named {nombre}. Open that client instead of creating another. If this is a different person, use their full name to tell them apart (last name, or middle initial).',
    'crm.duplicado.prospecto.nombre': 'You already have a lead named {nombre}. Open that lead instead of creating another. If this is a different person, use their full name to tell them apart (last name, or middle initial).',
    'portal.avatar.alt': 'Profile',
    'portal.usuario.cliente': 'Client',
    'portal.abrirMenu': 'Open main menu',
    'portal.nav.pagosFacturas': 'Billing & Invoices',
    'portal.pie.derechos': 'All rights reserved.',
    'estimados.error.noSeGuardo': 'That change did not save. Check your connection and try again.',
    'estimados.crear.titulo': 'Start a Job',
    'estimados.crear.alcance_general': 'General Job',
    'estimados.crear.error_crear_cliente': 'Error creating client. Please try again.',
    'estimados.crear.cerrar': 'Close',
    'estimados.crear.paso1': 'Step 1 of 3',
    'estimados.crear.para_quien': 'Who is this job for?',
    'estimados.crear.cliente_existente': 'Existing Client',
    'estimados.crear.prospecto': 'Lead',
    'estimados.crear.cliente_nuevo': 'New Client',
    'estimados.crear.buscar_cliente': 'Search by name or address...',
    'estimados.crear.sin_clientes_titulo': 'No clients found',
    'estimados.crear.sin_clientes_texto': 'No one matches that name or address.',
    'estimados.crear.crear_cliente': 'Create new client',
    'estimados.crear.prospecto_explicacion': 'No client is created yet. The proposal stays attached to the lead, and becomes a client the day you mark it paid.',
    'estimados.crear.buscar_prospecto': 'Search by name, phone or address...',
    'estimados.crear.sin_prospectos_titulo': 'No open leads',
    'estimados.crear.sin_prospectos_texto': 'Add one on the Leads board, then come back here to quote it.',
    'estimados.crear.sin_prospectos_filtro_titulo': 'No leads found',
    'estimados.crear.sin_prospectos_filtro_texto': 'No lead matches that search.',
    'estimados.crear.prospecto_sin_datos': 'No phone or address',
    'estimados.crear.prospecto_falta_direccion': 'Where is the job?',
    'estimados.crear.prospecto_direccion_nota': 'The proposal prints it, and it is what route planning uses.',
    'estimados.crear.prospecto_guardar_seguir': 'Save & continue',
    'estimados.crear.prospecto_titulo_fijo': 'Proposal for {nombre}',
    'estimados.crear.prospecto_sub_falta': 'All that is missing is where the job happens.',
    'estimados.crear.prospecto_sub_confirma': 'Confirm where the job happens.',
    'estimados.crear.prospecto_continuar': 'Continue',
    'estimados.crear.prospecto_error_direccion': 'Could not save the address. Please try again.',
    'estimados.crear.ejemplo_nombre': 'John Doe',
    'estimados.crear.campo_direccion': 'Property Address',
    'estimados.crear.buscar_direccion': 'Start typing to search address...',
    'estimados.crear.crear_y_seguir': 'Create & Continue',
    'estimados.crear.paso2': 'Step 2 of 3',
    'estimados.crear.tipo_trabajo': 'Type of Work',
    'estimados.crear.que_oficio': 'What trade for',
    'estimados.crear.blanco_titulo': 'Start blank',
    'estimados.crear.blanco_texto': 'Skip the trade and scope — go straight to building line items.',
    'estimados.crear.siguiente': 'Next',
    'estimados.crear.paso3': 'Step 3 of 3',
    'estimados.crear.que_haces': 'What Are You Doing?',
    'estimados.crear.ayuda_alcance': 'Pick the closest match — templates and tools will filter to this job.',
    'estimados.crear.buscar_alcance': 'Search work type...',
    'estimados.crear.sin_alcance': 'No work type matches',
    'estimados.crear.ver_todo': 'Show all',
    'estimados.crear.armar_estimado': 'Build Estimate',
    'empleadoDet.stat.horas14': '14-Day Hours',
    'empleadoDet.stat.diasTrabajados': 'Days Active',
    'empleadoDet.stat.proyectos': 'Projects',
    'empleadoDet.historial.titulo': 'Recent Work History',
    'empleadoDet.historial.vacio.titulo': 'No work logs yet',
    'empleadoDet.historial.vacio.texto': 'Nothing recorded in the past 14 days.',
    'empleadoDet.log.taller': 'General Shop',
    'empleadoDet.log.adentro': 'Active',
    'empleadoDet.log.entrada.aria': 'Clock in',
    'empleadoDet.log.salida.aria': 'Clock out',
    'empleadoDet.log.guardar.aria': 'Save time log',
    'empleadoDet.log.guardar.title': 'Save',
    'empleadoDet.log.editar.aria': 'Edit time log',
    'empleadoDet.log.editar.title': 'Edit Time',
    'estimado.constructor.atras': 'Back',
    'estimado.constructor.zona_borrada_con_partidas': { uno: 'Zone "{name}" and {count} item deleted.', otros: 'Zone "{name}" and {count} items deleted.' },
    'estimado.constructor.zona_borrada': 'Zone "{name}" deleted — its items moved to No Zone.',
    'estimado.constructor.margen_aplicado': 'This estimate’s target margin is now {value}%. Existing line items were left alone.',
    'estimado.constructor.markup_aplicado': 'This estimate’s material markup is now {value}%. Existing line items were left alone.',
    'estimado.constructor.aplicar_a_partidas': { uno: 'Apply to the {count} line item in this estimate', otros: 'Apply to the {count} line items in this estimate' },
    'estimado.constructor.politica_aplicada': { uno: 'Re-priced {count} line item with this estimate’s policy.', otros: 'Re-priced {count} line items with this estimate’s policy.' },
    'estimado.constructor.precios_alcance': 'Applies to this estimate only. Workspace defaults live in Settings ▸ Pricing.',
    'estimado.constructor.precios_bloqueados': 'This estimate already went to the client — prices stay as they saw them. Create a revision to change them.',
    'estimado.constructor.plantillas_guardadas': { uno: 'Saved {count} private template.', otros: 'Saved {count} private templates.' },
    'estimado.constructor.plantillas_fallaron': { uno: '{count} private template failed to save.', otros: '{count} private templates failed to save.' },
    'estimado.constructor.titulo': 'Estimate Builder',
    'estimado.constructor.contexto_ia': 'Estimate · {nombreCliente}',
    'estimado.constructor.ia_que_falta_label': 'What am I missing on this estimate?',
    'estimado.constructor.ia_que_falta_prompt': 'Review this estimate and tell me what line items or costs I might be missing. Be specific and practical.',
    'estimado.constructor.ia_margen_label': 'Help me hit my target margin',
    'estimado.constructor.ia_margen_prompt': 'My margin is below target. Suggest concrete changes to line items or pricing to improve profit without losing the job.',
    'estimado.constructor.ia_intro_label': 'Write a proposal intro',
    'estimado.constructor.ia_intro_prompt': 'Write a short, friendly proposal introduction I can send to this client for this job. Keep it professional and in plain language.',
    'estimado.constructor.precios_rapidos_tooltip': 'Quick pricing settings',
    'estimado.constructor.margen_objetivo': 'Target Margin %',
    'estimado.constructor.markup_material': 'Material Markup %',
    'estimado.constructor.abrir_gastos_precios': 'Open full overhead & pricing →',
    'estimado.constructor.guardar_y_ver': 'Save & Preview Proposal',
    'estimado.constructor.nombre_proyecto': 'Project name',
    'estimado.constructor.nombre_placeholder': 'Name this project…',
    'estimado.constructor.ver': 'Preview',
    'estimado.constructor.aviso_precios': 'Pricing isn’t configured — margins may be inaccurate.',
    'estimado.constructor.config_completa': 'Full settings →',
    'estimado.constructor.ver_documento': 'View Document',
    'estimado.constructor.agregar_partidas': 'Add Items & Tools',
    'estimado.constructor.condiciones_titulo': 'Project Conditions & Zones',
    'estimado.constructor.resumen_zonas': ' Zones · ',
    'estimado.constructor.resumen_dificultad': ' Difficulty · ',
    'estimado.constructor.resumen_suelo': ' Soil',
    'estimado.constructor.zonas_activas': 'Active Zones',
    'estimado.constructor.borrar_zona_aria': 'Delete zone {name}',
    'estimado.constructor.zona_nombre_placeholder': 'Zone name…',
    'estimado.constructor.confirmar_zona': 'Confirm zone',
    'estimado.constructor.cancelar_zona': 'Cancel',
    'estimado.constructor.total_partidas': 'Items (',
    'estimado.constructor.total_cargo_viaje': 'Trip Charge',
    'estimado.constructor.total_recargo_emergencia': 'Emergency Surcharge',
    'estimado.constructor.total_dificultad': 'Site Difficulty (',
    'estimado.constructor.total_descuento': 'Discount',
    'estimado.constructor.total_minimo': 'Min Job Fee',
    'estimado.constructor.total_impuesto': 'Tax (',
    'estimado.constructor.total_estimado': 'Total Estimate',
    'estimado.constructor.barra_costo': 'Cost: ',
    'estimado.constructor.barra_ingreso': 'Revenue: ',
    'estimado.constructor.barra_ganancia': 'Profit: ',
    'estimado.constructor.borrar_zona_titulo': 'Delete this zone?',
    'estimado.constructor.borrar_zona_texto': 'You’re about to delete',
    'estimado.constructor.borrar_zona_check1': 'Also delete the ',
    'estimado.constructor.borrar_zona_check2': ' inside this zone.',
    'estimado.constructor.borrar_zona_conservar': 'Uncheck to keep them — they’ll move to ',
    'estimado.constructor.sin_zona': 'No Zone',
    'estimado.constructor.borrar_zona_conservar2': ' instead.',
    'estimado.constructor.zona_sin_partidas': 'This zone has no items inside it.',
    'estDetalle.cerrarPanel': 'Close estimate details',
    'estDetalle.cerrar': 'Close',
    'estDetalle.kpi.total': 'Total Value',
    'estDetalle.kpi.margen': 'Proj. Margin',
    'estDetalle.costo': 'Cost',
    'estDetalle.ganancia': 'Profit',
    'estDetalle.costoTip': 'Cost',
    'estDetalle.gananciaTip': 'Profit',
    'estDetalle.abrirEditor': 'Open Editor',
    'estDetalle.editarPropuesta': 'Edit Proposal',
    'estDetalle.partidas': 'Line Items',
    'estDetalle.col.descripcion': 'Description',
    'estDetalle.col.tipo': 'Type',
    'estDetalle.col.cantidad': 'Qty',
    'estDetalle.cliente.titulo': 'Client Details',
    'estDetalle.cliente.nombre': 'Name',
    'estDetalle.cliente.direccion': 'Address',
    'estDetalle.marcarEnviado': 'Mark Sent',
    'estDetalle.aprobar': 'Approve & Win',
    'estDetalle.duplicar': 'Duplicate',
    'estDetalle.cerrarBoton': 'Close',
    'estDetalle.hacerCopia': 'Duplicate copy',
    'estDetalle.borrar': 'Delete estimate',
    'estDetalle.cerrarMenu': 'Close menu',
    'estDetalle.seguirEditando': 'Continue editing',
    'estDetalle.borrarBorrador': 'Delete draft',
    'leads.modal.titulo.editar': 'Edit Lead',
    'leads.modal.titulo.nuevo': 'New Lead',
    'leads.modal.aria.cerrar': 'Close',
    'leads.modal.campo.nombre': 'Prospect Name',
    'leads.modal.campo.email': 'Email (Optional)',
    'leads.modal.campo.direccion': 'Address',
    'leads.modal.placeholder.direccion': 'Start typing to search address...',
    'leads.modal.campo.servicio': 'Service Interest',
    'leads.modal.opcion.elige': 'Select...',
    'leads.modal.campo.valor': 'Est. Value ($)',
    'leads.modal.campo.notas': 'Initial Notes',
    'leads.modal.placeholder.notas': 'How did they hear about us? Specific requests?',
    'leads.modal.boton.cancelar': 'Cancel',
    'leads.modal.boton.guardando': 'Saving…',
    'leads.modal.boton.actualizar': 'Update Lead',
    'leads.modal.boton.crear': 'Create Lead',
    'login.tagline': 'Professional CRM',
    'login.titulo': 'Welcome back',
    'login.sub': 'Access your dashboard securely with Google.',
    'login.verificando': 'Verifying…',
    'login.entrarGoogle': 'Sign in with Google',
    'login.soloAutorizados': 'Authorized personnel only',
    'proyNuevo.titulo': 'New Project',
    'proyNuevo.cerrar': 'Close',
    'proyNuevo.escogeCliente': 'Select a client to start a new project.',
    'proyNuevo.buscar.placeholder': 'Search clients...',
    'proyNuevo.vacio.titulo': 'No clients found',
    'proyNuevo.vacio.texto': 'Try a different name or phone number.',
    'proyNuevo.campo.nombre': 'Project Name',
    'proyNuevo.campo.inicio': 'Start Date',
    'proyNuevo.campo.fin': 'Target End',
    'proyNuevo.campo.monto': 'Contract Value ($)',
    'proyNuevo.campo.estatus': 'Status',
    'proyNuevo.estatus.programado': 'Scheduled',
    'proyNuevo.estatus.enProceso': 'In Progress',
    'proyNuevo.estatus.terminado': 'Completed',
    'proyNuevo.estatus.cancelado': 'Cancelled',
    'proyNuevo.campo.descripcion': 'Description',
    'proyNuevo.errorDesconocido': 'Unknown error',
    'notificaciones.tiempo.ahorita': 'just now',
    'notificaciones.tiempo.minutos': '{min}m ago',
    'notificaciones.tiempo.horas': '{hr}h ago',
    'notificaciones.tiempo.dias': '{d}d ago',
    'notificaciones.panel.borrar_todo': 'Clear all',
    'notificaciones.panel.vacio': 'You’re all caught up.',
    'notificaciones.panel.quitar_tooltip': 'Dismiss',
    'notificaciones.panel.quitar_aria': 'Dismiss notification',
    'pricebook.modal.titulo_editar': 'Edit Item',
    'pricebook.modal.titulo_nuevo': 'New Pricebook Item',
    'pricebook.campo.nombre': 'Item Name',
    'pricebook.campo.nombre_placeholder': 'e.g. 2x4 Lumber',
    'pricebook.campo.categoria': 'Category',
    'pricebook.campo.proveedor': 'Vendor',
    'pricebook.campo.proveedor_placeholder': 'e.g. SiteOne',
    'pricebook.campo.unidad': 'Unit',
    'pricebook.campo.costo': 'Cost ($)',
    'pricebook.preview.precio_cliente': 'Client Price',
    'pricebook.preview.ganancia': 'Profit',
    'pricebook.boton.cancelar': 'Cancel',
    'pricebook.boton.actualizar': 'Update Item',
    'pricebook.boton.agregar': 'Add to Pricebook',
    'proyecto.detalle.borrar.titulo': 'Delete Project?',
    'proyecto.detalle.borrar.mensaje': 'This action cannot be undone. All associated tasks, materials, expenses, and invoice data will be permanently removed.',
    'proyecto.detalle.borrar.confirmar': 'Delete Project',
    'proyecto.detalle.borrar.cancelar': 'Cancel',
    'proyecto.detalle.borrar.errordesconocido': 'Unknown error',
    'proyecto.detalle.nota.autordefecto': 'User',
    'proyecto.detalle.tab.centro': 'Command Center',
    'proyecto.detalle.tab.tareas': 'Tasks',
    'proyecto.detalle.tab.mano': 'Labor & Time',
    'proyecto.detalle.tab.materiales': 'Procurement',
    'proyecto.detalle.tab.equipo': 'Equipment',
    'proyecto.detalle.tab.finanzas': 'Financials',
    'proyecto.detalle.nav.atras': '"Back"',
    'proyecto.detalle.estado.arialabel': 'Project status',
    'proyecto.detalle.estado.aceptado': 'Accepted',
    'proyecto.detalle.estado.programado': 'Scheduled',
    'proyecto.detalle.estado.enproceso': 'In Progress',
    'proyecto.detalle.estado.completado': 'Completed',
    'proyecto.detalle.estado.cancelado': 'Cancelled',
    'proyecto.detalle.borrar.title': 'Delete Project',
    'proyecto.detalle.borrar.arialabel': 'Delete project',
    'proyecto.detalle.vital.atrasado': 'Behind Pace',
    'proyecto.detalle.vital.aldia': 'On Track',
    'proyecto.detalle.vital.barratitle': '"% Complete"',
    'proyecto.detalle.vital.preciofijo': 'Fixed price',
    'proyecto.detalle.nota.tarea1': 'Task "',
    'proyecto.detalle.nota.tarea2': '" was completed by ',
    'precios.toast.guardado': 'Pricing saved — your estimates will use these numbers.',
    'precios.toast.error': 'Could not save pricing. Try again.',
    'precios.modal.titulo': 'Quick pricing setup',
    'precios.modal.subtitulo': 'Three numbers — then your margins stay on target.',
    'precios.campo.margen': 'Target profit margin (%)',
    'precios.campo.margen_ayuda': 'What you want to keep after all costs',
    'precios.campo.markup': 'Material markup (%)',
    'precios.campo.markup_ayuda': 'Added on top of your material cost',
    'precios.campo.viaje': 'Trip / service call charge ($)',
    'precios.boton.guardando': 'Saving…',
    'precios.boton.guardar': 'Save & start estimating',
    'precios.boton.saltar': 'Skip for now',
    'agenda.cita.titulo.editar': 'Edit Appointment',
    'agenda.cita.titulo.nueva': 'Schedule Appointment',
    'agenda.cita.cliente.pregunta': 'Who are you visiting?',
    'agenda.cita.cliente.buscar': 'Search clients by name or address...',
    'agenda.cita.cliente.vaciotitulo': 'No clients found',
    'agenda.cita.cliente.vaciotexto': 'Try a different name or address.',
    'agenda.cita.cliente.cambiar': 'Change',
    'agenda.cita.campo.titulo': 'Title',
    'agenda.cita.campo.fecha': 'Date',
    'agenda.cita.campo.tipo': 'Type',
    'agenda.cita.tipo.visita': 'Site Visit',
    'agenda.cita.tipo.servicio': 'Service Call',
    'agenda.cita.tipo.consulta': 'Consultation',
    'agenda.cita.tipo.recorrido': 'Final Walkthrough',
    'agenda.cita.campo.inicio': 'Start Time',
    'agenda.cita.campo.fin': 'End Time',
    'agenda.cita.campo.notas': 'Notes',
    'agenda.cita.notas.placeholder': 'Gate code, specific instructions, etc...',
    'agenda.cita.proyecto.etiqueta': 'Link to Project (optional)',
    'agenda.cita.proyecto.ninguno': '— None —',
    'agenda.cita.boton.cancelar': 'Cancel',
    'agenda.cita.boton.guardando': 'Saving…',
    'agenda.cita.boton.actualizar': 'Update Appointment',
    'agenda.cita.boton.guardar': 'Save Appointment',
    'servicios.plantilla.titulo_editar': 'Edit Service Template',
    'servicios.plantilla.titulo_nueva': 'New Service Template',
    'servicios.plantilla.nombre': 'Service Name',
    'servicios.plantilla.nombre_placeholder': 'e.g. Standard Installation',
    'servicios.plantilla.categoria': 'Category',
    'servicios.plantilla.descripcion': 'Scope Description',
    'servicios.plantilla.descripcion_placeholder': 'e.g. Complete installation per manufacturer specifications...',
    'servicios.plantilla.produccion_titulo': 'Production Rate Logic',
    'servicios.plantilla.unidad': 'Unit of Measure',
    'servicios.plantilla.cantidad_hora': 'Qty per Man-Hour',
    'servicios.plantilla.desperdicio': 'Standard Waste % (for Materials)',
    'servicios.plantilla.desperdicio_ayuda': 'Default waste allowed when estimating this task.',
    'servicios.plantilla.ejemplo_fin': 'in 1 hour.',
    'servicios.plantilla.material_ligado': 'Auto-Link Material (Optional)',
    'servicios.plantilla.sin_material': '-- No Material Linked --',
    'servicios.plantilla.material_ayuda': 'If selected, this material will be automatically added to estimates when this service is used.',
    'servicios.plantilla.cancelar': 'Cancel',
    'servicios.plantilla.actualizar': 'Update Template',
    'servicios.plantilla.crear': 'Create Template',
    'wiz.error.nombrenegocio': 'Business name is required.',
    'wiz.error.noterminar': 'Can’t complete setup yet',
    'wiz.error.guardar': 'Failed to save settings. You may not have permission.',
    'wiz.perfil.titulo': 'Business Profile',
    'wiz.perfil.tituloinicio': 'Let’s get started.',
    'wiz.perfil.subtitulo': 'Update your basic business information.',
    'wiz.perfil.subtituloinicio': 'Tell us a bit about your business to configure your pricing model.',
    'wiz.perfil.industria': 'Industry Type',
    'wiz.perfil.industriaayuda': 'Determines your calculators, pricebook categories, and AI assistant.',
    'wiz.perfil.industriaayudainicio': 'We pre-fill realistic burden, margin, and overhead suggestions for this industry. Adjust as needed.',
    'wiz.perfil.nombre': 'Business Name',
    'wiz.perfil.descripcion': 'Business Description',
    'wiz.perfil.descripcionplaceholder': 'Briefly describe your services — this helps the AI write better proposals and scopes.',
    'wiz.perfil.moneda': 'Currency',
    'wiz.perfil.zona': 'Primary Service Area',
    'wiz.perfil.zonaplaceholder': 'Start typing a city, zip code, or address...',
    'wiz.perfil.sugerencias': 'Address suggestions enabled',
    'wiz.contacto.titulo': 'Contact Information',
    'wiz.contacto.tituloinicio': 'How can clients reach you?',
    'wiz.contacto.subtitulo': 'This information appears on your proposals and invoices.',
    'wiz.contacto.email': 'Business Email',
    'wiz.contacto.emailplaceholder': 'hello@yourbusiness.com',
    'wiz.contacto.telefono': 'Phone Number',
    'wiz.contacto.web': 'Website',
    'wiz.contacto.webplaceholder': 'https://yourbusiness.com',
    'wiz.logo.subido': 'Logo uploaded!',
    'wiz.logo.error': 'Failed to upload logo.',
    'wiz.logo.titulo': 'Business Logo',
    'wiz.logo.tituloinicio': 'Brand your proposals',
    'wiz.logo.subtitulo': 'Upload your company logo. It will appear on proposals, invoices, and emails.',
    'wiz.logo.alt': 'Business Logo',
    'wiz.logo.vacio': 'No logo yet',
    'wiz.logo.subiendo': 'Uploading...',
    'wiz.logo.cambiar': 'Change Logo',
    'wiz.logo.subir': 'Upload Logo',
    'wiz.logo.formatos': 'PNG, JPG, or SVG. You can skip this and add it later in Settings.',
    'wiz.temporada.titulo': 'Season Settings',
    'wiz.temporada.subtitulo': 'We need to know your total capacity to calculate overhead recovery.',
    'wiz.temporada.semanas': 'Weeks per Year',
    'wiz.temporada.semanastip': 'How many weeks are you operational? Exclude winter shutdown.',
    'wiz.temporada.dias': 'Working Days / Week',
    'wiz.temporada.diastip': 'How many days do crews go out?',
    'wiz.temporada.horas': 'Crew Hours / Day',
    'wiz.temporada.horastip': 'Total shift length (e.g. 8am to 5pm = 9 hours)',
    'wiz.empleados.puestodefecto': 'Crew Member',
    'wiz.empleados.titulo': 'Employees',
    'wiz.empleados.subtitulo': 'Add everyone who does billable work (including yourself).',
    'wiz.empleados.agregar': 'Add Employee',
    'wiz.empleados.agregarvacio': 'Add Employee',
    'wiz.empleados.vaciotitulo': 'No employees yet',
    'wiz.empleados.vaciotexto': 'Your labor rate is built from the crew. Add yourself first, then anyone else who books billable hours.',
    'wiz.empleados.etiqueta': 'Employee',
    'wiz.empleados.nombreplaceholder': 'Name',
    'wiz.empleados.puesto': 'Role',
    'wiz.empleados.puestoplaceholder': 'Role',
    'wiz.empleados.pago': 'Wage ($)',
    'wiz.empleados.carga': 'Burden %',
    'wiz.empleados.cargatip': 'FICA + Workers\' Comp + benefits. Typical: 20–30%.',
    'wiz.empleados.quitar': 'Remove employee',
    'wiz.empleados.facturable': 'Billable',
    'wiz.empleados.disponible': 'Available %',
    'wiz.empleados.disponibletip': 'Of season hours, % NOT lost to PTO, weather, sick days, training.',
    'wiz.empleados.porcentajefacturable': 'Billable %',
    'wiz.empleados.porcentajefacturabletip': 'Of available time, % actually on a paying job (not driving/setup/cleanup).',
    'wiz.empleados.efectivo': 'Effective',
    'wiz.empleados.nofacturable': 'Cost rolls into overhead — not into the direct labor rate.',
    'wiz.gastos.rapidorenta': 'Rent',
    'wiz.gastos.rapidoseguro': 'Insurance',
    'wiz.gastos.rapidogasolina': 'Fuel',
    'wiz.gastos.titulo': 'Overhead',
    'wiz.gastos.subtitulo': 'Fixed costs that exist even if you have no work.',
    'wiz.gastos.otro': 'Custom',
    'wiz.gastos.vaciotitulo': 'No overhead items yet',
    'wiz.gastos.vaciotexto': 'Rent, insurance, marketing, software — anything you pay whether or not a crew rolls out.',
    'wiz.gastos.agregar': 'Add Item',
    'wiz.gastos.nombre': 'Expense Name',
    'wiz.gastos.nombreplaceholder': 'Expense Name',
    'wiz.gastos.monto': 'Amount',
    'wiz.gastos.frecuencia': 'Frequency',
    'wiz.gastos.mensual': 'Monthly',
    'wiz.gastos.quitar': 'Remove overhead item',
    'wiz.equipo.titulo': 'Equipment Cost Recovery',
    'wiz.equipo.subtitulo': 'Trucks, mowers, excavators — their depreciation and operating costs need to be recovered through billable hours. Skip if you don’t own depreciable assets.',
    'wiz.equipo.costoanual': 'Annual Equipment Cost',
    'wiz.equipo.costoanualayuda': 'Recovered across your billable hours.',
    'wiz.equipo.vaciotitulo': 'No equipment yet',
    'wiz.equipo.vaciotexto': 'Add trucks, mowers or machines so their depreciation and running costs land in your rate. You can skip this step.',
    'wiz.equipo.agregar': 'Add Equipment',
    'wiz.equipo.nombreplaceholder': 'Truck, Mower, etc.',
    'wiz.equipo.compra': 'Purchase $',
    'wiz.equipo.vida': 'Life (yrs)',
    'wiz.equipo.gasolina': 'Fuel $/yr',
    'wiz.equipo.mantenimiento': 'Maint $/yr',
    'wiz.equipo.quitar': 'Remove equipment',
    'wiz.precios.titulo': 'Pricing Goals',
    'wiz.precios.subtitulo': 'How much net profit do you want to make on top of your costs?',
    'wiz.precios.sobrevivir': 'Survival',
    'wiz.precios.crecer': 'Growth',
    'wiz.precios.fuerte': 'Strong',
    'wiz.precios.agresivo': 'Aggressive',
    'wiz.precios.ganancia': 'Target Net Profit',
    'wiz.precios.gananciaaria': 'Target net profit percent',
    'wiz.precios.equilibrio': 'Breakeven',
    'wiz.precios.premium': 'Premium',
    'wiz.precios.markup': 'Default Material Markup',
    'wiz.precios.markupayuda': 'Standard markup added to plants/materials.',
    'wiz.reglas.titulo': 'Minimum Job Rules',
    'wiz.reglas.subtitulo': 'Set guardrails so you never lose money on small jobs.',
    'wiz.reglas.minimo': 'Min Service Call ($)',
    'wiz.reglas.minimotip': 'The absolute lowest amount an invoice can be.',
    'wiz.reglas.viaje': 'Trip Charge ($)',
    'wiz.reglas.viajetip': 'Flat fee added to every visit for fuel/wear.',
    'wiz.reglas.horas': 'Minimum Man-Hours',
    'wiz.reglas.horastip': 'Minimum labor hours charged per visit.',
    'wiz.reglas.urgencia': 'Rush Surcharge (%)',
    'wiz.resumen.repaso': 'Review',
    'wiz.resumen.completo': 'Setup complete',
    'wiz.resumen.titulorepaso': 'Settings Review',
    'wiz.resumen.titulo': 'You’re all set.',
    'wiz.resumen.subtitulorepaso': 'Review your updated business metrics.',
    'wiz.resumen.subtitulo': 'Your financial blueprint has been generated.',
    'wiz.resumen.tarifa': 'Your Target Rate',
    'wiz.resumen.porhora': '/hr',
    'wiz.resumen.tarifaayuda': 'Charge this for every man-hour on the job.',
    'wiz.resumen.margen': 'Profit Margin',
    'wiz.resumen.semanas': 'Active Weeks',
    'wiz.resumen.cuadrilla': 'Crew Size',
    'wiz.nav.cerrar': 'Close settings',
    'wiz.nav.ajustes': 'Business Settings',
    'wiz.nav.asistente': 'Setup Wizard',
    'equipo.modal.error.faltandatos': 'Please provide both a Name and an Email address.',
    'equipo.modal.error.emailinvalido': 'Please enter a valid email address.',
    'equipo.modal.error.emailduplicado': 'A team member with that email already exists.',
    'equipo.modal.campo.email': 'Email (Google Account)',
    'equipo.modal.ayuda.emailfijo': 'Email cannot be changed once added.',
    'equipo.modal.ayuda.emailgoogle': 'Must match their Google Sign-In email.',
    'equipo.modal.placeholder.nombre': 'John Doe',
    'equipo.modal.campo.pestanas': 'Allowed Tabs',
    'equipo.modal.pestana.agenda': 'Schedule',
    'equipo.modal.pestana.reloj': 'Time Clock',
    'equipo.modal.pestana.propuestas': 'Proposals',
    'equipo.modal.aviso.permisosfijos': 'Permissions are locked for this role to ensure security.',
    'equipo.modal.admin.titulo': 'Full Access Granted',
    'equipo.modal.admin.descripcion': 'Admins have unrestricted access to all tabs, settings, and user management.',
    'equipo.modal.boton.cancelar': 'Cancel',
    'equipo.modal.boton.guardando': 'Saving...',
    'equipo.modal.boton.guardarcambios': 'Save Changes',
    'equipo.modal.boton.invitar': 'Invite User',
    'horas.editor.error_guardar': 'Error saving log.',
    'horas.editor.titulo': 'Edit Time Log',
    'horas.editor.cancelar': 'Cancel',
    'horas.editor.guardando': 'Saving…',
    'horas.editor.guardar': 'Save Changes',
    'horas.editor.proyecto': 'Project assignment',
    'horas.editor.taller': 'General Shop Time',
    'horas.editor.entrada': 'Clock in',
    'horas.editor.salida': 'Clock out',
    'horas.editor.aviso': 'Changing these times recalculates the shift’s net payroll minutes automatically.',
    'asistente.resumen.modelovivo': 'Live Business Model',
    'asistente.resumen.tunegocio': 'Your Business',
    'asistente.resumen.diasproduccion': 'Production Days',
    'asistente.resumen.dias': ' days',
    'asistente.resumen.capacidadtemporada': 'Season Capacity',
    'asistente.resumen.temporadacorta': 'Short season detected. Costs will be compressed.',
    'asistente.resumen.metricasmanodeobra': 'Labor Metrics',
    'asistente.resumen.horasfacturables': 'Billable Hours',
    'asistente.resumen.costomanodeobradirecta': 'Direct Labor Cost',
    'asistente.resumen.manodeobraindirecta': 'Indirect Labor',
    'asistente.resumen.agregaempleados': 'Add employees to calculate costs.',
    'asistente.resumen.gastosfijosequilibrio': 'Overhead & Break-Even',
    'asistente.resumen.gastosfijosanuales': 'Annual Overhead',
    'asistente.resumen.equilibriohora': 'Break-Even / Hr',
    'asistente.resumen.tarifameta': 'Target Hourly Rate',
    'asistente.resumen.margenganancia': 'Profit margin ',
    'asistente.resumen.gananciaposible': 'Potential profit',
    'asistente.resumen.poranio': '/yr',
    'asistente.resumen.minimostrabajo': 'Job Minimums',
    'asistente.resumen.cargoviaje': 'Trip Charge',
    'asistente.resumen.serviciominimo': 'Min Service',
    'flujo.volver.cliente': 'Back to client',
    'flujo.volver.panel': 'Back to home',
    'flujo.volver.estimados': 'Back to estimates',
    'flujo.guardado.titulo': 'Estimate Saved',
    'flujo.guardado.textoInicio': 'Build the proposal for',
    'flujo.guardado.textoFin': 'and send it from there.',
    'flujo.guardado.abrirPropuesta': 'Open Proposal',
    'flujo.guardado.volverEstimados': 'Back to Estimates',
    'admin.resumen.total_negocios': 'Total Tenants',
    'admin.resumen.activos': 'Active',
    'admin.resumen.suspendidos': 'Suspended',
    'admin.resumen.total_usuarios': 'Total Users',
    'admin.resumen.personal_campo': 'Field Staff',
    'admin.resumen.nuevo_negocio_titulo': 'New Tenant',
    'admin.resumen.nuevo_negocio_pista': 'Create workspace',
    'admin.resumen.invitar_titulo': 'Invite User',
    'admin.resumen.invitar_pista': 'Add to workspace',
    'admin.resumen.reportes_titulo': 'System Reports',
    'admin.resumen.reportes_pista': 'Error logs',
    'admin.resumen.actualizar_titulo': 'Refresh Data',
    'admin.resumen.actualizar_pista': 'Sync now',
    'admin.resumen.distribucion': 'Tenants — User Distribution',
    'admin.resumen.vacio_titulo': 'No tenants yet',
    'admin.resumen.vacio_descripcion': 'Create your first workspace to start onboarding a business.',
    'admin.resumen.nuevo_negocio_boton': 'New Tenant',
    // ── Educational videos (admin panel → Educational Videos) ──
    'admin.videos.titulo': 'Educational Videos',
    'admin.videos.descripcion': 'The "How to use" videos your team sees on each screen. Uploading one publishes it instantly.',
    'admin.videos.refrescar': 'Refresh',
    'admin.videos.progreso': '{hechos} of {total} published',
    'admin.videos.quitar_miniatura_titulo': 'Remove the thumbnail?',
    'admin.videos.quitar_miniatura_mensaje': 'The thumbnail for {tema} is deleted and the video goes back to starting on a black frame. The video itself is untouched.',
    'admin.videos.err.archivo_vacio': 'That file is empty.',
    'admin.videos.err.muy_grande': 'The file is {tamano} and the limit is {limite}. Trim the video or lower its quality.',
    'admin.videos.err.formato': 'The {tipo} format will not work here. Use MP4 or WebM.',
    'admin.videos.err.titulo_vacio': 'The title cannot be empty.',
    'admin.videos.err.titulo_largo': 'That title is too long ({max} characters max).',
    'admin.videos.err.sin_permiso': 'The database refused the change. Check that your account is still a platform admin.',
    'admin.videos.err.limite_servidor': 'Supabase rejected the file for size. Raise the ceiling in the Supabase dashboard: Project Settings → Storage → "Upload file size limit". The bucket is already set to 2 GB; that other limit is the one in charge.',
    'admin.videos.err.sin_sesion': 'Your session expired. Sign in again and retry.',
    'admin.videos.cancelar': 'Cancel',
    'admin.videos.subiendo': 'Uploading {pct}% — {enviado} of {total}',
    'admin.videos.subiendo_aria': 'Upload progress',
    'admin.videos.subiendo_aviso': 'If the connection drops the upload picks up where it left off. Closing this tab does cancel it.',
    'admin.videos.ayuda_formato': 'Formats: {formatos}. {limite} MB max per video. The upload resumes on its own if the connection drops, but do not close this tab while it runs.',
    'admin.videos.sin_video': 'No video',
    'admin.videos.previsualizar': 'Play the video',
    'admin.videos.estado_publicado': 'Published',
    'admin.videos.estado_pendiente': 'Pending',
    'admin.videos.campo_titulo': 'Title the user sees',
    'admin.videos.guardar': 'Save',
    'admin.videos.subir': 'Upload video',
    'admin.videos.reemplazar': 'Replace',
    'admin.videos.miniatura': 'Thumbnail',
    'admin.videos.cambiar_miniatura': 'Change thumbnail',
    'admin.videos.quitar_miniatura': 'Remove thumbnail',
    'admin.videos.borrar': 'Delete',
    'admin.videos.borrar_titulo': 'Delete this video?',
    'admin.videos.borrar_mensaje': 'The "How to use" button on {tema} goes back to saying "Tutorial coming soon". The file is deleted and cannot be recovered.',
    'admin.videos.borrar_confirmar': 'Delete video',
    'admin.videos.actualizado': 'Updated {fecha} by {quien}',
    'admin.videos.desconocido': 'the system',
    'admin.videos.pie': 'Thumbnails are optional (PNG, JPG or WebP, {max} max). Without one the video starts on a black frame.',
    'admin.videos.toast_subido': 'Video published',
    'admin.videos.toast_miniatura': 'Thumbnail updated',
    'admin.videos.toast_miniatura_borrada': 'Thumbnail removed',
    'admin.videos.toast_borrado': 'Video deleted',
    'admin.videos.toast_titulo': 'Title updated',
    'admin.videos.error_subir': 'Could not upload the file.',
    'admin.videos.error_borrar': 'Could not delete.',
    'admin.videos.error_titulo': 'Could not save the title.',
    'admin.videos.error_cargar_titulo': 'Could not load the videos',
    'admin.videos.error_cargar_desc': 'The list never arrived, so this screen does not know which videos exist. It does not mean there are none. Detail: {detalle}',
    'admin.videos.reintentar': 'Retry',
    'admin.videos.aviso_mov': 'Uploaded, but .mov rarely plays in Chrome or Firefox. Convert it to MP4 unless your team is on Safari.',

    'admin.uso_ia.titulo': 'AI Usage',
    'admin.uso_ia.descripcion': 'How much of this month’s AI allowance each company has used, as a percentage.',
    'admin.uso_ia.periodo': 'Month of {mes}',
    'admin.uso_ia.plataforma': 'Whole platform',
    'admin.uso_ia.plataforma_pista': 'All companies together, against this month’s limit.',
    'admin.uso_ia.renueva': 'Refills on {fecha}',
    'admin.uso_ia.bloqueado': 'Limit reached: AI is paused for every company until {fecha}.',
    'admin.uso_ia.empresas': 'Companies',
    'admin.uso_ia.leyenda_propio': 'Own use',
    'admin.uso_ia.leyenda_prestado': 'Used by other companies',
    'admin.uso_ia.pide_prestado': 'Using other companies’ allowance',
    'admin.uso_ia.presto': 'Lent {pct}',
    'admin.uso_ia.sin_uso': { uno: '{n} company with no usage this month', otros: '{n} companies with no usage this month' },
    'admin.uso_ia.mostrar': 'Show',
    'admin.uso_ia.ocultar': 'Hide',
    'admin.uso_ia.nadie_uso': 'No company has used AI this month.',
    'admin.uso_ia.vacio_titulo': 'No companies yet',
    'admin.uso_ia.fila_aria': '{nombre}: {pct} of its allowance',
    'admin.uso_ia.plataforma_aria': 'Whole platform: {pct} of this month’s limit',
    'admin.uso_ia.actualizar': 'Refresh',
    'admin.uso_ia.reintentar': 'Retry',
    'admin.uso_ia.error_cargar_titulo': 'Could not load AI usage',
    'admin.uso_ia.error_cargar_desc': 'The data never arrived, so this screen does not know how much has been used. It does not mean there is no usage. Detail: {detalle}',
    'admin.uso_ia.sin_permiso_titulo': 'Your account is not on the platform admin list',
    'admin.uso_ia.sin_permiso_desc': 'Only platform admins can see AI usage. Add this email in the Admins tab.',
    'ia.cupo.agotado': 'AI is paused until the 1st: this month’s usage limit has been reached.',

    'admin.plataforma.erroragregar': 'Error adding admin: ',
    'admin.plataforma.confirmartitulo': 'Remove Platform Admin?',
    'admin.plataforma.confirmarmensaje1': 'Revoke super-admin access for ',
    'admin.plataforma.confirmarmensaje2': 'They’ll keep their workspace access but lose platform control.',
    'admin.plataforma.quitaracceso': 'Revoke Access',
    'admin.plataforma.cancelar': 'Cancel',
    'admin.plataforma.toastquitado': 'Platform admin revoked',
    'admin.plataforma.errorquitar': 'Error removing admin: ',
    'admin.plataforma.titulo': 'Platform Admins',
    'admin.plataforma.descripcion': 'Accounts with unrestricted access to this control center.',
    'admin.plataforma.avisotitulo': 'Super admin privilege warning',
    'admin.plataforma.avisotexto1': 'Users added here will have unrestricted access to the Super Admin Panel. They can view, edit, and delete all tenants,',
    'admin.plataforma.avisotexto2': 'manage platform-wide users, and view application crashes. Do not grant this role lightly.',
    'admin.plataforma.respaldoentorno': 'Hardcoded environment fallback',
    'admin.plataforma.nosepuedeborrar': 'cannot be deleted',
    'admin.plataforma.placeholdercorreo': 'Enter Google Workspace email…',
    'admin.plataforma.daracceso': 'Grant Admin Access',
    'admin.plataforma.vaciotitulo': 'No dynamic admins yet',
    'admin.plataforma.vaciodesc': 'Only the hardcoded environment fallback can reach this panel right now.',
    'admin.plataforma.columnacorreo': 'Email Address',
    'admin.plataforma.columnaagregadopor': 'Added By',
    'admin.plataforma.columnaagregadoel': 'Added On',
    'admin.plataforma.sistema': 'System',
    'admin.plataforma.tooltipquitar': 'Revoke admin access',
    'admin.plataforma.ariaquitar': 'Revoke admin access for ',
    'admin.reportes.severidad_aviso': 'Warning',
    'admin.reportes.tiempo_ahorita': 'just now',
    'admin.reportes.tiempo_minutos': '${mins}m ago',
    'admin.reportes.tiempo_horas': '${hrs}h ago',
    'admin.reportes.tiempo_dias': '${days}d ago',
    'admin.reportes.marcado_activo': 'Marked as active',
    'admin.reportes.sin_permiso': 'Not authorized',
    'admin.reportes.marcado_resuelto': 'Marked as resolved',
    'admin.reportes.borrar_log_titulo': 'Delete log?',
    'admin.reportes.borrar_log_mensaje': 'This single error group will be permanently removed.',
    'admin.reportes.borrar_log_confirmar': 'Delete',
    'admin.reportes.borrado': 'Deleted',
    'admin.reportes.borrar_resueltos_titulo': 'Delete all resolved?',
    'admin.reportes.borrar_resueltos_mensaje': 'Permanently remove every error marked as resolved.',
    'admin.reportes.borrar_resueltos_confirmar': 'Delete Resolved',
    'admin.reportes.resueltos_borrados': 'Resolved logs cleared',
    'admin.reportes.borrar_todo_titulo': 'Clear ALL logs?',
    'admin.reportes.borrar_todo_mensaje': 'Wipes every error and warning. Cannot be undone.',
    'admin.reportes.borrar_todo_confirmar': 'Clear Everything',
    'admin.reportes.todo_borrado': 'All logs cleared',
    'admin.reportes.titulo': 'System Reports',
    'admin.reportes.descripcion': 'Client-side crashes and warnings grouped by fingerprint.',
    'admin.reportes.limpiar_resueltos': 'Clear resolved',
    'admin.reportes.limpiar_todo_aria': 'Clear all logs',
    'admin.reportes.limpiar_todo_tooltip': 'Clear all logs',
    'admin.reportes.kpi_errores_activos': 'Active Errors',
    'admin.reportes.kpi_unicos': '{count} unique',
    'admin.reportes.filtro_avisos': 'Warnings',
    'admin.reportes.filtro_resueltos': 'Resolved',
    'admin.reportes.filtro_errores': 'Errors',
    'admin.reportes.buscar': 'Search message, user, stack, route…',
    'admin.reportes.filtrar_workspace': 'Filter by workspace',
    'admin.reportes.todos_workspaces': 'All workspaces',
    'admin.reportes.vacio_titulo': 'No matching logs',
    'admin.reportes.vacio_texto': 'All clear, or the filters are too tight.',
    'admin.reportes.quitar_filtros': 'Reset filters',
    'admin.reportes.usuarios_afectados': 'Affected Users (',
    'admin.reportes.navegador': 'Browser',
    'admin.reportes.resuelto_por': 'Resolved by {quien} {cuando}',
    'admin.negocios.titulo': 'Tenants',
    'admin.negocios.descripcion': 'Every workspace on the platform and who owns it.',
    'admin.negocios.buscar': 'Search tenants…',
    'admin.negocios.nuevo': 'New Tenant',
    'admin.negocios.vacio_filtro_titulo': 'No workspaces match that search',
    'admin.negocios.vacio_titulo': 'No workspaces yet',
    'admin.negocios.vacio_filtro_desc': 'Try a different name, workspace ID or owner email.',
    'admin.negocios.vacio_desc': 'Create the first tenant to start onboarding a business.',
    'admin.negocios.limpiar_busqueda': 'Clear search',
    'admin.negocios.nuevo_vacio': 'New Tenant',
    'admin.negocios.suspender': 'Suspend',
    'admin.negocios.activar': 'Activate',
    'admin.negocios.borrar_tooltip': 'Permanently delete tenant and all data',
    'admin.usuarios.titulo': 'Users',
    'admin.usuarios.descripcion': 'Everyone with access to a workspace on the platform.',
    'admin.usuarios.buscar': 'Search users…',
    'admin.usuarios.invitar': 'Invite User',
    'admin.usuarios.vacio_filtro_titulo': 'No users match that search',
    'admin.usuarios.vacio_titulo': 'No users yet',
    'admin.usuarios.vacio_filtro_desc': 'Try a different name or email address.',
    'admin.usuarios.vacio_desc': 'Invite the first user and assign them to a workspace.',
    'admin.usuarios.limpiar_busqueda': 'Clear search',
    'admin.usuarios.invitar_vacio': 'Invite User',
    'admin.usuarios.borrar_tooltip': 'Permanently delete user',
    'chat.boton.preguntar.title': 'Ask Raza — AI assistant',
    'chat.boton.preguntar.aria': 'Ask Raza — AI assistant',
    'chat.boton.cerrarburbuja.aria': 'Close Raza AI assistant',
    'chat.cabecera.subtitulo': 'Ask anything about your business',
    'chat.privacidad.privada.title': 'Private — click to share with team',
    'chat.privacidad.compartida.title': 'Shared — click to make private',
    'chat.privacidad.privada.aria': 'Private conversation — share with team',
    'chat.privacidad.compartida.aria': 'Shared conversation — make private',
    'chat.boton.nueva.title': 'New conversation',
    'chat.boton.nueva.aria': 'New conversation',
    'chat.boton.pantallacompleta.title': 'Open in full view',
    'chat.boton.pantallacompleta.aria': 'Open in full view',
    'chat.boton.cerrar.title': 'Close',
    'chat.boton.cerrar.aria': 'Close assistant',
    'chat.estado.escribiendo': 'Raza AI is typing',
    'chat.input.aria': 'Message Raza AI',
    'chat.boton.voz.title': 'Talk with Raza AI',
    'chat.boton.voz.aria': 'Talk with Raza AI',
    'chat.boton.enviar.aria': 'Send message',
    'chat.pie.tecnologia': 'Powered by Gemini · Raza Pro AI',
    'ai.askraza.botontarjeta': 'Ask Raza about this',
    'ai.askraza.boton': 'Ask Raza',
    'ai.askraza.chatvacio': 'Open blank chat with this context',
    'adjuntos.miniatura.errorsubida': 'Upload failed',
    'adjuntos.miniatura.fallo': 'Failed',
    'adjuntos.miniatura.archivo': 'File',
    'ia.sugerencias.titulo': 'Tap one to get started',
    'calendario.encabezado.mes_anterior': 'Previous month',
    'calendario.encabezado.hoy': 'Today',
    'calendario.encabezado.mes_siguiente': 'Next month',
    'calendario.encabezado.agendar_visita': 'Schedule Visit',
    'calendario.dia.escoge_dia': 'Select a Date',
    'calendario.dia.vacio_titulo': 'No day selected',
    'calendario.dia.vacio_descripcion': 'Pick a date on the calendar to see its agenda.',
    'calendario.dia.en_curso': 'Ongoing',
    'calendario.dia.agregar': 'Add',
    'calendario.dia.agenda_vacia_titulo': 'Nothing scheduled',
    'calendario.dia.agenda_vacia_descripcion': 'This day is clear.',
    'calendario.dia.editar_cita_aria': 'Edit appointment',
    'calendario.dia.editar_cita_tooltip': 'Edit Appointment',
    'calendario.dia.borrar_evento_aria': 'Delete event',
    'calendario.dia.borrar_evento_tooltip': 'Delete Event',
    'panel.checklist.titulo': 'Get set up in minutes',
    'panel.checklist.ocultar': 'Hide checklist',
    'materiales.duplicado.inicio': 'You already have "',
    'materiales.duplicado.fin': 'in your pricebook. Saving again will add a duplicate row.',
    'materiales.toast.agregado_inicio': 'Added "',
    'materiales.toast.agregado_fin': 'to your pricebook.',
    'materiales.toast.error_desconocido': 'unknown error',
    'materiales.modal.titulo': 'Add new material',
    'materiales.modal.subtitulo': 'Saves to your pricebook and drops into the active zone.',
    'materiales.modal.cerrar': 'Close',
    'materiales.campo.nombre': 'Material name *',
    'materiales.campo.nombre_placeholder': 'e.g. Pine bark mulch, 3-inch shredded',
    'materiales.campo.costo': 'Cost / unit *',
    'materiales.campo.unidad': 'Unit *',
    'materiales.campo.categoria': 'Category *',
    'materiales.campo.markup_placeholder': '% markup)',
    'materiales.campo.proveedor': 'Vendor',
    'materiales.campo.opcional': 'Optional',
    'materiales.campo.notas': 'Notes',
    'materiales.campo.notas_placeholder': 'Optional — specs, vendor link, etc.',
    'materiales.boton.cancelar': 'Cancel',
    'materiales.boton.guardando': 'Saving…',
    'materiales.boton.guardar': 'Save material',
    'estimado.partidas.unidad.horas': 'MAN-HOURS',
    'estimado.partidas.unidad.piezas': 'UNITS',
    'estimado.partidas.vacio.titulo': 'Estimate is empty',
    'estimado.partidas.vacio.texto': 'Use the calculators or the service library to add your first line items.',
    'estimado.partidas.moverZona': 'Move to Zone…',
    'estimado.partidas.cambiarTipo': 'Change Type…',
    'estimado.partidas.borrarSel.title': 'Delete selected',
    'estimado.partidas.borrarSel.aria': 'Delete selected',
    'estimado.partidas.quitarSeleccion': 'Clear',
    'estimado.partidas.borrarZona.title': 'Delete zone',
    'estimado.partidas.seleccionarTodo': 'Select all items',
    'estimado.partidas.col.acciones': 'Row actions',
    'estimado.partidas.totalSinZona': 'No Zone total',
    'estimado.partidas.grupoSinZona': 'Unassigned Items',
    'estimado.partidas.agregarZona': 'Add new zone group',
    'comun.cancelar': 'Cancel',
    'estimado.snapshot.hr_punto_equilibrio': '/hr break-even',
    'estimado.snapshot.margen_sufijo': ' margin',
    'estimado.snapshot.tomado_el': 'Captured',
    'estimado.snapshot.precios_usados': 'Pricing used for this estimate',
    'estimado.snapshot.boton_actualizar': 'Refresh',
    'estimado.snapshot.aviso_equilibrio': 'Current break-even is',
    'estimado.snapshot.aviso_antes': '/hr (was',
    'estimado.snapshot.equilibrio_hr': 'Break-even / hr',
    'estimado.snapshot.meta_hr': 'Target / hr',
    'estimado.snapshot.overhead_hr': 'Overhead / hr',
    'estimado.tab.servicios': 'Templates',
    'estimado.tab.materiales': 'Materials',
    'estimado.tab.calculadoras': 'Calculators',
    'estimado.tab.precios': 'Pricing',
    'estimado.pista.escoge': 'Pick a service below and tap',
    'estimado.pista.agregar': 'Add',
    'estimado.pista.automatico': '— line items drop into your estimate automatically.',
    'estimado.zona.agregar': 'Add zone',
    'estimado.zona.confirmar': 'Confirm zone',
    'estimado.zona.cancelar': 'Cancel',
    'estimado.rapido.dumpfee': 'Dump Fee',
    'estimado.rapido.entrega': 'Delivery',
    'estimado.rapido.rentaequipo': 'Equipment Rental',
    'estimado.margen.bajometa': 'Below target',
    'estimado.margen.enmeta': 'On target',
    'estimado.grafica.manodeobra': 'Labor & Burden',
    'estimado.grafica.materiales': 'Materials & Subs',
    'estimado.grafica.ganancia': 'Net Profit',
    'estimado.anticipo.saldo': 'Balance',
    'estimado.anticipo.alterminar': 'on completion',
    'estimado.impuesto.aplicararia': 'Apply tax',
    'estimado.impuesto.tasaaria': 'Tax rate percent',
    'estimado.vacio.agregarmaterial': 'Add material',
    'precios.siguiente.titulo': 'Here’s what’s next',
    'precios.siguiente.pendiente': ' cost left to fill in',
    'precios.siguiente.pendientes': ' costs left to fill in',
    'precios.siguiente.seguardan': ' They save to your library for next time.',
    'precios.siguiente.sigue': 'Up next:',
    'precios.siguiente.mostrar': 'Show me the next one',
    'estimados.precios.modal.tip': 'Quick tip',
    'estimados.precios.modal.titulo': 'One thing first',
    'estimados.precios.modal.texto_inicio': 'You still have',
    'estimados.precios.modal.costo_singular': ' cost',
    'estimados.precios.modal.costo_plural': ' costs',
    'estimados.precios.modal.texto_fin': ' to fill in on this estimate.',
    'estimados.precios.modal.texto_explicacion': 'Fill those in first and they’ll stick for your next job.',
    'estimados.precios.modal.sigue': 'Up next:',
    'estimados.precios.modal.pendiente': 'Waiting to add:',
    'estimados.precios.modal.boton_siguiente': 'Show me what’s next',
    'estimados.precios.modal.boton_continuar': 'Add anyway — I’ll price later',
    'estimados.precios.modal.cerrar': 'Close',
    'plantillaPriv.error.faltaNombre': 'Template name is required.',
    'plantillaPriv.error.sinPartidas': 'A template needs at least one line.',
    'plantillaPriv.manoDeObraGeneral': 'General Labor',
    'plantillaPriv.cerrar': 'Close',
    'plantillaPriv.nombre': 'Template name',
    'plantillaPriv.traerCategoria': 'Seed materials from a category',
    'plantillaPriv.traerCategoriaOpcion': 'Add a category’s materials at $0…',
    'plantillaPriv.traerCategoriaAyuda': 'Adds the category’s materials unpriced — just set each cost. Priced rows also save to your catalog.',
    'plantillaPriv.partidas': 'Line items',
    'plantillaPriv.descripcionPartida': 'Line description',
    'plantillaPriv.cantidad': 'Qty',
    'plantillaPriv.costo': 'Cost $',
    'plantillaPriv.tip.manoDeObraBloqueada': 'Labor rate comes from your overhead calculator in Settings.',
    'plantillaPriv.quitarPartidaAria': 'Remove line',
    'plantillaPriv.quitarPartidaTip': 'Remove line',
    'plantillaPriv.vacio.titulo': 'No lines yet',
    'plantillaPriv.vacio.texto': 'Add a material or labor line below.',
    'plantillaPriv.avisoMinimo': 'A template needs at least one line to save.',
    'plantillaPriv.agregarMaterial': 'Add Material',
    'plantillaPriv.agregarManoDeObra': 'Add Labor',
    'plantillaPriv.notaPie': 'Adding material writes it to your pricebook. Labor pulls from your overhead calculator and is locked here.',
    'plantillaPriv.cancelar': 'Cancel',
    'estimado.plantillas.titulo': 'Save as private template?',
    'estimado.plantillas.cambiasteprecios': 'You changed prices on ',
    'estimado.plantillas.guardaconunombre': 'Save under your own name so you can reuse the new prices next time.',
    'estimado.plantillas.cerrar': 'Close',
    'estimado.plantillas.basadaen': 'Based on',
    'estimado.plantillas.guardar': 'Save',
    'estimado.plantillas.placeholdernombre': 'Give this template a name…',
    'estimado.plantillas.omitir': 'Skip and save estimate',
    'estimado.plantillas.nadaseleccionado': 'Nothing selected',
    'plantillas.toast.agregado': 'Added',
    'plantillas.toast.zonanueva': '→ new zone',
    'plantillas.boton.nueva': 'New line template',
    'plantillas.vacio.titulo': 'No private templates yet',
    'plantillas.vacio.texto': 'Use a Global template, edit your prices in the estimate, and save — you’ll be prompted to save it under your own name. Reuse it later with one click.',
    'plantillas.tab.globales': 'Global',
    'plantillas.tab.privadas': 'Private',
    'plantillas.toast.errorborrar': 'Delete failed',
    'plantillas.boton.editar.title': 'Edit template',
    'plantillas.boton.editar.aria': 'Edit template',
    'plantillas.boton.borrar.title': 'Delete private template',
    'plantillas.boton.borrar.aria': 'Delete template',
    'plantillas.boton.agregar': 'Add to estimate',
    'plantillas.boton.todascategorias': 'All categories',
    'calculadoras.shell.volver': 'Back to calculators',
    'estimado.concreto.forma_escalones': 'Steps',
    'estimado.concreto.base_losa': '{length}\' × {width}\' × {thicknessIn}" thick',
    'estimado.concreto.base_sonotube': '{tubeCount}× {diameterIn}" Ø × {heightFt}\' tall',
    'estimado.concreto.base_escalones': '{stepCount} steps × {stepRunIn}" run × {stepRiseIn}" rise × {stepWidthFt}\' wide',
    'estimado.concreto.partida_readymix': 'Ready-mix concrete',
    'estimado.concreto.partida_sacos': 'Concrete mix (80 lb bags)',
    'estimado.concreto.basis_desperdicio': '{basis}, +{waste}% waste',
    'estimado.concreto.basis_desperdicio_total': '{basis}, +{waste}% waste, {cuYd} cu yd total',
    'estimado.concreto.plantilla_nombre': 'Concrete — {basis}',
    'estimado.concreto.toast_readymix': 'Added {cuYd} cu yd ready-mix',
    'estimado.concreto.toast_sacos': 'Added {bags} × 80 lb bags',
    'estimado.concreto.campo_forma': 'Form',
    'estimado.concreto.seccion_medidas': 'Dimensions',
    'estimado.concreto.campo_largo': 'Length',
    'estimado.concreto.campo_grueso': 'Thickness',
    'estimado.concreto.seccion_tubo': 'Tube spec',
    'estimado.concreto.campo_diametro': 'Diameter',
    'estimado.concreto.campo_alto': 'Height',
    'estimado.concreto.campo_cantidad': 'Quantity',
    'estimado.concreto.unidad_tubos': 'tubes',
    'estimado.concreto.seccion_escalon': 'Step spec',
    'estimado.concreto.campo_num_escalones': 'Number of steps',
    'estimado.concreto.unidad_cada_uno': 'ea',
    'estimado.concreto.campo_run': 'Run (depth)',
    'estimado.concreto.campo_rise': 'Rise',
    'estimado.concreto.seccion_ocupas': 'You’ll need',
    'estimado.concreto.salida_sacos_valor': '{bags} × 80 lb bags',
    'estimado.concreto.hint_readymix': 'Includes {waste}% waste · rounded up to nearest 0.25 yd',
    'estimado.concreto.hint_sacos': 'Includes {waste}% waste · {cuYd} cu yd total',
    'estimado.concreto.salida_yardas': 'Cubic yards (with waste)',
    'estimado.concreto.salida_magic81': '"Magic 81" shortcut',
    'estimado.concreto.hint_magic81': 'Slab sqft ÷ 81 (4-inch) — sanity check',
    'estimado.concreto.salida_sacos_60': 'Bag count (60 lb)',
    'estimado.concreto.salida_sacos_40': 'Bag count (40 lb)',
    'estimado.concreto.salida_peso': 'Approximate weight',
    'estimado.concreto.boton_readymix': 'Add {cuYd} cu yd ready-mix',
    'estimado.concreto.boton_sacos': 'Add {bags} × 80 lb bags',
    'estimado.concreto.boton_sin_medidas': 'Enter dimensions to add',
    'estimados.calc.sod.zona_default': 'Front yard',
    'estimados.calc.sod.zona_nueva': { uno: 'Area {count}', otros: 'Area {count}' },
    'estimados.calc.sod.forma_curva': 'Curved edges (10%)',
    'estimados.calc.sod.forma_compleja': 'Complex / many cutouts (15%)',
    'estimados.calc.sod.pasto_mezcla': 'Fescue / Bluegrass blend',
    'estimados.calc.sod.areas': 'Lawn areas',
    'estimados.calc.sod.nombre_area': 'Area name',
    'estimados.calc.sod.quitar_area': 'Remove area',
    'estimados.calc.sod.largo': 'Length',
    'estimados.calc.sod.ancho': 'Width',
    'estimados.calc.sod.agregar_area': 'Add another area',
    'estimados.calc.sod.forma': 'Shape / waste',
    'estimados.calc.sod.tipo_pasto': 'Grass type',
    'estimados.calc.sod.necesitas': 'You’ll need',
    'estimados.calc.sod.cobertura': 'Coverage',
    'estimados.calc.sod.sqft_por_pallet': 'sqft per pallet',
    'estimados.calc.sod.sqft_por_slab': 'sqft per slab',
    'estimados.calc.sod.sqft_pedidos': ' sqft ordered',
    'estimados.calc.sod.boton_sin_medidas': 'Enter lawn dimensions to add',
    'estimados.calc.sod.toast_parte1': 'Added ',
    'estimados.calc.sod.toast_parte2': ' slabs of ',
    'calculadoras.mulch.nombre': 'Mulch / Soil / Aggregate Volume',
    'calculadoras.mulch.desc': 'Cubic yards from L × W × depth. Mulch, topsoil, gravel, sand, compost.',
    'calculadoras.sod.nombre': 'Sod / Lawn Area',
    'calculadoras.sod.desc': 'Square feet → slabs and pallets, with shape-based waste presets.',
    'calculadoras.concreto.nombre': 'Concrete',
    'calculadoras.concreto.desc': 'Cubic yards for slabs, footings, sonotubes, steps. Auto 10% waste.',
    'calculadoras.plantas.nombre': 'Plant Spacing',
    'calculadoras.plantas.desc': 'Plant count from area + on-center spacing. Triangular default.',
    'estimado.categorias.vacio_titulo': 'No categories yet',
    'estimado.categorias.vacio_descripcion': 'This trade is being prepared.',
    'estimado.plantillas.confianza.triangulado': 'Triangulated',
    'estimado.plantillas.confianza.aproximado': 'Estimated',
    'estimado.plantillas.aviso.catalogo': '⚠ catalog hint not found:',
    'estimado.plantillas.vacio.titulo': '— no templates yet',
    'estimado.plantillas.vacio.boton': 'Open calculator instead',
    'estimado.plantillas.ayuda.titulo': 'Where templates come from',
    'estimado.plantillas.ayuda.texto1': 'Drop the v2 templates JSON into',
    'estimado.plantillas.ayuda.texto2': 'and they will appear here.',
    'estimado.plantillas.toast.deconfiguracion': ' priced from your settings)',
    'estimado.plantillas.toast.zonafallback': 'zone',
    'estimado.plantillas.toast.zonanueva': '→ new zone "',
    'estimado.plantillas.conteo.servicios': ' services',
    'estimado.plantillas.boton.agregar': '\n                  Add\n',
    'estimado.plantillas.zona.title': 'Where to drop these lines',
    'estimado.plantillas.zona.arialabel': 'Destination zone',
    'estimado.plantillas.zona.auto': 'New zone (auto)',
    'estimado.plantillas.boton.menos': ' Less',
    'estimado.plantillas.meta.subcontrata': 'Often subcontracted',
    'estimado.plantillas.meta.calculadora': 'Calculator available',
    'estimado.plantillas.meta.calculadorapronto': 'Calculator coming soon',
    'estimado.plantillas.aviso.datos': 'Data review needed',
    'estimado.plantillas.aviso.precios': 'Pricing review',
    'estimado.plantillas.partidas.titulo': 'Line items (',
    'estimado.plantillas.partidas.suma': 'Line items sum',
    'estimado.plantillas.aviso.mediana1': 'Heads up — line items sum (',
    'estimado.plantillas.aviso.mediana2': ') doesn’t match the median (',
    'estimado.plantillas.aviso.mediana3': '). Drop will use the line items; edit the rows after if you want to match the median.',
    'estimado.plantillas.seccion.supuestos': 'Assumptions',
    'estimado.plantillas.seccion.exclusiones': 'Exclusions',
    'estimado.plantillas.seccion.normativa': 'Regulatory',
    'estimado.plantillas.seccion.fuentes': 'Sources',
    'finanzas.trabajos.filtro.todo': 'Everything',
    'finanzas.trabajos.filtro.proyectos': 'Projects',
    'finanzas.trabajos.filtro.visitas': 'Service visits',
    'finanzas.trabajos.stat.trabajos': 'Jobs shown',
    'finanzas.trabajos.stat.proyectosvisitas': 'Projects / visits',
    'finanzas.trabajos.stat.valor': 'Value shown',
    'finanzas.trabajos.placeholder.buscar': 'Search all work…',
    'finanzas.trabajos.vacio.titulo': 'No work to show',
    'finanzas.trabajos.etiqueta.sinfecha': 'No date',
    'contratos.stat.activos': 'Active contracts',
    'contratos.stat.recurrentemes': 'Recurring / month',
    'contratos.stat.soloactivos': 'Active only',
    'contratos.stat.visitashechas': 'Visits completed',
    'contratos.stat.ingresototal': 'Lifetime route revenue',
    'contratos.stat.facturado': 'Invoiced',
    'contratos.stat.facturadoAyuda': 'Paid or not',
    'contratos.stat.cobrado': 'Collected',
    'contratos.stat.porcobrar': 'Still owed',
    'contratos.stat.porcobrarAyuda': 'Unpaid invoices',
    'contratos.buscar.placeholder': 'Search clients or addresses…',
    'contratos.vacio.titulo': 'No contracts match',
    'contratos.vacio.texto': 'Recurring contracts created in Service Routes appear here.',
    'contratos.filtro.texto': 'Try a different search or status filter.',
    'contratos.tabla.cliente': 'Client',
    'contratos.tabla.frecuencia': 'Frequency',
    'contratos.tabla.estado': 'Status',
    'contratos.tabla.visitas': 'Visits',
    'contratos.tabla.pormes': 'Per month',
    'contratos.tabla.total': 'Lifetime',
    'contratos.tabla.abrirrutas': 'Open service routes',
    'contratos.movil.pormes': '/mo',
    'contratos.nota.rutas': 'Contracts are managed in Service Routes — selecting one takes you there.',
    'contratos.nota.facturacion': 'A month that has been invoiced is worth what its invoice says. A month nobody invoiced is valued per visit, which is an estimate.',
    'finanzasNum.fuente.proyectos': 'Project work',
    'finanzasNum.fuente.proyectosAyuda': 'One-off jobs, from accepted proposals through to completion',
    'finanzasNum.fuente.enPropuesta': 'recorded on the proposal',
    'finanzasNum.fuente.enFactura': 'recorded on a project invoice',
    'finanzasNum.fuente.rutas': 'Service routes',
    'finanzasNum.fuente.rutasAyuda': 'Completed recurring visits',
    'finanzasNum.titulo': 'Earnings by source',
    'finanzasNum.subtitulo': 'Money actually collected, counted on the day it landed.',
    'finanzasNum.kpi.recurrente': 'Recurring / month',
    'finanzasNum.kpi.recurrenteAyuda': 'Active service contracts',
    'finanzasNum.kpi.sinCobrar': 'Not yet collected',
    'finanzasNum.kpi.sinCobrarAyuda': 'Project value with no payment recorded',
    'finanzasNum.pl.titulo': 'Project profit & loss',
    'finanzasNum.pl.subtitulo': 'Every live project. Cancelled jobs excluded.',
    'finanzasNum.pl.materiales': 'Materials & expenses',
    'finanzasNum.pl.manoDeObra': 'Labor',
    'finanzasNum.pl.overhead': 'Share of running the business',
    'finanzasNum.pl.reparaciones': 'Repairs & maintenance',
    'finanzasNum.pl.neta': 'Net profit',
    'finanzasNum.pl.netaVacia': 'Net profit',
    'finanzasNum.pl.netaVaciaAyuda': 'Set your labor and overhead rates in Settings to see this',
    'finanzasNum.reparto.titulo': 'Where the money came from',
    'finanzasNum.reparto.vacioTitulo': 'No revenue in this period',
    'finanzasNum.reparto.vacioTexto': 'Revenue appears here once an invoice is marked Paid, a proposal is paid, or a service visit is completed.',
    'finanzasNum.reparto.nota1': 'These two add up to the Collected figure above. An accepted proposal becomes a',
    'finanzasNum.reparto.nota2': 'project, so proposal payments are project money — they are counted here, not',
    'finanzasNum.reparto.nota3': 'as a separate source.',
    'finanzasNum.libro.titulo': 'Project book (all time)',
    'finanzasNum.libro.subtitulo': 'Projects only — service-route revenue is not included here.',
    'finanzasNum.libro.gastos': 'Logged expenses',
    'finanzasNum.libro.bruta': 'Gross profit',
    'finanzasNum.libro.cobrado': 'Collected',
    'finanzasNum.libro.facturadoSinPagar': 'Invoiced, unpaid',
    'finanzasNum.libro.nota1': 'Gross profit is contract value minus logged expenses. Labor cost is tracked separately',
    'finanzasNum.libro.nota2': 'in time logs and is not deducted here.',
    'finanzasNum.mant.subtitulo': 'Logged repairs measured against the maintenance budget in your overhead.',
    'finanzasNum.mant.presupuestado': 'Budgeted',
    'finanzas.proyectos.buscar': 'Search projects or clients…',
    'finanzas.proyectos.filtro_todos': 'All',
    'finanzas.proyectos.vacio_titulo': 'No projects match',
    'finanzas.proyectos.col_proyecto': 'Project',
    'finanzas.proyectos.col_estado': 'Status',
    'finanzas.proyectos.col_terminado': 'Completed',
    'finanzas.proyectos.col_valor': 'Value',
    'finanzas.proyectos.col_materiales': 'Materials',
    'finanzas.proyectos.tooltip_ganancia_bruta': 'Value minus materials only — labor and overhead are not deducted',
    'finanzas.proyectos.col_ganancia_bruta': 'Gross profit',
    'finanzas.proyectos.tooltip_labor': 'Crew payroll + burden for the hours logged on this job',
    'finanzas.proyectos.tooltip_gastos_fijos': 'This job’s share of business overhead: hours × overhead per man-hour',
    'finanzas.proyectos.col_gastos_fijos': 'Overhead',
    'finanzas.proyectos.tooltip_ganancia_neta': 'Value minus materials, labor and overhead',
    'finanzas.proyectos.col_ganancia_neta': 'Net profit',
    'finanzas.proyectos.abrir_proyecto': 'Open project',
    'finanzas.proyectos.tooltip_sin_tarifas': 'Set your labor and overhead rates in Settings to see net profit',
    'finanzas.proyectos.tooltip_sin_horas': 'No hours were clocked against this job, so labor and overhead could not be charged. This is not a real net profit.',
    'finanzas.proyectos.sin_horas': 'no hours',
    'finanzas.proyectos.movil_terminado': 'Completed',
    'finanzas.proyectos.movil_neto': 'net',
    'finanzas.proyectos.movil_bruto': 'gross',
    'finanzas.proyectos.nota_pie': 'Open any project to see its full record — tasks, labor, materials and the photo log.',
    'seguimientos.columna.vistas': 'Viewed',
    'seguimientos.columna.ganadas': 'Won',
    'seguimientos.columna.perdidas': 'Lost',
    'seguimientos.tarjeta.vista': 'Viewed',
    'seguimientos.error.titulo': 'Could not load proposals',
    'seguimientos.error.descripcion': 'Check your connection and try again.',
    'seguimientos.error.reintentar': 'Retry',
    'seguimientos.detalle.propuesta_generica': 'Proposal',
    'seguimientos.detalle.cerrar': 'Close',
    'seguimientos.detalle.creada_por': 'Created by',
    'seguimientos.detalle.historial': 'Timeline',
    'seguimientos.detalle.enviada_titulo': 'Proposal Sent',
    'seguimientos.detalle.enviada_desc': 'You sent the client the link.',
    'seguimientos.detalle.vista_titulo': 'Client Viewed',
    'seguimientos.detalle.vista_desc': 'The client has seen the proposal.',
    'seguimientos.detalle.firma_titulo': 'Client signature',
    'seguimientos.detalle.firma_alt': 'Client Signature',
    'seguimientos.detalle.proyecto_creado': 'Project created',
    'seguimientos.detalle.convertir_proyecto': 'Convert to Project',
    'seguimientos.detalle.marcado_a_mano': 'You marked this by hand; the client never touched the link.',
    'seguimientos.mover.titulo': 'Heard back some other way?',
    'seguimientos.mover.descripcion': 'If they called, texted or told you in person, mark it here and the proposal moves to the right column.',
    'seguimientos.mover.titulo.reabrir': 'Did they come back to you?',
    'seguimientos.mover.descripcion.reabrir': 'Reopen it and it goes back on the board as if you had just sent it. If the date already passed, it gets {dias} more days so the client can still sign it.',
    'seguimientos.mover.reabrir.sinabrir': 'Reopen as unread',
    'seguimientos.mover.reabrir.abierta': 'Reopen as already seen',
    'seguimientos.reabrir.boton': 'Reopen',
    'seguimientos.reabrir.aria': 'Reopen {nombre}’s proposal',
    'seguimientos.mover.sinabrir': 'Not opened yet',
    'seguimientos.mover.abierta': 'They saw it',
    'seguimientos.mover.ganada': 'I won it',
    'seguimientos.mover.perdida': 'I lost it',
    'seguimientos.mover.moviendo': 'Moving…',
    'seguimientos.mover.ganadaesfinal': 'This one is already won and does not move from here — there is usually a signature and a project behind it. To correct it, change its status in Estimates.',
    'seguimientos.mover.perdidareabrir': 'To mark it won, reopen it first.',
    'seguimientos.mover.confirmaganada.titulo': 'Did you win this one?',
    'seguimientos.mover.confirmaganada.mensaje': 'You will not be able to move it back from the board. The button to turn it into a project shows up right after.',
    'seguimientos.mover.confirmaganada.si': 'Yes, I won it',
    'seguimientos.mover.listo': 'Done, it moved',
    'seguimientos.mover.listovigencia': 'Reopened, and good through {fecha}',
    'seguimientos.mover.error': 'Could not move the proposal',
    'seguimientos.mover.bloqueo.ganadaesfinal': '{nombre}’s proposal is already won — it does not come back from the board.',
    'seguimientos.mover.bloqueo.perdidanosalta': 'To mark {nombre}’s proposal won, reopen it first.',
    'seguimientos.arrastra.pista': 'Drag a card between columns to move it, or open it and mark it from there.',
    'ayuda.video.cerrar_tooltip': 'Close tutorial',
    'ayuda.video.cerrar_aria': 'Close tutorial',
    'ayuda.video.reproducir_aria': 'Play',
    'ayuda.video.proximamente': 'Tutorial coming soon',
    'ayuda.video.sin_video_desc': 'No video has been uploaded for this screen yet.',
    'ayuda.video.cargando': 'Loading tutorial…',
    'ayuda.video.error_titulo': 'This video would not load',
    'ayuda.video.error_reintentar': 'Retry',
    'ayuda.video.toca_para_ver': 'Tap to play',
    'ayuda.video.redimensionar_aria': 'Resize the window',
    'ayuda.video.restablecer': 'Back to the usual size and place',
    'ayuda.video.cerrar_confirma': 'Close the tutorial? You will lose your place.',
    'ayuda.video.cerrar_confirma_si': 'Close',
    'ayuda.video.cerrar_confirma_no': 'Keep watching',
    'ayuda.video.barra_aria': 'Seek',
    'ayuda.video.pantalla_completa_tooltip': 'Fullscreen',
    'ayuda.video.pantalla_completa_aria': 'Fullscreen',
    'ayuda.video.etiqueta': 'How to use',
    'ayuda.video.tooltip': 'Watch a short tutorial for this tab',
    'precios.catalogo.toast.guardado': 'Saved · ',
    'precios.catalogo.toast.error': 'Failed to save ',
    'precios.catalogo.grupo.sincategoria': '(uncategorized)',
    'precios.catalogo.titulo': 'Global Materials',
    'precios.catalogo.filtro.soloconprecio': 'Show only priced',
    'precios.catalogo.filtro.soloconpreciocorto': 'Priced only',
    'precios.catalogo.buscar.placeholder': 'Search materials…',
    'precios.catalogo.ayuda.titulo': 'Tap any price to set yours.',
    'precios.catalogo.ayuda.texto': 'The price you save here flows automatically into every estimate that uses this material.',
    'precios.catalogo.vacio.titulo': 'No items match your filters',
    'precios.catalogo.vacio.texto': 'Try another category or clear the search.',
    'precios.catalogo.costo.placeholder': 'Tap to set your cost',
    'precios.catalogo.tabla.conprecio': 'Priced',
    'precios.catalogo.tabla.nombre': 'Name',
    'precios.catalogo.tabla.unidad': 'Unit',
    'precios.catalogo.tabla.tipo': 'Kind',
    'precios.catalogo.tabla.tucosto': 'Your cost',
    'cuadrilla.error.desconocido': 'Unknown error',
    'cuadrilla.modal.titulo': 'Assign Crew',
    'cuadrilla.buscar.placeholder': 'Search by name or email…',
    'cuadrilla.filtro.incluirroles': 'Include Admin / Office roles',
    'cuadrilla.presets.titulo': 'Quick-add from preset crews',
    'cuadrilla.vacio.titulo': 'No matches',
    'cuadrilla.vacio.texto': 'No team members match your search or role filter.',
    'cuadrilla.boton.cancelar': 'Cancel',
    'cuadrilla.boton.guardando': 'Saving…',
    'proyecto.equipo.titulo': 'Equipment Assignments',
    'proyecto.equipo.descripcion': 'Sign out heavy machinery and fleet vehicles specific to this project.',
    'proyecto.equipo.cancelar': 'Cancel',
    'proyecto.equipo.registrarnuevo': 'Register New',
    'proyecto.equipo.registrorapido': 'Quick-register equipment',
    'proyecto.equipo.placeholdernombre': 'Equipment name (e.g. CAT 330 Excavator)',
    'proyecto.equipo.tipoherramienta': 'Tool',
    'proyecto.equipo.tipovehiculo': 'Vehicle',
    'proyecto.equipo.tipopesado': 'Heavy Equipment',
    'proyecto.equipo.tipotrailer': 'Trailer',
    'proyecto.equipo.tipootro': 'Other',
    'proyecto.equipo.registrando': 'Registering…',
    'proyecto.equipo.registrar': 'Register',
    'proyecto.equipo.enlaobra': 'On site',
    'proyecto.equipo.regresando': 'Returning…',
    'proyecto.equipo.regresaraltaller': 'Return to Shop',
    'proyecto.equipo.sinasignado': 'No equipment assigned',
    'proyecto.equipo.sinasignadodesc': 'Check out items from the shop inventory to track them on this job.',
    'proyecto.equipo.inventariotaller': 'Shop inventory',
    'proyecto.equipo.sacando': 'Checking out…',
    'proyecto.equipo.sacar': 'Check Out',
    'proyecto.equipo.sindisponible': 'No equipment available',
    'proyecto.equipo.sindisponibledesc': 'Everything is checked out or in the shop for maintenance.',
    'proyecto.equipo.toastsalioatrabajo': ' checked out to this job',
    'proyecto.equipo.toastnosepudosacar': 'Could not check out ',
    'proyecto.equipo.toastregresotaller': ' returned to the shop',
    'proyecto.equipo.toastnosepudoregresar': 'Could not return ',
    'proyecto.equipo.toastregistrado': ' registered',
    'proyecto.equipo.toastnosepudoregistrar': 'Could not register equipment: ',
    'finanzasProy.facturas.pagadaEl': 'Marked paid ',
    'finanzasProy.facturas.cambiarEstatus': 'Change invoice status',
    'finanzasProy.gastos.guardadoSinRecibo': ' — saving without receipt.',
    'finanzasProy.gastos.agregado': 'Expense added',
    'finanzasProy.gastos.reciboAgregado': 'Receipt attached',
    'finanzasProy.pdf.negocioSinNombre': 'Your Business',
    'finanzasProy.pdf.comoPagar': 'Payment Instructions',
    'finanzasProy.pdf.pagarEnLinea': 'Pay online: ',
    'finanzasProy.pdf.zelleTelefono': 'Zelle phone: ',
    'finanzasProy.pdf.cobrarA': 'Bill to',
    'finanzasProy.pdf.fecha': 'Invoice date:',
    'finanzasProy.pdf.sinConceptos': 'No line items',
    'finanzasProy.pdf.totalPagar': 'Total Due',
    'finanzasProy.pdf.gracias': 'Thank you for your business.',
    'finanzasProy.pdf.popupBloqueado': 'Pop-up blocked. Allow pop-ups to download the PDF.',
    'finanzasProy.kpi.facturado': 'Invoiced $',
    'finanzasProy.kpi.pagado': '· Paid $',
    'finanzasProy.kpi.margenNeto': 'Net margin ',
    'finanzasProy.costos.manoDeObra': 'Labor burden (',
    'finanzasProy.gastos.reciboVista': 'Receipt preview',
    'finanzasProy.gastos.quitarFoto.title': 'Remove photo',
    'finanzasProy.gastos.quitarFoto.aria': 'Remove photo',
    'finanzasProy.gastos.subiendoRecibo': 'Uploading receipt…',
    'finanzasProy.gastos.verRecibo.aria': 'View receipt',
    'finanzasProy.gastos.agregarFoto.aria': 'Attach receipt photo',
    'finanzasProy.gastos.cambiarRecibo.title': 'Replace receipt',
    'finanzasProy.gastos.cambiarRecibo.aria': 'Replace receipt',
    'comun.cerrar': 'Close',
    'finanzasProy.facturas.numero.placeholder': 'e.g. INV-001',
    'finanzasProy.facturas.terminos.alRecibir': 'Due on Receipt',
    'finanzasProy.facturas.conceptos': 'Line items',
    'finanzasProy.facturas.crearBoton': 'Create Invoice',
    'proyecto.materiales.columna.listacompras': 'Shopping List',
    'proyecto.materiales.columna.listo': 'Ready / Staged',
    'proyecto.materiales.item.sueltonombre': 'Ad-hoc Item',
    'proyecto.materiales.item.sueltotipo': 'Ad-hoc',
    'proyecto.materiales.grupo.sinproveedor': 'No vendor',
    'proyecto.materiales.compartir.titulo': 'Shopping List',
    'proyecto.materiales.compartir.vacio': 'Nothing to order.',
    'proyecto.materiales.imprimir.bloqueado': 'Pop-up blocked. Allow pop-ups to print.',
    'proyecto.materiales.csv.articulo': 'Item',
    'proyecto.materiales.csv.proveedor': 'Vendor',
    'proyecto.materiales.csv.tipo': 'Type',
    'proyecto.materiales.csv.cantidad': 'Quantity',
    'proyecto.materiales.csv.costo': 'Cost',
    'proyecto.materiales.csv.estado': 'Status',
    'proyecto.materiales.editar.descripcion': 'Description',
    'proyecto.materiales.tarjeta.editar.tooltip': 'Edit',
    'proyecto.materiales.tarjeta.quitar.tooltip': 'Remove',
    'proyecto.materiales.tarjeta.huerfano': 'Not in estimate',
    'proyecto.materiales.tarjeta.huerfano.tooltip': 'This line is no longer in the job’s estimate. It still counts in Financials; remove it if it no longer applies.',
    'proyecto.materiales.tarjeta.unidades': 'units',
    'proyecto.materiales.tarjeta.mover': 'Move',
    'proyecto.materiales.tarjeta.deshacer': 'Undo',
    'proyecto.materiales.titulo': 'Procurement Board',
    'proyecto.materiales.boton.cancelar': 'Cancel',
    'proyecto.materiales.boton.agregaralista': 'Add to Shopping List',
    'proyecto.materiales.form.cerrar': 'Close',
    'proyecto.materiales.form.nombre': 'Material name (e.g. 60 lb concrete bag)',
    'proyecto.materiales.form.sugerencias': 'From your pricebook',
    'proyecto.materiales.grupo.pedirtodo.tooltip': 'Mark all in this vendor group as Ordered',
    'proyecto.materiales.grupo.pedirtodo': 'Order all',
    'proyecto.materiales.columna.vacia': 'Nothing here',
    'proyecto.resumen.cargandoEspacio': 'Still loading your workspace — try again in a moment.',
    'proyecto.fechas.guardadas': 'Schedule saved',
    'proyecto.lider.asignado': '{quien} is in charge on {dia}.',
    'proyecto.lider.diaQuitado': 'That day was removed from the schedule.',
    'proyecto.fechas.errorGuardar': 'Couldn’t save the dates: ',
    'proyecto.geocerca.errorRadio': 'Couldn’t save the radius: ',
    'proyecto.bitacora.tipo.retraso': 'Delay',
    'proyecto.bitacora.tipo.avance': 'Milestone',
    'proyecto.bitacora.tipo.seguridad': 'Safety Issue',
    'proyecto.bitacora.placeholder': 'Log progress, delays, or notes…',
    'proyecto.bitacora.enviar': 'Send journal entry',
    'proyecto.bitacora.adjunto': 'Attachment',
    'proyecto.bitacora.fotoLista': 'Photo attached — press send',
    'proyecto.bitacora.quitarFoto.title': 'Remove photo',
    'proyecto.bitacora.quitarFoto.aria': 'Remove photo',
    'proyecto.bitacora.agregarFoto': 'Attach photo',
    'proyecto.cuadrilla.actividadEnObra': 'On-site activity',
    'proyecto.cuadrilla.editar.title': 'Edit crew assignment',
    'proyecto.cuadrilla.editar.aria': 'Edit crew assignment',
    'proyecto.cuadrilla.vacio.titulo': 'No crew assigned',
    'proyecto.cuadrilla.vacio.texto': 'Assign workforce so their hours and on-site status show up here.',
    'proyecto.cuadrilla.asignar': 'Assign workforce',
    'proyecto.cuadrilla.distanciaObra': 'ft from site',
    'proyecto.cuadrilla.fueraDeObra': 'Off-site',
    'proyecto.cuadrilla.enObra': 'On site',
    'proyecto.cuadrilla.sinRegistro': 'Not yet',
    'proyecto.geocerca.inactivaSinCoords': 'Inactive (no coordinates)',
    'proyecto.geocerca.activa': 'Active',
    'proyecto.geocerca.soloLectura': 'Only the office can change this job’s geofence.',
    'proyecto.geocerca.inactiva': 'Inactive',
    'proyecto.geocerca.direccion': 'Address to geocode',
    'proyecto.geocerca.direccion.placeholder': 'e.g. 123 Main St, City, ST',
    'proyecto.geocerca.buscar': 'Get',
    'proyecto.geocerca.radio': 'Radius (feet)',
    'comun.guardar': 'Save',
    'proyecto.fechas.inicio': 'Start',
    'proyecto.fechas.fin': 'Target end',
    'proyecto.fechas.guardar': 'Save dates',
    'proyecto.actividad.titulo': 'Project Activity',
    'proyecto.actividad.vacio.titulo': 'No activity yet',
    'proyecto.actividad.vacio.texto': 'Log a note in the Site Journal or clock in to start the timeline.',
    'proyecto.actividad.marcoEntrada': 'clocked in',
    'proyecto.actividad.trabajando': 'Currently active',
    'proyecto.actividad.fotoEntrada': 'Clock-in photo',
    'proyecto.actividad.verFoto': 'View photo',
    'proyecto.actividad.autorSistema': 'System log',
    'proyecto.actividad.abrirFoto': 'Open site photo',
    'proyectos.tareas.materiales_titulo': 'Mark linked materials as Installed?',
    'proyectos.tareas.materiales_confirmar': 'Yes, mark Installed',
    'proyectos.tareas.materiales_cancelar': 'Just complete the task',
    'proyectos.tareas.borrar_titulo': 'Delete task?',
    'proyectos.tareas.borrar_mensaje': 'This permanently removes the task. This cannot be undone.',
    'proyectos.tareas.borrar_cancelar': 'Cancel',
    'proyectos.tareas.hecho_por': 'Done by',
    'proyectos.tareas.asignada_a': 'Assigned to',
    'proyectos.tareas.asignar_tooltip': 'Assign to',
    'proyectos.tareas.asignar_aria': 'Assign to',
    'proyectos.tareas.fecha_tooltip': 'Due date',
    'proyectos.tareas.fecha_aria': 'Due date',
    'proyectos.tareas.mover_prep_tooltip': 'Move to Mobilization',
    'proyectos.tareas.mover_prep_aria': 'Move to Mobilization',
    'proyectos.tareas.borrar_tooltip': 'Delete task',
    'proyectos.tareas.borrar_aria': 'Delete task',
    'proyectos.tareas.fase_vacia': 'No tasks in this phase',
    'proyectos.tareas.poner_fase': 'Set the composer to',
    'proyectos.tareas.nueva_tarea_placeholder': 'Add a new task…',
    'proyectos.tareas.agregar_tarea_aria': 'Add task',
    'proyectos.tareas.cualquiera_valor': 'Anyone (Unassigned)',
    'proyectos.tareas.cualquiera_opcion': 'Anyone (Unassigned)',
    'proyectos.tareas.fase_prep': 'Prep',
    'proyectos.tareas.fase_ejecucion': 'Execution',
    'proyectos.tareas.fase_acabado': 'Finish',
    'proyectos.tareas.fecha_opcional_aria': 'Due date (optional)',
    'proyectos.tareas.quitar_fecha_aria': 'Clear due date',
    'proyectos.tareas.quitar_fecha_tooltip': 'Clear due date',
    'proyectos.tareas.vacio_titulo': 'No tasks yet',
    'proyectos.tareas.vacio_texto': 'Add the first task above to start tracking this job’s punch list.',
    'proyectos.tareas.autoetiquetar_tooltip': 'Distribute tasks into phases by keyword',
    'proyectos.tareas.autoetiquetar': 'Auto-tag phases',
    'proyecto.horas.csv_empleado': 'Employee',
    'proyecto.horas.csv_fecha': 'Date',
    'proyecto.horas.csv_desconocido': 'Unknown',
    'proyecto.horas.csv_activo': 'Active',
    'proyecto.horas.presupuesto_titulo': 'Labor Budget Burn',
    'proyecto.horas.presupuesto_desc': 'Track real-time hours vs estimate.',
    'proyecto.horas.barra_inicio': 'Start',
    'proyecto.horas.barra_limite': 'Budget limit',
    'proyecto.horas.excedido_titulo': 'Budget exceeded:',
    'proyecto.horas.excedido_texto1': 'you are',
    'proyecto.horas.excedido_texto2': 'hours over estimate.',
    'proyecto.horas.eficiencia_titulo': 'Crew Efficiency',
    'proyecto.horas.horas_registradas': 'hours logged',
    'proyecto.horas.sin_horas_titulo': 'No time logged yet',
    'proyecto.horas.sin_horas_desc': 'Hours appear here as the crew clocks in and out of this job.',
    'proyecto.horas.detalle_titulo': 'Detailed Logs',
    'proyecto.horas.exportar_csv': 'Export CSV',
    'proyecto.horas.col_empleado': 'Employee',
    'proyecto.horas.col_fecha': 'Date',
    'proyecto.horas.col_entrada_salida': 'In / Out',
    'proyecto.horas.col_duracion': 'Duration',
    'proyecto.horas.estado_activo': 'Active',
    'proyecto.horas.sin_checadas_titulo': 'No punches on this job',
    'proyecto.horas.sin_checadas_desc': 'Clock-ins for this project will be listed here.',
    'propuesta.general.etiqueta': 'Proposal',
    'propuesta.general.botonfirmar': 'Sign & Accept',
    'propuesta.estado.firmada': 'signed & accepted',
    'propuesta.resenas.eyebrow': 'Testimonials',
    'propuesta.resenas.titulo': 'What our clients say',
    'propuesta.resenas.dejargoogle': 'Leave us a review on Google',
    'propuesta.resenas.dejaryelp': 'Leave us a review on Yelp',
    'propuesta.social.titulo': 'Follow us online',
    'propuesta.pago.deposito': 'Pay deposit',
    'propuestas.editor.alerta.faltanpartidas.titulo': 'Missing Items',
    'propuestas.editor.alerta.faltanpartidas.mensaje': 'Please ensure the estimate has items before generating a scope.',
    'propuestas.editor.alerta.fallogenerar.titulo': 'Generation Failed',
    'propuestas.editor.tab.contenido': 'Content',
    'propuestas.editor.tab.diseno': 'Design',
    'propuestas.editor.tab.formato': 'Sections',
    'propuestas.editor.tab.precio': 'Pricing',
    'propuestas.editor.tab.compartir': 'Send',
    'propuestas.editor.boton.verpropuesta': 'Preview Proposal',
    'propuestas.editor.tooltip.descargarpdf': 'Letterhead, line items, totals and the signature lines — no cover photo, scope or legal terms.',
    'propuestas.editor.boton.descargarpdf': 'Download PDF',
    'propuestas.editor.pdf.solotarjeta.titulo': 'Change how you get paid before downloading',
    'propuestas.editor.pdf.solotarjeta.mensaje': 'Right now the card link is the only method turned on, and it is the one thing that does not survive paper: printed, it is a very long address nobody is going to type. Turn on Zelle or bank transfer and download it again.',
    'propuestas.editor.pdf.solotarjeta.ir': 'Go to Pricing',
    'propuestas.editor.boton.vercomocliente': 'View as client',
    'propuesta.editor.esvistacliente': 'This is your client’s view. They sign here, not you.',
    'propuesta.editor.editarseccion': 'Edit',
    'propuesta.editor.solotuloves': 'Only you see this',
    'propuesta.precios.contratoadjunto': 'On accepting, your client also signs "{contrato}".',
    'propuesta.requisito.titulo': 'Proposal title',
    'propuesta.arreglo.titulo': 'Give it a name your client will recognise, like "Back patio — the García house".',
    'propuesta.requisito.alcance': 'Scope of work written',
    'propuesta.arreglo.alcance': 'Tell them what you will do, or let AI build it from your line items.',
    'propuesta.requisito.partidas': 'The estimate has line items',
    'propuesta.arreglo.partidas': 'With no line items there is no price to show. Go back to the estimate and add them.',
    'propuesta.requisito.vigencia': 'Still within its validity date',
    'propuesta.arreglo.vigencia': 'The date has passed or is unset. Give it +7, +14 or +30 days under Pricing.',
    'propuesta.progreso.listo': 'Ready to send',
    'propuesta.progreso.cuenta': '{hechos} of {total} done',
    'propuesta.progreso.abrir': 'See what is missing',
    'propuesta.progreso.arreglar': 'Fix',
    'propuesta.layout.resenas.sincontenido': 'Turned on, but your business has no links or saved reviews yet, so nothing will show.',
    'propuesta.layout.resenas.sincontenido.enlace': 'Add them now',
    'propuesta.marca.galeria.encendidavacia': 'The gallery is on and has no photos: nothing will show on the proposal.',
    'propuesta.marca.galeria.quitada': 'Photo removed from the proposal',
    'propuesta.vacio.alcance.titulo': 'You have not described the job yet',
    'propuesta.vacio.alcance.desc': 'Write it your way, or let AI build it from your line items. It is the first thing the client reads.',
    'propuesta.vacio.galeria.titulo': 'The gallery is on, but has no photos',
    'propuesta.vacio.galeria.desc': 'Upload job photos under Design. Without them, the client sees nothing here.',
    'propuesta.vacio.resenas.titulo': 'Reviews are on, but there is nothing to show',
    'propuesta.vacio.resenas.desc': 'Add your Google, Yelp and social links under Estimates ▸ Proposal ▸ Marketing & brand.',
    'propuesta.autoguardado.reposo': 'Everything saves itself as you edit',
    'propuesta.autoguardado.guardando': 'Saving…',
    'propuesta.autoguardado.guardado': 'Saved',
    'propuesta.atajo.yaguardado': 'Already saved — you do not have to ask',
    'propuesta.galeria.etiqueta': 'Portfolio',
    'propuesta.galeria.titulo': 'Visualizing the project',
    'propuesta.galeria.imagenalt': 'Gallery',
    'propuesta.galeria.despuesalt': 'after',
    'propuesta.portada.imagenalt': 'Cover',
    'propuesta.portada.preparadopara': 'Prepared exclusively for',
    'propuesta.portada.clientepredeterminado': 'Valued Client',
    'propuesta.precios.upsellmantenimiento': 'Maintenance Plans',
    'propuesta.precios.upselltemporada': 'Seasonal Services',
    'propuesta.precios.upsellpaquete': 'Bundled Savings',
    'propuesta.precios.upsellrecomendado': 'Recommended Add-ons',
    'propuesta.precios.desglose': 'Investment breakdown',
    'propuesta.precios.estimado': 'Project estimate',
    'propuesta.precios.serviciopartida': 'Service / Item',
    'propuesta.precios.cantidad': 'Qty',
    'propuesta.precios.opcional': 'Optional',
    'propuesta.precios.upsellsactivos': 'Upsells enabled',
    'propuesta.precios.upsellsactivosdesc': 'AI recommendations or manually added upsells will appear here when configured.',
    'propuesta.precios.inversiontotal': 'Total investment',
    'propuesta.precios.incluyeimpuestos': 'Includes applicable taxes and fees',
    'propuesta.precios.vencioel': 'This proposal expired on ',
    'propuesta.precios.validahasta': 'Valid until ',
    'propuesta.precios.deposito': 'Deposit due (',
    'propuesta.precios.restoalterminar': 'Balance on completion',
    'propuesta.precios.mifirma': 'My signature',
    'propuesta.precios.altfirma': 'Signature',
    'propuesta.precios.rechazar': 'Decline',
    'propuesta.precios.firmado': ' signed',
    'propuesta.precios.pagardeposito': 'Pay deposit now',
    'propuesta.alcance.titulo': 'Scope of work',
    'propuesta.empresa.logoalt': 'Company Logo',
    'propuesta.empresa.textopredeterminado': 'We are dedicated to providing top-tier service with a focus on quality, reliability, and customer satisfaction.',
    'firma.alerta.faltafirma.titulo': 'Signature Required',
    'firma.alerta.faltafirma.texto': 'Please sign before accepting.',
    'firma.alerta.faltanombre.titulo': 'Name Required',
    'firma.alerta.faltanombre.texto': 'Please type your full legal name.',
    'firma.alerta.faltaconsentimiento.titulo': 'Consent Required',
    'firma.alerta.faltaconsentimiento.texto': 'Please confirm you agree to sign electronically.',
    'firma.modal.titulo': 'Sign to accept',
    'firma.modal.aviso': 'By signing below, you agree to the terms and conditions outlined in this',
    'firma.boton.borrar': 'Clear',
    'firma.linea.cliente': 'Client Signature',
    'firma.nombre.placeholder': 'Type your full legal name',
    'firma.consentimiento.texto': 'I agree to sign electronically, and that my electronic signature is the legal equivalent of my handwritten signature (ESIGN / UETA).',
    'firma.boton.cancelar': 'Cancel',
    'firma.boton.firmar': 'Sign & Accept',
    'propuesta.impresion.etiqueta.defecto': 'Proposal',
    'propuesta.impresion.ficha.cliente': 'Client',
    'propuesta.impresion.ficha.obra': 'Job address',
    'propuesta.impresion.ficha.vigencia': 'Valid until',
    'propuesta.impresion.portada.fecha': 'Date',
    'propuesta.impresion.portada.referencia': 'Reference',
    'propuesta.impresion.tabla.cantidad': 'Quantity',
    'propuesta.impresion.pago.metodo.tarjeta': 'Card or online payment',
    'propuesta.impresion.pago.metodo.zelle': 'Zelle',
    'propuesta.impresion.pago.metodo.banco': 'Bank transfer (ACH)',
    'propuesta.impresion.firmaenlinea.titulo': 'Sign it from your phone',
    'propuesta.impresion.firmaenlinea.texto': 'Point your camera at the code, or go to the address below. You can read the full proposal and sign it there without printing anything.',
    'propuesta.impresion.firmaenlinea.qr.alt': 'QR code with the link to the proposal',
    'propuesta.impresion.firmaenlinea.contrato': 'If you would rather sign by hand, the contract is attached at the end of this document.',
    'propuesta.impresion.anexo.etiqueta': 'Attachment',
    'propuesta.impresion.anexo.hoja': 'Sheet {n} of {total}',
    'propuesta.impresion.enlace.etiqueta': 'Your proposal online',
    'propuesta.impresion.tipo.recibo': 'Receipt',
    'propuesta.impresion.tipo.contrato': 'Agreement',
    'propuesta.impresion.logo.alt': 'Business',
    'propuesta.impresion.empresa.titulo': 'About us',
    'propuesta.impresion.titulo.antetitulo': 'Your Investment',
    'propuesta.impresion.titulo.defecto': 'Project Estimate',
    'propuesta.impresion.alcance.titulo': 'Scope of Work',
    'propuesta.impresion.partidas.titulo': 'The work, line by line',
    'propuesta.impresion.portada.departede': 'From',
    'propuesta.impresion.tabla.encabezado': 'Service / Item',
    'propuesta.impresion.extras.titulo': 'Optional add-ons — not included in the total',
    'propuesta.impresion.total.etiqueta': 'Total Investment',
    'propuesta.impresion.total.impuestos': 'Includes applicable taxes and fees.',
    'propuesta.impresion.firma.etiqueta': 'Signed by client',
    'propuesta.impresion.firma.alt': 'Client signature',
    'propuesta.impresion.total.anticipo': 'Deposit due',
    'propuesta.impresion.total.resto': 'Balance on completion',
    'propuesta.impresion.pago.titulo': 'Payment Instructions',
    'propuesta.impresion.pago.alaceptar': 'Due on acceptance:',
    'propuesta.impresion.pago.anticipo': '% deposit)',
    'propuesta.impresion.pago.resto': '— balance ',
    'propuesta.impresion.pago.alterminar': 'on completion.',
    'propuesta.impresion.vigencia.texto': 'This offer is valid until',
    'propuesta.impresion.vigencia.dias': { uno: '{dias} day left', otros: '{dias} days left' },
    'propuesta.impresion.vigencia.vencida': ' · expired',
    'propuesta.impresion.pago.enlinea': 'Pay online at:',
    'propuesta.impresion.pago.banco': 'Bank transfer (ACH):',
    'propuesta.impresion.pago.banco.ruta': 'routing',
    'propuesta.impresion.pago.banco.cuenta': 'account',
    'propuesta.impresion.pago.directo': 'Payment is arranged directly with us — we will be in touch after you accept.',
    'propuesta.impresion.contrato.adjunto': 'Attached contract:',
    'propuesta.impresion.contratoadjunto.titulo': 'Attached contract',
    'propuesta.impresion.contratoadjunto.hojas': { uno: '{hojas} page', otros: '{hojas} pages' },
    'propuesta.impresion.contratoadjunto.hoja.alt': 'Contract page {n}',
    'propuesta.impresion.contratoadjunto.preparando': 'Preparing the contract…',
    'propuesta.impresion.contratoadjunto.error': 'Could not prepare the contract. The proposal prints without it.',
    'propuesta.impresion.contrato.firmaenlinea': '— signed online from your proposal link.',
    'propuesta.impresion.terminos.titulo': 'Terms & Conditions',
    'propuesta.impresion.garantia.titulo': 'Warranty',
    'propuesta.impresion.firmas.titulo': 'Agreement & Signatures',
    'propuesta.impresion.firmas.aviso': 'By signing below, both parties agree to the pricing and terms set out in this document',
    'propuesta.impresion.firmas.fecha': ', dated ',
    'propuesta.impresion.firmas.cliente': 'Client',
    'propuesta.impresion.firmas.contratista': 'Contractor',
    'propuesta.impresion.firmas.lineafirma': 'Signature —',
    'propuesta.impresion.firmas.nombre': 'name',
    'propuesta.impresion.firmas.lineafecha': 'Date',
    'propuesta.marca.tema.moderno': 'Modern',
    'propuesta.marca.tema.natural': 'Natural',
    'propuesta.marca.tema.elegante': 'Elegant',
    'propuesta.marca.tema.urlpropia': 'Custom URL',
    'propuesta.marca.tipografia.sans': 'Modern Sans',
    'propuesta.marca.tipografia.serif': 'Classic Serif',
    'propuesta.marca.tipografia.mono': 'Technical Mono',
    'propuesta.marca.animacion.ninguna': 'None',
    'propuesta.marca.animacion.aparecer': 'Fade In',
    'propuesta.marca.animacion.subir': 'Slide Up',
    'propuesta.marca.animacion.zoom': 'Zoom',
    'propuesta.marca.titulo': 'Design & Media',
    'propuesta.marca.enlacebiblioteca': 'Branding library in Proposal',
    'propuesta.marca.colores.titulo': 'Brand Colors',
    'propuesta.marca.colores.principal.aria': 'Primary brand color',
    'propuesta.marca.colores.secundario': 'Secondary (Accent)',
    'propuesta.marca.colores.secundario.aria': 'Secondary accent color',
    'propuesta.marca.tipografia.titulo': 'Typography',
    'propuesta.marca.portada.guardadas': 'Your Saved Images',
    'propuesta.marca.portada.errorsubida': 'Failed to upload cover image',
    'propuesta.marca.portada.subir': 'Upload',
    'propuesta.marca.portada.ayuda': 'Tap the one you want, or upload your own.',
    'propuesta.marca.portada.guardada': 'Your photo {n}',
    'propuesta.marca.portada.tuya': 'Your photo',
    'propuesta.marca.portada.usar': 'Use the {nombre} cover',
    'propuesta.marca.portada.suelta': 'Drop it here',
    'propuesta.marca.portada.formatos': 'JPG, PNG or WEBP · up to 10 MB',
    'propuesta.marca.encuadre.restablecer': 'Reset',
    'propuesta.marca.encuadre.altprevia': 'Cover preview',
    'propuesta.marca.encuadre.llenar': 'Fill',
    'propuesta.marca.encuadre.llenar.ayuda': 'Crops to fill the banner',
    'propuesta.marca.encuadre.completa': 'Fit whole image',
    'propuesta.marca.encuadre.completa.ayuda': 'Shows the full image, letterboxed',
    'propuesta.marca.encuadre.horizontal': 'Horizontal',
    'propuesta.marca.encuadre.vertical': 'Vertical',
    'propuesta.marca.portada.opacidad': 'Overlay Opacity',
    'propuesta.marca.galeria.ayuda': 'Photos of work you have already done. Uploading the first one turns the gallery on for you.',
    'propuesta.marca.galeria.ayudaon': 'These photos show on the proposal.',
    'propuesta.marca.galeria.ayudaoff': 'The gallery is off — these photos do not show on the proposal.',
    'propuesta.marca.galeria.mostrar': 'Show the gallery',
    'propuesta.marca.galeria.agregarrapido': 'Quick Add from Library',
    'propuesta.marca.galeria.yaagregada': 'Already in this gallery',
    'propuesta.marca.galeria.errorsubida': 'Failed to upload image',
    'propuesta.marca.galeria.errorvarias': { uno: '1 photo could not be uploaded.', otros: '{count} photos could not be uploaded.' },
    'propuesta.marca.galeria.subir': 'Upload photos',
    'propuesta.marca.galeria.pieplaceholder': 'Caption (optional)',
    'propuesta.marca.galeria.piearia': 'Image caption',
    'propuesta.marca.galeria.principal.tooltip': 'Set as the feature (hero) tile',
    'propuesta.marca.galeria.esprincipal': 'Feature',
    'propuesta.marca.galeria.hacerprincipal': 'Set feature',
    'propuesta.marca.galeria.altdespues': 'after',
    'propuesta.marca.galeria.imagendespues': 'After image',
    'propuesta.marca.galeria.quitardespues.tooltip': 'Remove after image',
    'propuesta.marca.galeria.quitardespues.aria': 'Remove after image',
    'propuesta.marca.galeria.errordespues': 'Failed to upload after image',
    'propuesta.marca.galeria.agregardespues': 'Add a paired “after” image (revealed on hover)',
    'propuesta.marca.galeria.antesdespues': 'Before/After',
    'propuesta.marca.galeria.subirorden': 'Move image up',
    'propuesta.marca.galeria.bajarorden': 'Move image down',
    'propuesta.marca.galeria.quitar.tooltip': 'Remove',
    'propuesta.marca.galeria.quitar.aria': 'Remove image',
    'propuesta.marca.animacion.titulo': 'Entry Animation',
    'propContenido.titulo': 'Content & Messaging',
    'propContenido.tituloPropuesta': 'Proposal Title',
    'propContenido.tituloPropuesta.placeholder': 'e.g. Backyard Renovation',
    'propContenido.etiquetaDoc': 'Document Label',
    'propContenido.etiquetaDoc.valor': 'Proposal',
    'propContenido.etiquetaDoc.placeholder': 'Proposal',
    'propContenido.botonAccion': 'Action Button',
    'propContenido.botonAccion.valor': 'Sign & Accept',
    'propContenido.botonAccion.placeholder': 'Sign & Accept',
    'propContenido.alcance.titulo': 'Scope of Work',
    'propContenido.alcance.escribiendo': 'Writing…',
    'propContenido.alcance.generar': 'AI Generate',
    'propContenido.alcance.viejo': 'Line items changed since this scope was generated — consider regenerating.',
    'propContenido.alcance.placeholder': 'Write a personal note (Spanish/English) or generate a professional Scope of Work using AI...',
    'propContenido.alcance.markdown': 'Markdown supported',
    'propContenido.perfil.titulo': 'Company Profile',
    'propContenido.perfil.placeholder': 'Tell your company story here...',
    'propContenido.perfil.ayuda': 'Your story, exactly as the client reads it.',
    'propContenido.perfil.mostrar': 'Show company profile',
    'propContenido.legal.titulo': 'Warranty & Terms',
    'propContenido.garantia.escoger': 'Select Warranty',
    'propContenido.garantia.ninguna': 'None / Custom',
    'propContenido.garantia.mostrar': 'Show Warranty',
    'propContenido.terminos.titulo': 'Legal Terms',
    'propContenido.terminos.placeholder': 'Standard terms from settings...',
    'propContenido.contrato.ver': 'Preview ',
    'propContenido.contrato.adjunto': 'Attached contract',
    'propContenido.contrato.sinadjuntar': 'None yet',
    'propContenido.contrato.ira': 'Choose it in Share',
    'propContenido.contrato.dondeayuda': 'Attached from the Share tab, next to the checklist of what is still missing before sending.',
    'propuesta.layout.seccion_resumen': 'Executive Summary',
    'propuesta.layout.seccion_perfil': 'Company Profile',
    'propuesta.layout.seccion_galeria': 'Image Gallery',
    'propuesta.layout.seccion_precios': 'Pricing & Options',
    'propuesta.layout.seccion_partidas': 'Line Item Details',
    'propuesta.layout.seccion_totales': 'Total Summary',
    'propuesta.layout.seccion_terminos': 'Terms & Signature',
    'propuesta.layout.titulo': 'Structure & Sections',
    'propuesta.layout.orden_label': 'Section Order & Visibility',
    'propuesta.layout.ayuda': 'Use the arrows to reorder sections. Toggle switches to show or hide sections on the proposal.',

    // ── How the price breakdown is drawn ────────────────────────────
    'propuesta.layout.precios.titulo': 'How the price is shown',
    'propuesta.layout.modo.itemizado': 'Itemized',
    'propuesta.layout.modo.itemizado.sub': 'Every estimate line',
    'propuesta.layout.modo.agrupado': 'Grouped',
    'propuesta.layout.modo.agrupado.sub': 'Curated scope sections',
    'propuesta.layout.eje.titulo': 'Group items',
    'propuesta.layout.eje.tipo': 'By type',
    'propuesta.layout.eje.tipo.ayuda': 'Labor & Installation, Materials, Equipment…',
    'propuesta.layout.eje.zona': 'By area or zone',
    'propuesta.layout.eje.zona.ayuda': 'The estimate’s zones, each with its own total.',
    'propuesta.layout.eje.categoria': 'By category',
    'propuesta.layout.eje.categoria.ayuda': 'Each line’s category (Concrete, Planting…).',
    'propuesta.layout.eje.una': 'Everything in one',
    'propuesta.layout.eje.una.ayuda': 'A single section for the whole project.',
    'propuesta.layout.cobertura': 'All {partidas} items shown across {secciones} sections.',
    'propuesta.layout.restablecer': 'Reset to auto',
    'propuesta.layout.combinar': 'Combine into one',
    'propuesta.layout.condiciones.titulo': 'Project conditions line label',
    'propuesta.layout.condiciones.ayuda': 'How the site/project-conditions labor uplift is labeled on the client’s proposal. Defaults to "Project Conditions".',
    'propuesta.layout.secciones.titulo': 'Sections',
    'propuesta.layout.secciones.vacio': 'This estimate has no line items to group yet.',
    'propuesta.layout.seccion.nombre': 'Section name',
    'propuesta.layout.seccion.descripcion': 'Optional one-line description (what’s included)',
    'propuesta.layout.seccion.modo': 'How it shows',
    'propuesta.layout.seccion.precios': 'Itemize',
    'propuesta.layout.seccion.precios.ayuda': 'Show a price next to each line inside the section. The section total always shows.',
    'propuesta.layout.partidas.titulo': { uno: 'Reword 1 line', otros: 'Reword {count} lines' },
    'propuesta.layout.partidas.ayuda': 'How the client reads each line. Blank = whatever the estimate says. Editing here changes neither the estimate nor the price.',
    'propuesta.layout.partida.descripcion': 'What this line is called on the proposal',
    'propuesta.layout.partida.detalle': 'The line underneath (quantity, notes)',
    'propuesta.layout.seccion.subir': 'Move the {seccion} section up',
    'propuesta.layout.seccion.bajar': 'Move the {seccion} section down',
    'propuesta.layout.modo.desplegable': 'Expandable',
    'propuesta.layout.modo.abierta': 'Always open',
    'propuesta.layout.modo.resumen': 'Total only',
    'propuesta.precios.grupo.partidas': { uno: '1 item — tap to view', otros: '{count} items — tap to view' },
    'propuesta.precios.grupo.condiciones': 'Project Conditions',
    'propuesta.precios.grupo.condiciones.ayuda': 'Adjustment for site access, ground and slope.',
    'propuesta.layout.resenas.google.titulo': 'Google Reviews',
    'propuesta.layout.resenas.google.desc': 'Show Google reviews & link',
    'propuesta.layout.resenas.yelp.titulo': 'Yelp Reviews',
    'propuesta.layout.resenas.yelp.desc': 'Show Yelp button',
    'propuesta.layout.resenas.redes.titulo': 'Social Links',
    'propuesta.layout.resenas.redes.desc': 'Display social media icons on proposal',
    'propuesta.compartir.error.marcarenviada': 'Could not mark as sent',
    'propuesta.compartir.error.deshacerenviada': 'Could not undo',
    'propuesta.compartir.error.reintentar': 'Please try again.',
    'propuesta.compartir.copiar.manual.titulo': 'Copy the link manually',
    'propuesta.compartir.copiar.manual.mensaje': 'Your browser blocked clipboard access. Select the link above and copy it.',
    'propuesta.compartir.guardaprimero.titulo': 'Save your settings first',
    'propuesta.compartir.guardaprimero.mensaje': 'You have unsaved changes to your business settings. Save them, then refresh.',
    'propuesta.compartir.requisito.contratoelegido': 'Contract selected',
    'propuesta.compartir.requisito.contratotienefirma': 'Contract has a place to sign',
    'propuesta.compartir.requisito.contratofirmado': 'Contract signed by you',
    'propuesta.compartir.arreglo.subircontrato': 'Upload one below — it only takes a minute.',
    'propuesta.compartir.arreglo.elegircontrato': 'Pick one from the Contract dropdown below.',
    'propuesta.compartir.arreglo.sincamposfirma': 'This contract has no signature fields yet. Open it below and drag a Signature field onto the page, or the client will have nothing to sign.',
    'propuesta.compartir.arreglo.firmarcomocontratista': 'Open it below and use "Sign as contractor".',
    'propuesta.compartir.requisito.metodopago': 'Payment method chosen',
    'propuesta.compartir.arreglo.metodopago': 'On the Pricing tab: card, Zelle, bank transfer, or "no online payment". Any one is enough.',
    'propuesta.compartir.arreglo.irprecio': 'Go to Pricing',
    'propuesta.preciotab.titulo': 'Pricing & terms',
    'propuesta.preciotab.pagos': 'How you get paid',
    'propuesta.preciotab.pagos.ayuda': 'Turn on at least one. It is what the client sees to pay you after accepting.',
    'propuesta.preciotab.bloqueada.desc': 'The proposal is already signed, so the terms stay exactly as the client accepted them.',
    'propuesta.compartir.titulo': 'Sharing & Payment',
    'propuesta.compartir.anticipo.titulo': 'Deposit & validity',
    'propuesta.compartir.anticipo.ayuda': 'What the client pays at signing, and how long you honour this price. Both are printed on the proposal.',
    'propuesta.compartir.anticipo.etiqueta': 'Deposit',
    'propuesta.compartir.anticipo.sinanticipo': 'No deposit — the full amount is due on completion.',
    'propuesta.compartir.anticipo.alfirmar': 'At signing',
    'propuesta.compartir.anticipo.alterminar': 'On completion',
    'propuesta.compartir.anticipo.sliderAria': 'Deposit percent',
    'propuesta.compartir.vigencia.etiqueta': 'This price holds until',
    'propuesta.compartir.vigencia.faltan': '{dias} days left.',
    'propuesta.compartir.vigencia.ultimodia': 'Today is the last day.',
    'propuesta.compartir.vigencia.vencida': 'Already expired. The client sees the proposal marked as expired.',
    'propuesta.compartir.listaparaenviar': 'Ready to send',
    'propuesta.compartir.contrato.enarchivo': ' on file',
    'propuesta.compartir.contrato.ningunosubido': 'No contracts uploaded yet',
    'propuesta.compartir.contrato.sinadjuntar': 'No contract attached',
    'propuesta.compartir.contrato.necesitatufirma': 'This contract needs your signature before it can go out — open it below and use',
    'propuesta.compartir.contrato.subir': 'Upload a contract',
    'propuesta.compartir.contrato.administrar': 'Manage contracts',
    'propuesta.compartir.contrato.recargar.tooltip': 'Reload the contract library from the server',
    'propuesta.compartir.contrato.actualizando': 'Refreshing…',
    'propuesta.compartir.contrato.recargar': 'Just configured it? Refresh',
    'propuesta.compartir.contrato.ayuda1': 'The client signs this alongside the proposal. Opens in a new tab so you don’t lose',
    'propuesta.compartir.contrato.ayuda2': 'unsaved edits.',
    'propuesta.compartir.pago.tarjeta.switch': 'Card link',
    'propuesta.compartir.pago.tarjeta.aria': 'Client payment link',
    'propuesta.compartir.pago.tarjeta.ayudaon': 'Paste the client payment link. A "Pay with Card" button shows after they accept.',
    'propuesta.compartir.pago.tarjeta.ayudaoff': 'Card payment is off for this proposal.',
    'propuesta.compartir.pago.zelle.etiqueta': 'Zelle Transfer',
    'propuesta.compartir.pago.zelle.pagara': 'Pay to ',
    'propuesta.compartir.pago.zelle.sinconfigurar': 'Not configured for your business',
    'propuesta.compartir.pago.zelle.switch': 'Zelle transfer',
    'propuesta.compartir.pago.zelle.ayudaon': 'Shows your Zelle details on this proposal so the client can pay by transfer.',
    'propuesta.compartir.pago.zelle.ayudaoff1': 'Add your Zelle account in ',
    'propuesta.compartir.pago.zelle.ayudaoff.ruta': 'Estimates → Proposal → Payments',
    'propuesta.compartir.pago.zelle.ayudaoff2': ' to turn this on.',
    'propuesta.compartir.pago.banco.etiqueta': 'Bank transfer',
    'propuesta.compartir.pago.banco.switch': 'Bank transfer',
    'propuesta.compartir.pago.banco.depositara': 'Deposit to ',
    'propuesta.compartir.pago.banco.sinconfigurar': 'Not configured for your business',
    'propuesta.compartir.pago.banco.ayudaon': 'Shows your account details on this proposal so the client can wire the deposit.',
    'propuesta.compartir.pago.banco.ayudaoff1': 'Add your bank account in ',
    'propuesta.compartir.pago.banco.ayudaoff.ruta': 'Estimates → Proposal → Payments',
    'propuesta.compartir.pago.banco.ayudaoff2': ' to turn this on.',
    'propuesta.compartir.pago.efectivo.etiqueta': 'No Online Payment',
    'propuesta.compartir.pago.efectivo.desc': 'Cash, check, or arrange payment directly',
    'propuesta.compartir.pago.efectivo.switch': 'No online payment',
    'propuesta.compartir.pago.efectivo.ayudaon': 'No card or Zelle is shown. After the client signs, they’re told you’ll contact them about payment.',
    'propuesta.compartir.pago.efectivo.ayudaoff': 'Turn this on to send a cash/offline proposal without a card link or Zelle.',
    'propuesta.compartir.bloqueada.titulo': 'Proposal signed & locked',
    'propuesta.compartir.bloqueada.desc': 'This proposal has been accepted, so the live client view is closed. View or download the signed copy from the ',
    'propuesta.compartir.bloqueada.pestana': 'Signed',
    'propuesta.compartir.mandala.titulo': 'Send it yourself',
    'propuesta.compartir.mandala.desc1': 'Raza Pro does ',
    'propuesta.compartir.mandala.desc.no': 'not',
    'propuesta.compartir.mandala.desc2': ' email proposals.',
    'propuesta.compartir.mandala.desc3': 'Copy the link and send it however you normally reach this client — text, WhatsApp,',
    'propuesta.compartir.mandala.desc4': 'or your own email.',
    'propuesta.compartir.paso1': 'Copy the link',
    'propuesta.compartir.enlace.aria': 'Public proposal link',
    'propuesta.compartir.enlace.copiar.tooltip': 'Copy',
    'propuesta.compartir.enlace.copiar.aria': 'Copy public link',
    'propuesta.compartir.enlace.copiado': 'Copied. Send it to your client.',
    'propuesta.compartir.enlace.copiado.marcada': 'Copied and marked sent with today’s date. Send it to your client.',
    'propuesta.compartir.enlace.aviso': 'Anyone with this link can view and accept the proposal. Copying it marks the proposal as sent.',
    'propuesta.compartir.paso2': 'Mark it as sent',
    'propuesta.compartir.marcada.fecha': 'Marked sent on ',
    'propuesta.compartir.marcada.ya': 'Already marked as sent',
    'propuesta.compartir.marcada.desc': 'It’s on the Follow-ups board. You’ll see there when the client opens it.',
    'propuesta.compartir.marcada.auto': 'It marked itself when you copied the link. If you were only reviewing it, you can still take it back.',
    'propuesta.compartir.marcada.deshacer': 'Undo',
    'propuesta.compartir.marcando': 'Marking…',
    'propuesta.compartir.marcarenviada': 'Mark as Sent',
    'propuesta.compartir.marcar.aviso1': 'You usually will not need this — copying the link marks it. The button is for when you copied it',
    'propuesta.compartir.marcar.aviso2': 'by hand and the proposal stayed a draft, off the Follow-ups board.',
    'propuesta.compartir.fechaauto.titulo': 'The date fills itself in.',
    'propuesta.compartir.fechaauto.pendiente': 'You have not sent it yet, so it carries today’s and rolls over on its own tomorrow. It freezes the moment you mark it sent or the client signs. Right now it goes out dated',
    'propuesta.compartir.fechaauto.enviada': 'The proposal and the contract carry the day you sent it:',
    'propuesta.compartir.fechaauto.firmada': 'The documents are stamped with the day the client signed:',
    'propuesta.compartir.abrirvistacliente': 'Open Client View',
    'propuesta.firma.errorpdf': 'Failed to open PDF.',
    'propuesta.firma.revisayacepta': 'Review and accept',
    'propuesta.firma.suelta.explicacion': 'This contract has no signature box for you: sign in the box above, type your name and accept.',
    'nav.sinAcceso': 'You don’t have access to {vista}',
    'propuesta.top.volver.estimados': 'Back to Estimates',
    'propuesta.top.volver.herramientas': 'Back to Tools',
    'propuesta.firma.enviando': 'Submitting…',
    'propuesta.firma.firmarcontrato': 'Sign Contract',
    'propuesta.firma.firmaryaceptar': 'Sign & Accept',
    'propuesta.firma.paginaanterior': 'Previous page',
    'propuesta.firma.paginade': 'of',
    'propuesta.firma.paginasiguiente': 'Next page',
    'propuesta.firma.errorcontrato': 'Couldn’t open this contract',
    'propuesta.firma.placeholdernombre': 'Type your full legal name',
    'propuesta.firma.arianombre': 'Full legal name',
    'propuesta.firma.consentimiento': 'I agree to sign this document electronically, and that my electronic signature is the legal equivalent of my handwritten signature (ESIGN / UETA).',
    'propuesta.firma.firmadopor': 'Signed by ',
    'propuesta.firma.editar': 'Edit',
    'propuesta.firma.tocapara': 'Click to ',
    'propuesta.firma.accionfirmar': 'sign',
    'propuesta.firma.accionfecha': 'set date',
    'propuesta.firma.accionllenar': 'fill in',
    'propuesta.firma.firmaaqui': 'Sign here',
    'propuesta.firma.iniciales': 'Initials',
    'propuesta.firma.fecha': 'Date',
    'propuesta.firma.tocaparallenar': 'Tap to fill',
    'propuesta.firma.tufirma': 'Your signature',
    'propuesta.firma.tusiniciales': 'Your initials',
    'propuesta.firma.modo.escribir': 'Type',
    'propuesta.firma.modo.dibujar': 'Draw',
    'propuesta.firma.estilo': 'Style',
    'propuesta.firma.escribeparaver': 'Type to see how it looks',
    'propuesta.firma.placeholder.iniciales': 'e.g. KYC',
    'propuesta.firma.placeholder.nombre': 'Type your full name',
    'propuesta.firma.cargandofuente': 'Loading the typeface…',
    'propuesta.firma.dibujatufirma': 'Draw your signature',
    'propuesta.firma.dibujatusiniciales': 'Draw your initials',
    'propuesta.firma.ayudadibujo': 'Use your finger or mouse to draw above.',
    'propuesta.firma.borrar': 'Clear',
    'propuesta.firma.cancelarcanvas': 'Cancel',
    'propuesta.firma.guardarcanvas': 'Save',
    'propuesta.firma.confirmafecha': 'Confirm date',
    'propuesta.firma.llenar': 'Fill in',
    'propuesta.firma.fechaauto.titulo': 'The date fills itself in.',
    'propuesta.firma.fechaauto.cuerpo': 'You never write it: every proposal goes out dated the day you send it or download its PDF, and the client’s copy is dated their signature. That is why this box is reserved.',
    'propuesta.firma.fechaauto.campo': 'On send',
    'servicios.ruta.sin_cuadrillas_titulo': 'No crews yet',
    'servicios.ruta.sin_cuadrillas_descripcion': 'Create crews in Workforce → Crew Builder to start routing. Crews let you organize your team and assign service stops to specific groups.',
    'servicios.ruta.ir_cuadrillas': 'Go to Crew Settings',
    'servicios.ruta.tiempo_estimado': 'Est. time',
    'servicios.ruta.abrir_crew_builder': 'Open Crew Builder in Workforce',
    'servicios.ruta.administrar_cuadrillas': 'Manage crews',
    'servicios.ruta.deshacer': 'Reset',
    'servicios.ruta.guardando': 'Saving…',
    'servicios.ruta.guardar_orden': 'Save Order',
    'servicios.ruta.todas_paradas': 'All Stops',
    'servicios.ruta.orden_ruta': 'Route Order',
    'servicios.ruta.ayuda_todas': 'Showing every crew. Pick a crew above to reorder its route.',
    'servicios.ruta.ayuda_arrastrar': 'Drag stops to reorder the route sequence.',
    'servicios.ruta.sin_paradas_titulo': 'No stops to show',
    'servicios.ruta.sin_paradas_dia_1': 'No active contracts scheduled for ',
    'servicios.ruta.sin_paradas_dia_2': '. Check preferred day, frequency, and start date.',
    'servicios.ruta.sin_paradas_todas': 'No contracts are assigned to any crew yet.',
    'servicios.ruta.sin_paradas_cuadrilla': 'Assign contracts to this crew to build their route.',
    'servicios.ruta.sin_asignar': 'Unassigned',
    'servicios.ruta.mapa_cargando': 'Loading map…',
    'servicios.ruta.mapa_sin_paradas': 'No geocoded stops to preview',
    'servicios.ruta.mapa_ayuda': 'Stops appear on the map once their addresses are geocoded.',
    'servicios.analiticas.frecuencia_otra': 'Other',
    'servicios.analiticas.vacio_titulo': 'No data yet',
    'servicios.analiticas.vacio_desc': 'Analytics will populate as you add contracts and complete visits.',
    'servicios.analiticas.kpi_ingreso_mensual': 'Monthly revenue',
    'servicios.analiticas.kpi_cumplimiento': 'Completion rate',
    'servicios.analiticas.kpi_visitas_hint': 'visits (30 days)',
    'servicios.analiticas.kpi_duracion': 'Avg duration',
    'servicios.analiticas.kpi_duracion_hint': 'Per completed visit',
    'servicios.analiticas.kpi_clientes': 'Active clients',
    'servicios.analiticas.top_clientes': 'Top Clients by Revenue',
    'servicios.analiticas.sin_clientes': 'No clients yet',
    'servicios.analiticas.resumen_contratos': 'Contract Overview',
    'servicios.analiticas.estado_activos': 'Active',
    'servicios.analiticas.estado_pausados': 'Paused',
    'servicios.analiticas.estado_cancelados': 'Cancelled',
    'servicios.analiticas.actividad_30_dias': 'Last 30 days activity',
    'servicios.analiticas.visitas_completadas': 'completed',
    'servicios.analiticas.visitas_saltadas': 'skipped',
    'contratoServ.frec.semanal': 'Weekly',
    'contratoServ.frec.quincenal': 'Bi-Weekly',
    'contratoServ.frec.mensual': 'Monthly',
    'contratoServ.paso': 'Step',
    'contratoServ.pasoDe': 'of',
    'contratoServ.cerrar': 'Close',
    'contratoServ.buscarClientes': 'Search clients…',
    'contratoServ.sinClientes': 'No clients found.',
    'contratoServ.direccion': 'Service Address',
    'contratoServ.direccionPlaceholder': 'Start typing to search address…',
    'contratoServ.frecuencia': 'Frequency',
    'contratoServ.diaPreferido': 'Preferred Day',
    'contratoServ.cualquierDia': 'Any day',
    'contratoServ.horaPreferida': 'Preferred Time',
    'contratoServ.opcional': '(optional)',
    'contratoServ.horaAyuda': 'Visits with a time get that slot. Untimed stops fill in by proximity.',
    'contratoServ.fechaInicio': 'Start Date',
    'contratoServ.cobro': 'Billing',
    'contratoServ.asignarCuadrilla': 'Assign Crew',
    'contratoServ.notas': 'Notes',
    'contratoServ.notasPlaceholder': 'Any special instructions…',
    'contratoServ.serviciosIncluidos': 'Services Included',
    'contratoServ.servicioNuevo': 'Add custom service…',
    'contratoServ.agregar': 'Add',
    'contratoServ.quitarServicio': 'Remove {servicio}',
    'contratoServ.duracion': 'Estimated Duration',
    'contratoServ.cuatroHoras': '4 hrs',
    'contratoServ.resumen': 'Summary',
    'contratoServ.minPorVisita': 'min per visit',
    'contratoServ.atras': 'Back',
    'contratoServ.continuar': 'Continue',
    'servicios.equipo.titulo': 'Route Equipment',
    'servicios.equipo.descripcion': 'Sign tools and vehicles out to a crew for today’s route, and check them back in.',
    'servicios.equipo.cancelar': 'Cancel',
    'servicios.equipo.registrarnuevo': 'Register New',
    'servicios.equipo.registrorapido': 'Quick-register equipment',
    'servicios.equipo.placeholdernombre': 'Equipment name (e.g. Backpack Blower)',
    'servicios.equipo.tipoherramienta': 'Tool',
    'servicios.equipo.tipovehiculo': 'Vehicle',
    'servicios.equipo.tipopesado': 'Heavy Equipment',
    'servicios.equipo.tipotrailer': 'Trailer',
    'servicios.equipo.tipootro': 'Other',
    'servicios.equipo.registrando': 'Registering…',
    'servicios.equipo.registrar': 'Register',
    'servicios.equipo.selollleva': 'Checking out for',
    'servicios.equipo.sincuadrilla': 'No specific crew',
    'servicios.equipo.enlaruta': 'Out on route',
    'servicios.equipo.regresando': 'Returning…',
    'servicios.equipo.regresaraltaller': 'Return to Shop',
    'servicios.equipo.vacioruta': 'Nothing out on a route',
    'servicios.equipo.vaciorutadesc': 'Check items out from the shop to track what left with the crew today.',
    'servicios.equipo.inventariotaller': 'Shop inventory',
    'servicios.equipo.sacando': 'Checking out…',
    'servicios.equipo.sacar': 'Check Out',
    'servicios.equipo.sindisponible': 'No equipment available',
    'servicios.equipo.sindisponibledesc': 'Everything is out on a job, out on a route, or in the shop for repair.',
    'servicios.equipo.toastcargadopara': ' loaded for ',
    'servicios.equipo.toastsalioruta': ' out on the route',
    'servicios.equipo.toastnosepudosacar': 'Could not check out ',
    'servicios.equipo.toastregresotaller': ' returned to the shop',
    'servicios.equipo.toastnosepudoregresar': 'Could not return ',
    'servicios.equipo.toastregistrado': ' registered',
    'servicios.equipo.toastnosepudoregistrar': 'Could not register equipment: ',
    'servicios.visita.cerrar': 'Close visit',
    'servicios.visita.arrancar': 'Start Route',
    'servicios.visita.llegue': 'Arrived On Site',
    'servicios.visita.terminar': 'Complete Visit',
    'servicios.visita.saltar': 'Skip',
    'servicios.visita.terminada': 'Visit completed',
    'servicios.visita.marcadaPor': 'Marked by',
    'servicios.visita.saltada': 'Visit skipped',
    'servicios.visita.motivo_titulo': 'Reason for skipping',
    'servicios.visita.motivo_placeholder': 'Why is this visit being skipped? (e.g. gate locked, weather, client request)',
    'servicios.visita.cancelar': 'Cancel',
    'servicios.visita.confirmar_saltar': 'Confirm Skip',
    'servicios.visita.motivo_guardado': 'Skip reason: ',
    'servicios.visita.contrato': 'Contract details',
    'servicios.visita.por_mes': '/mo',
    'servicios.visita.por_visita': '/visit',
    'servicios.visita.checklist': 'Service checklist',
    'servicios.visita.notas_titulo': 'Crew notes',
    'servicios.visita.notas_placeholder': 'Notes for this visit…',
    'servicios.visita.empezo': 'Started at',
    'servicios.visita.total_min': ' min total',
    'servicios.visita.guardar': 'Save Notes & Checklist',
    'visitas.csv.fecha': 'Date',
    'visitas.csv.cliente': 'Client',
    'visitas.csv.direccion': 'Address',
    'visitas.csv.estatus': 'Status',
    'visitas.csv.duracion': 'Duration (min)',
    'visitas.csv.notas': 'Notes',
    'visitas.vacio.titulo': 'No visit history yet',
    'visitas.vacio.texto': 'Completed and skipped visits will appear here.',
    'visitas.kpi.total': 'Total visits',
    'visitas.kpi.hechas': 'Completed',
    'visitas.kpi.saltadas': 'Skipped',
    'visitas.kpi.duracionPromedio': 'Avg duration',
    'visitas.buscar.placeholder': 'Search client, address, notes…',
    'visitas.filtro.todos': 'All status',
    'visitas.filtro.desde': 'From date',
    'visitas.filtro.hasta': 'To date',
    'visitas.filtroVacio.titulo': 'No visits match your filters',
    'visitas.filtroVacio.texto': 'Try a different search, status, or date range.',
    'visitas.col.fecha': 'Date',
    'visitas.col.cliente': 'Client',
    'visitas.col.tiempo': 'Time',
    'visitas.col.estatus': 'Status',
    'visitas.col.detalles': 'Details',
    'visitas.borrar.aria': 'Delete {cliente}\'s visit',
    'visitas.borrar.titulo': 'Delete this visit from the history?',
    'visitas.borrar.texto': '{cliente} — {fecha}. If it was completed and the contract bills per visit, that month\'s revenue drops. This cannot be undone.',
    'visitas.borrar.sinCliente': 'No client',
    'visitas.borrar.confirmar': 'Yes, delete it',
    'visitas.borrar.ok': 'Visit deleted from the history',
    'visitas.borrar.error': 'Could not delete the visit',
    'visitas.detalle.porMes': '/mo',
    'visitas.detalle.porVisita': '/visit',
    'visitas.paginacion.mostrando': 'Showing',
    'comun.anterior': 'Prev',
    'comun.siguiente': 'Next',
    'servicios.visitas.vacio.titulo': 'No stops for this date',
    'servicios.visitas.vacio.descripcion': 'Create a service contract with the right preferred day to populate routes.',
    'servicios.visitas.boton.ruta': 'Route',
    'servicios.visitas.etiqueta.inicio': 'Start',
    'servicios.visitas.etiqueta.empezo': 'Started',
    'servicios.visitas.opcion.asignarcrew': 'Assign crew…',
    'servicios.visitas.tooltip.empezaraqui': 'Start the route from this stop',
    'servicios.visitas.boton.empezaraqui': 'Start here',
    'servicios.calendario.semanaanterior': 'Previous week',
    'servicios.calendario.semanasiguiente': 'Next week',
    'servicios.calendario.estasemana': 'This Week',
    'servicios.calendario.paradas': 'Stops',
    'servicios.calendario.tiempoest': 'Est. time',
    'servicios.calendario.lunes': 'Monday',
    'servicios.calendario.martes': 'Tuesday',
    'servicios.calendario.miercoles': 'Wednesday',
    'servicios.calendario.jueves': 'Thursday',
    'servicios.calendario.viernes': 'Friday',
    'servicios.calendario.sabado': 'Saturday',
    'servicios.calendario.domingo': 'Sunday',
    'servicios.calendario.sinparadas': 'No stops',
    'servicios.calendario.cargasemanal': 'Weekly crew load',
    'ajustes.burden.titulo': 'Burden Builder',
    'ajustes.burden.usar': 'Use builder',
    'ajustes.burden.info_parte2': 'FICA + FUTA + state SUTA + Workers\' Comp + benefits. Far more accurate than guessing.',
    'ajustes.burden.estado': 'Your state',
    'ajustes.burden.estado_nota': '(drives SUTA + Workers\' Comp defaults)',
    'ajustes.burden.estado_placeholder': '— Select state —',
    'ajustes.burden.fica': 'FICA (Social Security + Medicare)',
    'ajustes.burden.futa': 'FUTA (effective)',
    'ajustes.burden.seguro_medico_unidad': '($/employee/month)',
    'ajustes.burden.retiro': 'Retirement / 401k match',
    'ajustes.burden.otros': 'Other benefits / bonuses',
    'ajustes.burden.total': 'Total burden',
    'ajustes.burden.nota_parte1': 'This total replaces the per-employee Burden % field. To use different burden for an individual',
    'ajustes.burden.nota_parte2': 'employee, turn the builder off and edit them one at a time.',
    'ajustes.burden.restablecer': 'reset',
    'ajustes.gastos.descripcion': 'Pick from common categories or add a custom one.',
    'ajustes.gastos.cerrar': 'Close',
    'ajustes.gastos.buscar': 'Search expenses (e.g. internet, fuel, insurance)…',
    'ajustes.gastos.sin_resultados_titulo': 'No matches',
    'ajustes.gastos.sin_resultados_1': 'Nothing in the catalog matches “',
    'ajustes.gastos.sin_resultados_2': '”. You can still add it as a custom expense.',
    'ajustes.gastos.agregar_propio': 'Add a custom expense',
    'ajustes.gastos.pie': 'Click an expense to add it. Multiple selections allowed.',
    'ajustes.gastos.gasto_propio': 'Custom expense',
    'ajustes.metricas.titulo.manodeobra': 'Direct Labor (Billable Staff)',
    'ajustes.metricas.vacio.facturables': 'No billable employees yet.',
    'ajustes.metricas.texto.repartidoen': ' over ',
    'ajustes.metricas.titulo.gastosfijos': 'Overhead (annualized)',
    'ajustes.metricas.vacio.gastosfijos': 'No overhead expenses entered.',
    'ajustes.metricas.etiqueta.manodeobraindirecta': 'Indirect labor:',
    'ajustes.metricas.etiqueta.equipo': 'Equipment (depreciation + fuel + maintenance)',
    'ajustes.metricas.titulo.cuentas': 'The Math',
    'metricas.error.titulo': 'Your break-even rate can’t be trusted yet',
    'metricas.aviso.titulo': 'Heads up — these may distort the number',
    'ajustes.guardar.exito': 'Settings saved successfully!',
    'ajustes.guardar.error': 'Failed to save settings.',
    'ajustes.guardar.faltaColumna': 'This database is missing a column ({detalle}). The migrations in supabase/migrations need to be run. Nothing was saved.',
    'ajustes.guardar.region': 'Unsaved settings',
    'ajustes.guardar.titulo': 'You have unsaved changes',
    'ajustes.guardar.nota': 'Nothing is stored until you save.',
    'ajustes.guardar.guardando': 'Saving…',
    'ajustes.guardar.boton': 'Save Changes',
    'ui.lightbox.alt_default': 'Photo',
    'ui.lightbox.abrir_original': 'Open original',
    'ui.lightbox.cerrar_foto': 'Close photo',
    'ui.lightbox.ayuda_cerrar': 'Press Esc or click outside to close',
    'tabla.acciones.encabezado': 'Actions',
    'rastreo.banner.activo': 'Location Tracking: Active',
    'rastreo.banner.info_tooltip': 'What data is collected?',
    'rastreo.banner.info_titulo': 'What we track while you’re clocked in:',
    'rastreo.banner.info_gps': 'Your GPS location when you clock in and clock out',
    'rastreo.banner.info_foto': 'Your verification photo at start and end of shift',
    'rastreo.banner.info_app': 'Location updates only while this app is open',
    'rastreo.banner.info_promesa': 'The instant you clock out, all tracking stops completely. We never track you off the clock.',
    'voz.orbe.parar': 'Tap to stop',
    'voz.orbe.escucho': 'I hear you',
    'voz.orbe.hablar': 'You can talk',
    'voz.error.sinmicrofono': 'Microphone not available.',
    'voz.orbe.arialabel': 'AI voice {effectiveOrbState} — tap orb to close',
    'equipo.mapa.leyenda.todavia_trabajando': 'still on the clock',
    'equipo.mapa.aviso_gps': 'Positions are recorded once at clock-in and once at clock-out — not tracked continuously.',
    'equipo.mapa.tooltip.entro': 'Clocked in',
    'equipo.mapa.tooltip.salio': 'Clocked out',
    'equipo.mapa.tooltip.lonche': 'On lunch',
    'equipo.mapa.tooltip.trabajado': 'worked',
    'equipo.mapa.tooltip.fuera_sitio': 'Off-site',
    'equipo.mapa.lista.titulo': 'Shifts',
    'equipo.mapa.lista.con_ubicacion': 'with location',
    'equipo.mapa.lista.vacio_titulo': 'No shifts with a location',
    'equipo.mapa.lista.vacio_descripcion': 'Pins appear when employees clock in or out with GPS enabled.',
    'equipo.mapa.fila.lonche': 'On lunch',
    'equipo.mapa.fila.trabajando': 'Working',
    'equipo.mapa.fila.sin_ubicacion_salida': 'No clock-out location recorded',
    'equipo.mapa.fila.salio_fuera_sitio': 'Clocked out off-site',
    'cuadrillas.actualizada': 'Crew updated',
    'cuadrillas.creada': 'Crew created',
    'cuadrillas.errorGuardar': 'Failed to save crew: ',
    'cuadrillas.borrar.titulo': 'Delete Crew?',
    'cuadrillas.borrar.mensaje': ' will be permanently removed. Members keep their accounts, only the crew grouping is deleted.',
    'cuadrillas.borrar.confirmar': 'Delete Crew',
    'cuadrillas.borrada': 'Crew deleted',
    'cuadrillas.errorBorrar': 'Failed to delete crew: ',
    'comun.desconocido': 'Unknown',
    'cuadrillas.crear': 'Create Crew',
    'cuadrillas.editar.titulo': 'Edit Crew',
    'cuadrillas.nueva.titulo': 'New Crew',
    'cuadrillas.nombre.placeholder': 'e.g. East Side Crew',
    'cuadrillas.lider.placeholder': '— Select Leader —',
    'cuadrillas.escogerTrabajadores': 'Select workers',
    'cuadrillas.sinTrabajadoresDisponibles': 'No laborers or maintenance workers found. Add team members first.',
    'comun.guardando': 'Saving…',
    'cuadrillas.creando': 'Creating…',
    'cuadrillas.guardarCambios': 'Save Changes',
    'cuadrillas.crear.boton': 'Create Crew',
    'cuadrillas.vacio.titulo': 'No crews yet',
    'cuadrillas.vacio.texto': 'Create your first crew to start assigning workers to service routes.',
    'cuadrillas.editar.title': 'Edit crew',
    'cuadrillas.editar.aria': 'Edit crew',
    'cuadrillas.borrar.title': 'Delete crew',
    'cuadrillas.borrar.aria': 'Delete crew',
    'reloj.foto.alt': 'Verification',
    'reloj.foto.lista': 'Verification photo ready',
    'reloj.foto.quitar.title': 'Remove photo',
    'reloj.foto.quitar.aria': 'Remove photo',
    'reloj.destino.general': 'General time',
    'reloj.error.turnoAbierto': 'You already have an open shift.',
    'reloj.error.perfilCargando': 'Still loading your profile — please try again in a moment.',
    'reloj.error.sinInternet.entrada': 'You’re offline — clocking in needs a connection. Reconnect and try again.',
    'reloj.error.sinInternet.salida': 'You’re offline — clocking out needs a connection. Reconnect and try again.',
    'reloj.error.sinInternet.traslado': 'You’re offline — moving your clock needs a connection. You’re still clocked in where you were.',
    'reloj.error.sinInternet.lunch': 'You’re offline — lunch can’t be saved without a connection. Reconnect and try again.',
    'reloj.salida.confirmar.titulo': 'Clock Out?',
    'reloj.salida.confirmar.no': 'Stay Clocked In',
    'reloj.estado.enLunch': 'On lunch break',
    'reloj.estado.entradaALas': 'Clocked in at ',
    'reloj.estado.fueraPor': 'Off-site by ',
    'reloj.destino.pregunta': 'What are you working on?',
    'reloj.destino.tallerGeneral': 'Shop / general time',
    'reloj.destino.grupo.proyectos': 'Projects',
    'reloj.destino.grupo.rutas': 'Service Routes',
    'reloj.destino.sinAsignar': 'Nothing assigned to you yet — ask an admin to add you to a project or service route, or just clock in to general time.',
    'reloj.destino.sinNada': 'No active projects or service routes in this workspace yet. You can still clock in to general time.',
    'reloj.destino.verTodo': 'Show every job in this workspace',
    'reloj.destino.sinTaller': 'Your shop has no address yet, so these hours land unclassified. Add one in Settings → Office.',
    'reloj.geofence.activo': 'Geofence active (',
    'reloj.foto.boton.salida': 'Add a clock-out photo (optional)',
    'reloj.foto.boton.entrada': 'Add a verification photo (optional)',
    'reloj.boton.entrada': 'Clock In',
    'reloj.boton.empezarLunch': 'Start Lunch',
    'reloj.boton.terminarLunch': 'End Lunch',
    'reloj.lunch.tomado': 'Lunch taken (',
    'reloj.lunch.tomadoTurno': 'You already took your lunch break on this shift.',
    'reloj.lunch.terminaPrimero': 'End your lunch break before clocking out.',
    'reloj.horas.hoy': 'Logged Today',
    'reloj.horas.semana': 'This Week',
    'reloj.gente.titulo': 'Currently Clocked In',
    'reloj.gente.vacio.titulo': 'No one clocked in',
    'reloj.gente.vacio.texto': 'Team members will appear here when they start their shift.',
    'reloj.gente.fueraObraDistancia': { uno: 'Off-site · {count} ft', otros: 'Off-site · {count} ft' },
    'reloj.gente.fueraObra': 'Off-site',
    'reloj.gente.desde': ' • Since ',
    'equipo.directorio.quitar_titulo': 'Remove Team Member?',
    'equipo.directorio.quitar_confirmar': 'Remove',
    'equipo.directorio.quitar_cancelar': 'Cancel',
    'equipo.directorio.buscar': 'Search team…',
    'equipo.directorio.invitar': 'Invite Member',
    'equipo.directorio.vacio_titulo': 'No team members found',
    'equipo.directorio.col_nombre': 'Name',
    'equipo.directorio.col_correo': 'Email',
    'equipo.directorio.col_rol': 'Role',
    'equipo.directorio.col_cuadrilla': 'Crew',
    'equipo.directorio.col_agregado': 'Added',
    'equipo.directorio.editar_tooltip': 'Edit member',
    'equipo.directorio.editar_aria': 'Edit member',
    'equipo.directorio.quitar_tooltip': 'Remove member',
    'equipo.directorio.quitar_aria': 'Remove member',
    'reloj.titulo': 'Time Clock',
    'reloj.descripcion': 'Track your daily shifts, breaks, and location.',
    'personal.horas.taller': 'Shop / general',
    'personal.horas.rango_fechas': 'Date range',
    'personal.horas.ultimos_7': 'Last 7 days',
    'personal.horas.ultimos_14': 'Last 14 days',
    'personal.horas.ultimos_30': 'Last 30 days',
    'personal.horas.ultimos_90': 'Last 90 days',
    'personal.horas.vista_por_empleado': 'By Employee',
    'personal.horas.vista_compacta': 'Compact',
    'personal.horas.abrir_todo': 'Expand all',
    'personal.horas.cerrar_todo': 'Collapse all',
    'personal.horas.exportar_csv': 'Export CSV',
    'personal.horas.buscar': 'Search name, email, project…',
    'personal.horas.filtro_empleado': 'Employee filter',
    'personal.horas.todos_empleados': 'All employees',
    'personal.horas.filtro_estatus': 'Status filter',
    'personal.horas.todos_estatus': 'All statuses',
    'personal.horas.opcion_activo': 'Active (not clocked out)',
    'personal.horas.filtro_cuadrilla': 'Crew filter',
    'personal.horas.todas_cuadrillas': 'All crews',
    'personal.horas.resumen_empleado_uno': 'employee',
    'personal.horas.resumen_empleado_varios': 'employees',
    'personal.horas.vacio_titulo': 'No time logs found',
    'personal.horas.vacio_texto': 'Try widening the date range or clearing the filters.',
    'personal.horas.tarjeta_turnos': 'Shifts',
    'personal.horas.editar_turno_tabla_tooltip': 'Edit shift',
    'personal.horas.editar_turno_tabla_aria': 'Edit shift',
    'personal.horas.editar_turno_fila_tooltip': 'Edit shift',
    'personal.horas.editar_turno_fila_aria': 'Edit shift',
    'equipo.tabs.checar': 'Clock In',
    'equipo.tabs.equipo': 'Team',
    'equipo.tabs.horas': 'Timesheets',
    'equipo.tabs.mapa': 'Clock Map',
    'equipo.encabezado.titulo': 'Team',
    'equipo.encabezado.descripcion': 'Clock in, manage your team, and review timesheets. Crews are managed in Service Routes.',
    'equipo.kpi.miembros': 'Team members',
    'equipo.kpi.crews_activas': 'Active crews',
    'equipo.kpi.de_total': { uno: 'of {count}', otros: 'of {count}' },
    'equipo.kpi.trabajando': 'On the clock',
    'chat.errores.iniciar_conversacion': 'Could not start the conversation — check your connection and try again.',
    'chat.errores.crear_conversacion_voz': 'Could not create conversation',
    'chat.errores.voz_stream': 'Voice stream failed',
    'dialogo.boton.confirmar': 'Confirm',
    'dialogo.boton.ok': 'OK',
    'dialogo.boton.enviar': 'Submit',
    'dialogo.input.placeholder': 'Enter details...',
    'ajustes.terminos.predeterminados': '1. Payment Terms: A 50% deposit is required to schedule the project. The remaining balance is due upon completion.\n\n2. Scheduling: Dates provided are estimates and subject to change.',
    'ajustes.garantia.nombre': 'Standard 1-Year Workmanship',
    'ajustes.garantia.texto': 'We guarantee workmanship for 1 year from the date of completion. Plant material is guaranteed for 30 days unless an automatic irrigation system is installed.',
    'ajustes.email.asunto': 'Proposal: {{proposalTitle}}',
    'ajustes.email.cuerpo': 'Hi,\n\nPlease review your proposal here:\n{{publicLink}}\n\nThanks,\n{{businessName}}',
    'ajustes.error.divergencia': 'Saved settings produced a different break-even on re-read (${remoto} vs ${local}). Refresh and re-check.',
    'espacio.nombrePorDefecto': 'Company Name',
    'espacio.nombreCorto': 'Company',
    'espacio.cargando': 'Loading...',
    'chat.tiempo.ahorita': 'just now',
    'chat.historial.titulo': 'Chat History',
    'chat.historial.subtitulo': 'Past Raza AI conversations',
    'chat.historial.buscar': 'Search conversations…',
    'chat.historial.vacio.titulo': 'No conversations yet',
    'chat.historial.vacio.sinresultados': 'No conversation matches that search.',
    'chat.historial.vacio.descripcion': 'Start a new chat to see it listed here.',
    'chat.historial.borrar.tooltip': 'Delete conversation',
    'chat.encabezado.volver.tooltip': 'Back to Dashboard',
    'chat.encabezado.volver.aria': 'Back to Dashboard',
    'chat.encabezado.conversaciones.tooltip': 'Conversations',
    'chat.encabezado.conversaciones.aria': 'Show conversations',
    'chat.encabezado.nuevaconversacion': 'New Conversation',
    'chat.encabezado.compartidaequipo': 'Shared with team',
    'chat.voz.completa.tooltip': 'Full Voice: AI will speak back',
    'chat.voz.dictado.tooltip': 'Dictation: Voice-to-text only',
    'chat.voz.completa.aria': 'Full voice mode — switch to dictation',
    'chat.voz.dictado.aria': 'Dictation mode — switch to full voice',
    'chat.voz.boton.voz': 'Voice',
    'chat.voz.boton.dictado': 'Dictation',
    'chat.privacidad.privada.tooltip': 'Private — click to share with team',
    'chat.privacidad.compartida.tooltip': 'Shared — click to make private',
    'chat.mensajes.escribiendo': 'Raza AI is typing',
    'chat.entrada.aria': 'Message Raza AI',
    'chat.entrada.micro.tooltip': 'Talk with Raza AI',
    'chat.entrada.micro.aria': 'Talk with Raza AI',
    'chat.entrada.enviar': 'Send message',
    'chat.audio.hablando': 'Raza AI is speaking…',
    'cliente.facturacion.errorcuenta.titulo': 'Account error',
    'cliente.facturacion.errorcuenta.descripcion': 'We could not link your account to a specific client record. Please contact support.',
    'cliente.facturacion.titulo': 'Billing & Invoices',
    'cliente.facturacion.descripcion': 'Your invoices and payment history.',
    'cliente.facturacion.saldo.titulo': 'Total amount due',
    'cliente.facturacion.saldo.comopagar': 'To pay an invoice or ask about your balance, reply to the\n                                    invoice email or contact your contractor directly.',
    'cliente.facturacion.porpagar.titulo': 'Unpaid invoices',
    'cliente.facturacion.porpagar.vacio': 'Nothing outstanding right now.',
    'cliente.facturacion.historial.titulo': 'Payment History',
    'cliente.facturacion.historial.vacio.titulo': 'No payments yet',
    'cliente.facturacion.historial.vacio.descripcion': 'Once an invoice is paid it will show up here.',
    'cliente.facturacion.tabla.factura': 'Invoice',
    'cliente.facturacion.tabla.fecha': 'Date',
    'cliente.facturacion.tabla.estado': 'Status',
    'cliente.facturacion.tabla.monto': 'Amount',
    'cliente.facturacion.tabla.recibo': 'Receipt',
    'cliente.panel.error_cuenta_titulo': 'Account error',
    'cliente.panel.error_cuenta_descripcion': 'We could not link your account to a specific client record. Please contact support.',
    'cliente.panel.bienvenida': 'Welcome back',
    'cliente.panel.bienvenida_descripcion': 'View your active projects, review pending estimates, and track upcoming service visits all in one place.',
    'cliente.panel.proyectos_vacio_titulo': 'No active projects',
    'cliente.panel.proyectos_vacio_descripcion': 'You don’t have any active projects right now.',
    'cliente.panel.estimados_titulo': 'Your Estimates',
    'cliente.panel.estimado_para': 'Estimate for ',
    'cliente.panel.creado': 'Created ',
    'cliente.panel.estimados_vacio_titulo': 'No estimates on file',
    'cliente.panel.estimados_vacio_descripcion': 'Estimates shared with you will show up here.',
    'cliente.panel.servicios_titulo': 'Upcoming Services',
    'cliente.panel.visita_hoy': 'Scheduled today',
    'cliente.panel.visita_proxima': 'Upcoming maintenance',
    'cliente.panel.servicios_vacio_titulo': 'Nothing scheduled',
    'cliente.panel.servicios_vacio_descripcion': 'No upcoming service visits scheduled.',
    'cliente.panel.pedir_servicio': 'Request Service',
    'clientes.toast.actualizado': 'Client updated',
    'clientes.toast.creado': 'Client created',
    'clientes.borrar.titulo': 'Delete Client',
    'clientes.borrar.confirmar': 'Delete Client',
    'clientes.borrar.cancelar': 'Cancel',
    'clientes.fila.acciones': 'Client actions',
    'clientes.tab.clientes': 'Clients',
    'clientes.tab.prospectos': 'Leads Pipeline',
    'clientes.tab.cerrados': 'Closed',
    'cerrados.tab.ganados': 'Won',
    'cerrados.tab.perdidos': 'Lost',
    'cerrados.buscar': 'Search by name, address, phone or service…',
    'cerrados.valorTotal': 'Value:',
    'cerrados.limpiar': { uno: 'Clear 1', otros: 'Clear {count}' },
    'cerrados.sinFecha': 'No date',
    'cerrados.sinDireccion': 'No address',
    'cerrados.fecha.ganado': 'Won on {fecha}',
    'cerrados.fecha.apuntado': 'Added on {fecha}',
    'cerrados.vacio.ganados': 'You have not won any yet',
    'cerrados.vacio.perdidos': 'No lost leads',
    'cerrados.vacio.descripcion': 'Closed deals collect here, grouped by month.',
    'cerrados.vacio.busqueda': 'Nothing matches that search',
    'cerrados.vacio.busquedaDesc': 'Try another name, address, or part of the phone number.',
    'cerrados.error.titulo': 'Could not load closed leads',
    'cerrados.error.descripcion': 'Check your connection and try again.',
    'cerrados.error.reintentar': 'Retry',
    'prospectos.columna.verTodos': 'See all',
    'prospectos.columna.masOcultos': { uno: '1 more in Closed', otros: '{count} more in Closed' },
    'clientes.vacio.boton.nuevo': 'New Client',
    'clientes.vacio.titulo': 'No clients yet',
    'clientes.vacio.desc': 'Add your first client and their jobs, estimates and revenue will start showing up here.',
    'clientes.filtro.sinresultados': 'No matches',
    'clientes.filtro.sinresultados.desc': 'No clients match this filter.',
    'clientes.filtro.limpiar': 'Clear filters',
    'clientes.tabla.direccion': 'Address',
    'clientes.tabla.trabajosactivos': 'Active jobs',
    'clientes.menu.cerrar': 'Close menu',
    'clientes.menu.editar': 'Edit client',
    'clientes.menu.borrar': 'Delete client',
    'clientes.buscar': 'Search name, address or phone…',
    'clientes.stat.total': 'Total clients',
    'clientes.stat.contrabajo': 'With active jobs',
    'clientes.filtro.todos': 'All',
    'camara.errores.elegirproyecto': 'Please select a project first.',
    'camara.subida.usuariodesconocido': 'Unknown User',
    'camara.subida.exito': 'Image uploaded successfully!',
    'camara.subida.error': 'Failed to upload image. Please try again.',
    'camara.borrar.confirmar': 'Are you sure you want to delete this photo?',
    'camara.borrar.mensaje': 'The photo will be removed from the job and cannot be recovered.',
    'camara.borrar.aceptar': 'Delete photo',
    'camara.borrar.cancelar': 'Cancel',
    'camara.borrar.error': 'Failed to delete image.',
    'camara.foreman.descripcion': 'Capture and upload photos directly to your active projects.',
    'camara.foreman.opcionvacia': 'Choose a project…',
    'camara.foreman.subiendo': 'Uploading…',
    'camara.foreman.tomarfoto': 'Take or upload photo',
    'camara.admin.titulo': 'Company Camera',
    'camara.admin.descripcion': 'Manage, download, and review project photos.',
    'camara.admin.opcionvacia': 'Select a project…',
    'camara.vacio.sinproyecto.titulo': 'Select a project',
    'camara.vacio.sinproyecto.descripcion': 'Choose a project from the dropdown above to view its photo gallery.',
    'camara.vacio.sinfotos.titulo': 'No photos yet',
    'camara.vacio.sinfotos.descripcion': 'This project doesn’t have any uploaded photos. Use the upload button to add some.',
    'camara.galeria.altfoto': 'Project Photo',
    'camara.visor.descargar.aria': 'Download photo',
    'camara.visor.descargar.tooltip': 'Download',
    'camara.visor.borrar.aria': 'Delete photo',
    'camara.visor.borrar.tooltip': 'Delete',
    'camara.visor.cerrar.aria': 'Close photo',
    'camara.visor.cerrar.tooltip': 'Close',
    'camara.visor.altcompleta': 'Full View',
    'panel.stat.pipeline': 'Pipeline + Annual',
    'panel.stat.pipeline.desc': 'Open estimates + service contracts',
    'panel.stat.proyectosactivos': 'Active Projects',
    'panel.stat.verproyectos': 'View projects ',
    'panel.stat.reloj': 'Time Clock',
    'panel.stat.reloj.trabajando': 'on the clock',
    'panel.stat.reloj.administrar': 'Manage time ',
    'panel.ingresos.anioanterior': 'Previous year',
    'panel.ingresos.aniosiguiente': 'Next year',
    'panel.ingresos.cobradoen': 'collected in ',
    'panel.ingresos.sinbase': 'no comparable baseline',
    'panel.ingresos.vacio.titulo': 'No revenue collected in ',
    'panel.ingresos.vacio.desc': 'Revenue lands here when an invoice or proposal is marked paid.',
    'panel.ingresos.tooltip.cobrado': 'Collected',
    'panel.proyectosrecientes.titulo': 'Recent projects',
    'panel.proyectosrecientes.vacio': 'No active projects yet.',
    'panel.prospectos.titulo': 'New leads',
    'panel.prospectos.vacio': 'No new leads pending.',
    'designLab.nav.panel': 'Dashboard',
    'designLab.nav.clientes': 'Clients',
    'designLab.nav.estimados': 'Estimates',
    'designLab.nav.precios': 'Pricing Library',
    'designLab.nav.seguimientos': 'Follow-ups',
    'designLab.nav.proyectos': 'Projects',
    'designLab.nav.rutas': 'Service Routes',
    'designLab.nav.finanzas': 'Finance',
    'designLab.nav.gente': 'Team',
    'designLab.nav.agenda': 'Schedule',
    'designLab.nav.grupo.ventas': 'Sales',
    'designLab.nav.grupo.operaciones': 'Operations',
    'designLab.nav.grupo.equipo': 'Team',
    'designLab.nav.chat': 'Chat history',
    'designLab.nav.tienda': 'Raza Store',
    'designLab.boton.contorno': 'Outline',
    'designLab.boton.chico': 'Small',
    'designLab.boton.mediano': 'Medium',
    'designLab.boton.grande': 'Large',
    'disenos.presentacion.vacio.titulo': 'No design available',
    'disenos.presentacion.vacio.descripcion': 'Please attach a design to a lead first.',
    'disenos.presentacion.boton.volver': 'Return to Leads',
    'disenos.presentacion.etiqueta.presentando': 'Presenting to:',
    'disenos.presentacion.tooltip.salir': 'Exit presentation',
    'disenos.presentacion.aria.salir': 'Exit presentation',
    'disenos.presentacion.titulo.iframe3d': '3D Design View',
    'disenos.presentacion.aria.anterior': 'Previous image',
    'disenos.presentacion.aria.siguiente': 'Next image',
    'disenos.presentacion.tab.antes': 'Before',
    'disenos.presentacion.tab.propuesta': 'Proposed Design',
    'disenos.presentacion.tab.tresd': 'Interactive 3D',
    'equipo.error.guardarmantenimiento': 'Could not save the maintenance log.',
    'equipo.error.regresartaller': 'Could not return the item to the shop.',
    'equipo.filtro.todos': 'All',
    'equipo.filtro.disponible': 'Available',
    'equipo.filtro.entrabajo': 'On Job',
    'equipo.reparacion.sinresultados.desc': 'No item in repair matches this search.',
    'equipo.reparacion.desde': 'In since ',
    'equipo.reparacion.sinregistros': 'Marked In Shop, but no maintenance has been logged for it yet.',
    'equipo.reparacion.registro.uno': 'log',
    'equipo.reparacion.registro.varios': 'logs',
    'equipo.reparacion.regresando': 'Returning…',
    'equipo.mant.sinresultados.desc': 'Nothing matches this search.',
    'equipo.tabla.equipoborrado': 'Deleted item',
    'equipo.vacio.agregar': 'Add Equipment',
    'equipo.campo.nombre.placeholder': 'e.g. Skid Steer #3',
    'equipo.campo.ubicacionactual': 'Current Location',
    'equipo.campo.ubicacion.placeholder': 'Shop or Project Name',
    'equipo.campo.proximoservicio.label': 'Next Service Date',
    'equipo.campo.notas': 'Internal Notes',
    'equipo.campo.notas.placeholder': 'Any specific care instructions or historical notes...',
    'equipo.boton.verdetalles': 'View Details',
    'equipo.boton.registrarmantenimiento': 'Log Maintenance',
    'equipo.modal.cerrar': 'Close',
    'equipo.mant.campo.fecha': 'Date*',
    'equipo.mant.campo.trabajo.placeholder': 'e.g. Replace hydraulic filter and top off fluid',
    'equipo.mant.campo.costo': 'Cost ($)',
    'equipo.mant.campo.costo.aria': 'Cost in dollars',
    'equipo.mant.campo.hechopor': 'Performed By',
    'equipo.mant.campo.hechopor.placeholder': 'Name or shop',
    'equipo.mant.yaentaller': 'Already in ',
    'equipo.mant.yaentaller.cola': ' — it stays there until you mark it repaired.',
    'equipo.mant.mandartaller': 'Send to ',
    'equipo.mant.historial': 'Recent history',
    'equipo.mant.guardando': 'Saving…',
    'estimados.lista.borrar_varios_titulo': { uno: 'Delete {count} estimate?', otros: 'Delete {count} estimates?' },

    'estimados.lista.borrar_varios_mensaje': { uno: 'This will permanently delete {count} estimate. Linked projects (if any) will keep their data but lose the estimate reference. This action cannot be undone.', otros: 'This will permanently delete {count} estimates. Linked projects (if any) will keep their data but lose the estimate reference. This action cannot be undone.' },
    'estimados.lista.borrar_varios_confirmar': 'Delete {count}',
    'estimados.lista.borrar_varios_cancelar': 'Cancel',
    'estimados.lista.borrados_ok': { uno: 'Deleted {count} estimate.', otros: 'Deleted {count} estimates.' },
    'estimados.lista.borrados_error': { uno: '{count} estimate failed to delete and was restored.', otros: '{count} estimates failed to delete and were restored.' },
    'estimados.lista.borrar_con_proyecto_titulo': 'Delete Estimate AND Project?',
    'estimados.lista.borrar_con_proyecto_mensaje': 'This estimate created the project "{name}". Delete both? Choosing Cancel keeps the project but unlinks it from this estimate.',
    'estimados.lista.borrar_los_dos': 'Delete Both',
    'estimados.lista.dejar_proyecto': 'Keep Project (unlink)',
    'estimados.lista.error_borrar': 'Failed to delete: {error}',
    'estimados.lista.borrar_mensaje': 'Are you sure you want to delete this estimate? This action cannot be undone.',
    'estimados.lista.borrar_confirmar': 'Delete',
    'estimados.lista.borrar_cancelar': 'Cancel',
    'estimados.lista.error_borrar_estimado': 'Failed to delete estimate: {error}',
    'estimados.lista.modo_estimados': 'Estimates',
    'estimados.lista.modo_propuesta': 'Proposal',
    'estimados.lista.modo_firmadas': 'Signed',
    'estimados.lista.kpi_borradores': 'Draft pipeline',
    'estimados.lista.kpi_por_aprobar': 'Pending approval',
    'estimados.lista.kpi_ganado': 'Won revenue',
    'estimados.lista.kpi_cierre': 'Win rate',
    'estimados.lista.kpi_cierre_nota': 'Based on closed deals',
    'estimados.lista.tab_todos': 'All',
    'estimados.lista.tab_borrador': 'Draft',
    'estimados.lista.tab_enviado': 'Sent',
    'estimados.lista.tab_aceptado': 'Accepted',
    'estimados.lista.tab_rechazado': 'Declined',
    'estimados.lista.tab_vencido': 'Expired',
    'estimados.lista.tab_archivado': 'Archived',
    'estimados.lista.buscar': 'Search estimates…',
    'estimados.lista.quitar_seleccion_todo': 'Deselect all',
    'estimados.lista.seleccionar_todo': 'Select all',
    'estimados.lista.sin_titulo': 'Untitled Estimate',
    'estimados.lista.cambiar_estatus': 'Change status',
    'estimados.lista.sin_proyecto_tooltip': 'This accepted estimate has no project yet. Click to create it.',
    'estimados.lista.sin_proyecto_crear': 'No project — create',
    'estimados.lista.vacio_filtro_titulo': 'No matches',
    'estimados.lista.vacio_filtro_busqueda': 'Nothing matched “{searchQuery}”.',
    'estimados.lista.vacio_filtro_estatus': 'No estimates in this status.',
    'estimados.lista.quitar_filtros': 'Clear filters',
    'estimados.lista.vacio_titulo': 'No estimates yet',
    'estimados.lista.vacio_texto': 'Start a job to build your first estimate — pricing, margin and the client proposal all come from here.',
    'estimados.lista.cerrar_menu': 'Close menu',
    'estimados.lista.seguir_editando': 'Continue editing',
    'estimados.lista.borrar_borrador': 'Delete draft',
    'finanzas.encabezado.titulo': 'Finance',
    'finanzas.encabezado.desc': 'Earnings, jobs and contracts across the whole business.',
    'finanzas.restringido.titulo': 'Financials are restricted',
    'finanzas.restringido.desc': 'Your role can browse jobs and contracts, but not revenue figures.',
    'seguimientos.cabecera.titulo': 'Follow-ups',
    'seguimientos.cabecera.descripcion': 'Track where every sent proposal stands',
    'seguimientos.estado.proyectocreado': 'Project created',
    'seguimientos.estado.pagado': 'Paid',
    'seguimientos.estado.completado': 'Completed',
    'seguimientos.estado.ganado': 'Won',
    'seguimientos.columna.enviadas': 'Sent (Unread)',
    'seguimientos.vacio.enviadas': 'No unread proposals',
    'seguimientos.vacio.enviadaspista': 'Proposals land here once you mark them',
    'seguimientos.vacio.enviadasmarca': 'Sent',
    'seguimientos.vacio.enviadasdonde': 'do that in the proposal’s Share tab, right after you send the link.',
    'seguimientos.tarjeta.hacepoco': 'Recently',
    'seguimientos.vacio.abiertas': 'No viewed proposals',
    'seguimientos.vacio.ganadas': 'No accepted proposals',
    'seguimientos.estado.vencida': 'Expired',
    'seguimientos.estado.rechazada': 'Declined',
    'seguimientos.vacio.perdidas': 'No declined proposals',
    'seguimientos.dia.sinfecha': 'No date',
    'prospectos.convertir.titulo': 'Convert to Client',
    'prospectos.convertir.confirmar': 'Convert & Win',
    'prospectos.convertir.cancelar': 'Cancel',
    'prospectos.convertir.error': 'Could not convert this lead. Please try again.',
    'prospectos.buscar.placeholder': 'Search pipeline…',
    'prospectos.filtro.todos': 'All',
    'prospectos.filtro.vencidos': 'Due',
    'prospectos.boton.nuevo': 'Add Lead',
    'prospectos.pista.arrastrar': 'Drag a lead card onto another column to move it to that stage.',
    'marketing.vista.titulo': 'Marketing Hub',
    'marketing.vista.descripcion': 'Campaigns, reviews, and social media.',
    'marketing.vista.vacio_titulo': 'Marketing isn’t connected yet',
    'marketing.vista.vacio_descripcion': 'Unified Inbox, Reputation and Social Planner will appear here once the marketing engine is connected to this workspace. Until then nothing is sent from Raza Pro.',
    'precios.titulo': 'Pricing Library',
    'precios.sub': 'Materials, templates, and your custom prices in one place.',
    'precios.tab.materiales': 'Materials',
    'precios.tab.plantillas': 'Templates',
    'precios.tab.materialesPlantilla': 'Template Materials',
    'precios.tab.mios': 'My Custom Items',
    'precios.aviso.titulo': 'Market Volatility Warning',
    'precios.aviso.texto': 'Material prices fluctuate daily due to fuel costs and availability. Always verify vendor pricing before finalizing large contracts.',
    'precios.exportarCsv': 'Export CSV',
    'precios.nuevoMaterial': 'New Item',
    'precios.frescura.fresco': 'Fresh',
    'precios.frescura.medio': 'Aging',
    'precios.frescura.viejo': 'Stale',
    'precios.borrar.titulo': 'Delete Material',
    'precios.borrar.mensaje1': 'Are you sure you want to delete',
    'precios.borrar.mensaje2': 'This will not affect existing estimates, but may affect templates linked to it.',
    'precios.borrar.confirmar': 'Delete',
    'precios.borrar.cancelar': 'Cancel',
    'precios.importar.errorTamano': 'File size exceeds 10MB limit.',
    'precios.importar.leyendo': 'AI is analyzing and parsing the material list...',
    'precios.importar.sinMateriales': 'AI could not find any valid material items in this file.',
    'precios.importar.sinNombre': 'Unknown Item',
    'precios.importar.avisoCosto': 'Cost is $0',
    'precios.importar.listo': { uno: 'AI successfully parsed {count} item!', otros: 'AI successfully parsed {count} items!' },
    'precios.importar.preciosActualizados': { uno: '{count} catalog price updated', otros: '{count} catalog prices updated' },
    'precios.importar.nuevosAgregados': { uno: '{count} new item added', otros: '{count} new items added' },
    'precios.importar.resumen': 'Imported —',
    'precios.importar.nada': 'Nothing to import.',
    'precios.importar.error': 'Failed to import some items. Please try again.',
    'precios.exportar.listo': 'Pricebook exported!',
    'precios.importar.titulo': 'Reading your file',
    'precios.importar.sub': 'Extracting materials, categories, and pricing. This may take 10–30 seconds for large files.',
    'precios.vacio.titulo': 'No items found',
    'precios.vacio.texto': 'Upload your price list to get started, or add items manually.',
    'precios.vacio.filtro': 'Try adjusting your filters or search.',
    'precios.tabla.material': 'Item name',
    'precios.tabla.precioVenta': 'Sell price',
    'precios.editar': 'Edit',
    'precios.borrarFila': 'Delete',
    'precios.preview.titulo': 'Import Preview',
    'precios.preview.llenanCatalogo': 'will fill a catalog price',
    'precios.cerrar': 'Close',
    'precios.preview.formato': 'Detected format:',
    'precios.preview.total': 'Total items:',
    'precios.preview.valor': 'Estimated value:',
    'precios.preview.confianza.alta': 'High confidence',
    'precios.preview.confianza.media': 'Medium confidence (verify)',
    'precios.preview.confianza.baja': 'Low confidence (needs attention)',
    'precios.preview.confianza.desconocida': 'Unknown confidence',
    'precios.tabla.coincidencia': 'Catalog match',
    'precios.preview.usarPrecio': 'Apply price',
    'precios.preview.agregarNuevo': 'Add new',
    'precios.preview.sinCoincidencia': 'No match → add new',
    'proyectos.tarjeta.vaTarde': 'Overdue',
    'proyectos.tarjeta.arranque.poner': 'Set start date',
    'proyectos.tarjeta.arranque.fecha': 'Starts {fecha}',
    'proyectos.tarjeta.arranque.ayuda': 'When the day arrives the job moves to In Progress on its own',
    'proyectos.tarjeta.cambiarEstatus': 'Change status',
    'proyectos.estatus.aceptado': 'Accepted',
    'proyectos.estatus.programado': 'Scheduled',
    'proyectos.estatus.enProceso': 'In Progress',
    'proyectos.estatus.terminado': 'Completed',
    'proyectos.estatus.cancelado': 'Cancelled',
    'proyectos.tarjeta.sinPendientes': 'All tasks complete',
    'proyectos.tarjeta.sinGente': 'No crew',
    'proyectos.columna.vacia': 'Nothing here',
    'proyectos.columna.masViejos': 'older completed',
    'proyectos.columna.dondeQuedaron': 'Kept in Finance → Projects',
    'proyectos.cargando': 'Loading project…',
    'proyectos.titulo': 'Projects',
    'proyectos.vista.tablero': 'Board',
    'proyectos.vista.agenda': 'Schedule',
    'proyectos.agenda.porColocar': 'Jobs to place',
    'proyectos.agenda.ayudaArrastre': '— drag a job onto someone\'s day, or drag it back here to unschedule',
    'proyectos.agenda.todoColocado': 'Everything is scheduled.',
    'proyectos.agenda.hoy': 'Today',
    'proyectos.agenda.imprimir': 'Print',
    'proyectos.agenda.dia': 'Day',
    'proyectos.agenda.semana': 'Week',
    'proyectos.agenda.agendados': 'scheduled',
    'proyectos.agenda.cuadrilla': 'Crew',
    'proyectos.agenda.sinAsignar': 'Unassigned',
    'proyectos.agenda.sinEquipo': 'No team members yet — add people under Team to build a schedule.',
    'proyectos.agenda.agregarAqui': 'Put a job here',
    'proyectos.agenda.agregarAria': 'Put a job on {persona} for {dia}',
    'proyectos.agenda.elegirTitulo': '{persona} — {dia}',
    'proyectos.agenda.sinTrabajos': 'No jobs to schedule. Completed and cancelled work is not listed here.',
    'proyectos.agenda.errorAgendar': 'Could not schedule that job.',
    'proyectos.agenda.errorQuitar': 'Could not unschedule that job.',
    'proyectos.agenda.quitado': '{trabajo} removed from that day.',
    'proyectos.agenda.quitarBoton': 'Remove from this day',
    'proyectos.agenda.quitarAria': 'Remove {trabajo} from this day',
    'proyectos.agenda.anterior': 'Previous',
    'proyectos.agenda.siguiente': 'Next',
    'proyectos.descripcion': 'Active jobs and production schedule.',
    'proyectos.buscar.placeholder': 'Search projects…',
    'proyectos.nuevo': 'New Project',
    'proyectos.stat.dineroActivo': 'Active revenue',
    'proyectos.stat.trabajosActivos': 'Active jobs',
    'proyectos.columna.aceptado': 'Accepted',
    'proyectos.columna.programado': 'Scheduled',
    'proyectos.columna.enProceso': 'In Progress',
    'proyectos.columna.terminado': 'Completed',
    'proyectos.columna.cancelado': 'Cancelled',
    'propuesta.aceptar.titulo': 'Proposal Accepted',
    'propuesta.aceptar.mensaje': 'Proposal marked as Accepted.',
    'propuesta.aceptar.error': 'Could Not Accept / Failed to update proposal status. Please try again.',
    'propuesta.rechazar.titulo': 'Decline Proposal',
    'propuesta.rechazar.motivo': 'Decline Reason (optional):',
    'propuesta.rechazar.placeholder': 'Enter reason...',
    'propuesta.rechazada.titulo': 'Proposal Declined',
    'propuesta.rechazada.mensaje': 'Proposal marked as Declined.',
    'propuesta.rechazar.error': 'Could Not Decline / Failed to update proposal status. Please try again.',
    'propuesta.top.titulo_preview': 'Proposal Preview',
    'propuesta.top.titulo_editor': 'Proposal Editor',
    'propuesta.top.sub_movil': 'Tap ← to return to tools',
    'propuesta.top.sub_editor': 'Drafting for ',
    'propuesta.publica.error.noencontrada': 'Proposal not found. It may have been deleted or the link is incorrect.',
    'propuesta.publica.error.nocargo': 'Unable to load proposal. Please try again later.',
    'propuesta.publica.error.abrircontrato.titulo': 'Could not open the contract',
    'propuesta.publica.error.abrircontrato.mensaje': 'Please contact the business for a copy.',
    'propuesta.publica.error.descargarfirmado': 'Could not open the signed contract. Please contact the business.',
    'propuesta.publica.tarea.recorrido': 'Site Walkthrough & Safety Check',
    'propuesta.publica.tarea.material': 'Material Verification',
    'propuesta.publica.tarea.equipo': 'Equipment Staging',
    'propuesta.publica.proyecto.nombre': 'Project for ',
    'propuesta.publica.proyecto.desc': 'Auto-created project',
    'propuesta.publica.error.aceptar.mensaje': 'Error accepting proposal. Please contact the business directly.',
    'propuesta.publica.error.aceptar.titulo': 'Acceptance Error',
    'propuesta.publica.rechazar.titulo': 'Decline Proposal',
    'propuesta.publica.rechazar.mensaje': 'Please tell us why you are declining (Optional):',
    'propuesta.publica.rechazar.placeholder': 'Reason for declining',
    'propuesta.publica.rechazada.titulo': 'Proposal Declined',
    'propuesta.publica.rechazada.mensaje': 'Proposal declined.',
    'propuesta.publica.error.rechazar.mensaje': 'Error declining proposal. Please try again.',
    'propuesta.publica.error.sincronizacion': 'Sync Error',
    'propuesta.publica.error.nodisponible': 'Proposal unavailable',
    'propuesta.publica.default.intro': 'Please review the estimate below.',
    'propuesta.publica.imprimir.volver': '← Back to proposal',
    'propuesta.publica.imprimir.guardarpdf': 'Print / Save as PDF',
    'propuesta.publica.banner.rechazada': 'This proposal was declined and is no longer active.',
    'propuesta.publica.banner.firmada': 'This proposal has been signed and is locked — no further changes can be made.',
    'propuesta.publica.banner.vistaprevia': 'Preview mode — this is your client view. Clients sign from the shared link; you can’t sign your own proposal.',
    'propuesta.publica.contrato.titulo': 'Contract you’ll be signing',
    'propuesta.publica.contrato.yafirmadopor': ' — already signed by ',
    'propuesta.publica.contrato.leelocompleto': 'Please read it in full before you accept.',
    'propuesta.publica.contrato.abriendo': 'Opening…',
    'propuesta.publica.contrato.leer': 'Read contract',
    'propuesta.publica.exito.contratofirmado': 'Contract signed',
    'propuesta.publica.exito.propuestaaceptada': 'Proposal accepted',
    'propuesta.publica.exito.sinpagoenlinea': 'Thank you for signing. We will contact you shortly for the payment.',
    'propuesta.publica.exito.sigueelpago': 'Thank you for signing. The next step is payment.',
    'propuesta.publica.exito.graciasaceptar': 'Thank you for accepting this proposal.',
    'propuesta.publica.pago.titulo': 'Ready to receive payment',
    'propuesta.publica.pago.zelle.titulo': 'Zelle transfer',
    'propuesta.publica.pago.zelle.correo': 'Email',
    'propuesta.publica.pago.zelle.telefono': 'Phone',
    'propuesta.publica.pago.zelle.nota': 'Once your transfer is received, we’ll schedule your job.',
    'propuesta.publica.pago.banco.titulo': 'Bank transfer (ACH)',
    'propuesta.publica.pago.banco.titular': 'Account holder',
    'propuesta.publica.pago.banco.banco': 'Bank',
    'propuesta.publica.pago.banco.tipo': 'Account type',
    'propuesta.publica.pago.banco.tipo.corriente': 'Checking',
    'propuesta.publica.pago.banco.tipo.ahorro': 'Savings',
    'propuesta.publica.pago.banco.ruta': 'Routing number',
    'propuesta.publica.pago.banco.cuenta': 'Account number',
    'propuesta.publica.pago.banco.nota': 'Send the payment from your bank with these details. ACH transfers take 1 to 3 business days.',
    'propuesta.publica.pago.tarjeta': 'Pay with card ',
    'propuesta.publica.imprimir.contratocompleto': 'Print full agreement',
    'propuesta.publica.copia.preparando': 'Preparing your copy…',
    'propuesta.publica.error.generarpdf': 'Could not generate the signed contract PDF. Please contact the business.',
    'propuesta.publica.exito.verpropuesta': 'View proposal',
    'propuesta.publica.exito.imprimirrecibo': 'Print receipt',
    'servicios.rutas.sin_paradas_con_direccion': 'No stops with addresses for {crewLabel}.',
    'servicios.rutas.limite_paradas': 'Showing first 23 stops in Google Maps (max waypoints).',
    'servicios.rutas.ruta_de_hoy': 'today’s route',
    'servicios.rutas.pane_paradas': 'Stops',
    'servicios.rutas.pane_mapa': 'Map',
    'servicios.tab_rutas': 'Routes',
    'servicios.tab_contratos': 'Contracts',
    'servicios.tab_armar_ruta': 'Route Builder',
    'servicios.tab_cuadrillas': 'Crews',
    'servicios.tab_historial': 'History',
    'servicios.tab_analisis': 'Analytics',
    'servicios.tab_calendario': 'Scheduler',
    'servicios.tab_equipo': 'Equipment',
    'servicios.tab_cobros': 'Billing',
    'cobros.mes.anterior': 'Previous month',
    'cobros.mes.siguiente': 'Next month',
    'cobros.generar.nada': 'Nothing to generate',
    'cobros.generar.boton': { uno: 'Generate {count} invoice', otros: 'Generate {count} invoices'},
    'cobros.generar.ok': { uno: '{count} invoice generated for {periodo}.', otros: '{count} invoices generated for {periodo}.'},
    'cobros.generar.yaEstaba': '{periodo} was already generated.',
    'cobros.generar.error': 'Could not generate the invoices.',
    'cobros.actualizar.error': 'Could not update the invoice.',
    'cobros.total.facturado': 'Invoiced',
    'cobros.total.cobrado': 'Collected',
    'cobros.total.pendiente': 'Outstanding',
    'cobros.total.vencidas': 'Overdue',
    'cobros.vacio.titulo': 'No invoices for {periodo}',
    'cobros.vacio.hayQueGenerar': { uno: '{count} contract can be billed for this month — press Generate above.', otros: '{count} contracts can be billed for this month — press Generate above.'},
    'cobros.vacio.sinContratos': 'No active contract covers this month.',
    'cobros.factura.vence': 'Due {fecha}',
    'cobros.factura.pagada': 'paid {fecha}',
    'cobros.factura.contratoBorrado': 'Deleted contract',
    'cobros.estado.Draft': 'Draft',
    'cobros.estado.Sent': 'Sent',
    'cobros.estado.Paid': 'Paid',
    'cobros.estado.Overdue': 'Overdue',
    'cobros.borrar.titulo': 'Delete this invoice?',
    'cobros.borrar.texto': '{cliente} — {periodo}, {importe}. You can generate the month again afterwards.',
    'cobros.borrar.confirmar': 'Delete invoice',
    'cobros.borrar.ok': 'Invoice deleted.',
    'cobros.borrar.error': 'Could not delete the invoice.',
    'cobros.borrar.aria': 'Delete invoice',
    'cobros.descargar.aria': 'Download invoice as PDF',
    'cobros.descripcion.editar': 'Change what the invoice says',
    'cobros.descripcion.vacia': 'No work description',
    'cobros.descripcion.placeholder': 'What was done this month…',
    'cobros.descripcion.aria': 'Work description on the invoice',
    'facturaDoc.titulo': 'Invoice',
    'facturaDoc.facturarA': 'Bill to',
    'facturaDoc.periodo': 'Period',
    'facturaDoc.emitida': 'Issued',
    'facturaDoc.vence': 'Due',
    'facturaDoc.pagada': 'Paid',
    'facturaDoc.tabla.descripcion': 'Description',
    'facturaDoc.tabla.importe': 'Amount',
    'facturaDoc.sinDescripcion': 'Route service',
    'facturaDoc.totalDebido': 'Total due',
    'facturaDoc.totalPagado': 'Total paid',
    'facturaDoc.comoPagar': 'How to pay',
    'facturaDoc.pago.zelle': 'Zelle',
    'facturaDoc.pago.banco': 'Bank transfer',
    'facturaDoc.pago.enlace': 'Pay online',
    'facturaDoc.pie': 'Thank you for your business — {negocio}',

    'servicios.rutas.sin_contrato': 'Could not find the contract for this stop.',
    'servicios.rutas.sin_ruta': 'Could not find the route for this stop.',
    'servicios.rutas.primera_parada': '"{cliente}" is now the first stop.',
    'servicios.rutas.error_primera_parada': 'Couldn’t set as start: {error}',
    'servicios.rutas.visita_marcada': 'Visit marked "{newStatus}"',
    'servicios.rutas.error_actualizar_visita': 'Could not update visit: {error}',
    'servicios.rutas.notas_guardadas': 'Visit notes saved',
    'servicios.rutas.error_guardar_notas': 'Could not save notes: {error}',
    'servicios.rutas.contratos_otro_dia_pero': ' — but ',
    'servicios.rutas.contratos_otro_dia_revisa': '. Check those contracts.',
    'servicios.rutas.dia_anterior': 'Previous day',
    'servicios.rutas.dia_siguiente': 'Next day',
    'servicios.rutas.ver_cuadrillas_tooltip': 'See the crews working today',
    'servicios.rutas.tiempo_estimado': 'Est. time',
    'servicios.rutas.geocodificando': 'Geocoding',
    'servicios.rutas.sin_paradas_tooltip': 'No stops to route',
    'servicios.rutas.abrir_maps_tooltip': 'Open all stops as a route in Google Maps',
    'servicios.contratos.buscar': 'Search by client or address…',
    'servicios.contratos.actualizado': 'Contract updated!',
    'servicios.contratos.creado': 'Contract created!',
    'servicios.contratos.error_guardar': 'Failed to save contract',
    'servicios.contratos.cancelar_titulo': 'Cancel this contract?',
    'servicios.contratos.cancelar_mensaje': 'Stop recurring service for {cliente} at {direccion}? It stops counting toward monthly revenue and stops generating visits. Past visits are kept, and you can reactivate it later.',
    'servicios.contratos.cancelar_confirmar': 'Cancel Contract',
    'servicios.contratos.cancelar_negar': 'Keep Active',
    'servicios.contratos.reactivado': 'Contract reactivated',
    'servicios.contratos.pausado': 'Contract paused — no visits will be generated',
    'servicios.contratos.cancelado': 'Contract cancelled',
    'servicios.contratos.error_actualizar': 'Failed to update contract',
    'servicios.contratos.borrado': 'Contract deleted',
    'servicios.contratos.error_borrar': 'Failed to delete contract',
    'servicios.contratos.vacio_filtro_titulo': 'No contracts match your filters',
    'servicios.contratos.vacio_titulo': 'No service contracts yet',
    'servicios.contratos.vacio_filtro_texto': 'Try a different search or clear the status filter.',
    'servicios.contratos.vacio_texto': 'Create your first contract to start managing recurring services.',
    'servicios.contratos.editar': 'Edit contract',
    'servicios.contratos.borrar_tooltip': 'Delete contract permanently',
    'servicios.contratos.tarifa_mensual': '${tarifa}/mo',
    'servicios.contratos.tarifa_visita': '${tarifa}/visit',
    'servicios.contratos.asignar_cuadrilla': 'Assign crew…',
    'servicios.titulo': 'Service Routes',
    'servicios.descripcion': 'Manage recurring service contracts, routes, and visit history.',
    'servicios.rutas.sin_cuadrillas': 'No crews created yet',
    'servicios.rutas.sin_miembros': 'No members assigned',
    'servicios.contratos.borrar_titulo': 'Delete this contract?',
    'servicios.contratos.borrar_texto': 'You’re about to permanently delete the service contract for',
    'servicios.contratos.borrar_aviso': 'This action can’t be undone.',
    'servicios.contratos.borrando': 'Deleting…',
    'servicios.contratos.borrar_confirmar': 'Delete contract',
    'ajustes.nav.titulo': 'Settings',
    'ajustes.nav.mi_cuenta': 'My Account',
    'ajustes.nav.overhead': 'Overhead & Pricing',
    'ajustes.nav.equipo': 'Team',
    'propFirmadas.error.plantillaBorrada': 'The contract template for this proposal no longer exists.',
    'propFirmadas.ok.contratoGuardado': 'Signed contract archived.',
    'propFirmadas.titulo': 'Signed Proposals',
    'propFirmadas.descripcion': 'Accepted & signed proposals — view or download a copy for your records.',
    'propFirmadas.buscar': 'Search client or title…',
    'propFirmadas.vacio.titulo': 'No signed proposals yet',
    'propFirmadas.limpiarBusqueda': 'Clear search',
    'propFirmadas.col.cliente': 'Client',
    'propFirmadas.col.propuesta': 'Proposal',
    'propFirmadas.col.monto': 'Amount',
    'propFirmadas.col.firmada': 'Signed',
    'propFirmadas.col.contrato': 'Contract',
    'propFirmadas.col.estado': 'Status',
    'propFirmadas.col.acciones': 'Actions',
    'propFirmadas.clienteSinNombre': 'Client',
    'propFirmadas.propuestaSinTitulo': 'Proposal',
    'propFirmadas.estado.guardado': 'On file',
    'propFirmadas.tip.sinGuardar': 'Signed, but no permanent copy stored yet — press File a copy',
    'propFirmadas.estado.sinGuardar': 'Not filed',
    'propFirmadas.sinContrato': 'No contract',
    'propFirmadas.verPropuesta': 'View proposal',
    'propFirmadas.pdfPropuestaLink': 'Proposal PDF',
    'propFirmadas.tip.guardarCopia': 'Store a permanent copy so it survives edits to the contract template',
    'propFirmadas.guardarCopia': 'File a copy',
    'propFirmadas.abrirContrato': 'Open contract',
    'propFirmadas.pdfPropuestaBoton': 'Proposal PDF',
    'propFirmadas.tip.pdfPropuesta': 'Print-ready copy of the proposal',
    'superAdmin.error.agregarEmpresa': 'Error adding tenant: ',
    'superAdmin.error.invitar': 'Error inviting user',
    'superAdmin.error.invitarToast': 'Error inviting user: ',
    'superAdmin.error.borrarUsuario': 'Error deleting user',
    'superAdmin.error.borrarUsuarioToast': 'Error deleting user: ',
    'superAdmin.error.borrarEmpresa': 'Error deleting tenant',
    'superAdmin.error.borrarEmpresaToast': 'Error deleting tenant: ',
    'superAdmin.estado.activo': 'Active',
    'superAdmin.estado.suspendido': 'Suspended',
    'superAdmin.rol.tecnicoEtiqueta': 'Service Tech',
    'superAdmin.tab.resumen': 'Overview',
    'superAdmin.tab.empresas': 'Tenants',
    'superAdmin.tab.usuarios': 'Users',
    'superAdmin.tab.admins': 'Admins',
    'superAdmin.tab.reportes': 'Reports',
    'superAdmin.tab.videos': 'Educational Videos',
    'superAdmin.tab.uso_ia': 'AI Usage',
    'superAdmin.tab.clases': 'Classes',

    // ── Classes (crew view + admin tab) ──
    'nav.clases': 'Classes',
    'clases.titulo': 'Classes',
    'clases.descripcion': 'Recorded classes and the Friday Q&A meeting. Watch them whenever you can.',
    'clases.buscar': 'Search for a class…',
    'clases.reproducir': 'Play',
    'clases.borrador': 'Draft',
    'clases.sin_grabacion': 'No recording yet',
    'clases.vacio_titulo': 'No classes yet',
    'clases.vacio_desc': 'The first recording will show up here once it is uploaded.',
    'clases.sin_resultados': 'No classes match',
    'clases.sin_resultados_desc': 'Try another word, or clear the search.',
    'clases.error_titulo': 'Could not load the classes',
    'clases.error_desc': 'The list never arrived, so this screen does not know which classes exist. It does not mean there are none. Detail: {detalle}',
    'clases.error_reproducir': 'This video would not open. Try again; if it keeps failing, let the team know.',
    'clases.reintentar': 'Retry',

    'clases.admin_titulo': 'Classes',
    'clases.admin_desc': 'Upload each class recording here. Nobody sees it until you hit Publish.',
    'clases.admin_vacio': 'You have not created any classes yet',
    'clases.admin_vacio_desc': 'Create the class first, then upload its video.',
    'clases.progreso': '{publicadas} of {total} published',
    'clases.actualizar': 'Refresh',
    'clases.nueva': 'New class',
    'clases.crear': 'Create class',
    'clases.crear_aviso': 'Created as a draft. The video is uploaded afterwards, from the class card.',
    'clases.titulo_placeholder': 'Friday meeting — questions and answers',
    'clases.desc_placeholder': 'What was covered, in a line or two.',
    'clases.campo_titulo': 'Title',
    'clases.campo_fecha': 'Class date',
    'clases.campo_desc': 'Description',
    'clases.ayuda': 'Formats: MP4 or WebM, up to {limite}. The upload resumes on its own if the connection drops, but do not close this tab while it runs.',
    'clases.subir': 'Upload video',
    'clases.reemplazar': 'Replace video',
    'clases.miniatura': 'Thumbnail',
    'clases.cambiar_miniatura': 'Change thumbnail',
    'clases.publicar': 'Publish',
    'clases.ocultar': 'Unpublish',
    'clases.borrar': 'Delete',
    'clases.estado_publicada': 'Published',
    'clases.estado_borrador': 'Draft',
    'clases.meta': '{peso} · {duracion}',
    'clases.subiendo': 'Uploading {pct}% — {enviado} of {total}',
    'clases.subiendo_aria': 'Upload progress',
    'clases.subiendo_aviso': 'If the connection drops the upload picks up where it left off. Closing this tab does cancel it.',
    'clases.cancelar': 'Cancel',
    'clases.cancelar_titulo': 'Cancel the upload?',
    'clases.cancelar_mensaje': 'What has been uploaded so far is lost and you would start over.',
    'clases.cancelar_confirmar': 'Yes, cancel',
    'clases.cancelar_seguir': 'Keep uploading',
    'clases.borrar_titulo': 'Delete this class?',
    'clases.borrar_mensaje': '"{titulo}" and its recording are deleted. This cannot be undone.',
    'clases.borrar_confirmar': 'Delete class',
    'clases.toast_creada': 'Class created',
    'clases.toast_subida': 'Video uploaded',
    'clases.toast_miniatura': 'Thumbnail updated',
    'clases.toast_publicada': 'Class published',
    'clases.toast_oculta': 'Class unpublished',
    'clases.toast_borrada': 'Class deleted',
    'clases.err.archivo_vacio': 'That file is empty.',
    'clases.err.muy_grande': 'The file is {tamano} and the limit is {limite}.',
    'clases.err.formato': 'The {tipo} format will not work here. Use MP4 or WebM.',
    'clases.err.titulo_vacio': 'The class needs a title.',
    'clases.err.sin_permiso': 'The database refused the change. Check that your account is still a platform admin.',
    'clases.err.sin_sesion': 'Your session expired. Sign in again and retry.',
    'clases.err.sin_video': 'A class cannot be published without a recording.',
    'clases.comprimir.titulo': 'Compress it right here',
    'clases.comprimir.ya_esta_bien': 'This file is fine as it is. You can still compress it if you want it smaller.',
    'clases.convertidor.abrir': 'Convert a video',
    'clases.convertidor.titulo': 'Video converter',
    'clases.convertidor.intro': 'Shrink a video using your own computer. Nothing is uploaded: it converts here and you keep the file.',
    'clases.convertidor.elegir': 'Choose a video',
    'clases.convertidor.salida': 'The converted file downloads to your computer. Upload it to whichever class you want afterwards.',
    'clases.convertidor.descargar': 'Download the video',
    'clases.convertidor.otro': 'Convert another',
    'clases.convertidor.guarda_ya': 'Download it before closing this window — closing without saving deletes the file.',
    'clases.convertidor.descartar': 'Discard',
    'clases.convertidor.pendiente_titulo': 'You have a converted video that was never saved',
    'clases.convertidor.pendiente_desc': 'It was converted earlier and kept here because the page reloaded. Download it or discard it.',
    'clases.comprimir.intro': 'Your computer can fix this video without installing anything. Pick one:',
    'clases.comprimir.opcion_comprimir': 'Compress the video',
    'clases.comprimir.opcion_comprimir_desc': 'Makes it smaller and fixes the slow start. Takes a while and uses your graphics card.',
    'clases.comprimir.opcion_rapido': 'Just fix the slow start',
    'clases.comprimir.opcion_rapido_desc': 'Seconds. Changes nothing about the picture, only makes it start immediately.',
    'clases.comprimir.opcion_subir': 'Upload it as is',
    'clases.comprimir.archivo': 'File: {peso} · {bitrate}',
    'clases.comprimir.no_soportado': 'Your browser cannot compress here. Use Chrome, or compress the file before uploading.',
    'clases.comprimir.listo': 'Done: {antes} down to {despues}',
    'clases.comprimir.error': 'Could not compress the video.',
    'clases.comprimir.cancelar': 'Cancel',
    'clases.comprimir.etapa.leyendo': 'Reading the file…',
    'clases.comprimir.etapa.midiendo': 'Measuring your computer…',
    'clases.comprimir.etapa.trabajando': 'Compressing…',
    'clases.comprimir.etapa.cerrando': 'Closing the file…',
    'clases.comprimir.aviso_tiempo': 'Do not close this tab. You can keep using the rest of the panel.',
    'clases.comprimir.err.no_soportado': 'Your browser cannot compress here. Use Chrome.',
    'clases.comprimir.err.sin_pista': 'That file has no video track.',
    'clases.comprimir.err.sin_audio': 'The audio would have been lost, so it was cancelled. Upload as is, or convert it with ffmpeg.',
    'clases.comprimir.err.sin_espacio': 'Not enough disk space for the compressed file.',
    'clases.comprimir.err.sin_ahorro': 'The result was not smaller, so it was not uploaded. The video was already well compressed.',
    'clases.comprimir.err.indice': 'The compressed file came out malformed and was not uploaded.',
    'clases.comprimir.err.decodificar': 'Chrome could not read this video to re-encode it. Use "Just fix the slow start" instead — it does not re-encode, so it works.',
    'clases.comprimir.err.cancelado': 'Compression cancelled.',
    'clases.aviso.titulo': 'This video will cause problems',
    'clases.aviso.faststart': "The video's index sits at the END of the file, so the browser downloads all of it before the first frame: it looks broken, not slow. Seconds to fix, no quality lost.",
    'clases.aviso.bitrate': 'It is at {actual}. A screen recording looks identical at {sugerido} — about four times smaller: faster to upload, faster to start, and far cheaper to serve.',
    'clases.aviso.comando': 'Run this before uploading (needs ffmpeg):',
    'clases.aviso.subir_igual': 'Upload it anyway',
    'clases.aviso.mejor_no': "I'll fix it first",
    'clases.err.limite_servidor': 'Supabase rejected the file for size. Raise the ceiling in the Supabase dashboard: Project Settings → Storage → "Upload file size limit". The bucket is already set to 5 GB; that other limit is the one in charge.',
    'clases.err.crear': 'Could not create the class.',
    'clases.err.subir': 'Could not upload the file.',
    'clases.err.publicar': 'Could not change the status.',
    'clases.err.borrar': 'Could not delete.',
    'clases.err.guardar': 'Could not save the change.',
    'superAdmin.subtitulo': 'Platform Control Center',
    'superAdmin.refrescarAria': 'Refresh data',
    'superAdmin.refrescarTip': 'Refresh data',
    'superAdmin.salir': 'Sign Out',
    'superAdmin.empresaNueva': 'New Tenant',
    'superAdmin.crearEmpresa': 'Create Tenant',
    'superAdmin.nombreNegocio': 'Business Name',
    'superAdmin.nombreNegocioPlaceholder': 'e.g., Raza Pro HVAC',
    'superAdmin.idAuto': 'A unique workspace ID will be auto-generated from this name.',
    'superAdmin.correoDueno': 'Owner Email',
    'superAdmin.nombreCorto': 'Short Name',
    'superAdmin.nombreCortoPlaceholder': 'e.g., RPH',
    'superAdmin.colorMarca': 'Brand Color',
    'superAdmin.colorMarcaAria': 'Brand color',
    'superAdmin.invitarTitulo': 'Invite User',
    'superAdmin.invitarBoton': 'Invite User',
    'superAdmin.correo': 'Email Address',
    'superAdmin.nombreCompleto': 'Full Name',
    'superAdmin.nombrePlaceholder': 'John Doe',
    'superAdmin.rol': 'Role',
    'superAdmin.rol.admin': 'Admin (Workspace Manager)',
    'superAdmin.rol.oficina': 'Office Staff',
    'superAdmin.rol.trabajador': 'Laborer',
    'superAdmin.rol.tecnico': 'Service Tech',
    'superAdmin.asignarEspacios': 'Assign to Workspaces',
    'superAdmin.borrarUsuarioTitulo': 'Permanently Delete User',
    'superAdmin.borrarParaSiempre': 'Delete Forever',
    'superAdmin.borrarUsuarioAviso': 'This will permanently remove:',
    'superAdmin.borrarUsuarioDetalle': 'The user will be erased from the authentication system and all workspace access records.',
    'superAdmin.borrarEmpresaTitulo': 'Delete Tenant',
    'superAdmin.borrarEmpresaBoton': 'Delete Tenant',
    'superAdmin.borrarEmpresaAviso': 'This will permanently destroy:',
    'superAdmin.borrarEmpresaUsuarios': 'user(s) will lose access',
    'superAdmin.borrarEmpresaDatos': 'All clients, estimates, projects, equipment, time logs, appointments, pricebook items, and settings will be erased',
    'superAdmin.borrarEmpresaDetalle': 'All workspace data will be permanently destroyed.',
    'superAdmin.derechos': 'All Rights Reserved',
    'plantillas.vista.toast_usar': 'is added from the estimate builder’s Services tab — open or start an estimate to drop it in. On this page you can review its materials and manage your own line templates.',
    'plantillas.vista.titulo': 'Templates',
    'plantillas.vista.descripcion': 'Review the predetermined service templates and their materials, and create or edit your own line templates. These are the same templates the estimate builder’s Services tab uses.',
    'ajustesNeg.perfil.email': 'Email',
    'ajustesNeg.perfil.emailEjemplo': 'contact@business.com',
    'ajustesNeg.perfil.telefono': 'Phone',
    'ajustesNeg.perfil.sitioWeb': 'Website',
    'ajustesNeg.perfil.sitioEjemplo': 'www.yourbusiness.com',
    'ajustesNeg.temporada.semanasAnio.ayuda': 'Operational weeks (excluding winter).',
    'ajustesNeg.campo.salarioAnual': 'Annual Salary ($)',
    'ajustesNeg.campo.pagoPorHora': 'Wage ($/hr)',
    'ajustesNeg.empleados.burden.auto': '(computed by Burden Builder above)',
    'ajustesNeg.empleados.noCobrable': 'Cost rolls into overhead',
    'ajustesNeg.empleados.disponiblePct.ayuda': '(after PTO/weather)',
    'ajustesNeg.empleados.cobrablePct.ayuda': '(of available time)',
    'ajustesNeg.empleados.efectivo': 'Effective billable:',
    'ajustesNeg.empleados.horasTemporada': 'season hrs',
    'ajustesNeg.empleados.horasCobrables': 'billable hrs/yr',
    'ajustesNeg.empleados.vacio.texto': 'Add the people whose labor cost belongs in your shop rate — including yourself if you work jobs.',
    'ajustesNeg.stat.gastosAnuales': 'Annual Overhead',
    'ajustesNeg.stat.gastosAnuales.pista': 'Total fixed costs per year',
    'ajustesNeg.stat.gastoMensual': 'Monthly Burn',
    'ajustesNeg.stat.gastoMensual.pista': 'Avg. monthly cost',
    'ajustesNeg.gastos.nombre': 'Expense Name',
    'ajustesNeg.gastos.frecuencia': 'Frequency',
    'ajustesNeg.gastos.quitar': 'Remove expense',
    'ajustesNeg.gastos.vacio.titulo': 'No overhead expenses',
    'ajustesNeg.gastos.vacio.texto': 'Rent, insurance, software and vehicles belong here — they’re what your overhead recovery rate has to pay for.',
    'ajustesNeg.gastos.vacio.accion': 'Add first expense',
    'ajustesNeg.stat.costoMaquinaria': 'Annual Equipment Cost',
    'ajustesNeg.stat.costoMaquinaria.pista': 'Added to overhead recovery',
    'ajustesNeg.maquinaria.nombreEjemplo': 'Truck, Excavator, Mower...',
    'ajustesNeg.maquinaria.precioCompra': 'Purchase Price ($)',
    'ajustesNeg.maquinaria.vidaUtil': 'Useful Life (yrs)',
    'ajustesNeg.maquinaria.gasolina': 'Fuel ($/yr)',
    'ajustesNeg.maquinaria.mantenimiento': 'Maintenance ($/yr)',
    'ajustesNeg.maquinaria.quitar': 'Remove equipment',
    'ajustesNeg.maquinaria.desglose.gasolina': '/yr + fuel $',
    'ajustesNeg.maquinaria.desglose.manten': '+ maint $',
    'ajustesNeg.maquinaria.vacio.titulo': 'No equipment added',
    'ajustesNeg.maquinaria.vacio.texto': 'Skip this if you don’t own depreciable assets. Otherwise, trucks and machines belong here so their cost reaches your rate.',
    'ajustesNeg.maquinaria.vacio.accion': 'Add first equipment',
    'ajustesNeg.precios.titulo': 'Pricing & Margins',
    'ajustesNeg.precios.margenMeta.ayuda': 'Profit goal after costs',
    'ajustesNeg.precios.margenTope': 'Capped at 80% — beyond that the markup formula breaks.',
    'ajustesNeg.precios.markupMateriales': 'Default Material Markup (%)',
    'ajustesNeg.precios.markupMateriales.ayuda': 'Auto-applied to new materials.',
    'ajustesNeg.precios.impuestoMO': 'Tax Labor by Default?',
    'ajustesNeg.precios.impuestoMO.ayuda1': 'If enabled, sales tax will be calculated on the entire subtotal (Labor + Materials).',
    'ajustesNeg.precios.impuestoMO.ayuda2': 'If disabled, tax is calculated only on Materials/Equipment.',
    'ajustesNeg.reglas.titulo': 'Job Minimums & Rules',
    'ajustesNeg.reglas.minimoVisita': 'Minimum Service Call ($)',
    'ajustesNeg.reglas.cargoViaje': 'Trip Charge ($)',
    'ajustesNeg.reglas.horasMinimas': 'Min Labor Hours',
    'ajustesNeg.reglas.recargoEmergencia': 'Emergency Labor Markup (%)',
    'ajustesNeg.reglas.recargoEmergencia.ayuda': 'Multiplier applied only to Labor items.',
    'ajustesNeg.resumen.titulo': 'Financial Summary',
    'ajustesNeg.resumen.sub': 'Your calculated shop rates based on season, payroll, and overhead.',
    'ajustesNeg.resumen.tarifaBase': 'Base Labor Rate',
    'ajustesNeg.resumen.tarifaBase.pista': 'Avg. hourly wage',
    'ajustesNeg.resumen.cargaGastos': 'Overhead Load',
    'ajustesNeg.resumen.cargaGastos.pista': 'Per man-hour',
    'ajustesNeg.resumen.puntoEquilibrio': 'Break Even Rate',
    'ajustesNeg.resumen.puntoEquilibrio.pista': 'Cost to operate / hr',
    'ajustesNeg.resumen.tarifaMeta': 'Target Billable Rate',
    'ajustesNeg.resumen.tarifaMeta.pista1': 'Includes',
    'ajustesNeg.resumen.tarifaMeta.pista2': '% net profit',
    'ajustesNeg.rapido.puntoEquilibrio.pista': 'Cost to operate',
    'ajustesNeg.rapido.tarifaMeta.pista': '% target margin',
    'ajustesNeg.tab.rapido': 'Quick View',
    'ajustesNeg.tab.negocio': 'My Business',
    'ajustesNeg.tab.costos': 'Costs',
    'ajustesNeg.tab.precios': 'Pricing',
    'ajustesNeg.tab.diagnostico': 'Diagnostics',
    'contrato.editor.pdfNoExiste': 'This contract’s PDF no longer exists in storage. Delete this contract and upload it again.',
    'contrato.editor.errorAbrir': 'Failed to open PDF.',
    'contrato.editor.sinCampos': 'Saved with no fields. The client will have nothing to sign — drag a Signature field onto the page before sending this contract.',
    'contrato.editor.sinFirmaCliente': 'Heads up: no Client signature field on this contract.',
    'contrato.editor.errorGuardar': 'Could not save the fields. They are still here — try again.',
    'contrato.editor.sub': 'Place signature, initial, date, and text fields. The placement saves with the contract.',
    'contrato.editor.fechaauto.titulo': 'That date is a preview.',
    'contrato.editor.fechaauto.cuerpo': 'Nobody fills it in by hand. Every proposal carrying this contract goes out dated the day you send it, and once the client signs, dated their signature.',
    'contrato.editor.cancelar': 'Cancel',
    'contrato.editor.guardando': 'Saving…',
    'contrato.editor.guardar': 'Save fields',
    'contrato.editor.errorPdfTitulo': 'Couldn’t open this PDF',
    'contrato.editor.cargandoPdf': 'Loading PDF…',
    'ajustes.contratos.campos.errorguardar': 'Could not save the field placement. Please try again.',
    'ajustes.contratos.firma.exito': 'Signed as contractor.',
    'ajustes.contratos.firma.errorguardar': 'Signed, but saving failed. Press Save Changes to keep it.',
    'ajustes.contratos.subir.solopdf': 'Only PDF files are supported.',
    'ajustes.contratos.subir.muypesado': 'File is over 10 MB. Please compress and try again.',
    'ajustes.contratos.subir.exito': 'Contract uploaded and saved.',
    'ajustes.contratos.borrar.bloqueado.titulo': 'Cannot delete this contract yet',
    'ajustes.contratos.borrar.bloqueado.evidencia': 'Deleting it now would destroy that evidence.',
    'ajustes.contratos.borrar.exito': 'Contract removed. Save settings to confirm.',
    'ajustes.contratos.titulo': 'Contracts',
    'ajustes.contratos.descripcion': 'Upload the contract PDFs you’d like to attach to estimates. Selected contracts surface in the estimate builder under Analysis → Attached Contract.',
    'ajustes.contratos.subir.titulo': 'Upload a contract',
    'ajustes.contratos.subir.limite': 'PDF only · max 10 MB',
    'ajustes.contratos.subir.subiendo': 'Uploading…',
    'ajustes.contratos.subir.elegir': 'Choose PDF',
    'ajustes.contratos.vacio.titulo': 'No contracts yet',
    'ajustes.contratos.vacio.descripcion': 'Upload your first PDF so you can attach it to an estimate and collect signatures.',
    'ajustes.contratos.fila.sincampos': 'Nothing to sign yet',
    'ajustes.contratos.fila.firmadoporti': 'Signed by you',
    'ajustes.contratos.fila.faltatufirma': 'Awaiting your signature',
    'ajustes.contratos.fila.camposporfirmar': 'of your fields still unsigned',
    'ajustes.contratos.fila.completarfirma': 'Finish signing',
    'ajustes.contratos.fila.firmar.tooltip': 'Sign this contract as the contractor',
    'ajustes.contratos.fila.volverafirmar': 'Re-sign',
    'ajustes.contratos.fila.firmarcomocontratista': 'Sign as contractor',
    'ajustes.contratos.fila.configurar.tooltip': 'Place signature fields',
    'ajustes.contratos.fila.abrirpdf': 'Open PDF',
    'ajustes.contratos.fila.borrar': 'Delete contract',
    'ajustes.contratos.pregunta.despues': 'Decide later',
    'ajustes.contratos.pregunta.titulo': 'Is this contract already signed?',
    'ajustes.contratos.pregunta.cuerpo1': 'Did you already sign',
    'ajustes.contratos.limpio.titulo': 'Upload the blank contract too',
    'ajustes.contratos.limpio.porque': 'The one you uploaded has your signature inside it, so the printed copy would carry it. With a clean copy the client gets it blank and both sign by hand.',
    'ajustes.contratos.limpio.elegir': 'Choose the blank PDF',
    'ajustes.contratos.limpio.omitir': "Don’t have it",
    'ajustes.contratos.limpio.exito': 'Blank copy saved.',
    'ajustes.contratos.fila.limpio.falta': 'Blank missing',
    'ajustes.contratos.fila.limpio.falta.tooltip': 'Upload the unsigned contract: it is the one printed with the proposal for hand-signing.',
    'ajustes.contratos.fila.limpio.puesto': 'Blank',
    'ajustes.contratos.fila.limpio.reemplazar.tooltip': 'Replace the unsigned copy.',
    'ajustes.contratos.pregunta.yafirmado.titulo': 'It’s already signed',
    'ajustes.contratos.pregunta.yafirmado.ayuda': 'You’ll only place fields for your client to complete.',
    'ajustes.contratos.pregunta.firmarahora.titulo': 'No — I’ll sign it now',
    'ajustes.legal.terminos_descripcion': 'These terms are added to all new proposals by default. You can edit them later on a per-proposal basis.',
    'ajustes.legal.terminos_placeholder': 'Enter your standard agreement rules...',
    'ajustes.legal.terminos_markdown': 'Markdown formatting supported.',
    'ajustes.legal.garantias_descripcion': 'Create predefined warranties to select from while building specific proposals.',
    'ajustes.legal.garantia_agregar': 'Add Warranty',
    'ajustes.legal.garantia_quitar': 'Remove Warranty',
    'ajustes.legal.garantia_nombre_placeholder': 'e.g. Standard 1-Year Workmanship',
    'ajustes.legal.garantia_falta_nombre': 'Enter a warranty name first',
    'ajustes.legal.garantia_generar_tooltip': 'Generate warranty text with AI',
    'ajustes.legal.garantia_texto_placeholder': 'Warranty terms details...',
    'ajustes.legal.garantias_vacio_titulo': 'No custom warranties',
    'ajustes.legal.garantias_vacio_descripcion': 'Create predefined warranties so you can drop them into proposals instead of rewriting them.',
    'ajustes.legal.garantia_agregar_vacio': 'Add Warranty',

    // ── Estimates ▸ Proposal ▸ Payments ─────────────────────────
    'ajustes.pagos.encabezado': 'How you get paid',
    'ajustes.pagos.descripcion': 'Set this up once for the whole business. On each proposal you pick which of these the client sees, from its Share tab.',
    'ajustes.pagos.zelle.titulo': 'Zelle',
    'ajustes.pagos.zelle.descripcion': 'The fastest way to collect the deposit. The client sees these details when you turn Zelle on for a proposal.',
    'ajustes.pagos.zelle.mostrar': 'Accept Zelle payments',
    'ajustes.pagos.zelle.correo': 'Zelle email',
    'ajustes.pagos.zelle.correo_placeholder': 'payments@business.com',
    'ajustes.pagos.zelle.telefono': 'Zelle phone (optional)',
    'ajustes.pagos.zelle.falta': 'Add the email or the phone. Without one of them, Zelle can’t be turned on for a proposal.',
    'ajustes.pagos.banco.titulo': 'Bank transfer (ACH)',
    'ajustes.pagos.banco.descripcion': 'Let clients send the deposit straight to your account. Shown to the client only when you pick "Bank transfer" on a proposal.',
    'ajustes.pagos.banco.mostrar': 'Accept bank transfers',
    'ajustes.pagos.banco.titular': 'Account holder name',
    'ajustes.pagos.banco.titular_placeholder': 'Alex Landscaping LLC',
    'ajustes.pagos.banco.banco': 'Bank name',
    'ajustes.pagos.banco.banco_placeholder': 'Chase',
    'ajustes.pagos.banco.tipo': 'Account type',
    'ajustes.pagos.banco.tipo.corriente': 'Checking',
    'ajustes.pagos.banco.tipo.ahorro': 'Savings',
    'ajustes.pagos.banco.ruta': 'Routing number',
    'ajustes.pagos.banco.ruta_placeholder': '9 digits',
    'ajustes.pagos.banco.ruta_error': 'A routing number is 9 digits.',
    'ajustes.pagos.banco.cuenta': 'Account number',
    'ajustes.pagos.banco.cuenta_placeholder': 'Digits only',
    'ajustes.pagos.banco.cuenta_error': 'An account number is digits only (4 to 17).',
    'ajustes.pagos.banco.instrucciones': 'Payment instructions (optional)',
    'ajustes.pagos.banco.instrucciones_placeholder': 'e.g. Put the proposal number in the memo',
    'ajustes.pagos.banco.falta': 'Missing details. Without the holder, routing and account number, bank transfer can’t be turned on for a proposal.',
    'ajustes.pagos.banco.aviso': 'The account only travels on the proposals where you turn the transfer on — not on all of them.',
    'ajustes.marketing.quitar_imagen': 'Remove from library',
    'ajustes.marketing.quitar_nota': 'Removing only takes it out of this list. Proposals already using it keep showing it.',
    'ajustes.marketing.portadas_cupo': 'Using {usadas} of {max}. These show as choices on every proposal.',
    'ajustes.marketing.portadas_lleno': 'All {max} slots are full. Remove one to upload another.',
    'ajustes.marketing.sin_imagenes_titulo': 'No images yet',
    'ajustes.marketing.subir_imagen': 'Upload image',
    'ajustes.marketing.portadas_desc': 'Your fixed covers: they show as choices on every proposal, next to the three built-in ones. Anything you upload inside a proposal stays with that proposal only.',
    'ajustes.marketing.galeria_desc': 'Pre-load images here. They will be available as quick-add options in the proposal’s gallery section.',
    'ajustes.marketing.redes_desc': 'Add your social profiles. They can be shown on proposals when enabled.',
    'ajustes.marketing.resenas_titulo': 'Review Links & Featured Reviews',
    'ajustes.marketing.google_enlace': 'Google Reviews Link',
    'ajustes.marketing.google_destacada': 'Featured Google Review',
    'ajustes.marketing.google_autor_placeholder': 'Reviewer Name (e.g. John D.)',
    'ajustes.marketing.google_texto_placeholder': 'Paste your best Google review here...',
    'ajustes.marketing.yelp_enlace': 'Yelp Reviews Link',
    'ajustes.marketing.yelp_destacada': 'Featured Yelp Review',
    'ajustes.marketing.yelp_autor_placeholder': 'Reviewer Name (e.g. Sarah M.)',
    'ajustes.marketing.yelp_texto_placeholder': 'Paste your best Yelp review here...',
    'ajustes.cuenta.toast.fotook': 'Profile picture updated.',
    'ajustes.cuenta.toast.fotoerror': 'Failed to upload profile picture.',
    'ajustes.cuenta.toast.nombreerror': 'Failed to update name completely. Check console for details.',
    'ajustes.cuenta.titulo': 'My Account',
    'ajustes.cuenta.descripcion': 'Manage your personal profile and login details.',
    'ajustes.cuenta.foto.arialabel': 'Change profile picture',
    'ajustes.cuenta.foto.alt': '"Profile"',
    'ajustes.cuenta.foto.boton': 'Change Picture',
    'ajustes.cuenta.nombre.etiqueta': 'Display Name',
    'ajustes.cuenta.nombre.placeholder': 'Your Name',
    'ajustes.cuenta.nombre.guardar': '\n                Save\n',
    'ajustes.cuenta.nombre.ayuda': 'Updates across all time logs, projects, and team lists.',
    'ajustes.cuenta.correo.etiqueta': 'Email Address',
    'ajustes.cuenta.correo.ayuda': 'Email cannot be changed.',
    'ajustes.cuenta.rol.etiqueta': 'Role & Permissions',
    'ajustes.cuenta.rol.defecto': 'User',
    'ajustes.cuenta.grupo.perfil': 'Your profile',
    'ajustes.cuenta.grupo.perfilSub': 'Your photo and name, the way your crew sees you.',
    'ajustes.cuenta.grupo.acceso': 'How you sign in',
    'ajustes.cuenta.nombre.seGuardaSolo': 'Saves on its own when you leave the field.',
    'ajustes.cuenta.permisos.titulo': 'What you can do',
    'ajustes.cuenta.permisos.sub': 'Your role decides this. To change it, talk to whoever manages the account.',
    'ajustes.cuenta.permisos.dinero': 'See the money: pricing, profit and invoices',
    'ajustes.cuenta.permisos.gente': 'Invite people and change their roles',
    'ajustes.cuenta.permisos.clientes': 'Create and edit clients and leads',
    'ajustes.cuenta.permisos.agenda': 'Plan the schedule and routes',
    'ajustes.cuenta.permisos.proyectos': 'Configure projects and assign leads',
    'ajustes.cuenta.permisos.costos': 'Log costs and hours on a project',
    'ajustes.cuenta.permisos.ia': 'Use the AI assistant',
    'ajustes.cuenta.permisos.no': 'Not available with your role',
    'ajustes.cuenta.idioma.titulo': 'Language',
    'ajustes.cuenta.idioma.sub': 'Only changes how you see the app. Your crew and clients are unaffected.',
    'ajustes.equipo.quitar_titulo': 'Remove User',
    'ajustes.equipo.quitar_mensaje_1': 'Are you sure you want to remove ',
    'ajustes.equipo.quitar_mensaje_2': ' from the team? They will no longer be able to log in.',
    'ajustes.equipo.quitar_confirmar': 'Remove User',
    'ajustes.equipo.cancelar': 'Cancel',
    'ajustes.equipo.error_quitar': 'Error removing user: ',
    'ajustes.equipo.titulo': 'Team Management',
    'ajustes.equipo.descripcion': 'Control who accesses your data and tracks time.',
    'ajustes.equipo.agregar': 'Add User',
    'ajustes.equipo.acceso_total': 'Unrestricted Access',
    'ajustes.equipo.editar_permisos': 'Edit Permissions',
    'ajustes.equipo.quitar_tooltip': 'Remove User',
    'ajustes.equipo.vacio_titulo': 'No team members yet',
    'ajustes.equipo.vacio_descripcion': 'Invite the people who need to log time or access your data.',
    'ajustes.equipo.vacio_agregar': 'Add User',
    'contrato.campo.firma': 'Signature',
    'contrato.campo.iniciales': 'Initial',
    'contrato.campo.fecha': 'Date',
    'contrato.campo.texto': 'Text',
    'contrato.campo.cambiarRol': 'Toggle role',
    'contrato.campo.quitar': 'Remove field',
    'contrato.campo.fecha.auto': 'auto',
    'contrato.campo.fecha.auto.tooltip': 'The date shown is a sample. The real one is stamped when the proposal is sent.',
    'contratos.editor.campo.firma': 'Signature',
    'contratos.editor.campo.iniciales': 'Initial',
    'contratos.editor.campo.fecha': 'Date',
    'contratos.editor.campo.texto': 'Text',
    'contratos.editor.titulo.tipocampo': 'Field Type',
    'ajustes.contratos.campos.guardados': 'Saved:',
    'ajustes.contratos.campos.delcliente': 'for the client',
    'ajustes.contratos.campos.tuyos': 'yours',
    'contrato.editor.previa': 'Preview',
    'contrato.editor.previa.tooltip': 'See the contract the way your client will, with your signature already in place.',
    'contratos.previa.titulo': 'Client preview',
    'contratos.previa.sub': 'How the contract reaches your client: your signature stamped in, their boxes waiting.',
    'contratos.previa.cerrar': 'Back to editing',
    'contratos.previa.leyenda.tuyo': 'Your signature (already placed)',
    'contratos.previa.leyenda.cliente': 'Client fills this in',
    'contratos.previa.tuyosinfirmar': 'Unsigned',
    'contratos.previa.aviso.sinfirmar': 'of your fields will arrive blank. Save and sign them before sending.',
    'contratos.previa.fechaauto': 'Stamped with the date the proposal is sent.',
    'contratos.previa.cliente.firma': 'Client signature',
    'contratos.previa.cliente.iniciales': 'Initials',
    'contratos.previa.cliente.fecha': 'Date',
    'contratos.previa.cliente.texto': 'Text',
    'contratos.editor.ayuda.arrastra': 'Drag a field onto the spot in the document where it belongs.',
    'contratos.editor.ayuda.suelta': 'Release over the page to place it.',
    'contratos.editor.titulo.rol': 'Role for next field',
    'contratos.editor.rol.solocliente': 'Client only',
    'contratos.editor.ayuda.yafirmado': 'This contract is already signed by you, so only client fields are needed.',
    'contratos.editor.ayuda.cambiarrol': 'Toggle a placed field’s role by clicking its label pill.',
    'contratos.editor.boton.anterior': '← Prev',
    'contratos.editor.boton.siguiente': 'Next →',
    'contratos.editor.conteo.campo': 'field',
    'contratos.editor.conteo.campos': 'fields',
    'clienteDet.contacto.prefiere': 'Preferred: ',
    'clientes.modal.contacto_texto': 'Text',
    'clientes.modal.correo': 'Email',
    'clientes.modal.telefono': 'Phone',
    'estimados.crear.campo_email': 'Email',
    'estimados.crear.campo_telefono': 'Phone',
    'estimados.crear.campo_nombre': 'Name',
    'estimado.constructor.borrar_zona_check_partida': { uno: 'item', otros: 'items' },
    'estimado.constructor.barra_partidas': ' items',
    'estimado.constructor.precios_rapidos_titulo': 'Quick Pricing',
    'estimado.constructor.ganancia': 'Profit',
    'estimado.constructor.margen': 'Margin',
    'leads.modal.campo.direccionopcional': '(Optional)',
    'leads.modal.campo.telefono': 'Phone',
    'notificaciones.panel.titulo': 'Notifications',
    'pricebook.preview.por_unidad': 'per ',
    'wiz.gastos.anual': 'Annual',
    'wiz.gastos.rapido': 'Quick add',
    'wiz.empleados.quitarescritorio': 'Remove employee',
    'equipo.modal.campo.rol': 'Role',
    'equipo.modal.campo.nombre': 'Name',
    'admin.reportes.detalle': 'Detail',
    'admin.reportes.primera_vez': 'first ',
    'admin.reportes.kpi_avisos': 'Warnings',
    'admin.reportes.kpi_resueltos': 'Resolved',
    'admin.reportes.filtro_activos': 'Active',
    'admin.negocios.col_suscripcion': 'Subscription',
    'admin.negocios.col_creado': 'Created',
    'admin.negocios.col_usuarios': 'Users',
    'admin.negocios.col_estatus': 'Status',
    'admin.negocios.col_dueno': 'Owner',
    'admin.usuarios.sin_workspaces': 'None',
    'admin.usuarios.col_agregado': 'Added',
    'admin.usuarios.col_rol': 'Role',
    'admin.usuarios.col_correo': 'Email',
    'admin.usuarios.col_usuario': 'User',
    'materiales.toast.fallo': 'Failed: ',
    'estimado.partidas.conteo': { uno: 'item', otros: 'items' },
    'estimado.partidas.zonaVacia.texto': 'Add lines from the service library or a calculator.',
    'estimado.partidas.zonaVacia.titulo': 'No items in this zone',
    'estimado.partidas.quitar': 'Remove item',
    'estimado.partidas.sinZona.opcion': 'No Zone',
    'estimado.partidas.descripcion.placeholder': 'Item description…',
    'estimado.partidas.tipo.cargo': 'Fee',
    'estimado.partidas.tipo.maquinaria': 'Equipment',
    'estimado.partidas.tipo.manoDeObra': 'Labor',
    'estimado.partidas.col.margen': 'Margin',
    'estimado.partidas.campo.precio': 'Rate',
    'estimado.partidas.campo.costo': 'Cost',
    'estimado.partidas.campo.cantidad': 'Qty',
    'estimado.partidas.col.descripcion': 'Description',
    'estimado.partidas.col.tipo': 'Type',
    'estimado.partidas.borrarConfirm.titulo': 'Delete',
    'estimado.partidas.conteoZona': 'items',
    'estimado.partidas.seleccionadas': 'selected',
    'estimado.snapshot.margen': 'Margin',
    'estimado.plantillas.botonplantilla': 'template',
    'estimado.plantillas.botonguardar': 'Save',
    'estimado.plantillas.partidas': { uno: 'line item', otros: 'line items' },
    'estimado.plantillas.conteoPartidas': { uno: '{count} line', otros: '{count} lines' },
    'estimado.plantillas.horasHombre': { uno: '{count} man-hour', otros: '{count} man-hours' },
    'estimado.partida.horasViejas': 'This quantity is in client units (crew-day, visit), not man-hours, so the labor is being costed too low. The estimate stays as it was sent; to bill it by the hour, re-drop the template.',
    'estimado.partida.horasViejas.corto': 'client units',
    'estimado.concreto.campo_ancho_losa': 'Width',
    'estimados.calc.sod.desperdicio': '% waste = ',
    'estimado.plantillas.zona.prefijo': 'Zone: ',
    'estimado.plantillas.boton.agregar2': '\n                  Add\n',
    'finanzasNum.pl.bruta': 'Gross profit',
    'finanzasNum.pl.contrato': 'Contract value',
    'finanzasNum.reparto.deEso': 'of which',
    'seguimientos.detalle.cliente_estado': 'The client marked the proposal as {estado}.',
    'seguimientos.detalle.propuesta_estado': 'Proposal',
    'finanzasProy.gastos.recibo.alt': 'Receipt',
    'finanzasProy.facturas.venceEl': 'Due',
    'finanzasProy.facturas.terminos': 'Terms',
    'finanzasProy.pdf.col.monto': 'Amount',
    'finanzasProy.pdf.col.descripcion': 'Description',
    'finanzasProy.pdf.vence': 'Due:',
    'finanzasProy.pdf.trabajo': 'Project: ',
    'finanzasProy.pdf.encabezado': 'INVOICE',
    'finanzasProy.facturas.etiqueta': 'Invoice',
    'finanzasProy.gastos.errorSubirRecibo': 'Receipt upload failed: ',
    'proyecto.materiales.form.titulo': 'Add to shopping list',
    'proyecto.materiales.editar.guardar': 'Save',
    'proyecto.materiales.editar.cancelar': 'Cancel',
    'proyecto.actividad.fotoObra': 'Site photo',
    'proyecto.visitas.titulo': 'Scheduled Visits',
    'proyecto.fechas.titulo': 'Schedule',
    'proyecto.geocerca.activaCon': 'Active: ',
    'proyecto.geocerca.estado': 'Status',
    'proyecto.geocerca.titulo': 'Geofence Settings',
    'proyecto.geocerca.ayudaDireccion': 'If the client\'s address is different from the job site, change it here.',
    'proyecto.cuadrilla.titulo': 'Crew',
    'proyecto.bitacora.titulo': 'Site Journal',
    'proyectos.tareas.opcional': 'Optional',
    'proyectos.tareas.vence_etiqueta': 'Due',
    'proyectos.tareas.fase_etiqueta': 'Phase',
    'proyectos.tareas.asignar_etiqueta': 'Assign to',
    'proyectos.tareas.sin_asignar_opcion': 'Unassigned',
    'proyectos.tareas.borrar_confirmar': 'Delete',
    'propuesta.resenas.pieyelp': 'Yelp Review',
    'propuesta.resenas.piegoogle': 'Google Review',
    'propuesta.galeria.pista': 'Hover: before → after',
    'propuesta.marca.galeria.despues': 'After',
    'propuesta.marca.galeria.activa': 'Active Gallery',
    'propuesta.marca.galeria.titulo': 'Image Gallery',
    'propuesta.marca.encuadre.titulo': 'Framing',
    'propuesta.marca.portada.titulo': 'Cover Image',
    'propuesta.marca.colores.principal': 'Primary',
    'propuesta.compartir.bloqueada.pestana.cola': ' tab.',
    'propuesta.compartir.pago.tarjeta.etiqueta': 'Card Link',
    'propuesta.firma.de': 'of',
    'servicios.ruta.sin_gps_todas': 'No GPS',
    'servicios.ruta.paradas': 'Stops',
    'servicios.ruta.todos_dias': 'All days',
    'servicios.ruta.todas_cuadrillas': 'All crews',
    'servicios.analiticas.cliente_contratos': { uno: 'contract', otros: 'contracts' },
    'contratoServ.servicio': { uno: 'service', otros: 'services' },
    'contratoServ.porVisita': '/visit',
    'contratoServ.porMes': '/mo',
    'contratoServ.error.cliente': 'Choose the client for this contract.',
    'contratoServ.error.direccion': 'The service address is missing.',
    'contratoServ.error.tarifaVisita': 'Set the per-visit price. At zero, the crew works and nobody gets billed.',
    'contratoServ.error.tarifaMensual': 'Set the monthly rate. At zero, the crew works and nobody gets billed.',
    'contratoServ.error.servicios': 'Tick at least one service the crew will do on each visit.',
    'geocoder.error.rechazado': 'Google Maps refused to geocode. Check that the "Geocoding API" is enabled and that the API key allows this domain.',
    'geocoder.error.noEncontrado': 'No map location found for {cuantas} address(es). Check the spelling.',
    'crm.convertir.otroDispositivo': 'This lead is being converted on another device. Wait a few seconds and try again.',
    'servicios.visita.avance': 'Progress',
    'visitas.paginacion.de': 'of',
    'visitas.detalle.notas': 'Notes',
    'visitas.detalle.checklist': 'Checklist',
    'visitas.detalle.laHizo': 'Completed by',
    'visitas.detalle.contrato': 'Contract',
    'visitas.filtro.hecha': 'Completed',
    'visitas.detalle.empezo': 'Started',
    'visitas.col.cuadrilla': 'Crew',
    'visitas.col.direccion': 'Address',
    'visitas.filtro.enCamino': 'En Route',
    'visitas.filtro.enObra': 'On Site',
    'visitas.filtro.saltada': 'Skipped',
    'ajustes.burden.valor_default': 'default',
    'ajustes.burden.seguro_medico': 'Health insurance',
    'ajustes.gastos.ya_agregado_busqueda': 'Already added',
    'ajustes.gastos.titulo': 'Add Expense',
    'ajustes.metricas.sufijo.anual.gasto': '/yr',
    'ajustes.metricas.boton.comosecalculo': 'How this was calculated',
    'rastreo.banner.apagado': 'Tracking: OFF — You’re off the clock',
    'equipo.mapa.leyenda.salio': 'Clocked out',
    'equipo.mapa.leyenda.entro': 'Clocked in',
    'cuadrillas.tarjeta.sinTrabajadores': 'No workers',
    'cuadrillas.tarjeta.trabajadores': 'Workers',
    'cuadrillas.tarjeta.sinLider': 'None assigned',
    'cuadrillas.tarjeta.lider': 'Leader',
    'cuadrillas.vacio.boton': 'Create Crew',
    'comun.cancelar.boton': 'Cancel',
    'cuadrillas.lider': 'Crew leader',
    'cuadrillas.nombre': 'Crew name',
    'reloj.estado.taller': 'Shop / general',
    'reloj.salida.confirmar.si': 'Clock Out',
    'personal.horas.opcion_en_obra': 'On-site',
    'personal.horas.opcion_fuera_obra': 'Off-site',
    'personal.horas.opcion_auto_cerrado': 'Auto-closed',
    'personal.horas.estatus_activo': 'Active',
    'personal.horas.col_estatus': 'Status',
    'personal.horas.col_proyecto': 'Project',
    'personal.horas.tarjeta_horas': 'Hours',
    'personal.horas.col_lonche': 'Lunch',
    'personal.horas.col_salida': 'Out',
    'personal.horas.col_entrada': 'In',
    'personal.horas.col_fecha': 'Date',
    'personal.horas.col_trabajador': 'Worker',
    'personal.horas.tarjeta_overtime': 'Overtime',
    'personal.horas.tarjeta_auto': 'Auto',
    'dialogo.boton.cancelar': 'Cancel',
    'chat.mensajes.soloautor': 'You’re viewing a shared conversation by {activeConversation.user_name}. Only the author can add messages.',
    'chat.encabezado.privada': 'Private',
    'cliente.facturacion.tabla.pagada': 'Paid',
    'cliente.panel.avance': 'Completion',
    'clientes.tabla.ingresostotales': 'Lifetime revenue',
    'clientes.tabla.cliente': 'Client',
    'camara.foreman.elegirproyecto': 'Select project',
    'camara.foreman.titulo': 'Company Camera',
    'panel.tabla.avance': 'Progress',
    'panel.tabla.estado': 'Status',
    'panel.tabla.cliente': 'Client',
    'panel.tabla.proyecto': 'Project',
    'equipo.modal.mantenimiento.titulo': 'Log Maintenance',
    'equipo.estado.entrabajo': 'On Job',
    'equipo.estado.disponible': 'Available',
    'equipo.campo.estado': 'Status',
    'estimados.lista.col_actualizado': 'Updated',
    'estimados.lista.col_estatus': 'Status',
    'estimados.lista.col_margen': 'Margin',
    'estimados.lista.num_partidas': ' line items',
    'estimados.lista.col_proyecto': 'Project',
    'estimados.lista.col_cliente': 'Client',
    'estimados.lista.boton_seleccionar': 'Select',
    'estimados.lista.boton_cancelar': 'Cancel',
    'estimados.lista.boton_borrar': 'Delete ',
    'estimados.lista.borrar_titulo': 'Delete Estimate',
    'estimados.borrar.noEncontrado': 'That estimate is no longer in the list. Refresh and try again.',
    'estimados.borrar.bloqueado.ganado': 'The client already accepted this estimate, so it can’t be deleted. Archive it to get it off your list — the document stays.',
    'estimados.borrar.bloqueado.firmado': 'This estimate is signed. The signature and contract are the record of what was agreed, so it can’t be deleted. Archive it to get it off your list.',
    'estimados.borrar.bloqueado.convertido': 'This estimate produced a project that’s still open. Deleting it would leave that project with no record of what was agreed. Archive it to get it off your list.',
    'estimados.borrar.protegido.titulo': 'This estimate can’t be deleted',
    'estimados.borrar.archivar': 'Archive it',
    'estimados.borrar.entendido': 'Got it',
    'estimados.borrar.archivado': 'Archived. You’ll find it under the Done tab.',
    'estimados.borrar.saltados': { uno: 'Deleted what could be deleted. {saltados} was kept: it’s already accepted or signed.', otros: 'Deleted what could be deleted. {saltados} were kept: they’re already accepted or signed.' },
    'estimados.borrar.ningunoBorrable': { uno: 'Nothing deleted: that estimate is already accepted or signed.', otros: 'Nothing deleted: those estimates are already accepted or signed.' },
    'precios.preview.proveedor': 'Supplier:',
    'precios.tabla.actualizado': 'Updated:',
    'precios.mios.sub': 'Items you’ve added yourself that aren’t in the global materials catalog.',
    'propuesta.default.titulo_para': 'for',
    'propuesta.publica.copia.descargar': 'Download signed contract',
    'propuesta.publica.copia.abriendo': 'Opening…',
    'propuesta.publica.default.titulo': 'Proposal',
    'servicios.contratos.filtro_cancelados': 'Cancelled',
    'servicios.contratos.filtro_pausados': 'Paused',
    'servicios.contratos.filtro_activos': 'Active',
    'servicios.contratos.filtro_todos': 'All',
    'servicios.rutas.paradas': 'Stops',
    'ajustesNeg.maquinaria.sub': 'Trucks, trailers, mowers, excavators. Annual cost = (purchase price ÷ useful life) + fuel + maintenance + insurance. Rolls into overhead.',
    'ajustesNeg.empleados.sub': 'Add employees here to calculate your shop rate. For login access, use the “Team” tab.',
    'ajustes.contratos.biblioteca.titulo': 'Library',
    'ajustes.legal.garantia_texto': 'Warranty Text',
    'ajustes.legal.garantia_nombre': 'Warranty Name',
    'ajustes.legal.garantias_titulo': 'Warranties Library',
    'ajustes.legal.terminos_titulo': 'Standard Legal Terms',
    'ajustes.marketing.resenas_desc': 'Provide links to your review pages, and optionally add a pre-loaded "Best Review" to showcase on your proposals.',
    'ajustes.marketing.redes_titulo': 'Social Media Links',
    'ajustes.marketing.galeria_titulo': 'Gallery Images Library',
    'ajustes.marketing.portadas_titulo': 'Cover Images Library',
    'ajustes.equipo.mas_vistas': 'more',
    'ajustes.equipo.columna_permisos': 'Permissions',
    'ajustes.equipo.columna_rol': 'Role',
    'ajustes.equipo.columna_usuario': 'User',
    'contratos.editor.conteo.puestos': ' placed',
    'contratos.editor.titulo.pagina': 'Page',
    'estimado.plantillas.boton.agregar3': '\n                  Add\n',
    'tarifas.grupo.diseno': 'Design',
    'tarifas.grupo.arborista': 'Arborist',
    'tarifas.grupo.campo': 'Field',
    'tarifas.grupo.cuadrilla': 'Crew (per day)',
    'tarifas.puesto.senior-designer-la': 'Senior Designer / LA',
    'tarifas.puesto.designer': 'Designer',
    'tarifas.puesto.designer-horticulturist': 'Designer (Horticulturist)',
    'tarifas.puesto.archviz-specialist': '3D / ArchViz Specialist',
    'tarifas.puesto.project-administrator': 'Project Administrator',
    'tarifas.puesto.structural-engineer': 'Structural Engineer',
    'tarifas.puesto.climber-arborist': 'Climber / Certified Arborist',
    'tarifas.puesto.senior-climber-arborist': 'Senior Climber',
    'tarifas.puesto.isa-traq-arborist': 'ISA TRAQ Arborist',
    'tarifas.puesto.groundman': 'Groundman',
    'tarifas.puesto.equipment-operator': 'Equipment Operator',
    'tarifas.puesto.laborer': 'General Laborer',
    'tarifas.puesto.field-technician': 'Field Technician',
    'tarifas.puesto.survey-technician': 'Survey Technician',
    'tarifas.puesto.demolition-crew': 'Demolition Crew',
    'tarifas.puesto.grading-crew': 'Grading Crew',
    'tarifas.puesto.clearing-crew': 'Clearing Crew',
    'tarifas.puesto.planting-crew': 'Planting Crew',
    'tarifas.puesto.hedge-crew': 'Hedge Crew',
    'tarifas.puesto.sod-removal-crew': 'Sod Removal Crew',
    'tarifas.puesto.tree-handling-crew': 'Tree Handling Crew (specimen)',
    'finanzasNum.encabezado.cobrado': 'Collected — {rango}',
    'finanzasNum.pl.horasRegistradas': '{horas} h logged',
    'finanzasNum.pl.sinHoras': 'No hours logged yet',
    'finanzasNum.pl.overhead.pista': 'Rent, insurance, phone, trucks: spread across the hours clocked',
    'finanzasNum.pl.overhead.pistaMenosMant': 'Share of running the business, less budgeted maintenance',
    'finanzasNum.pl.reparaciones.pista': 'Logged in Inventory › Maintenance',
    'finanzasNum.pl.reparaciones.sinNada': 'No repairs logged yet',
    'finanzasNum.pl.margen': '{margen}% margin',
    'finanzasNum.reparto.nota': 'These two add up to the Collected figure above. An accepted proposal becomes a project, so proposal payments are project money — they are counted here, not as a separate source.',
    'finanzasNum.libro.soloProyectos': 'Projects only — service-route revenue is not included here.',
    'finanzasNum.pl.gananciaNeta': 'Net profit',
    'finanzasNum.libro.nota': 'Gross profit is contract value minus logged expenses. Labor cost is tracked separately in time logs and is not deducted here.',
    'finanzasNum.mant.real': 'Actual ({cuantas} logged)',
    'finanzasNum.mant.arriba': 'Over budget',
    'finanzasNum.mant.abajo': 'Under budget',
    'finanzasNum.mant.sinPresupuesto': 'No maintenance budget set on your equipment yet.',
    'finanzasNum.mant.noComparable': 'An annual budget can’t be compared to an all-time total — pick This Month or This Year.',
    'estimado.plantillas.etiqueta.permiso': '>Permit ',
    'estimado.plantillas.etiqueta.estado': '>State ',
    'estimado.plantillas.boton.agregar4': '\n                  Add\n',
    'frecuencia.weekly': 'Weekly',
    'frecuencia.weekly.desc': 'Every week',
    'frecuencia.biweekly': 'Bi-Weekly',
    'frecuencia.biweekly.desc': 'Every 2 weeks',
    'frecuencia.monthly': 'Monthly',
    'frecuencia.monthly.desc': 'Once a month',
    'dia.monday': 'Monday',
    'dia.tuesday': 'Tuesday',
    'dia.wednesday': 'Wednesday',
    'dia.thursday': 'Thursday',
    'dia.friday': 'Friday',
    'dia.saturday': 'Saturday',
    'dia.sunday': 'Sunday',
    'servicio.mowing': 'Mowing',
    'servicio.edging': 'Edging',
    'servicio.blowing': 'Blowing',
    'servicio.trimming': 'Trimming',
    'servicio.weeding': 'Weeding',
    'servicio.mulching': 'Mulching',
    'servicio.cleanup': 'Clean Up',
    'servicio.trashremoval': 'Trash Removal',
    'servicio.windowcleaning': 'Window Cleaning',
    'servicio.pressurewashing': 'Pressure Washing',
    'contratoServ.error.guardar': 'Failed to save contract: {motivo}',
    'contratoServ.errorDesconocido': 'Unknown error',
    'contratoServ.titulo.editar': 'Edit Contract',
    'contratoServ.titulo.nuevo': 'New Service Contract',
    'contratoServ.sinDireccion': 'No address on file',
    'contratoServ.cobro.mensual': 'Monthly Flat',
    'contratoServ.vencimiento.etiqueta': 'Payment due',
    'contratoServ.vencimiento.dia': 'day',
    'contratoServ.vencimiento.defecto': '10',
    'contratoServ.vencimiento.ayudaMensual': 'of each month (billed in advance)',
    'contratoServ.vencimiento.ayudaPorVisita': 'of the following month (billed at month close)',
    'contratoServ.cobro.porVisita': 'Per Visit',
    'contratoServ.tarifa.mensual': 'Monthly rate',
    'contratoServ.tarifa.porVisita': 'Per-visit rate',
    'contratoServ.resumen.losDias': 'on {dia}s',
    'contratoServ.boton.guardando': 'Saving…',
    'contratoServ.boton.actualizar': 'Update Contract',
    'contratoServ.boton.crear': 'Create Contract',
    'proyecto.fechas.faltan': 'Both dates are required.',
    'proyecto.fechas.ordenMal': 'End date must be on or after the start date.',
    'comun.errorDesconocido': 'unknown error',

    'error404.titulo': "That page doesn't exist",
    'error404.desc': "The link you opened doesn't lead anywhere. The address might be misspelled, or left over from an older version of the app.",
    'error404.direccion': 'Address you asked for',
    'error404.irAlPanel': 'Go to dashboard',
    'error404.volver': 'Back',

    'conexion.sinRed': "No connection — what you do now may be lost",
    'conexion.reintentar': 'Try again',

    'ajustes.negocio.titulo': 'Your business',
    'ajustes.negocio.ayuda': "This is the name your clients see on proposals, contracts and emails.",
    'ajustes.negocio.guardar': 'Save',
    'ajustes.negocio.guardado': 'Done — your business name has been changed.',
    'ajustes.negocio.error': "Couldn't save. Check your connection and try again.",
    'ajustes.negocio.soloAdmin': 'Only the owner or the office can change the business name.',
    'ajustes.nav.negocio': 'Business',
    'ajustes.negocio.dondeElResto': 'Wages, fixed costs, equipment and pricing live under Estimates → Overhead.',
    'ajustes.negocio.irAGastos': 'Go to Overhead',
    'calendario.modo.calendario': 'Calendar',
    'calendario.modo.agenda': 'Agenda',
    'comun.opcional': '(Optional)',
    'propuesta.publica.error.sinToken': 'This link no longer works. Ask your contractor to send you a fresh one.',
    'estimado.estado.revision': 'Review Needed',
    'estimado.estado.completado': 'Completed',
    'estimado.estado.pagado': 'Paid',
    'dato.proyecto.aceptado': 'Accepted',
    'dato.proyecto.agendado': 'Scheduled',
    'dato.proyecto.encurso': 'In Progress',
    'dato.proyecto.terminado': 'Completed',
    'dato.proyecto.cancelado': 'Cancelled',
    'dato.trabajo.activo': 'Active',
    'dato.trabajo.pausado': 'Paused',
    'dato.trabajo.encamino': 'En Route',
    'dato.trabajo.ensitio': 'On Site',
    'dato.trabajo.saltado': 'Skipped',
    'dato.rol.admin': 'Admin',
    'dato.rol.dueno': 'Owner',
    'dato.rol.oficina': 'Office',
    'dato.rol.capataz': 'Foreman',
    'dato.rol.trabajador': 'Laborer',
    'dato.rol.servicio': 'Service Tech',
    'dato.partida.material': 'Material',
    'dato.partida.mano': 'Labor',
    'dato.partida.equipo': 'Equipment',
    'dato.partida.equiporenta': 'Equipment (rented)',
    'dato.partida.equipopropio': 'Equipment (owned)',
    'dato.partida.otro': 'Other',
    'estimado.partidas.tipo.material': 'Material',
    'estimado.partidas.tipo.subcontratista': 'Sub',
    'contratos.visitas.hechas': {
        uno: '1 visit',
        otros: '{count} visits',
    },
    'calc.plantas.agregadas': {
        uno: 'Added 1 plant — set the per-plant cost in the estimate',
        otros: 'Added {count} plants — set the per-plant cost in the estimate',
    },
    'chat.adjunto.fallo': 'Upload failed: {archivo}',
    'chat.adjunto.omitidos': {
        uno: '1 attachment skipped (still uploading or failed).',
        otros: '{count} attachments skipped (still uploading or failed).',
    },

    'ajustesNeg.perfil.grupo.identidad': 'Identity',
    'ajustesNeg.perfil.grupo.identidadSub': 'Your logo and name, exactly as they sign off the work.',
    'ajustesNeg.perfil.grupo.contacto': 'Contact',
    'ajustesNeg.perfil.grupo.contactoSub': 'How a client reaches you once your proposal is in their hands.',
    'ajustesNeg.perfil.logoAyuda': 'PNG, JPG or SVG. Square and 512 px or larger stays sharp in print.',
    'ajustesNeg.perfil.cambiarLogo': 'Change logo',
    'ajustesNeg.perfil.previa.titulo': 'What your clients see',
    'ajustesNeg.perfil.previa.sub': 'The header on your proposals and contracts.',
    'ajustesNeg.perfil.previa.sinNombre': 'Your business',
    'ajustesNeg.perfil.previa.sinLogo': 'No logo',
    'ajustesNeg.perfil.previa.documento': 'Proposal',

    'proyLider.titulo': 'In charge today',
    'proyLider.asignarDia': 'Assign a day',
    'proyLider.cancelar': 'Cancel',
    'proyLider.quitarDia': 'Remove this day',
    'proyLider.quitarDiaAria': 'Remove {dia}',
    'proyLider.capataz': 'Foreman',
    'proyLider.nadie': 'Nobody on this job today',
    'proyLider.siguiente': 'Next up: {quien} on {dia}',
    'proyLider.puedesApuntar': 'Assign a day here, or drag the job onto a foreman in the Schedule.',
    'proyLider.sinDias': 'No days scheduled yet.',
    'proyLider.sinCapataces': "No foremen on the team yet. Set someone's role to Foreman in Team first.",
    'proyLider.dia': 'Day',
    'proyLider.elegir': 'Choose…',
    'proyLider.guardando': 'Saving…',
    'proyLider.ponerAlFrente': 'Put them in charge that day',
    'proyLider.proximos': 'Coming up',
    'proyLider.anteriores': 'Previously in charge',
    'proyLider.deLaAgenda': 'Read from the weekly Schedule — assigning here books the same day on the board.',

    'aria.abrirMenu': 'Open menu',
    'aria.cerrarMenu': 'Close menu',
    'aria.colapsarMenu': 'Collapse menu',
    'aria.expandirMenu': 'Expand menu',
    'aria.limpiarBusqueda': 'Clear search',
    'comun.buscarPlaceholder': 'Search…',
    'aria.cerrarAviso': 'Dismiss notice',
    'aria.seccionesProyecto': 'Project sections',
    'aria.faltaEnPaso': 'Something is missing in this step',
    'calc.medidasDelArriate': 'Bed dimensions',
    'calc.loQueNecesitas': "You'll need",
    'obra.dificultadAcceso': 'How hard it is to get on site',
    'clientes.error.cargar': "Couldn't load your clients",
    'clientes.error.reintenta': 'Check your connection and try again.',
    'prospectos.error.cargar': "Couldn't load your leads",
    'estimado.yaTieneProyecto': 'Opening the project this estimate already produced.',
    'conexion.yaSubio': 'Synced',
    'conexion.pendientes': { uno: '{count} punch to upload', otros: '{count} punches to upload' },
    'errores.guardarCambio': 'Could not save the change: {motivo}',
    'proyecto.geo.faltaLlave': 'Google Maps API Key is missing.',
    'proyecto.geo.sinCoordenadas': 'Could not find coordinates.',
    'proyecto.geo.error': 'Error fetching coordinates.',
    'proyecto.geocerca.radioPuesto': 'Geofence radius set to {radio} ft',
    'proyecto.actividad.movimientos': 'events',
    'proyecto.actividad.turno': 'Shift:',
    'proyecto.actividad.horas': 'hrs',
    'proyecto.nota.tipo.retraso': 'Delay',
    'proyecto.nota.tipo.avance': 'Milestone',
    'proyecto.nota.tipo.seguridad': 'Safety issue',
    'proyecto.nota.tipo.nota': 'Note',
    'propuesta.tema.modern': 'Modern',
    'propuesta.tema.natural': 'Natural',
    'propuesta.tema.elegant': 'Elegant',
    'propuesta.tema.custom': 'Custom URL',
    'propuesta.fuente.sans': 'Modern Sans',
    'propuesta.fuente.serif': 'Classic Serif',
    'propuesta.fuente.mono': 'Technical Mono',
    'propuesta.anim.none': 'None',
    'propuesta.anim.fade': 'Fade In',
    'propuesta.anim.slide': 'Slide Up',
    'propuesta.anim.zoom': 'Zoom',
    'propuesta.marca.usarPortada': 'Use saved cover image {n}',
    'propuesta.marca.agregarFoto': 'Add library image {n}',
    'propuesta.marca.subiendo': 'Uploading…',
    'finanzasProy.facturas.estatusAria': 'Status for invoice {id}',
    'finanzasProy.facturas.pagadaElDia': 'Marked paid {fecha}',
    'finanzasProy.facturas.cambiarEstatus2': 'Change invoice status',
    'finanzasProy.gastos.borrarAria': 'Delete expense {que}',
    'finanzasProy.factura.conceptoDesc': 'Line item {n} description',
    'finanzasProy.factura.conceptoMonto': 'Line item {n} amount',
    'finanzasProy.factura.quitarConcepto': 'Remove line item {n}',
    'finanzasProy.facturas.pdfAria': 'Download invoice {id} as PDF',
    'finanzasProy.facturas.borrarAria': 'Delete invoice {id}',
    'estimado.partidas.manoObraBloqueada2': 'Labor rate is set by your overhead calculator in Settings.',
    'estimado.partidas.sinCosto': 'Tap to add a cost — it saves for your next job.',
    'estimado.partidas.seleccionar': 'Select item',
    'estimado.partidas.sobreCosto': 'on the cost',
    'estimado.partidas.sobrePrecio': 'on the price',
    'estimado.partidas.teLlevas': 'you keep',
    'estimado.partidas.deMargen': 'margin',
    'estimado.partidas.deMarkup': 'markup',
    'estimado.partidas.fijadoAMano': 'Set by hand',
    'estimado.partidas.volverAlTaller': 'Back to the workshop rate',
    'estimado.partidas.factorNeto': "The estimate’s net-margin factor",
    'estimado.partidas.markup': 'Markup %',
    'estimado.partidas.margen': 'Margin %',
    'estimado.partidas.campo.unidad': 'Unit',
    'estimado.partidas.campo.zona': 'Zone',
    'estimado.partidas.unidadBloqueada': 'Labor is quoted in man-hours; its unit is fixed.',
    'propuesta.seccion.scope': 'Executive Summary',
    'propuesta.seccion.companyProfile': 'Company Profile',
    'propuesta.seccion.gallery': 'Image Gallery',
    'propuesta.seccion.pricing': 'Pricing & Options',
    'propuesta.seccion.lineItemPricing': 'Line Item Details',
    'propuesta.seccion.totals': 'Total Summary',
    'propuesta.seccion.terms': 'Terms & Signature',
    'propuesta.seccion.reviews': 'Reviews & Social',
    'propuesta.seccion.movida': '{seccion}, position {pos} of {total}',
    'propuesta.seccion.subir': 'Move {seccion} up',
    'propuesta.seccion.bajar': 'Move {seccion} down',
    'propuesta.seccion.mostrar': 'Show {seccion}',
    'reloj.foto.quitar': 'Remove photo',
    'reloj.foto.errorEntrada': 'Photo upload failed — clocking in without it.',
    'reloj.foto.errorSalida': 'Photo upload failed — clocking out without it.',
    'reloj.entrada.sinGPS': 'Clocked in without location — GPS unavailable here.',
    'reloj.salida.confirmar.mensaje': 'You’ve worked {tiempo} this shift. Clock out now?',
    'reloj.salida.fueraDeObra': 'Clocked out — {pies} ft off-site.',
    'reloj.lunch.errorEmpezar': 'Couldn’t start lunch: {motivo}',
    'reloj.lunch.errorTerminar': 'Couldn’t end lunch: {motivo}',
    'reloj.aviso.masDe10h': 'You’ve been clocked in over 10 hours — don’t forget to clock out.',
    'reloj.boton.ubicando': 'Getting location…',
    'reloj.boton.salida': 'Clock Out',
    'plantillaPriv.yaEstan': 'All materials from that category are already in this template.',
    'plantillaPriv.agregados': { uno: 'Added {cuantos} {categoria} material at $0 — set your prices.', otros: 'Added {cuantos} {categoria} materials at $0 — set your prices.' },
    'plantillaPriv.guardada': { uno: '{verbo} "{nombre}" — {cuantas} line', otros: '{verbo} "{nombre}" — {cuantas} lines' },
    'plantillaPriv.verbo.creada': 'Created',
    'plantillaPriv.verbo.guardada': 'Saved',
    'plantillaPriv.errorGuardar': 'Save failed: {motivo}',
    'plantillaPriv.modo.crear': 'Create line template',
    'plantillaPriv.modo.editar': 'Edit private template',
    'plantillaPriv.nombreVacio': 'New line template',
    'plantillaPriv.ponPrecio': 'Set a price so this material persists to your pricebook.',
    'plantillaPriv.boton.creando': 'Creating…',
    'plantillaPriv.boton.guardando': 'Saving…',
    'plantillaPriv.boton.crear': 'Create template',
    'plantillaPriv.boton.guardar': 'Save changes',
    'chat.hace.ahorita': 'just now',
    'chat.hace.minutos': '{n}m ago',
    'chat.hace.horas': '{n}h ago',
    'chat.hace.dias': '{n}d ago',
    'chat.historial.de': 'by {quien}',
    'chat.historial.borrarAria': 'Delete conversation {titulo}',
    'chat.encabezado.de': '· by {quien}',
    'chat.visibilidad.privada': 'Private',
    'chat.visibilidad.compartida': 'Shared',
    'rutas.sinAsignar': 'Unassigned',
    'horas.resumen.total': 'total',
    'horas.resumen.fueraDeObra': 'off-site',
    'horas.resumen.autoCerrados': 'auto-closed',
    'horas.resumen.overtime': 'overtime',
    'horas.tallerGeneral': 'Shop / general',
    'horas.sinNombre': 'Unknown',
    'horas.lonche': 'Lunch:',
    'horas.totalTurnos': { uno: 'Total — {cuantos} shift', otros: 'Total — {cuantos} shifts' },
    'horas.destino.todos': 'All targets',
    'horas.destino.aria': 'Filter by target',
    'horas.destino.enObra': 'On jobs',
    'horas.destino.taller': 'Shop',
    'horas.destino.sinClasificar': 'Unclassified',
    'horas.destino.eliminada': 'Deleted job',
    'horas.resumen.turnos': { uno: 'shift', otros: 'shifts' },
    'horas.resumen.enObra': 'on jobs',
    'horas.resumen.taller': 'at the shop',
    'horas.resumen.sinClasificar': 'unclassified',
    'horas.resumen.eliminadas': 'on deleted jobs',
    'horas.turno.tramos': { uno: '{cuantos} leg', otros: '{cuantos} legs' },
    'horas.turno.verTramosAria': 'Show this shift’s legs',
    'horas.revisarTooltip': 'The auto-close didn’t know when this shift ended: these hours are incomplete.',
    'rutas.asignada': 'Assigned to {cuadrilla}',
    'rutas.cuadrillaGenerica': 'crew',
    'rutas.errorAsignar': 'Couldn’t assign crew: {motivo}',
    'frecuencia.otros': 'Other',
    'rutas.boton.reanudar': 'Resume',
    'rutas.boton.reactivar': 'Reactivate',
    'rutas.miembroDesconocido': 'Unknown',
    'calc.mulch.nombre': 'Mulch / Soil / Aggregate Volume',
    'calc.mulch.desc': 'Cubic yards from L × W × depth. Mulch, topsoil, gravel, sand, compost.',
    'calc.sod.nombre': 'Sod / Lawn Area',
    'calc.sod.desc': 'Square feet → slabs and pallets, with shape-based waste presets.',
    'calc.concreto.nombre': 'Concrete',
    'calc.concreto.desc': 'Cubic yards for slabs, footings, sonotubes, steps. Auto 10% waste.',
    'calc.plantas.nombre': 'Plant Spacing',
    'calc.plantas.desc': 'Plant count from area + on-center spacing. Triangular default.',
    'calc.badge.diario': 'Daily',
    'calc.badge.porTrabajo': 'Per-job',
    'analitica.sinAsignar': 'Unassigned',
    'analitica.enPausa': '{n} paused',
    'analitica.canceladas': '{n} cancelled',
    'analitica.porMes': '/mo',
    'admin.severidad.error': 'Error',
    'admin.severidad.aviso': 'Warning',
    'admin.severidad.info': 'Info',
    'admin.hace.ahorita': 'just now',
    'admin.hace.minutos': '{n}m ago',
    'admin.hace.horas': '{n}h ago',
    'admin.hace.dias': '{n}d ago',
    'admin.boton.reabrir': 'Reopen',
    'admin.boton.resolver': 'Resolve',
    'gente.rol.desc.admin': 'Full access to all tabs, settings, and user management.',
    'gente.rol.desc.office': 'All views enabled by default. Customizable tab access.',
    'gente.rol.desc.foreman': 'Projects + Time Clock only. Cannot modify settings.',
    'gente.rol.desc.laborer': 'Time Clock only. Minimal access for field workers.',
    'gente.rol.desc.maintenance': 'Time Clock + Services. For dedicated maintenance crews.',
    'gente.errorGuardar': 'Error saving user: {motivo}. You might not have permission to perform this action.',
    'gente.modal.editar': 'Edit User Permissions',
    'gente.modal.invitar': 'Invite New User',
    'comun.sinNombre': 'Unknown',
    'tareas.yaEtiquetadas': 'No tasks needed re-tagging.',
    'tareas.fase1': 'Phase 1: Mobilization & Prep',
    'tareas.fase2': 'Phase 2: Execution',
    'tareas.fase3': 'Phase 3: Finish & Closeout',
    'propFirmadas.errorAbrir': 'Could not open the signed contract.',
    'propFirmadas.errorGuardarCopia': 'Could not archive this contract.',
    'propFirmadas.vacio.busqueda': 'No signed proposals match your search.',
    'propFirmadas.vacio.aun': 'When a client signs and accepts a proposal, it will appear here.',
    'propFirmadas.copiaGuardada': 'Executed copy stored {fecha} — both signatures plus the audit certificate',
    'propFirmadas.abrirGuardado': 'Open the stored contract of record — both signatures plus the audit certificate',
    'propFirmadas.rearmar': 'Rebuild the signed contract from the template (no permanent copy stored yet)',
    'tareas.materialesLigados': { uno: 'This task is linked to {n} material in Procurement. Mark it as Installed too?', otros: 'This task is linked to {n} materials in Procurement. Mark them as Installed too?' },
    'estimado.confianza.sourced': 'Sourced',
    'estimado.confianza.triangulated': 'Triangulated',
    'estimado.confianza.estimated': 'Estimated',
    'estimado.plantillas.sinCatalogo': '⚠ catalog hint not found:',
    'estimado.plantillas.sinPlantillas': 'This category has no service templates loaded yet.',
    'estimado.plantillas.zonaGenerica': 'zone',
    'estimado.plantillas.avisoMediana': 'Heads up — line items sum (',
    'estimado.plantillas.avisoMediana.cierre': '). Drop will use the line items; edit the rows after if you want to match the median.',
    'seguimientos.sistema': 'System',
    'equipo.mant.enviadoAReparacion': '{nombre} sent to In Repair.',
    'equipo.mant.registrado': 'Maintenance logged for {nombre}.',
    'equipo.mant.reparado': '{nombre} is repaired and back in the shop.',
    'comun.sinResultados': 'No matches',
    'materiales.estado.porComprar': 'Shopping List',
    'materiales.estado.pedido': 'Ordered',
    'materiales.estado.listo': 'Ready / Staged',
    'materiales.estado.instalado': 'Installed',
    'materiales.editarAria': 'Edit {que}',
    'materiales.quitarAria': 'Remove {que}',
    'gente.quitar.mensaje': '{nombre} will lose access to the app. Their logged hours and history stay.',
    'gente.quitado': '{nombre} removed.',
    'gente.errorQuitar': 'Failed to remove member: {motivo}',
    'gente.vacio.invita': 'Invite your first team member to start.',
    'gente.vacio.busqueda': 'No one matches this search — try a different name.',
    'gente.estaPersona': 'This member',
    'gente.guard.dueno': 'The business owner cannot be removed or have their role changed. To hand the business to someone else, contact support.',
    'gente.guard.otroAdmin': 'An Admin cannot remove or change the role of another Admin. Only the business owner can do that.',
    'gente.guard.ultimoAdmin': 'This is the only Admin left. Make someone else an Admin before removing this person.',
    'gente.dueno.tooltip': 'Business owner — cannot be removed or have their role changed.',
    'ajustes.terminosPorDefecto': '1. Payment Terms: A 50% deposit is required to schedule the project. The remaining balance is due upon completion.\n\n2. Scheduling: Dates provided are estimates and subject to change.',
    'ajustes.correo.asunto': 'Proposal: {{proposalTitle}}',
    'ajustes.correo.cuerpo': 'Hi,\n\nPlease review your proposal here:\n{{publicLink}}\n\nThanks,\n{{businessName}}',
    'ajustes.error.noCargan': 'We could not load your settings. Check your connection and try again.',
    'ajustes.error.recargados': 'Loaded your latest settings — please review and save again.',
    'avisos.hace.ahorita': 'just now',
    'avisos.hace.minutos': '{n}m ago',
    'avisos.hace.horas': '{n}h ago',
    'avisos.hace.dias': '{n}d ago',
    'avisos.sinLeer': { uno: '{n} unread notification', otros: '{n} unread notifications' },
    'avisos.titulo': 'Notifications',
    'contratos.campo.firma': 'Signature',
    'contratos.campo.iniciales': 'Initial',
    'contratos.campo.fecha': 'Date',
    'contratos.campo.texto': 'Text',
    'contratos.editor.arrastraArmado': 'Drag it onto the page, or just click where you want the {tipo} field.',
    'contratos.editor.arrastra': 'Drag a field onto the page, or click one here and then click the page.',
    'finanzas.trabajos.vacio.aun': 'Completed projects and service visits build up here over time.',
    'finanzas.trabajos.vacio.busqueda': 'Try a different search or filter.',
    'ajustes.contratos.sinCamposContratista': 'No contractor fields were placed, so there is nothing for you to sign. Use "Sign as contractor" later if you add some.',
    'ajustes.contratos.errorSubir': 'Upload failed: {motivo}',
    'ajustes.contratos.dependen': { uno: '{n} signed proposal still depends on this template to rebuild its signed copy. ', otros: '{n} signed proposals still depend on this template to rebuild their signed copies. ' },
    'ajustes.contratos.archivaPrimero': { uno: 'Open Estimates → Signed and press Archive on that proposal first — once archived it no longer needs this file.', otros: 'Open Estimates → Signed and press Archive on those proposals first — once archived they no longer need this file.' },
    'estimado.concreto.forma.slab': 'Slab (patio / pad / driveway)',
    'estimado.concreto.forma.footing': 'Footing (continuous)',
    'estimado.concreto.forma.sonotube': 'Sonotube / round pier',
    'estimado.concreto.forma.steps': 'Steps',
    'estimado.concreto.pedirReadyMix': 'Order (ready-mix)',
    'estimado.concreto.mezclarEnObra': 'Mix on-site',
    'direccion.placeholderBuscar': 'Start typing to search address...',
    'propuesta.default.intro': 'Thank you for the opportunity to work on your property. We have prepared this proposal with care, focusing on quality materials and expert craftsmanship.',
    'estimado.partidas.totalZona': '{zona} total',
    'estimado.partidas.borrarSeleccion': { uno: 'The selected line item will be removed from this estimate. This can’t be undone.', otros: 'The selected line items will be removed from this estimate. This can’t be undone.' },
    'estimado.sinTitulo': 'Untitled Estimate',
    'estimado.margen.debajo': 'Below the {objetivo}% target',
    'estimado.margen.objetivo': 'Target {objetivo}%',
    'estimado.margen.avisoDebajo': 'Margin is under the {objetivo}% target.',
    'estimado.margen.propio': '{valor} on your work',
    'estimado.margen.facturado': '{valor} billed',
    'estimado.margen.paseDirecto': 'Includes {monto} of permits and fees billed at cost, which make no profit.',
    'superadmin.sincronizado': 'Synced',
    'superadmin.hace.ahorita': 'just now',
    'superadmin.hace.minutos': '{n}m ago',
    'superadmin.errorDueno': 'Error auto-provisioning owner',
    'marca.errorSubir': 'Failed to upload image.',
    'marca.subiendo': 'Uploading…',
    'marca.subirPortada': 'Upload Cover image',
    'marca.subirLogo': 'Upload Logo image',
    'marca.sinImagen': 'Upload an image to make it selectable here.',
    'legal.generarIA': 'AI Generate',
    'legal.escribiendo': 'Writing…',
    'marca.subirPortada2': 'Upload Cover Image',
    'marca.subirGaleria': 'Upload Gallery Image',
    'marca.sinImagenPortada': 'Upload a cover image to make it selectable in the Proposal Editor.',
    'marca.sinImagenGaleria': 'Upload a gallery image to make it selectable in the Proposal Editor.',
    'proyecto.nombrePorDefecto': 'Project for {cliente}',
    'proyecto.boton.creando': 'Creating…',
    'proyecto.boton.crear': 'Create Project',
    'mapa.enLonche': 'On lunch',
    'mapa.trabajando': 'Working',
    'mapa.sinUbicacionSalida': 'No clock-out location recorded',
    'mapa.salioFuera': 'Clocked out off-site',
    'estimado.sod.forma.rectangular': 'Rectangle (5%)',
    'estimado.sod.forma.curveada': 'Curved edges (10%)',
    'estimado.sod.forma.complicada': 'Complex / many cutouts (15%)',
    'direccion.sinResultados': 'No matches — try a more complete address.',
    'video.pausa': 'Pause',
    'video.reproducir': 'Play',
    'video.ponerSonido': 'Unmute',
    'video.quitarSonido': 'Mute',
    'wiz.gastos.porAno': '/yr',
    'finanzasNum.pl.ponTarifas': 'Set your labor and overhead rates in Settings to see this',
    'finanzasNum.mant.nota.arriba': 'Repairs are running above the budget in your overhead rate — every estimate you send is under-recovering equipment cost. Raise maintenance in Settings › Equipment.',
    'finanzasNum.mant.nota.normal': 'Actual repairs are deducted in the P&L above, and the budgeted slice is removed from overhead first so the same repair is never charged twice.',
    'precios.catalogo.de': 'of',
    'precios.catalogo.conPrecio': 'priced',
    'proyecto.encabezado.avance': 'Schedule',
    'proyecto.encabezado.horasPresupuestadas': 'Labor Budget',
    'proyecto.encabezado.valorContrato': 'Contract Value',
    'proyecto.horas.csv_entrada': 'Clock In',
    'proyecto.horas.csv_salida': 'Clock Out',
    'proyecto.horas.csv_duracion': 'Duration (hrs)',
    'finanzas.proyectos.sinFechaFin': 'No completion timestamp recorded — showing the planned end date',
    'finanzas.proyectos.vacio.aun': 'Projects appear here as soon as the first job is created.',
    'finanzas.proyectos.vacio.busqueda': 'Try a different search or status filter.',
    'estimado.rapido.entregaBoton': 'Delivery',
    'estimado.rapido.rentaBoton': 'Rental',
    'estimado.anticipo': 'Deposit',
    'metricas.puntoEquilibrio': 'Break-even = ',
    'metricas.gastosPorHora': 'Overhead share = ',
    'metricas.loQueDebesCobrar': 'Target = ',
    'volver.alCliente': 'Back to client',
    'volver.alProspecto': 'Back to leads',
    'volver.alPanel': 'Back to home',
    'volver.aEstimados': 'Back to estimates',
    'estimado.snapshot.tarifas': 'Rates:',
    'estimado.snapshot.actualizar': ' Refresh to re-price at current rates.',
    'estimado.snapshot.bloqueado': ' Locked — estimate already sent.',
    'comun.total': 'Total',
    'comun.borrar': 'Delete',
    'comun.nombre': 'Name',
    'comun.costo': 'Cost',
    'comun.atras': 'Back',
    'comun.vence': 'Due',
    'comun.cliente': 'Client',
    'comun.prospecto': 'Lead',
    'comun.aviso': 'Warning',
    'comun.quitar': 'Remove',
    'comun.monto': 'Amount',
    'comun.seleccionar': 'Select',
    'comun.quitarSeleccion': 'Deselect',
    'comun.cerrarMenu': 'Close menu',
    'comun.sinConexionSeguro': 'Check your connection and try again. Your data is safe.',
    'ajustes.gastos.porHoraHombre': 'Added to every man-hour',
    'plantillas.borrar.pregunta': 'Delete this template?',
    'plantillas.borrar.si': 'Delete template',
    'ajustes.contratos.borrar.pregunta': 'Delete this contract?',
    'ajustes.contratos.borrar.si': 'Delete contract',
    'analitica.sinContratos': 'No active contracts',
    'chat.sinVoz': 'No speech detected',
    'propuesta.noSePudoRechazar': 'Could Not Decline',
    'propuesta.noSePudoAceptar': 'Could Not Accept',
    'comun.proyectosActivos': 'Active Projects',
    'analitica.ingresosPorCuadrilla': 'Revenue by Crew',
    'precios.misMateriales': 'My Custom Items',
    'propuesta.errorDescarga': 'Download Error',
    'rutas.totalClientes': 'Total clients',
    'empresa.nombre': 'Company Name',
    'rutas.contratoNuevo': 'New Contract',
    'ajustes.empleados.agregar': 'Add Employee',
    'ajustes.gastos.nombre': 'Expense Name',
    'estimado.zona.borrar': 'Delete zone',
    'metricas.disponiblePct': 'Available %',
    'panel.empezarTrabajo2': 'Start a Job',
    'precios.importarArchivo': 'Import File',
    'clientes.nuevo': 'New Client',
    'ajustes.contratos.escogerPDF': 'Choose PDF',
    'chat.nuevo': 'New Chat',
    'comun.verTodos': 'View all',
    'finanzasProy.factura': 'Invoice',
    'propuesta.clienteDesconocido': 'Unknown Client',
    'superadmin.estaAccionEs': 'This action is',
    // -- Landing (public page) ------------------------------------
    'lp.nav.laApp': 'The app',
    'lp.nav.comoEmpieza': 'How it works',
    'lp.nav.academy': 'Academy',
    'lp.nav.tienda': 'Store',
    'lp.nav.preguntas': 'FAQ',
    'lp.nav.principal': 'Main',
    'lp.nav.marca': 'Raza Pro, home',
    'lp.nav.abrirMenu': 'Open menu',
    'lp.nav.menuPrincipal': 'Main menu',
    'lp.nav.cerrarMenu': 'Close menu',
    'lp.nav.saltar': 'Skip to content',
    'lp.nav.deAcademy': 'From Raza Perrona Academy',
    'lp.cta.gratis': 'Start free',
    'lp.cta.dashboard': 'Go to dashboard',
    'lp.cta.crearCuenta': 'Create my free account',

    'lp.hero.titular1': 'Your business.',
    'lp.hero.titular2': 'One app.',
    'lp.hero.sub': 'Estimates, crews, routes and getting paid. All of it in one place.',

    'lp.reemplaza.titular': 'Right now your business lives in ten different places.',
    'lp.reemplaza.remate': 'Raza Pro replaces every one of them.',
    'lp.reemplaza.enlace': 'See inside the app',
    'lp.reemplaza.h.excel': 'Excel',
    'lp.reemplaza.h.whatsapp': 'WhatsApp',
    'lp.reemplaza.h.cuaderno': 'The notebook',
    'lp.reemplaza.h.word': 'Word',
    'lp.reemplaza.h.recibos': 'Receipts in the truck',
    'lp.reemplaza.h.fotos': 'Photos on your phone',
    'lp.reemplaza.h.calendario': 'The wall calendar',
    'lp.reemplaza.h.llamadas': 'Missed calls',
    'lp.reemplaza.h.notas': 'Notes on your phone',
    'lp.reemplaza.h.servilletas': 'Napkins',
    'lp.muro.lectura': 'Right now your business lives in {lista}. {remate} replaces every one.',

    'lp.producto.titular': 'This is how it looks inside.',
    'lp.producto.lead': 'Four screens that replace ten tools. Keep scrolling and they change on their own.',
    'lp.producto.1.titulo': 'Estimates that actually close',
    'lp.producto.1.texto': 'Describe the job in your own words and Raza AI writes the full scope. Out comes a PDF with your logo, and the client signs it from their phone. Nothing to print.',
    'lp.producto.2.titulo': 'Your crew, in plain sight',
    'lp.producto.2.texto': 'Time clock with a geofence: you know who clocked in, when, and on which job. Hours flow straight into payroll, with nobody writing anything in a notebook.',
    'lp.producto.3.titulo': 'The day, already sorted',
    'lp.producto.3.texto': 'Every stop of the day in order, with address, job and status. Your crew knows where to go without calling you, and you see the progress from wherever you are.',
    'lp.producto.4.titulo': 'An AI that knows',
    'lp.producto.4.texto': 'Not a generic bot: it knows the materials, the timelines and the traps of your trade. It writes your scopes, your follow-up emails and your replies to clients.',
    'lp.producto.oficio.techos': 'roofing',
    'lp.producto.oficio.pintura': 'painting',
    'lp.producto.oficio.concreto': 'concrete',
    'lp.producto.oficio.jardineria': 'landscaping',
    'lp.producto.oficio.remodelacion': 'remodeling',

    'lp.pasos.titular': 'From zero to running, today.',
    'lp.pasos.lead': 'Nothing to install, no contract, and nobody has to train you.',
    'lp.pasos.1.titulo': 'Create your account',
    'lp.pasos.1.texto': 'With your Google email. Thirty seconds and you are in. We do not ask for a card.',
    'lp.pasos.2.titulo': 'Build your first estimate',
    'lp.pasos.2.texto': 'Tell Raza AI what the job is. It writes the scope; you set the price.',
    'lp.pasos.3.titulo': 'Add your people',
    'lp.pasos.3.texto': 'Each one with their time clock on their phone. You see the hours as they happen.',
    'lp.pasos.4.titulo': 'Get paid without chasing',
    'lp.pasos.4.texto': 'Invoices, reminders and payments go out on their own. You go sell the next job.',
    'lp.pasos.oficios': 'Built for roofing, painting, concrete, landscaping, remodeling, drywall, flooring, plumbing, electrical and fencing.',

    'lp.banda.oficio': 'You already know the work. We bring the system.',
    'lp.banda.cita': '“I want to, I can, and I am going to make it.”',
    'lp.banda.autorRol': ', founder of Raza Perrona Academy',
    'lp.banda.historia': 'Read his story',
    'superadmin.esIrreversible': 'This action is',
    'empresa.nombrePorDefecto': 'Company Name',
    'empresa.cortoPorDefecto': 'Company',
    'lp.academy.titular': 'The app is half of it. The other half is the trade.',
    'lp.academy.parrafo1': 'Raza Pro comes out of ',
    'lp.academy.parrafoMarca': 'Raza Perrona Academy',
    'lp.academy.parrafo2': ', the school founded by Aurelio Olivera. More than 500 business owners have trained there across twenty years in the trade: how to charge, how to close a deal, and how to lead people without losing your shirt.',
    'lp.academy.s1.titulo': 'Professional brand',
    'lp.academy.s1.texto': 'Logo, identity and everything that makes you look serious to a client.',
    'lp.academy.s2.titulo': 'Website',
    'lp.academy.s2.texto': 'Modern, built for the phone and ready to receive campaigns.',
    'lp.academy.s3.titulo': 'Training',
    'lp.academy.s3.texto': 'Learn to price right, close deals and lead your people.',
    'lp.academy.s4.titulo': 'Legal documents',
    'lp.academy.s4.texto': 'Contracts and letters that protect your money when a job goes sideways.',
    'lp.academy.s5.titulo': 'Advertising',
    'lp.academy.s5.texto': 'Google Ads, Facebook Ads and print that actually brings in calls.',
    'lp.academy.s6.titulo': 'Community',
    'lp.academy.s6.texto': 'Steady backup from people who already walked the road.',
    'lp.academy.programa.titulo': 'From Helper to Owner',
    'lp.academy.programa.texto': 'The program many of them started with: build your business in under ninety days. If you already know how to work hard, you have the hard part solved.',
    'lp.academy.programa.boton': 'See the program',

    'lp.tienda.titular': 'Documents that protect your money.',
    'lp.tienda.lead': 'Contracts and letters written for the trade, ready to use today. Pay once and they are yours.',
    'lp.tienda.enlace': 'See the whole store',
    'lp.tienda.d1.nombre': 'Residential contracts',
    'lp.tienda.d1.texto': 'For work on homes. Scope, payments and deadlines in writing.',
    'lp.tienda.d1.nota': 'Best seller',
    'lp.tienda.d2.nombre': 'Commercial contracts',
    'lp.tienda.d2.texto': 'For commercial jobs, with change-order clauses.',
    'lp.tienda.d3.nombre': 'Residential letters',
    'lp.tienda.d3.texto': 'Collections, late notices and job close-out.',
    'lp.tienda.d4.nombre': 'Commercial letters',
    'lp.tienda.d4.texto': 'Claims, extensions and dealing with general contractors.',

    'lp.faq.titular': 'What people ask us most.',
    'lp.faq.1.q': 'Is it really free to start?',
    'lp.faq.1.a': 'Yes. You create your account and work without putting in a card. You only pay once you are growing and need more users, unlimited AI or integrations.',
    'lp.faq.2.q': 'Do I need to know computers?',
    'lp.faq.2.a': 'No. If you can use WhatsApp, you can use Raza Pro. It is built for contractors, not office workers, and Raza AI walks you through it when you get stuck.',
    'lp.faq.3.q': 'Does it work on my phone?',
    'lp.faq.3.a': 'Yes, with nothing to install. Your people clock in from their phones and you check everything from wherever you are: phone, tablet or computer.',
    'lp.faq.4.q': 'Is my information safe?',
    'lp.faq.4.a': 'It lives on SOC 2 certified infrastructure and travels encrypted. Only you and the people you authorize see your business data.',
    'lp.faq.5.q': 'Is there support in Spanish?',
    'lp.faq.5.a': 'All support is in Spanish and from real people who understand the contracting business. By WhatsApp, by phone or by chat.',
    'lp.faq.6.q': 'Can I cancel whenever I want?',
    'lp.faq.6.a': 'Whenever you want, in one click. No contract, no penalty, and we refund the part of the month you did not use.',
    'lp.faq.duda': 'Still have a question?',
    'lp.faq.whatsapp': 'Ask us on WhatsApp',

    'lp.cierre.titular': 'All the way to the top, together.',
    'lp.cierre.lead': 'Hundreds of Latino contractors already run their business from here. Start today: it takes less time than a coffee.',
    'lp.cierre.whatsapp': 'Or message us on WhatsApp',
    'lp.cierre.nota': 'You start free and without a card. Cancel whenever you want. Your data lives on SOC 2 certified infrastructure and support comes from people who speak Spanish.',

    'lp.pie.descripcion': 'The Raza Perrona Academy app for Latino contractors in the United States.',
    'lp.pie.academy': 'Academy',
    'lp.pie.contacto': 'Contact',
    'lp.pie.a1': 'Main site',
    'lp.pie.a2': 'Document store',
    'lp.pie.a3': 'From Helper to Owner',
    'lp.pie.a4': 'Aurelio Olivera',
    'lp.pie.derechos': '© {anio} Raza Perrona Academy. All rights reserved.',

    'lp.acceso.bienvenido': 'Welcome',
    'lp.acceso.sub': 'Sign in with your Google account. We do not ask for a card.',
    'lp.acceso.google': 'Continue with Google',
    'lp.acceso.verificando': 'Verifying…',
    'lp.acceso.seguro': 'Secure sign-in',
    'lp.acceso.cerrar': 'Close',

    'lp.idioma.etiqueta': 'Language',
    'lp.idioma.es': 'Español',
    'lp.idioma.en': 'English',
    'lp.idioma.bienvenida.titulo': 'Which language do you prefer?',
    'lp.idioma.bienvenida.sub': 'You can change it any time from the menu up top.',
    'precios.tabla.vendedor': 'Vendor',
    'precios.tabla.unidad': 'Unit',
    'ajustesNeg.stat.recuperacionGastos': 'Overhead Recovery',
    'chat.voz.sinVoz': 'No speech detected. Try again.',
    'visitas.fotoAlt': 'Visit photo {n}',
    'cuadrillas.colorAria': 'Crew color {color}',
    'contratoServ.sinDireccionCorta': 'No address',
    'camara.abrirFotoDe': 'Open photo uploaded by {quien}',
    'camara.subidaPor': 'Uploaded by {quien} on',
    'facturas.vence': 'Due {fecha}',
    'facturas.descargarRecibo': 'Download receipt {id}',
    'propuesta.terminosGenericos': 'Standard business terms apply. Please contact us with any questions.',
    'cuenta.nombreActualizado': 'Name updated to "{nombre}" successfully across the system.',
    'admin.usuariosCuenta': { uno: '{n} user', otros: '{n} users' },
    'plantillas.borrada': 'Deleted "{nombre}"',
    'prospectos.convertir.mensaje': 'Convert {nombre} to a Client? This creates a permanent client record and removes the lead from this list.',
    'prospectos.yaEsCliente': '{nombre} is now a client.',
    'prospectos.errorGuardar': 'Could not save the lead. Please try again.',
    'rutas.abrirEnMaps': 'Open {cuadrilla}\'s route in Google Maps',
    'admin.borrarAria': 'Delete {quien}',
    'comun.quitarAria': 'Remove {que}',
    'comun.nombreObligatorio': 'Name is required',
    'app.errorGrave': 'Something went wrong',
    'ui.fotoAlt': 'Photo',
    'ajustes.burden.explicacion': 'Turn this on to replace the typed "Burden %" on each employee with an itemized stack — payroll taxes, insurance, and benefits.',
    'ajustes.burden.ayudaPatron': '% of wage matched by employer. Typical 3–6%.',
    'ajustes.burden.ayudaOtros': 'Catch-all for year-end bonuses, vehicle allowances, training, etc.',
    'panel.checklist.progreso': '{hechos} of {total} done — finish these so your estimates are accurate.',
    'direccion.buscadorNoDisponible': 'Address search unavailable (status: {estatus}). Check Google Cloud → Places API.',
    'prospectos.convertir.mensajeLargo': 'Convert {nombre} to a Client? This creates a permanent client record and marks the deal WON. The lead card clears off the pipeline 24 hours later — the details live on in Clients.',
    'direccion.buscadorNoDisponibleLargo': 'Address search unavailable (status: {estatus}). Check Google Cloud → Places API is enabled and the API key allows it.',
    'propuesta.terminosGenericos2': 'Standard business terms apply. Please contact us for more details.',
    'lp.alt.oficio': 'Carpenter nailing the timber frame of a new house, seen from below against the sky',
    'lp.alt.casco': 'Worker seen from behind in a yellow hard hat at the end of the day, jobsite lights blurred behind him',
    'lp.alt.cuadrilla': 'Crew of seven workers standing in front of a freshly formed concrete slab, seen from above',
    'lp.alt.cap.propuesta': 'A Raza Pro proposal as the client sees it: cover page with the job name, the contractor logo and the total',
    'lp.alt.cap.partidas': 'Line-item breakdown of the estimate with the project total and the accept or decline buttons',
    'lp.alt.cap.firma': 'Electronic signature window: the client signs with a finger and confirms they accept the estimate',
    'lp.alt.cap.reloj': 'Raza Pro time clock screen listing who is working right now, their role and their hours',
    'lp.alt.cap.mapa': 'Punch map: the spots where each worker clocked in and out during the day',
    'lp.alt.cap.rutas': 'Service routes screen with the map of the day and each crew stop in order, with addresses',
    'lp.alt.cap.parada': 'Detail of one stop: address, job progress, contract data and notes for the crew',
    'lp.alt.cap.ia': 'Raza AI answering inside the app: the contractor asks about their estimate pipeline and the AI replies',
    'panel.ingresos.titulo': 'Revenue',
    'mes.1': 'Jan',
    'mes.2': 'Feb',
    'mes.3': 'Mar',
    'mes.4': 'Apr',
    'mes.5': 'May',
    'mes.6': 'Jun',
    'mes.7': 'Jul',
    'mes.8': 'Aug',
    'mes.9': 'Sep',
    'mes.10': 'Oct',
    'mes.11': 'Nov',
    'mes.12': 'Dec',
    'panel.ingresos.semana': 'Week {n}',
    'rango.esteMes': 'This Month',
    'rango.esteAno': 'This Year',
    'rango.siempre': 'All Time',
    'rango.hoy': 'Today',
    'rango.ultimos7': 'Last 7 days',
    'propuesta.descargarFirmado': 'Download signed contract',
    'analitica.ingresosPorFrecuencia': 'Revenue by Frequency',
    'rutas.contratosActivos': 'Active contracts',
    'rutas.ingresoMensual': 'Monthly revenue',
    'rutas.verRuta': 'Get Route',
    'comun.markupPct': 'Markup %',
    'comun.markup': 'Markup',
    'preciolista.markup.delDial': 'from dial',
    'comun.descargar': 'Download',
    'comun.vistaPrevia': 'Preview',
    'comun.deshacer': 'Undo',
    'comun.activo': 'Active',
    'comun.subir': 'Upload',
    'comun.hoy': 'Today',
    'comun.ayer': 'Yesterday',
    'comun.enviado': 'Sent',
    'comun.agregar': 'Add',

    // ── Estimates ▸ Overhead (unified view) ─────────────────────
    'gastos.tab': 'Overhead',
    'gastos.titulo': 'Overhead & Pricing',
    'gastos.sub': 'Everything that decides your hourly price, in order and in one place.',
    'gastos.nav.titulo': 'The steps',
    'gastos.nav.sub': 'Top to bottom. Each step feeds the next.',
    'gastos.paso': 'Step {n}',
    'gastos.listo': 'Done',
    'gastos.faltante': 'Missing',

    // Headline numbers
    'gastos.stat.recuperacion': 'Overhead recovery',
    'gastos.stat.recuperacion.pista': 'Added to every man-hour',
    'gastos.stat.equilibrio': 'What an hour costs you',
    'gastos.stat.equilibrio.pista': 'No profit — just breaking even',
    'gastos.stat.meta': 'What you should charge',
    'gastos.stat.meta.pista': 'Includes {margen}% net profit',

    // Step 1 — Season & capacity
    'gastos.p1.titulo': 'Season & capacity',
    'gastos.p1.sub': 'How much your crew works per year, and where you operate. Everything else builds on this.',
    'gastos.p1.semanas': 'Working weeks per year',
    'gastos.p1.semanas.ayuda': 'Count only the weeks you actually work, not all 52.',
    'gastos.p1.dias': 'Days per week',
    'gastos.p1.horas': 'Hours per day',
    'gastos.p1.estado': 'State you operate in',
    'gastos.p1.estado.ayuda': 'Sets SUTA and workers\' comp only — those are set by state law.',
    'gastos.p1.estado.placeholder': 'Pick your state',
    'gastos.p1.oficio': 'Your trade',
    'gastos.p1.oficio.ayuda': 'Sets the payroll burden and the billable hours typical for your trade.',
    'gastos.p1.total': 'Season hours per person',
    'gastos.p1.total.pista': '{semanas} weeks × {dias} days × {horas} hours',

    // Step 2 — Your crew
    'gastos.p2.titulo': 'Your crew',
    'gastos.p2.sub': 'By position, not by name. One row can stand for a whole crew.',
    'gastos.p2.agregar': 'Add a position',
    'gastos.p2.dueno.pregunta': 'Are you working in the field too?',
    'gastos.p2.dueno.texto': 'If you don’t count what your hour is worth, your break-even comes out lower than it really is and you end up bidding below cost.',
    'gastos.p2.dueno.boton': 'Count me in',
    'gastos.p2.nombre': 'Name',
    'gastos.p2.nombre.placeholder': 'Who is it?',
    'gastos.p2.puesto': 'Position',
    'gastos.p2.puesto.placeholder': 'Pick a position',
    'gastos.p2.puesto.otro': 'Other — I’ll type it',
    'gastos.p2.puesto.propio': 'Position name',
    'gastos.puesto.general': 'General',
    'gastos.puesto.general.ayuda': 'Out on the job doing the work.',
    'gastos.puesto.foreman': 'Foreman',
    'gastos.puesto.foreman.ayuda': 'Runs the crew and works alongside it.',
    'gastos.puesto.office': 'Office',
    'gastos.puesto.office.ayuda': 'Admin and accounting. Does not bill hours.',
    'gastos.p2.cuantos': 'How many?',
    'gastos.p2.cuantos.ayuda': 'People in this position',
    'gastos.p2.tipoPago': 'How you pay them',
    'gastos.p2.porHora': 'Hourly',
    'gastos.p2.salario': 'Salary',
    'gastos.p2.pago': 'Hourly wage',
    'gastos.p2.pagoAnual': 'Annual salary',
    'gastos.p2.enCampo': 'Do they work in the field?',
    'gastos.p2.enCampo.si': 'Their hours get billed to customers',
    'gastos.p2.enCampo.no': 'Their cost goes to overhead, not the rate',
    'gastos.p2.carga': 'Payroll burden',
    'gastos.p2.carga.auto': 'Automatic',
    'gastos.p2.carga.manual': 'Manual',
    'gastos.p2.carga.propio': 'Use my own number',
    'gastos.p2.carga.ver': 'See where it comes from',
    'gastos.p2.carga.ocultar': 'Hide the breakdown',
    'gastos.p2.cobrables': 'Billable hours per year',
    'gastos.p2.cobrables.auto': 'Calculated from your trade',
    'gastos.p2.ajustar': 'Adjust',
    'gastos.p2.cerrarAjuste': 'Done',
    'gastos.p2.disponible': 'Available %',
    'gastos.p2.disponible.ayuda': 'What’s left after PTO, holidays, weather and training.',
    'gastos.p2.facturable': 'Billable %',
    'gastos.p2.facturable.ayuda': 'Of those available hours, the ones that land on a paying job.',
    'gastos.p2.costoHora': 'True cost of one billed hour',
    'gastos.p2.quitar': 'Remove this position',
    'gastos.p2.vacio.titulo': 'No positions yet',
    'gastos.p2.vacio.texto': 'Add the positions on your crew. One is enough to get real numbers.',

    // Step 3 — Overhead expenses
    'gastos.p3.titulo': 'Fixed expenses',
    'gastos.p3.sub': 'What you pay even in a week with no work at all.',
    'gastos.frecuencia.mensual': 'Monthly',
    'gastos.frecuencia.anual': 'Annual',

    // Step 4 — Equipment
    'gastos.p4.titulo': 'Equipment cost recovery',
    'gastos.p4.sub': 'What it costs per year to own and run your machines.',
    'gastos.p4.nombre': 'Machine',
    'gastos.p4.seguro': 'Insurance ($/yr)',
    'gastos.p4.depreciacion': 'Depreciation $',

    // Step 5 — Profit
    'gastos.p5.titulo': 'Profit & markup',
    'gastos.p5.sub': 'The only thing on this page that RAISES your price. Everything above is cost.',

    // Step 6 — Minimums
    'gastos.p6.titulo': 'Job minimums & rules',
    'gastos.p6.sub': 'The rules that protect your small jobs.',

    // Review
    'gastos.p7.titulo': 'Review',
    'gastos.p7.sub': 'Where every number came from, in case you want to check it.',
    'chat.rapido.hoy.etiqueta': 'What should I do today?',
    'chat.rapido.hoy.pista': 'Top 3 things to focus on',
    'chat.rapido.hoy.prompt': 'Look at my leads, follow-ups, schedule, and open estimates. Give me a simple numbered list of the 3 most important things to do today. Use plain language — no business jargon.',
    'chat.rapido.mensaje.etiqueta': 'Write a client message',
    'chat.rapido.mensaje.pista': 'Text or email you can copy & send',
    'chat.rapido.mensaje.prompt': 'Help me write a short, friendly message to a client (text or email). If you need a name or topic, ask one quick question first. Then give me something ready to copy and send.',
    'chat.rapido.dinero.etiqueta': 'How’s my money looking?',
    'chat.rapido.dinero.pista': 'Open jobs & what to chase',
    'chat.rapido.dinero.prompt': 'Explain my pipeline and open estimates in simple terms. How much potential work do I have, and what should I focus on closing? Keep it short and practical — no accounting jargon.',
    'chat.bienvenida.titulo': 'Need a hand?',
    'chat.bienvenida.cuerpo': 'Tap a button below — or type your question like you would text a friend.',
    'chat.entrada.placeholder': 'Type your question here…',
    'clienteDet.ia.seguimiento.prompt': 'Write a friendly follow-up text or email to {cliente} about their open work. Keep it short and professional.',
    'clienteDet.ia.resumen.prompt': 'Give me a quick summary of {cliente} — relationship status, open work, and anything I should remember before calling.',
    'estimado.constructor.contexto_focus': 'Estimate builder for client "{cliente}".{oficio}{alcance} {partidas} line items, total {total}, margin {margen}%. Help the contractor price this job, suggest missing line items, or draft proposal language.',
    'finanzas.tab.numeros': 'Numbers',
    'finanzas.tab.proyectos': 'Projects',
    'finanzas.tab.contratos': 'Contracts',
    'finanzas.tab.trabajos': 'All Work',
    'metricas.aviso.utilizacionAlta': '{quien} has utilization above 100% ({pct}%). Capped at 100% for calc.',
    'metricas.aviso.burdenBajo': {
        uno: 'One position has payroll burden under {piso}% — typical for the trade is 20–30% (FICA + workers comp + benefits).',
        otros: '{count} positions have payroll burden under {piso}% — typical for the trade is 20–30% (FICA + workers comp + benefits).',
    },
    'metricas.aviso.sueldoHoraRaro': '{quien} is set to ${monto} an hour. The usual range is ${min} to ${max} — could that be a day or week rate?',
    'metricas.aviso.sueldoAnualRaro': '{quien} is set to ${monto} a year. The usual range is ${min} to ${max} — could that be an hourly wage?',
    'metricas.aviso.gastoRaro': '“{nombre}” adds up to ${anual} a year ({frecuencia}). Check whether an annual figure went into a monthly row.',
    'metricas.aviso.tarifaAlta': 'Your cost works out to ${tarifa} an hour. Above ${max} is almost always a mistyped figure — check wages and expenses.',
    'metricas.aviso.tarifaBaja': 'Your cost works out to ${tarifa} an hour. Below ${min} will not cover the work — you are missing expenses.',
    'metricas.aviso.combustibleDoble': 'Fuel is counted twice: ${gastos} a year in fixed expenses and ${maquinaria} stored on your equipment. Add them together on the “Fuel (whole fleet)” line and zero out the machines.',
    'metricas.aviso.combustibleEnMaquina': 'You have ${maquinaria} a year of fuel stored on your equipment. It is still being counted, but move it to the “Fuel (whole fleet)” expense line — fuel now lives in one place.',
    'metricas.aviso.seguroDoble': 'You have ${gastos} a year of insurance in fixed expenses, and workers comp is already inside your payroll burden. If your policy bundles them, leave only the general liability part here.',
    'metricas.aviso.datoInvalido': 'The “{campo}” field on {quien} does not hold a valid number, so it is not being counted.',
    'metricas.aviso.sinVidaUtil': 'Equipment "{nombre}" has no useful life set — depreciation skipped.',
    'metricas.aviso.margenImposible': 'Target margin of {pct}% is impossible — keep it under {tope}%.',
    'metricas.aviso.sinEmpleados': 'Add at least one employee to compute a break-even rate. Without staff, the formula has no billable hours to divide by.',
    'metricas.aviso.sinHorasCobrables': 'You have employees but no billable hours. Check that at least one employee is marked billable with utilization above 0%.',
    'metricas.aviso.sinGastos': 'No overhead expenses entered. Most contractors have rent, insurance, fuel, and software costs — your break-even will be artificially low without them.',
    'metricas.aviso.equilibrioBajoSueldo': 'Break-even (${equilibrio}/hr) is below your average wage (${sueldo}/hr). Check burden % and overhead — something is missing.',
    'propuesta.ajustes.titulo': 'Proposal Setup',
    'propuesta.ajustes.sub': 'Workspace-wide proposal defaults',
    'propuesta.seccion.legal': 'Legal & Communications',
    'propuesta.seccion.pagos': 'Payments',
    'propuesta.seccion.contratos': 'Contracts',
    'propuesta.seccion.marca': 'Marketing & Branding',
    'legal.errorTerminos': 'Could not generate the terms. Please try again.',
    'legal.errorGarantia': 'Could not generate the warranty text. Please try again.',

    /* ══════════════════════════════════════════════════════════════════════
       CAMPAÑA DE FINANZAS · BLOQUES ANCLA (Ola 0 — 2026-08-10)
       El gemelo en inglés de los bloques del objeto ES. Mismas reglas:
       cada agente escribe SÓLO dentro del bloque que lleva su letra, y toda
       clave que exista allí tiene que existir aquí — `EN` es
       `Record<Clave, Texto>`, así que una huérfana rompe `tsc`.
       ══════════════════════════════════════════════════════════════════════ */

    /* ─── ANCLA A · INICIO ─── agente 1 · motor de cálculo ─── */

    /* CONTRACTS TAB · the label that lied, and the missing margin. */
    'contratos.dinero.cobradoAyuda': 'Paid invoices. This money actually came in.',
    'contratos.dinero.porFacturar': 'Worked, not invoiced',
    'contratos.dinero.porFacturarAyuda': {
        uno: '1 month of work nobody has invoiced',
        otros: '{meses} months of work nobody has invoiced',
    },
    'contratos.dinero.nota': 'The three figures above add up to {total}: that is what every route job you have done is worth, not what reached the bank. What reached the bank is the collected figure, and only that.',
    'contratos.margen.filaPorFacturar': '+{monto} to invoice',

    'contratos.margen.statCosto': 'What serving them costs',
    'contratos.margen.statCostoAyuda': 'Crew and overhead for one month of visits',
    'contratos.margen.statMargen': 'What you keep each month',
    'contratos.margen.statMargenSinTarifas': 'Set your labor rate in Settings and this tab will show what each route leaves you.',
    'contratos.margen.baseMedida': {
        uno: 'Timed on 1 of {rutas} active routes',
        otros: 'Timed on {medidas} of {rutas} active routes',
    },
    'contratos.margen.baseEstimada': 'Based on the minutes the contract claims: nobody has timed a visit yet.',
    'contratos.margen.visitasHechas': {
        uno: '1 visit completed',
        otros: '{visitas} visits completed',
    },
    'contratos.margen.colCosto': 'Monthly cost',
    'contratos.margen.colCostoAyuda': 'One visit’s minutes times your cost per man-hour, times the visits you run each month. It counts time on site: not travel, not materials.',
    'contratos.margen.colMargen': 'Leaves per month',
    'contratos.margen.colMargenAyuda': 'What you bill each month minus what it costs to go. This is what decides which route to keep — not which one is biggest.',
    'contratos.margen.minutosMedidos': {
        uno: '{minutos} min measured on 1 visit',
        otros: '{minutos} min measured on {visitas} visits',
    },
    'contratos.margen.minutosContrato': '{minutos} min the contract claims',
    'contratos.margen.estimado': 'estimated',
    'contratos.margen.estimadoAyuda': 'Nobody has timed this route. The cost comes from the minutes the contract claims, not from how long the crew really takes, so this margin is an idea, not a fact.',
    'contratos.margen.sinTarifas': 'Your business has no labor rate set, so there is no way to know what an hour of crew costs. It does not mean serving this route is free.',
    'contratos.margen.sinDuracion': 'no duration set',
    'contratos.margen.sinDuracionAyuda': 'This contract does not say how long a visit takes and nobody has timed one. With no minutes there is no cost to work out.',
    'contratos.margen.descartadasAyuda': {
        uno: '1 visit was left out: it was opened and closed within the same minute, which is a tap on a screen, not a visit.',
        otros: '{descartadas} visits were left out: they were opened and closed within the same minute, which is a tap on a screen, not a visit.',
    },
    'contratos.margen.nota': 'A route costs the minutes of the visit times your cost per man-hour, which already carries wages, burden and overhead. It leaves out materials and travel, so what you really keep is a little less.',
    'contratos.margen.notaSinDuracion': {
        uno: '1 active route has no duration recorded and has never been timed: it adds nothing to the cost above, so it shows up leaving more than it does.',
        otros: '{rutas} active routes have no duration recorded and have never been timed: they add nothing to the cost above, so they show up leaving more than they do.',
    },
    'contratos.movil.margen': 'margin',
    /* ─── ANCLA A · FIN ─── */

    /* ─── ANCLA B · INICIO ─── agente 2 · interfaz de Finanzas ─── */

    'finanzas.dato.sinCapturar': 'not logged',
    'finanzas.dato.sinCapturarAyuda': 'This is zero because nobody logged it, not because it cost nothing.',
    'finanzasNum.pl.materialesSinCapturar': 'No materials or expenses have been logged on your projects. The work cost something — it just was not written down.',
    'finanzasNum.libro.gastosSinCapturar': 'Not logged',
    'finanzasNum.libro.gastosSinCapturarAyuda': 'Not a single expense is logged on your projects. It is not that they were free — nobody has written them down.',
    'finanzasNum.libro.notaSinGastos': 'With no expenses logged, the gross profit above is the whole contract: there is nothing to subtract from it. Log the materials on each job and it will start to be a real profit.',

    'finanzasNum.promesa.titulo': 'What you promised when you sold it',
    'finanzasNum.promesa.subtitulo': {
        uno: 'The margin your estimate worked out for 1 job, the day you sold it.',
        otros: 'The margin your estimates worked out for {obras} jobs, the day you sold them.',
    },
    'finanzasNum.promesa.vendido': 'Sold for',
    'finanzasNum.promesa.costoCotizado': 'Quoted cost',
    'finanzasNum.promesa.ganancia': 'Promised profit',
    'finanzasNum.promesa.margen': 'Promised margin',
    'finanzasNum.promesa.bajoObjetivo': {
        uno: '1 job worth {monto} was sold below your {objetivo}% target. Working harder does not fix that one: it gets fixed when you quote.',
        otros: '{obras} jobs worth {monto} were sold below your {objetivo}% target. Working harder does not fix those: they get fixed when you quote.',
    },
    'finanzasNum.promesa.nota': 'This is what you said you would make, not what you made. Compare it with the ladder above: the gap between the two is what the work is costing you over and above the quote.',
    'finanzasNum.promesa.notaSinCotizar': {
        uno: 'This is what you said you would make, not what you made. There is also 1 job sold with no costed estimate behind it, so it stays out of this count.',
        otros: 'This is what you said you would make, not what you made. There are also {obras} jobs sold with no costed estimate behind them, so they stay out of this count.',
    },

    'finanzas.proyectos.sin_costo': 'not logged',
    'finanzas.proyectos.tooltip_sin_costo': 'This job has no materials and no hours logged against it. Net profit would come out equal to the full contract value, and that is not profit.',

    'finanzas.alcance.siempre': 'All time',
    'finanzas.alcance.ayudaSiempre': 'This card counts everything since you opened the business in the app. The picker above does not move it.',
    'finanzas.alcance.ayudaRango': 'This card does change with the picker above.',
    'finanzasNum.kpi.recurrenteAlcance': 'Service contracts active right now. The picker above does not move it.',
    'finanzasNum.kpi.sinCobrarAlcance': 'Project value with no payment recorded, all time. The picker above does not move it.',

    'finanzasNum.pl.tituloEjecutado': 'Profit and loss on work actually done',
    'finanzasNum.pl.subtituloEjecutado': 'Only jobs under way or finished. Signed work nobody has started yet sits on its own, right below.',

    'finanzasNum.pl.netaSinTarifas': 'Your business has no labor or overhead rate set yet. Without them there is no way to know what an hour of crew cost.',
    'finanzasNum.pl.netaSinHoras': 'Nobody has clocked hours against these jobs. With no hours, labor comes in at zero and net profit would come out equal to gross.',
    'finanzasNum.pl.netaSinCosto': 'These jobs have no expense and no hours logged against them. Net profit would come out equal to the whole contract, and that is not profit.',
    'finanzasNum.pl.netaSinDato': 'There is nothing to calculate net profit from yet. The reason is on the left.',
    'finanzasNum.pl.sinEjecutado': 'No job is under way or finished yet, so there is no work to make a profit on.',

    'finanzasNum.cartera.titulo': 'Signed work, not started yet',
    'finanzasNum.cartera.subtitulo': 'Accepted or scheduled work. It sits outside the statement above on purpose: nobody has moved a shovel, so there is no cost to subtract.',
    'finanzasNum.cartera.valor': 'Signed value',
    'finanzasNum.cartera.obras': 'Jobs',
    'finanzasNum.cartera.cobrado': 'Collected up front',
    'finanzasNum.cartera.nota': 'Whether this counts as profit the day it is signed or the day it is worked is your accountant’s call, not the app’s. Here it sits apart and named, which is true either way.',
    'finanzasNum.libro.conCartera': {
        uno: 'Projects only. Signed work not started yet is counted here too: 1 job worth {monto} with no cost to subtract.',
        otros: 'Projects only. Signed work not started yet is counted here too: {obras} jobs worth {monto} with no cost to subtract.',
    },

    'finanzasNum.mant.tituloLlano': 'Equipment maintenance',
    'finanzasNum.mant.sinReparaciones': 'No repair is logged yet, so there is nothing to compare against the budget.',
    'finanzasNum.mant.nota.sinNada': 'With not a single repair logged, this card cannot tell you whether you are doing well or badly. Log repairs under Inventory › Maintenance and the comparison will start to mean something.',

    'finanzas.confianza.titulo': 'These figures are missing the data that would make them true',
    'finanzas.confianza.resumen': {
        uno: 'One of your {total} jobs under way or finished has nothing to calculate a profit from: {monto} of billing.',
        otros: '{obras} of your {total} jobs under way or finished have nothing to calculate a profit from: {monto} of billing.',
    },
    'finanzas.confianza.resumenTodas': {
        uno: 'Your only job under way or finished has nothing to calculate a profit from: {monto} of billing.',
        otros: 'None of your {obras} jobs under way or finished has anything to calculate a profit from: {monto} of billing.',
    },
    'finanzas.confianza.sinTarifas': 'Your business has no labor rate set, so there is no way to know what an hour of crew cost.',
    'finanzas.confianza.sinTarifasAccion': 'Set up the rates',
    'finanzas.confianza.sinHoras': 'There are jobs with not a single hour clocked against them.',
    'finanzas.confianza.sinHorasDetalle': {
        uno: 'There is 1 job worth {monto} with a rate set and zero hours clocked: its net profit comes out identical to gross.',
        otros: 'There are {obras} jobs worth {monto} with a rate set and zero hours clocked: their net profit comes out identical to gross.',
    },
    'finanzas.confianza.sinHorasAccion': 'Go to the time clock',
    'finanzas.confianza.sinCosto': 'There are jobs with not a single expense logged: no materials, no equipment rental, no subs.',
    'finanzas.confianza.sinCostoAccion': 'Open the projects',

    'finanzasNum.horas.titulo': 'Where the hours went',
    'finanzasNum.horas.sub': {
        uno: '{horas} h clocked across 1 closed shift',
        otros: '{horas} h clocked across {turnos} closed shifts',
    },
    'finanzasNum.horas.vacioTitulo': 'No hours clocked yet',
    'finanzasNum.horas.vacioTexto': 'As soon as your crew starts clocking in, this is where you will see how many of those hours land on a job and how many do not.',
    'finanzasNum.horas.veredictoPoco': 'Most of your hours are not attributed to any job: only {pct}% land on one.',
    'finanzasNum.horas.veredictoBien': '{pct}% of your hours are attributed to a job.',
    'finanzasNum.horas.consecuencia': 'Hours with no job still get paid, but they add cost to nothing. That is why the labor cost above is incomplete and your profit looks better than it is.',
    'finanzasNum.horas.obra': 'On a job',
    'finanzasNum.horas.obraAyuda': 'The job was picked at clock-in. These are the only hours that add cost to any work today.',
    'finanzasNum.horas.huerfanas': 'On a job that no longer exists',
    'finanzasNum.horas.huerfanasAyuda': 'A job was picked at clock-in and that job was deleted afterwards.',
    'finanzasNum.horas.general': 'No job',
    'finanzasNum.horas.generalAyuda': 'Nobody picked a job at clock-in: shop, driving, service route, whatever it was.',
    'finanzasNum.horas.h': '{horas} h',
    'finanzasNum.horas.fichajesPct': {
        uno: '1 clock-in · {pct}%',
        otros: '{turnos} clock-ins · {pct}%',
    },
    'finanzasNum.horas.enRuta': 'Of those, {horas} h do know which service route they went to. They are not lost — job costing only knows how to ask about jobs.',
    'finanzasNum.horas.huerfanasNota': {
        uno: 'One clock-in worth {horas} h points at a deleted job. That is not a decision, it is broken data: somebody did attribute it and deleting the job lost it.',
        otros: '{turnos} clock-ins worth {horas} h point at deleted jobs. That is not a decision, it is broken data: somebody did attribute them and deleting the jobs lost it.',
    },
    'finanzasNum.horas.abiertos': {
        uno: 'There is 1 shift still running. It stays out of these figures until it closes.',
        otros: 'There are {turnos} shifts still running. They stay out of these figures until they close.',
    },

    'finanzasNum.gf.verDesglose': 'See what it is made of',
    'finanzasNum.gf.titulo': 'What keeping the doors open costs you, per year',
    'finanzasNum.gf.manoIndirecta': 'Payroll no client is billed for: {puesto}',
    'finanzasNum.gf.equipo': 'Trucks and equipment: depreciation, fuel and maintenance',
    'finanzasNum.gf.totalAnual': 'Total per year',
    'finanzasNum.gf.reparto': 'That total is spread across the {horas} billable hours you figure on a year: {tarifa} for every hour clocked.',
    'finanzasNum.gf.sinHorasCobrables': 'Your business has no billable hours figured yet, so this total is spread across nothing and the line above comes out at zero.',
    'finanzasNum.gf.cargado': 'Jobs under way or finished were charged for {horas} h clocked, and that is where the line above comes from.',
    'finanzasNum.gf.sinHoras': 'Nobody has clocked hours against these jobs, so none of this has been charged to any work.',
    'finanzasNum.gf.menosMant': 'The line above already had {monto} of budgeted maintenance taken out: it is counted below, with the repairs actually paid for.',

    /* ─── ANCLA B · FIN ─── */

    /* ─── ANCLA C · INICIO ─── agente 3 · captura de gastos ─── */
    /* ─── ANCLA C · FIN ─── */
    // ── Text that used to be hardcoded in the JSX ────────────────────────────
    'propuesta.compartir.pendientes.resto': {
        uno: ' thing left before you can send',
        otros: ' things left before you can send',
    },
    'propuesta.compartir.puedesenviar': {
        uno: 'You can send it — 1 thing missing',
        otros: 'You can send it — {count} things missing',
    },

    'agenda.cita.borrar.titulo': 'Delete this appointment?',
    'agenda.cita.borrar.mensaje': '“{titulo}” is removed from the calendar and cannot be recovered.',

    'estimado.salir.titulo': 'You have unsaved changes',
    'estimado.salir.mensaje': 'If you leave now you lose the line items and settings you just made. Cancel and hit Save to keep them.',
    'estimado.salir.confirmar': 'Leave without saving',

    'prospectos.convertir.faltadireccion.titulo': 'The address is missing',
    'prospectos.convertir.faltadireccion.mensaje': 'Before creating the client, type the property address for {nombre}. It is the one used for proposals and for planning routes.',
    'prospectos.convertir.faltadireccion.confirmar': 'Save and convert',
    'prospectos.convertir.faltadireccion.placeholder': 'Street address, city',
    'prospectos.convertir.faltadireccion.invalida': 'Type the complete street address, including the street number.',
    'prospectos.convertir.direccion.tambienEnCliente': 'Heads up: {nombre} already has this address, in Clients. If it is the same property, go ahead. If it is another home in the same building, add the apartment or unit number.',
    'prospectos.convertir.direccion.tambienEnProspecto': 'Heads up: {nombre} already has this address, in Leads. If it is the same property, go ahead. If it is another home in the same building, add the apartment or unit number.',

    'proyecto.estado.guardia.titulo': 'Change a {estado} project?',
    'proyecto.estado.guardia.mensaje': '“{nombre}” is marked {estado}. Move it to “{nuevo}”?',
    'proyecto.estado.guardia.confirmar': 'Yes, change status',
    'proyecto.estado.guardia.cancelar': 'Keep as is',

    'panel.errorCarga': 'We could not load your data',
    'contratoServ.faltaprecio': ' · price missing',

    'ajustes.burden.ayudaFica': 'What you pay into Social Security and Medicare for each employee. Set by the federal government; it cannot be changed.',
    'ajustes.burden.ayudaFuta': 'Federal unemployment tax. Also fixed.',
    'ajustes.burden.ayudaSuta': 'STATE unemployment tax. Your rate arrives by letter from your state every year and changes with your layoff history. Typical range is 1% to 6%.',
    'ajustes.burden.ayudaWc': 'Workers’ compensation insurance. The exact rate is on your policy; it depends on the trade (roofing and concrete pay a lot more than landscaping), the state, and your history.',

    'obra.titulo': 'Site Conditions',
    'obra.sub': 'Advisory checklist — only the Access Difficulty Multiplier below adjusts pricing',
    'obra.anchoAcceso': 'Gate / Access Width',
    'obra.pulgadas': 'Inches',
    'obra.avisoAccesoEstrecho': 'Tight access — hand-labor is slower; consider raising the Access Difficulty Multiplier below.',
    'obra.tipoSuelo': 'Soil Type',
    'obra.suelo.arena': 'Sand',
    'obra.suelo.arcilla': 'Clay',
    'obra.suelo.rocoso': 'Rocky',
    'obra.avisoArcilla': 'Clay soil — you may need geotextile and ~30% swell allowance; add those as line items.',
    'obra.distanciaAcarreo': 'Haul Distance',
    'obra.pies': 'Feet',
    'obra.avisoBomba': 'Consider Concrete Pump / Buggy',
    'obra.pendiente': 'Grade / Slope',
    'obra.avisoPendiente': 'Sloped grade — may require regrading; add it to your scope/line items.',
    'obra.clima': 'Weather',
    'obra.clima.frio': 'Cold',
    'obra.clima.normal': 'Normal',
    'obra.clima.calor': 'Hot',
    'obra.avisoCalor': 'Concrete sets fast — add retarder, more crew, schedule early',
    'obra.avisoFrio': 'Slow cure — heaters / blankets may be required',
    'obra.dificultad.estandar': 'Standard',
    'obra.dificultad.dificil': 'Hard',
    'obra.dificultad.extremo': 'Extreme',

    'propuesta.premium.terminos.titulo': 'Terms & conditions',
    'propuesta.premium.terminos.pordefecto': 'Standard business terms apply. Please contact us for more details.',
    'propuesta.premium.garantia.titulo': 'Warranty information',
    'propuesta.premium.zelle.titulo': 'Zelle payment accepted',
    'propuesta.premium.zelle.desc': 'You can conveniently pay via Zelle using the following details:',
    'propuesta.premium.banco.titulo': 'Bank transfer accepted',
    'propuesta.premium.banco.desc': 'You can send the payment straight from your bank with these details:',
    'propuesta.premium.banco.ahorros': ' · Savings',
    'propuesta.premium.banco.cheques': ' · Checking',
    'propuesta.premium.banco.numeros': 'Routing {ruta} · Account {cuenta}',

    'app.sesion.como': 'Signed in as',
    'prospectos.columna.vacia': 'Nothing here',
    'rutas.mapa.cargando': 'Loading map…',
    'calc.campo.titulo': 'Field Calculators',
    'calc.campo.sub': 'Quick takeoff math — pick a calculator, enter the measurements, and drop the result straight into your estimate.',
    'metricas.facturablePct': 'Billable %',
    'metricas.ajustesNegocio': 'Settings → Business',
    'metricas.sinHorasFacturables': 'Break-even cannot be calculated: there are no billable hours. In {donde}, mark at least one employee as billable and give them a {disponible} and a {facturable} above zero.',

    'equipo.tipo.minicargador': 'Skid Steer',
    'equipo.tipo.excavadora': 'Excavator',
    'equipo.tipo.podadora': 'Mower',
    'equipo.tipo.camion': 'Truck',
    'equipo.tipo.remolque': 'Trailer',
    'equipo.tipo.herramienta': 'Hand Tool (Gas/Electric)',
    'equipo.tipo.otro': 'Other',

    'equipo.estado.enreparacion': 'In Repair',
    'equipo.mant.tipo.aceite': 'Oil Change',
    'equipo.mant.tipo.reparacion': 'Repair',
    'equipo.mant.tipo.inspeccion': 'Inspection',
    'equipo.mant.tipo.otro': 'Other',
    'equipo.mant.mandartaller.cola': ' — it stays there until you mark it repaired.',

    'calc.patron.triangular': 'Triangular (denser)',
    'calc.patron.cuadrado': 'Square grid',

    'idioma.enfrase.es': 'Spanish',
    'idioma.enfrase.en': 'English',
    'propuesta.compartir.idioma.titulo': 'Proposal language',
    'propuesta.compartir.idioma.aviso': 'Your client will read it in {idioma}.',
    'propuesta.compartir.idioma.explica': 'Proposals always go out in English. What you wrote stays as it is until you translate it below.',
    'propuesta.compartir.idioma.escritaen': 'You wrote it in {idioma}.',
    'propuesta.compartir.idioma.traducir': 'Translate the content to {idioma}',
    'propuesta.compartir.idioma.traduciendo': 'Translating…',
    'propuesta.compartir.idioma.retraducir': 'Translate again',
    'propuesta.compartir.idioma.falta': 'The content is still in {idioma}: the title, the scope and the line items will reach your client that way.',
    'propuesta.compartir.idioma.obsoleta': 'You changed the content after translating it. Your client would read the old version.',
    'propuesta.compartir.idioma.lista': 'Content translated and reviewed.',
    'propuesta.compartir.idioma.original': 'Your original {idioma} text is untouched.',
    'propuesta.compartir.idioma.error': 'Could not translate. Please try again.',
    'propuesta.compartir.idioma.errorvacio': 'The translation came back empty. Please try again.',

    'propuesta.legal.pendiente': 'You still need to review the terms and the warranty.',
    'propuesta.legal.porque': 'A mistranslated clause changes what you are promising. Read them before sending.',
    'propuesta.legal.revisar': 'Review terms and warranty',
    'propuesta.legal.terminosnegocio': 'The terms your client will read come from Settings, exactly as you wrote them.',
    'propuesta.legal.terminosnegocio.porque': 'This proposal is written in {idioma} and the AI does not translate the terms on purpose: legal text has to read the same every time. If yours are not in English, your client will read them in {idioma}.',
    'propuesta.legal.terminosnegocio.ir': 'Review them in Settings',
    'propuesta.legal.titulo': 'Review the translated legal text',
    'propuesta.legal.intro': 'This is a commitment to your client, so the AI translates it but you approve it. You can fix anything right here.',
    'propuesta.legal.terminos': 'Terms & conditions',
    'propuesta.legal.garantia': 'Warranty',
    'propuesta.legal.original': 'What you wrote ({idioma})',
    'propuesta.legal.traducido': 'What your client will read ({idioma})',
    'propuesta.legal.aprobar': 'I have read it and I approve it',
    'propuesta.legal.aprobado': 'Approved',


    'contrato.idioma.etiqueta': 'Contract language',
    'contrato.idioma.ayuda': 'Not translated: it is the document you uploaded. Stored so we can warn you when it does not match the proposal.',
    'propuesta.contrato.desajuste': 'The proposal goes out in {propuesta} and the attached contract is in {contrato}.',
    'propuesta.contrato.desajuste.porque': 'Your client would read the proposal in one language and sign in another. Swap in a contract in {propuesta}, or send the proposal in {contrato}.',

    'ajustes.nav.oficina': 'Shop',
    'ajustes.oficina.titulo': 'Your shop',
    'ajustes.oficina.sub': 'The address your crew clocks in at when they are not on a job, and how far from it still counts as being at the shop.',
    'ajustes.oficina.cargando': 'Loading…',
    'ajustes.oficina.errorCargar': "Couldn't load your shop. Check your connection and try again.",
    'ajustes.oficina.nombre': 'What it is called',
    'ajustes.oficina.nombre.placeholder': 'Shop / Main Office',
    'ajustes.oficina.nombre.ayuda': 'This is what your crew sees when they pick where they are clocking in.',
    'ajustes.oficina.direccion': 'Address',
    'ajustes.oficina.direccion.placeholder': 'Type and pick one from the list',
    'ajustes.oficina.direccion.ayuda': 'Pick a suggestion: that way Google normalises the address and the geofence lands where it should.',
    'ajustes.oficina.buscar': 'Save',
    'ajustes.oficina.guardando': 'Saving…',
    'ajustes.oficina.radio': 'Geofence radius (feet)',
    'ajustes.oficina.radio.ayuda': 'Between {min} and {max} feet. Below {min} nobody ever clocks in inside it: a phone GPS is already off by tens of feet.',
    'ajustes.oficina.estado': 'Shop geofence',
    'ajustes.oficina.activa': 'Set',
    'ajustes.oficina.inactiva': 'Not set',
    'ajustes.oficina.activaCon': 'Set at: ',
    'ajustes.oficina.sinCoords': 'No coordinates yet: shop clock-ins are not measured against anything.',
    'ajustes.oficina.faltaLlave': 'The Google Maps key is missing, so the address cannot be located.',
    'ajustes.oficina.geoFallo': "That address couldn't be found, so nothing was saved. The shop geofence is exactly as it was.",
    'ajustes.oficina.sinGuardar': 'What is typed here is not the geofence yet. Press Save to locate this address.',
    'ajustes.oficina.guardado': 'Done. The shop geofence is now {radio} feet.',
    'ajustes.oficina.errorGuardar': "Couldn't save: {motivo}",
    'ajustes.oficina.actualizado': 'Last changed: {cuando}',
    'ajustes.oficina.actualizadoPor': 'Last changed: {cuando}, by {quien}',
    'ajustes.oficina.candado.titulo': 'Clock in only on assigned jobs',
    'ajustes.oficina.candado.etiqueta': 'Require the job to be assigned',
    'ajustes.oficina.candado.ayuda': 'With this on, anyone not assigned to a job cannot clock in to it. Only turn it on once your jobs have people assigned — otherwise your crew is left unable to clock in at all.',
    'ajustes.oficina.candado.inerte': 'This does nothing yet. Your choice is saved, but the check itself ships in a later release.',
    'ajustes.oficina.candado.guardado': 'Saved.',

    'reloj.transferir.SHIFT_NOT_FOUND': 'That shift isn’t there anymore. Refresh the screen and try again.',
    'reloj.transferir.SHIFT_NOT_OPEN': 'That shift is already closed. Clock in again to start a new one.',
    'reloj.transferir.NOT_YOUR_SHIFT': 'That shift belongs to someone else. You can only move your own.',
    'reloj.transferir.ON_LUNCH': 'End your lunch break first, then move your clock.',
    'reloj.transferir.ALREADY_ON_TARGET': 'You’re already clocked in there. Pick a different place to keep working.',
    'reloj.transferir.INVALID_TARGET_KIND': 'That destination can’t be used. Pick a job, a service route, or the shop.',
    'reloj.transferir.TARGET_NOT_FOUND': 'That job no longer exists. Pick another one, or move to shop time.',
    'reloj.transferir.NO_OFFICE_CONFIGURED': 'No shop address is set up yet. Ask your office to add one in Settings.',
    'reloj.transferir.NOT_ASSIGNED_TO_PROJECT': 'You’re not assigned to that job. Ask your foreman to add you.',
    'reloj.transferir.TRANSFER_BEFORE_CLOCK_IN': 'You can’t move your clock to a time before you clocked in. Check the time on your phone.',
    'reloj.transferir.TRANSFER_IN_FUTURE': 'Your phone’s clock is running ahead. Fix the time on it and try again.',
    'reloj.transferir.error': 'Couldn’t move your clock. You’re still clocked in where you were — try again.',
    'reloj.transferir.boton': 'Move my clock',
    'reloj.transferir.listo': 'Done. Your clock is now on {destino}.',
};

export const DICCIONARIOS = { es: ES, en: EN } as const;
export type Idioma = keyof typeof DICCIONARIOS;
